import {
  Activity,
  Archive,
  AtSign,
  ArrowLeft,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  ArrowUpDown,
  BarChart3,
  Bell,
  BellOff,
  BookOpen,
  Building2,
  CalendarDays,
  CalendarPlus,
  Camera,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  ClipboardList,
  Clock3,
  Contact,
  CreditCard,
  Download,
  ExternalLink,
  FileDown,
  FileText,
  Filter,
  Globe2,
  GitMerge,
  HelpCircle,
  Hospital,
  ImagePlus,
  Images,
  LayoutDashboard,
  LayoutGrid,
  KeyRound,
  LogIn,
  LogOut,
  Mail,
  MapPin,
  Menu,
  MessageSquareText,
  PencilLine,
  Paperclip,
  Phone,
  Plus,
  RefreshCw,
  ScanLine,
  Search,
  Save,
  ShieldCheck,
  Sparkles,
  Settings,
  SlidersHorizontal,
  StickyNote,
  Table2,
  Trash2,
  Upload,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { lazy, Suspense, type CSSProperties, type FormEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { calendarDaysSince } from "../shared/domain";
import { scoreContactDuplicate } from "../shared/duplicates";
import { individualLeadTrackingEnabled } from "../shared/features";
import { MIN_PASSWORD_LENGTH } from "../shared/security";
import {
  canAccessSource,
  canAssignTasks,
  canCollaborateOnSources,
  canCreateSources,
  canCreateTasks,
  canLogSourceActivity,
  canManageEvents,
  canViewAllSources,
  canViewTeamReports,
  workspaceRoleLabel,
  type WorkspaceRole,
} from "../shared/source-access";
import {
  type ContactNote,
  type ContactMethod,
  type ContactMethodType,
  type ContactPhoto,
  type DemoSource,
  type DemoTask,
  type LeadActivity,
  type LeadDetail,
  type LeadStatus,
  type LeadSummary,
  type ReferralReceipt,
  type SourceActivityRecord,
  type SourceAttachment,
  type SourceContact,
  type SourceNoteHistory,
} from "./data";
import { helpTopicById, helpTopics, helpTopicSearchText, type HelpTopic } from "./help-content";
import { PLAN_CATALOG, planForCode, type PlanCode } from "../shared/plans";
import {
  autoMapImportColumns,
  fileFingerprint,
  importFieldDefinitions,
  mapImportRows,
  prepareImportHeaders,
  readImportGrid,
  type ImportColumnMapping,
  type ImportRow,
} from "./import-file";

const mutationRequestKeys = new Map<string, string>();

function fetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const requestUrl = new URL(input instanceof Request ? input.url : String(input), window.location.origin);
  if (requestUrl.origin !== window.location.origin || !requestUrl.pathname.startsWith("/api/")) {
    return globalThis.fetch(input, init);
  }
  const headers = new Headers(input instanceof Request ? input.headers : undefined);
  new Headers(init.headers).forEach((value, name) => headers.set(name, value));
  headers.set("cache-control", "no-cache, no-store, max-age=0");
  headers.set("pragma", "no-cache");
  const method = (init.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase();
  let mutationSignature: string | null = null;
  if (method === "POST" && !requestUrl.pathname.startsWith("/api/auth/") && requestUrl.pathname !== "/api/signup") {
    const bodySignature = typeof init.body === "string" ? init.body : crypto.randomUUID();
    mutationSignature = `${requestUrl.pathname}\n${bodySignature}`;
    const key = mutationRequestKeys.get(mutationSignature) ?? crypto.randomUUID();
    mutationRequestKeys.set(mutationSignature, key);
    if (!headers.has("idempotency-key")) headers.set("idempotency-key", key);
  }
  const response = globalThis.fetch(input, { ...init, headers, cache: "no-store" });
  if (mutationSignature) {
    const completedSignature = mutationSignature;
    void response.then(() => mutationRequestKeys.delete(completedSignature), () => undefined);
  }
  return response;
}
import { VERTICAL_CATALOG, verticalForCode, type VerticalCode } from "../shared/verticals";

const GrowthCenter = lazy(() => import("./GrowthCenter"));

type View = "sources" | "contacts" | "leads" | "today" | "events" | "growth" | "reports" | "admin" | "platform" | "help";
const APP_HISTORY_MARKER = "referral-tracking-app";

function updateAppHistory(path: string, mode: "push" | "replace" = "push") {
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (mode === "push" && currentPath === path) {
    window.history.replaceState({ ...(window.history.state ?? {}), app: APP_HISTORY_MARKER }, "", path);
    return;
  }
  window.history[mode === "push" ? "pushState" : "replaceState"]({ app: APP_HISTORY_MARKER }, "", path);
}

function viewPath(view: View, parameters: Record<string, string | undefined> = {}) {
  const query = new URLSearchParams({ view });
  for (const [key, value] of Object.entries(parameters)) if (value) query.set(key, value);
  return `/?${query.toString()}`;
}
type AuthState = "loading" | "signed-out" | "live";
type SessionMember = {
  userId: string;
  name: string;
  avatarUrl: string | null;
  role: WorkspaceRole;
  workspaceId: string;
  impersonation: { administrator: { id: string; name: string } } | null;
  workspaceName: string;
  platformRole: "user" | "superadmin";
  organizationIndependent: boolean;
  planCode: PlanCode;
  seatLimit: number;
  subscriptionStatus: "beta" | "trialing" | "active" | "past_due" | "canceled";
  brandName: string;
  brandLogoUrl: string | null;
  brandPrimaryColor: string;
  brandAccentColor: string;
  verticalCode: VerticalCode;
  sourceLabelSingular: string;
  sourceLabelPlural: string;
  leadLabelSingular: string;
  leadLabelPlural: string;
  leadPipelineEnabled: boolean;
  leadContactDetailsEnabled: boolean;
  leadActivityTrackingEnabled: boolean;
  individualLeadTrackingEnabled: boolean;
};

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}
type SourceAssignee = {
  id: string;
  name: string;
  email: string;
  role: WorkspaceRole;
};
type AppNotification = {
  id: string;
  kind: "contact-note-mention" | "task-assigned" | "task-reassigned";
  sourceId?: string;
  sourceContactId?: string;
  taskId?: string;
  taskTitle?: string;
  taskDueAt?: string;
  taskPriority?: DemoTask["priority"];
  message: string;
  actorName: string;
  sourceName?: string;
  contactName?: string;
  readAt?: string;
  createdAt: string;
};
type ActiveLookupOption = {
  type: "source_category" | "activity_type" | "payer" | "acuity" | "channel" | "outcome";
  code: string;
  label: string;
};
type SourceActivity = {
  id: string;
  actor: string;
  entity: string;
  summary: string;
  occurredAt: string;
};
type LogContactInput = {
  activityTypeCode: string;
  occurredAt: string;
  summary: string;
  notes: string;
  sourceContactId?: string;
  eventId?: string;
  contextLabel?: string;
  referrals: Array<{ receivedByUserId: string; name: string }>;
};
type WorkspaceEvent = {
  id: string;
  sourceId?: string;
  sourceName?: string;
  networkingGroupId?: string;
  networkingGroupName?: string;
  title: string;
  location: string;
  occurredAt: string;
  description?: string;
  attendees: Array<{ id: string; name: string }>;
  sourceAttendees: Array<{ id: string; name: string; category?: string }>;
  contactAttendees: Array<{ id: string; name: string; title?: string; sourceId: string; sourceName?: string }>;
  createdBy: string;
  version: number;
  attachmentCount: number;
  coverPhotoId?: string;
};
type EventPhoto = {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  caption?: string;
  createdBy: string;
  createdAt: string;
  version: number;
};
type NetworkingGroupPhoto = EventPhoto & {
  eventId: string;
  eventTitle: string;
  eventLocation: string;
  eventOccurredAt: string;
};
type NetworkingGroupCadence = "weekly" | "monthly" | "quarterly" | "custom" | "irregular";
type NetworkingGroup = {
  id: string;
  name: string;
  description?: string;
  website?: string;
  primaryLocation?: string;
  cadenceType: NetworkingGroupCadence;
  cadenceDetails?: string;
  notes?: string;
  version: number;
  createdBy?: string;
  createdAt?: string;
  updatedAt: string;
  eventCount: number;
  upcomingEventCount: number;
  contactCount: number;
  lastMeetingAt?: string;
  nextMeetingAt?: string;
};
type NetworkingGroupContact = WorkspaceEvent["contactAttendees"][number] & {
  eventCount: number;
  lastSeenAt?: string;
};
type NetworkingGroupDetail = NetworkingGroup & { events: WorkspaceEvent[]; contacts: NetworkingGroupContact[]; photos: NetworkingGroupPhoto[] };
type DirectoryContact = {
  id: string;
  sourceId: string;
  sourceName: string;
  sourceCategory: string;
  ownerUserId?: string;
  ownerName?: string;
  organizations: Array<{
    sourceId: string;
    sourceName: string;
    sourceCategory: string;
    ownerUserId?: string;
    ownerName?: string;
    isPrimary: boolean;
  }>;
  name: string;
  title?: string;
  phone?: string;
  mobilePhone?: string;
  fax?: string;
  contactMethods: ContactMethod[];
  email?: string;
  address?: string;
  isPrimary: boolean;
  preferredContactMethod: SourceContact["preferredContactMethod"];
  bestContactTime?: string;
  contactStatus: SourceContact["contactStatus"];
  lastVerifiedAt?: string;
  primaryPhotoId?: string;
  photoCount?: number;
  version: number;
  updatedAt: string;
};
type ContactProfileEvent = {
  id: string;
  sourceId?: string;
  sourceName?: string;
  networkingGroupId?: string;
  networkingGroupName?: string;
  title: string;
  location: string;
  occurredAt: string;
  description?: string;
  attendees: Array<{ id: string; name: string }>;
  createdBy: string;
  attachmentCount: number;
};
type ContactProfile = DirectoryContact & {
  createdAt?: string;
  notes: ContactNote[];
  referralReceipts: ReferralReceipt[];
  events: ContactProfileEvent[];
  contactInteractions: SourceActivityRecord[];
  organizationActivities: SourceActivityRecord[];
  photos: ContactPhoto[];
};

function contactPhotoFromApi(row: Record<string, unknown>): ContactPhoto {
  const kind = String(row.kind ?? row.photoKind ?? "photo");
  return {
    id: String(row.id), contactId: String(row.contactId ?? row.sourceContactId ?? ""), sourceId: String(row.sourceId ?? ""),
    kind: kind === "business_card" ? "business_card" : "photo",
    contactName: row.contactName ? String(row.contactName) : undefined,
    contactTitle: row.contactTitle ? String(row.contactTitle) : undefined,
    fileName: String(row.fileName ?? "Contact photo"), contentType: String(row.contentType ?? "image/jpeg"),
    sizeBytes: Number(row.sizeBytes ?? 0), caption: row.caption ? String(row.caption) : undefined,
    createdBy: String(row.createdBy ?? "Team member"), createdAt: String(row.createdAt ?? ""), version: Number(row.version ?? 1),
  };
}

function sourceAttachmentFromApi(row: Record<string, unknown>): SourceAttachment {
  return {
    id: String(row.id),
    eventId: row.eventId ? String(row.eventId) : undefined,
    eventTitle: row.eventTitle ? String(row.eventTitle) : undefined,
    fileName: String(row.fileName ?? "Attachment"),
    contentType: String(row.contentType ?? "application/octet-stream"),
    sizeBytes: Number(row.sizeBytes ?? 0),
    caption: row.caption ? String(row.caption) : undefined,
    createdBy: String(row.createdBy ?? "Team member"),
    createdAt: String(row.createdAt ?? ""),
    version: Number(row.version ?? 1),
  };
}

function workspaceEventFromApi(row: Record<string, unknown>): WorkspaceEvent {
  const parsePeople = (value: unknown, fallback: string): Array<Record<string, unknown>> => {
    let rows: Array<Record<string, unknown>> = [];
    try {
      const parsed = typeof value === "string" ? JSON.parse(value) : value;
      if (Array.isArray(parsed)) rows = parsed as Array<Record<string, unknown>>;
    } catch { rows = []; }
    return rows.map((person) => ({ ...person, id: String(person.id), name: String(person.name ?? fallback) }));
  };
  const attendees = parsePeople(row.attendees ?? row.attendeesJson, "Team member")
    .map((person) => ({ id: String(person.id), name: String(person.name) }));
  const sourceAttendees = parsePeople(row.sourceAttendees ?? row.sourceAttendeesJson, "Referral source")
    .map((source) => ({ id: String(source.id), name: String(source.name), category: source.category ? String(source.category) : undefined }));
  const contactAttendees = parsePeople(row.contactAttendees ?? row.contactAttendeesJson, "Source contact")
    .map((contact) => ({
      id: String(contact.id), name: String(contact.name),
      title: contact.title ? String(contact.title) : undefined,
      sourceId: String(contact.sourceId ?? row.sourceId ?? ""),
      sourceName: contact.sourceName ? String(contact.sourceName) : undefined,
    }));
  return {
    id: String(row.id),
    sourceId: row.sourceId ? String(row.sourceId) : undefined,
    sourceName: row.sourceName ? String(row.sourceName) : undefined,
    networkingGroupId: row.networkingGroupId ? String(row.networkingGroupId) : undefined,
    networkingGroupName: row.networkingGroupName ? String(row.networkingGroupName) : undefined,
    title: String(row.title ?? "Untitled event"),
    location: String(row.location ?? "Location not recorded"),
    occurredAt: String(row.occurredAt ?? ""),
    description: row.description ? String(row.description) : undefined,
    attendees,
    sourceAttendees,
    contactAttendees,
    createdBy: String(row.createdBy ?? "Team member"),
    version: Number(row.version ?? 1),
    attachmentCount: Number(row.attachmentCount ?? 0),
    coverPhotoId: row.coverPhotoId ? String(row.coverPhotoId) : undefined,
  };
}

function networkingGroupFromApi(row: Record<string, unknown>): NetworkingGroup {
  const cadence = String(row.cadenceType ?? "irregular");
  return {
    id: String(row.id), name: String(row.name ?? "Networking group"),
    description: row.description ? String(row.description) : undefined,
    website: row.website ? String(row.website) : undefined,
    primaryLocation: row.primaryLocation ? String(row.primaryLocation) : undefined,
    cadenceType: (["weekly", "monthly", "quarterly", "custom", "irregular"].includes(cadence) ? cadence : "irregular") as NetworkingGroupCadence,
    cadenceDetails: row.cadenceDetails ? String(row.cadenceDetails) : undefined,
    notes: row.notes ? String(row.notes) : undefined,
    version: Number(row.version ?? 1), createdBy: row.createdBy ? String(row.createdBy) : undefined,
    createdAt: row.createdAt ? String(row.createdAt) : undefined,
    updatedAt: String(row.updatedAt ?? ""), eventCount: Number(row.eventCount ?? 0),
    upcomingEventCount: Number(row.upcomingEventCount ?? 0), contactCount: Number(row.contactCount ?? 0),
    lastMeetingAt: row.lastMeetingAt ? String(row.lastMeetingAt) : undefined,
    nextMeetingAt: row.nextMeetingAt ? String(row.nextMeetingAt) : undefined,
  };
}

function networkingGroupDetailFromApi(row: Record<string, unknown>): NetworkingGroupDetail {
  const events = (Array.isArray(row.events) ? row.events : []).map((event) => workspaceEventFromApi(event as Record<string, unknown>));
  const group = networkingGroupFromApi(row);
  const now = Date.now();
  const fallbackContacts = new Map<string, NetworkingGroupContact>();
  for (const event of events) for (const contact of event.contactAttendees) {
    const existing = fallbackContacts.get(contact.id);
    fallbackContacts.set(contact.id, {
      ...contact,
      eventCount: (existing?.eventCount ?? 0) + 1,
      lastSeenAt: !existing?.lastSeenAt || new Date(event.occurredAt).getTime() > new Date(existing.lastSeenAt).getTime()
        ? event.occurredAt
        : existing.lastSeenAt,
    });
  }
  const contacts = Array.isArray(row.contacts)
    ? row.contacts.map((contact) => {
        const record = contact as Record<string, unknown>;
        return {
          id: String(record.id), name: String(record.name ?? "Contact"),
          title: record.title ? String(record.title) : undefined,
          sourceId: String(record.sourceId ?? ""), sourceName: record.sourceName ? String(record.sourceName) : undefined,
          eventCount: Number(record.eventCount ?? 0), lastSeenAt: record.lastSeenAt ? String(record.lastSeenAt) : undefined,
        };
      })
    : [...fallbackContacts.values()];
  const photos = (Array.isArray(row.photos) ? row.photos : []).map((photo) => {
    const record = photo as Record<string, unknown>;
    return {
      id: String(record.id), eventId: String(record.eventId), eventTitle: String(record.eventTitle ?? "Group meeting"),
      eventLocation: String(record.eventLocation ?? "Location not recorded"), eventOccurredAt: String(record.eventOccurredAt ?? ""),
      fileName: String(record.fileName ?? "Event photo"), contentType: String(record.contentType ?? "image/jpeg"),
      sizeBytes: Number(record.sizeBytes ?? 0), caption: record.caption ? String(record.caption) : undefined,
      createdBy: String(record.createdBy ?? "Team member"), createdAt: String(record.createdAt ?? ""), version: Number(record.version ?? 1),
    };
  });
  return {
    ...group, events, contacts, photos, eventCount: events.length, contactCount: contacts.length,
    upcomingEventCount: events.filter((event) => new Date(event.occurredAt).getTime() > now).length,
    nextMeetingAt: events.find((event) => new Date(event.occurredAt).getTime() > now)?.occurredAt ?? group.nextMeetingAt,
    lastMeetingAt: [...events].reverse().find((event) => new Date(event.occurredAt).getTime() <= now)?.occurredAt ?? group.lastMeetingAt,
  };
}

function contactMethodsFromApi(row: Record<string, unknown>): ContactMethod[] {
  let contactMethods: ContactMethod[] = [];
  try {
    const raw = row.contactMethodsJson ?? row.contact_methods_json ?? row.contactMethods;
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (Array.isArray(parsed)) contactMethods = parsed.flatMap((method: Record<string, unknown>) => {
      const type = String(method.type ?? "other");
      const value = String(method.value ?? "").trim();
      if (!value || !["work", "direct", "mobile", "home", "fax", "other"].includes(type)) return [];
      return [{
        id: String(method.id ?? crypto.randomUUID()), type: type as ContactMethodType,
        label: String(method.label ?? type), value, isPrimary: Boolean(method.isPrimary), canText: Boolean(method.canText),
      }];
    });
  } catch { contactMethods = []; }
  const addLegacyMethod = (type: ContactMethodType, label: string, value: unknown, canText = false) => {
    const normalized = value ? String(value).trim() : "";
    if (!normalized || contactMethods.some((method) => method.value === normalized && method.type === type)) return;
    contactMethods.push({ id: `legacy-${type}`, type, label, value: normalized, isPrimary: contactMethods.length === 0, canText });
  };
  addLegacyMethod("work", "Work", row.phone);
  addLegacyMethod("mobile", "Mobile", row.mobilePhone ?? row.mobile_phone, true);
  addLegacyMethod("fax", "Fax", row.fax);
  if (contactMethods.length && !contactMethods.some((method) => method.isPrimary && method.type !== "fax")) {
    const firstCallable = contactMethods.find((method) => method.type !== "fax");
    if (firstCallable) firstCallable.isPrimary = true;
  }
  return contactMethods;
}

function directoryContactFromApi(row: Record<string, unknown>): DirectoryContact {
  const preferred = String(row.preferredContactMethod ?? "any");
  const status = String(row.contactStatus ?? "active");
  let organizations: DirectoryContact["organizations"] = [];
  try {
    const parsed = typeof row.organizationsJson === "string" ? JSON.parse(row.organizationsJson) : row.organizations;
    if (Array.isArray(parsed)) organizations = parsed.map((organization: Record<string, unknown>) => ({
      sourceId: String(organization.sourceId ?? ""), sourceName: String(organization.sourceName ?? "Organization"),
      sourceCategory: String(organization.sourceCategory ?? "Uncategorized"),
      ownerUserId: organization.ownerUserId ? String(organization.ownerUserId) : undefined,
      ownerName: organization.ownerName ? String(organization.ownerName) : undefined,
      isPrimary: Boolean(organization.isPrimary),
    })).sort((left, right) => Number(right.sourceId === String(row.sourceId)) - Number(left.sourceId === String(row.sourceId)) || left.sourceName.localeCompare(right.sourceName));
  } catch { organizations = []; }
  if (!organizations.length && row.sourceId) organizations = [{
    sourceId: String(row.sourceId), sourceName: String(row.sourceName ?? "Organization"),
    sourceCategory: String(row.sourceCategory ?? "Uncategorized"),
    ownerUserId: row.ownerUserId ? String(row.ownerUserId) : undefined,
    ownerName: row.ownerName ? String(row.ownerName) : undefined, isPrimary: Boolean(row.isPrimary),
  }];
  return {
    id: String(row.id),
    sourceId: String(row.sourceId),
    sourceName: String(row.sourceName ?? "Organization"),
    sourceCategory: String(row.sourceCategory ?? "Uncategorized"),
    ownerUserId: row.ownerUserId ? String(row.ownerUserId) : undefined,
    ownerName: row.ownerName ? String(row.ownerName) : undefined,
    organizations,
    name: String(row.name ?? "Contact"),
    title: row.title ? String(row.title) : undefined,
    phone: row.phone ? String(row.phone) : undefined,
    mobilePhone: row.mobilePhone || row.mobile_phone ? String(row.mobilePhone ?? row.mobile_phone) : undefined,
    fax: row.fax ? String(row.fax) : undefined,
    contactMethods: contactMethodsFromApi(row),
    email: row.email ? String(row.email) : undefined,
    address: row.address ? String(row.address) : undefined,
    isPrimary: Boolean(row.isPrimary),
    preferredContactMethod: (["any", "phone", "email", "text", "visit"].includes(preferred) ? preferred : "any") as DirectoryContact["preferredContactMethod"],
    contactStatus: (["active", "former", "unresponsive", "do-not-contact"].includes(status) ? status : "active") as DirectoryContact["contactStatus"],
    bestContactTime: row.bestContactTime ? String(row.bestContactTime) : undefined,
    lastVerifiedAt: row.lastVerifiedAt ? String(row.lastVerifiedAt) : undefined,
    primaryPhotoId: row.primaryPhotoId ? String(row.primaryPhotoId) : undefined,
    photoCount: Number(row.photoCount ?? 0),
    version: Number(row.version ?? 1),
    updatedAt: String(row.updatedAt ?? ""),
  };
}

function contactProfileFromApi(row: Record<string, unknown>): ContactProfile {
  const directory = directoryContactFromApi(row);
  const noteRows = Array.isArray(row.notes) ? row.notes as Array<Record<string, unknown>> : [];
  const receiptRows = Array.isArray(row.referralReceipts) ? row.referralReceipts as Array<Record<string, unknown>> : [];
  const eventRows = Array.isArray(row.events) ? row.events as Array<Record<string, unknown>> : [];
  const activityRows = Array.isArray(row.organizationActivities) ? row.organizationActivities as Array<Record<string, unknown>> : [];
  const interactionRows = Array.isArray(row.contactInteractions) ? row.contactInteractions as Array<Record<string, unknown>> : [];
  const photoRows = Array.isArray(row.photos) ? row.photos as Array<Record<string, unknown>> : [];
  const notes = noteRows.map((note): ContactNote => {
    let history: ContactNote["history"] = [];
    try {
      const parsed = typeof note.historyJson === "string" ? JSON.parse(note.historyJson) : note.historyJson;
      if (Array.isArray(parsed)) history = parsed.map((entry: Record<string, unknown>) => ({
        id: String(entry.id), noteText: String(entry.noteText ?? ""), pinned: Boolean(entry.pinned),
        changedBy: entry.changedBy ? String(entry.changedBy) : undefined, createdAt: String(entry.createdAt ?? ""),
      }));
    } catch { history = []; }
    return {
      id: String(note.id), sourceContactId: directory.id, noteText: String(note.noteText ?? ""),
      pinned: Boolean(note.pinned), version: Number(note.version ?? 1),
      createdBy: note.createdBy ? String(note.createdBy) : undefined,
      updatedBy: note.updatedBy ? String(note.updatedBy) : undefined,
      createdAt: String(note.createdAt ?? ""), updatedAt: String(note.updatedAt ?? ""), history,
    };
  });
  const events = eventRows.map((event): ContactProfileEvent => {
    let attendees: ContactProfileEvent["attendees"] = [];
    try {
      const parsed = typeof event.attendeesJson === "string" ? JSON.parse(event.attendeesJson) : event.attendeesJson;
      if (Array.isArray(parsed)) attendees = parsed.map((attendee: Record<string, unknown>) => ({ id: String(attendee.id), name: String(attendee.name ?? "Team member") }));
    } catch { attendees = []; }
    return {
      id: String(event.id), sourceId: event.sourceId ? String(event.sourceId) : undefined,
      sourceName: event.sourceName ? String(event.sourceName) : undefined,
      networkingGroupId: event.networkingGroupId ? String(event.networkingGroupId) : undefined,
      networkingGroupName: event.networkingGroupName ? String(event.networkingGroupName) : undefined,
      title: String(event.title ?? "Event"), location: String(event.location ?? "Location not recorded"),
      occurredAt: String(event.occurredAt ?? ""), description: event.description ? String(event.description) : undefined,
      attendees, createdBy: String(event.createdBy ?? "Team member"), attachmentCount: Number(event.attachmentCount ?? 0),
    };
  });
  return {
    ...directory,
    createdAt: row.createdAt ? String(row.createdAt) : undefined,
    notes,
    referralReceipts: receiptRows.map((receipt) => ({
      id: String(receipt.id), sourceContactId: directory.id, quantity: Number(receipt.quantity ?? 0),
      receivedOn: String(receipt.receivedOn ?? ""), recordedBy: receipt.recordedBy ? String(receipt.recordedBy) : undefined,
      createdAt: String(receipt.createdAt ?? ""),
    })),
    events,
    photos: photoRows.map(contactPhotoFromApi),
    contactInteractions: interactionRows.map((activity) => ({
      id: String(activity.id), activityTypeCode: String(activity.activityTypeCode ?? "note"),
      occurredAt: String(activity.occurredAt ?? ""), summary: String(activity.summary ?? "Interaction"),
      notes: activity.notes ? String(activity.notes) : undefined,
      actorName: activity.actorName ? String(activity.actorName) : undefined,
      eventId: activity.eventId ? String(activity.eventId) : undefined,
      eventTitle: activity.eventTitle ? String(activity.eventTitle) : undefined,
      contextLabel: activity.contextLabel ? String(activity.contextLabel) : undefined,
    })),
    organizationActivities: activityRows.map((activity) => ({
      id: String(activity.id), activityTypeCode: String(activity.activityTypeCode ?? "note"),
      sourceName: activity.sourceName ? String(activity.sourceName) : undefined,
      occurredAt: String(activity.occurredAt ?? ""), summary: String(activity.summary ?? "Relationship activity"),
      notes: activity.notes ? String(activity.notes) : undefined,
      actorName: activity.actorName ? String(activity.actorName) : undefined,
      eventId: activity.eventId ? String(activity.eventId) : undefined,
      eventTitle: activity.eventTitle ? String(activity.eventTitle) : undefined,
      contextLabel: activity.contextLabel ? String(activity.contextLabel) : undefined,
    })),
  };
}
type FollowUpItem = {
  id: string;
  entity: string;
  title: string;
  timing: "overdue" | "today" | "upcoming";
  dueAt: string;
  sortAt: number;
  task?: DemoTask;
  source?: DemoSource;
};
type CollectionView = "cards" | "table";
type SourceSortKey = "name" | "hospital" | "category" | "contact" | "last-contact" | "contact-age" | "next-contact" | "owner" | "health" | "referrals" | "priority";
type SortDirection = "ascending" | "descending";
type TableSortValue = string | number | boolean | null | undefined;
type HospitalFilter = "all" | "hospitals" | "non-hospitals";

function compareTableValues(left: TableSortValue, right: TableSortValue, direction: SortDirection): number {
  const leftBlank = left === null || left === undefined || left === "";
  const rightBlank = right === null || right === undefined || right === "";
  if (leftBlank !== rightBlank) return leftBlank ? 1 : -1;
  if (leftBlank && rightBlank) return 0;
  const comparison = typeof left === "number" && typeof right === "number"
    ? left - right
    : String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: "base" });
  return direction === "ascending" ? comparison : -comparison;
}

function useSortableTable<T, K extends string>(rows: T[], initialKey: K, valueFor: (row: T, key: K) => TableSortValue, initialDirection: SortDirection = "ascending") {
  const [sortKey, setSortKey] = useState<K>(initialKey);
  const [sortDirection, setSortDirection] = useState<SortDirection>(initialDirection);
  const sortedRows = rows.map((row, index) => ({ row, index })).sort((left, right) =>
    compareTableValues(valueFor(left.row, sortKey), valueFor(right.row, sortKey), sortDirection) || left.index - right.index,
  ).map(({ row }) => row);
  function changeSort(nextKey: K) {
    if (nextKey === sortKey) setSortDirection((current) => current === "ascending" ? "descending" : "ascending");
    else { setSortKey(nextKey); setSortDirection("ascending"); }
  }
  return { sortedRows, sortKey, sortDirection, changeSort };
}

function SortableTableHeader<K extends string>({ column, label, sortKey, sortDirection, onSort }: {
  column: K;
  label: string;
  sortKey: K;
  sortDirection: SortDirection;
  onSort: (column: K) => void;
}) {
  const active = sortKey === column;
  const nextDirection = active && sortDirection === "ascending" ? "descending" : "ascending";
  return <th aria-sort={active ? sortDirection : "none"}><button type="button" className={`sortable-heading${active ? " active" : ""}`} onClick={() => onSort(column)} title={`Sort ${label.toLowerCase()} ${nextDirection}`}>{label}{active ? sortDirection === "ascending" ? <ArrowUp /> : <ArrowDown /> : <ArrowUpDown />}</button></th>;
}
type BulkSourceChange = { category?: string; ownerUserId?: string; isHospital?: boolean };
type SourceSavedView = {
  id: string;
  name: string;
  search: string;
  category: string;
  hospitalFilter: HospitalFilter;
  owner: string;
  qualityOnly: boolean;
  sortKey: SourceSortKey;
  sortDirection: SortDirection;
};

function UserAvatar({ name, avatarUrl, className = "admin-avatar" }: { name: string; avatarUrl?: string | null; className?: string }) {
  const initials = name.split(/\s+/).filter(Boolean).map((word) => word[0]).join("").slice(0, 2).toUpperCase();
  return (
    <span className={`${className} user-avatar`} aria-label={`${name} profile photo`}>
      <span aria-hidden="true">{initials || "?"}</span>
      {avatarUrl && <img src={avatarUrl} alt="" onError={(event) => { event.currentTarget.hidden = true; }} />}
    </span>
  );
}

function ContactAvatar({ name, photoId, className }: { name: string; photoId?: string | null; className: string }) {
  return <span className={`${className} contact-photo-avatar`} aria-label={`${name} contact photo`}>
    <span>{name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase()}</span>
    {photoId && <img src={`/api/contact-photos/${encodeURIComponent(photoId)}/content`} alt="" onError={(event) => { event.currentTarget.hidden = true; }} />}
  </span>;
}

function ContextHelp({ topic, label = "Help" }: { topic: string; label?: string }) {
  return (
    <a
      className="context-help-button"
      href={`/?view=help&topic=${encodeURIComponent(topic)}`}
      target="_blank"
      rel="noreferrer"
      aria-label={`${label}: open related help`}
    >
      <HelpCircle />
      <span>{label}</span>
    </a>
  );
}

function useCollectionView(storageKey: string): [CollectionView, (view: CollectionView) => void] {
  const [view, setView] = useState<CollectionView>(() => window.localStorage.getItem(storageKey) === "cards" ? "cards" : "table");
  const updateView = (next: CollectionView) => {
    setView(next);
    window.localStorage.setItem(storageKey, next);
  };
  return [view, updateView];
}

function ViewToggle({ value, onChange, label }: { value: CollectionView; onChange: (view: CollectionView) => void; label: string }) {
  return (
    <div className="view-toggle" role="group" aria-label={label}>
      <button type="button" className={value === "table" ? "active" : ""} aria-pressed={value === "table"} onClick={() => onChange("table")}><Table2 />Table</button>
      <button type="button" className={value === "cards" ? "active" : ""} aria-pressed={value === "cards"} onClick={() => onChange("cards")}><LayoutGrid />Cards</button>
    </div>
  );
}

function formatSourceDate(value: unknown) {
  if (!value) return "Not recorded";
  const text = String(value);
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text;
  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: date.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
  });
}

function cadenceTypeDays(value: unknown): number {
  if (value === "daily") return 1;
  if (value === "weekly") return 7;
  return 30;
}

function cadenceLabel(days: number, usesDefault: boolean): string {
  return `${usesDefault ? "Default · " : ""}every ${days} day${days === 1 ? "" : "s"}`;
}

function referralPriorityLabel(priority: DemoSource["referralPriority"]): string {
  return priority[0].toUpperCase() + priority.slice(1);
}

function priorityFromCadenceValue(value: unknown): DemoSource["referralPriority"] {
  return ["daily", "weekly", "monthly"].includes(String(value))
    ? value as DemoSource["referralPriority"]
    : "other";
}

function cadenceForSourcePriority(priority: DemoSource["referralPriority"], customDays: number | null) {
  if (priority === "daily") return { cadenceType: "daily" as const, cadenceDays: 1 };
  if (priority === "weekly") return { cadenceType: "weekly" as const, cadenceDays: 7 };
  if (priority === "monthly") return { cadenceType: "monthly" as const, cadenceDays: null };
  return { cadenceType: "custom" as const, cadenceDays: customDays };
}

function effectiveDaysForSourcePriority(
  priority: DemoSource["referralPriority"],
  customDays: number,
  useDefault: boolean,
  defaultDays: number,
) {
  if (priority === "daily") return 1;
  if (priority === "weekly") return 7;
  if (priority === "monthly") return 30;
  return useDefault ? defaultDays : customDays;
}

function activityTypeLabel(value: string | undefined): string {
  if (!value) return "Interaction";
  if (value === "call" || value === "phone-call") return "Phone call";
  if (value === "legacy-outreach") return "Outreach";
  return value.replaceAll("-", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function activitySummary(activity: SourceActivityRecord): string {
  const label = activityTypeLabel(activity.activityTypeCode);
  const prefix = `${label} · `;
  return activity.summary.toLowerCase().startsWith(prefix.toLowerCase())
    ? `Logged by ${activity.summary.slice(prefix.length)}`
    : activity.summary;
}

function earliestDate(...values: unknown[]): string | undefined {
  const dates = values
    .filter(Boolean)
    .map(String)
    .filter((value) => !Number.isNaN(new Date(value).getTime()))
    .sort((left, right) => new Date(left).getTime() - new Date(right).getTime());
  return dates[0];
}

function demoTaskFromApi(row: Record<string, unknown>): DemoTask | null {
  const due = new Date(String(row.dueAt));
  if (Number.isNaN(due.getTime())) return null;
  const now = Date.now();
  const timing: DemoTask["timing"] =
    due.getTime() < now ? "overdue" : due.toDateString() === new Date().toDateString() ? "today" : "upcoming";
  return {
    id: String(row.id),
    sourceId: row.sourceId ? String(row.sourceId) : undefined,
    title: String(row.title),
    entity: String(row.sourceName ?? row.referralName ?? "General workspace"),
    entityType: row.sourceId ? "source" : "general",
    dueAt: due.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }),
    dueAtIso: due.toISOString(),
    timing,
    assignee: String(row.assigneeName ?? "Unassigned"),
    assigneeUserId: row.assigneeUserId ? String(row.assigneeUserId) : undefined,
    description: row.description ? String(row.description) : undefined,
    priority: ["low", "normal", "high", "urgent"].includes(String(row.priority)) ? row.priority as DemoTask["priority"] : "normal",
    recurrenceType: row.recurrenceType ? row.recurrenceType as DemoTask["recurrenceType"] : undefined,
    recurrenceDays: row.recurrenceDays ? Number(row.recurrenceDays) : undefined,
    version: Number(row.version ?? 1),
  };
}

function sourceFollowUps(tasks: DemoTask[], sources: DemoSource[], includeUpcoming = false): FollowUpItem[] {
  const dueTasks = includeUpcoming ? tasks : tasks.filter((task) => task.timing !== "upcoming");
  const taskSourceIds = new Set(
    tasks
      .map((task) => task.sourceId ?? sources.find((source) => source.name === task.entity)?.id)
      .filter((id): id is string => Boolean(id)),
  );
  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  return [
    ...dueTasks.map((task) => {
      const source = sources.find((item) => item.id === task.sourceId) ?? sources.find((item) => item.name === task.entity);
      return {
        id: task.id,
        entity: task.entity,
        title: task.title,
        timing: task.timing,
        dueAt: task.dueAt,
        sortAt: new Date(task.dueAtIso).getTime(),
        task,
        source,
      };
    }),
    ...sources
      .filter((source) => {
        if (!source.nextContactAt || taskSourceIds.has(source.id)) return false;
        const nextContactAt = new Date(source.nextContactAt).getTime();
        return !Number.isNaN(nextContactAt) && nextContactAt <= endOfToday.getTime();
      })
      .map((source) => {
        const next = new Date(source.nextContactAt as string);
        return {
          id: `cadence_${source.id}`,
          entity: source.name,
          title: `Scheduled every ${source.effectiveCadenceDays} day${source.effectiveCadenceDays === 1 ? "" : "s"}`,
          timing: next.getTime() < startOfToday.getTime() ? "overdue" as const : "today" as const,
          dueAt: formatSourceDate(source.nextContactAt),
          sortAt: next.getTime(),
          source,
        };
      }),
  ].sort((left, right) => left.sortAt - right.sortAt);
}

function sourceQualityIssues(source: DemoSource): string[] {
  const issues: string[] = [];
  if (source.contact === "No contact added") issues.push("No individual contact");
  if (!source.contactPhone && !source.contactEmail && !source.organizationPhone) issues.push("No phone or email");
  if (!source.category || source.category === "Uncategorized") issues.push("No category");
  if (!source.ownerUserId) issues.push("No owner");
  if (!source.description && !source.notes) issues.push("No relationship notes");
  return issues;
}

function relationshipHealth(source: DemoSource): { code: "healthy" | "attention" | "stale" | "new"; label: string; rank: number } {
  if (source.activityCount === 0) return { code: "new", label: "New relationship", rank: 2 };
  const nextContact = source.nextContactAt ? new Date(source.nextContactAt).getTime() : Number.NaN;
  if (Number.isNaN(nextContact)) return { code: "attention", label: "Needs scheduling", rank: 1 };
  const daysLate = Math.floor((Date.now() - nextContact) / 86_400_000);
  if (daysLate >= 30) return { code: "stale", label: "Relationship stale", rank: 0 };
  if (daysLate >= 0) return { code: "attention", label: "Needs attention", rank: 1 };
  return { code: "healthy", label: "On track", rank: 3 };
}

function sourceContactAge(source: DemoSource): { days: number | null; label: string; status: "current" | "due" | "stale" | "never" } {
  const days = calendarDaysSince(source.lastInteractionAtIso);
  if (days === null) return { days, label: "Never", status: "never" };
  if (days > source.effectiveCadenceDays) return { days, label: `${days} days`, status: "stale" };
  if (days >= source.effectiveCadenceDays) return { days, label: days === 1 ? "1 day" : `${days} days`, status: "due" };
  return { days, label: days === 0 ? "Today" : days === 1 ? "1 day" : `${days} days`, status: "current" };
}

function readSavedSourceViews(): SourceSavedView[] {
  try {
    const parsed = JSON.parse(window.localStorage.getItem("referral-source-saved-views") ?? "[]") as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is Omit<SourceSavedView, "hospitalFilter"> & { hospitalFilter?: HospitalFilter } => Boolean(
      item && typeof item === "object" && "id" in item && "name" in item
      && typeof item.id === "string" && typeof item.name === "string",
    )).map((item) => ({
      ...item,
      hospitalFilter: ["hospitals", "non-hospitals"].includes(item.hospitalFilter ?? "") ? item.hospitalFilter! : "all",
    }));
  } catch {
    return [];
  }
}

function dateInputValue(daysFromToday: number): string {
  const date = new Date();
  date.setDate(date.getDate() + daysFromToday);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function normalizeWebsite(value: string): string | null {
  const website = value.trim();
  if (!website) return null;
  return /^[a-z][a-z\d+.-]*:\/\//i.test(website) ? website : `https://${website}`;
}

function apiContacts(contacts: SourceEditorContact[]) {
  return contacts.map((contact) => ({
    ...(contact.id ? { id: contact.id } : {}),
    name: contact.name,
    title: contact.title || null,
    phone: contact.phone || null,
    mobilePhone: contact.mobilePhone || null,
    fax: contact.fax || null,
    contactMethods: contact.contactMethods,
    email: contact.email || null,
    address: contact.address || null,
    isPrimary: contact.isPrimary,
    preferredContactMethod: contact.preferredContactMethod,
    bestContactTime: contact.bestContactTime || null,
    contactStatus: contact.contactStatus,
    lastVerifiedAt: contact.lastVerifiedAt || null,
  }));
}

const nav: Array<{ id: View; label: string; icon: typeof LayoutDashboard }> = [
  { id: "sources", label: "Referral sources", icon: Building2 },
  { id: "contacts", label: "Contacts", icon: Contact },
  { id: "leads", label: "Individual leads", icon: ClipboardList },
  { id: "today", label: "Tasks", icon: CalendarDays },
  { id: "events", label: "Events", icon: Users },
  { id: "growth", label: "Growth Operations", icon: Sparkles },
  { id: "reports", label: "Reports", icon: BarChart3 },
  { id: "admin", label: "Admin", icon: SlidersHorizontal },
  { id: "platform", label: "Platform", icon: ShieldCheck },
  { id: "help", label: "Help Center", icon: BookOpen },
];

function canViewAppSection(
  role: WorkspaceRole,
  view: View,
  platformRole: SessionMember["platformRole"] = "user",
  leadTrackingEnabled = true,
  organizationIndependent = false,
): boolean {
  if (view === "help") return true;
  if (organizationIndependent) return view === "platform";
  if (view === "platform") return platformRole === "superadmin";
  if (view === "admin") return role === "admin";
  if (view === "leads") return leadTrackingEnabled && role !== "task_user";
  if (role === "task_user") return view === "sources" || view === "contacts" || view === "today";
  if (role === "reports") return view === "sources" || view === "contacts" || view === "events" || view === "growth" || view === "reports";
  return true;
}

function AppMark() {
  return (
    <div className="app-mark">
      <span className="mark-glyph" aria-hidden="true"><i /><i /><i /></span>
      <span><strong>Referral Source</strong><small>Tracker</small></span>
    </div>
  );
}

function TenantMark({ name, logoUrl }: { name: string; logoUrl: string | null }) {
  return <div className="tenant-mark">
    {logoUrl
      ? <span className="tenant-logo"><img src={logoUrl} alt={`${name} logo`} /></span>
      : <span className="tenant-monogram" aria-hidden="true">{name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase()}</span>}
    <span><strong>{name}</strong><small>Referral Tracking</small></span>
  </div>;
}

function readableForeground(hex: string): "#FFFFFF" | "#211428" {
  const value = hex.replace("#", "");
  if (!/^[0-9A-Fa-f]{6}$/.test(value)) return "#FFFFFF";
  const [red, green, blue] = [0, 2, 4].map((offset) => Number.parseInt(value.slice(offset, offset + 2), 16));
  return (red * 299 + green * 587 + blue * 114) / 1000 > 156 ? "#211428" : "#FFFFFF";
}

function MarketingSite() {
  return (
    <main className="marketing-site">
      <header className="marketing-nav">
        <AppMark />
        <nav aria-label="Marketing navigation">
          <a href="#capabilities">Capabilities</a>
          <a href="#plans">Plans</a>
          <a href="/?login=1">Sign in</a>
          <a className="marketing-nav-cta" href="/?signup=1">Start free beta <ArrowRight /></a>
        </nav>
      </header>

      <section className="marketing-hero">
        <div className="marketing-hero-copy">
          <p className="marketing-kicker"><Sparkles /> Now accepting beta organizations</p>
          <h1>Relationships drive growth.<br /><em>Keep every one moving.</em></h1>
          <p className="marketing-lede">Referral Tracking gives teams one clear place to remember every source, plan every follow-up, record every interaction, and see which relationships create results.</p>
          <div className="marketing-hero-actions">
            <a className="marketing-primary" href="/?signup=1">Create your workspace <ArrowRight /></a>
            <a className="marketing-secondary" href="#capabilities">See how it works</a>
          </div>
          <div className="marketing-proof"><span><Check />No card during beta</span><span><Check />Private organization workspace</span><span><Check />Built for any referral-driven team</span></div>
        </div>
        <div className="marketing-product" aria-label="Referral Tracking product preview">
          <div className="product-window-bar"><i /><i /><i /><span>Relationship command center</span></div>
          <div className="product-preview-grid">
            <aside><AppMark /><b>Today</b><span>Sources</span><span>Tasks</span><span>Events</span><span>Reports</span></aside>
            <section>
              <header><div><small>WEDNESDAY</small><strong>Good morning, Jordan.</strong></div><button>+ Add source</button></header>
              <div className="preview-metrics"><article><small>FOLLOW-UPS DUE</small><strong>8</strong><i /></article><article><small>RELATIONSHIPS ACTIVE</small><strong>42</strong><i /></article><article><small>REFERRALS THIS MONTH</small><strong>17</strong><i /></article></div>
              <div className="preview-list"><header><strong>Relationship pulse</strong><span>Last contact</span></header>{[
                ["Northstar Realty", "Partner", "2 days"], ["Harbor Legal Group", "Professional", "6 days"], ["Main Street Collective", "Community", "Today"],
              ].map(([name, category, days], index) => <article key={name}><i>{index + 1}</i><span><strong>{name}</strong><small>{category}</small></span><b>{days}</b><em /></article>)}</div>
            </section>
          </div>
          <div className="product-float-card"><span><CheckCircle2 /></span><div><small>FOLLOW-UP COMPLETED</small><strong>Northstar Realty</strong><p>Visit recorded · next touch scheduled</p></div></div>
        </div>
      </section>

      <section className="marketing-category-strip"><span>Built for relationship-led growth in</span><div><b>Professional services</b><b>Home services</b><b>Real estate</b><b>Education</b><b>Hospitality</b><b>Healthcare</b></div></section>

      <section className="marketing-capabilities" id="capabilities">
        <header><p className="marketing-kicker">One operating rhythm</p><h2>From “we should follow up”<br />to <em>already done.</em></h2><p>Replace scattered spreadsheets, private notes, and forgotten promises with a shared system your whole organization can trust.</p></header>
        <div className="capability-grid">
          <article className="capability-feature"><span>01</span><div className="capability-icon"><Building2 /></div><h3>Know every source</h3><p>Keep organizations, individual contacts, addresses, notes, documents, priorities, and relationship owners together.</p><div className="mini-source"><b>Harbor Legal Group</b><small>Weekly priority · owned by Maya</small><i><span /></i></div></article>
          <article><span>02</span><div className="capability-icon"><CalendarDays /></div><h3>Never lose the next step</h3><p>Turn outreach cadence into daily work, assign tasks, snooze responsibly, and keep managers ahead of overdue follow-ups.</p></article>
          <article><span>03</span><div className="capability-icon"><Activity /></div><h3>Log what actually happened</h3><p>Record calls, emails, visits, meetings, marketing, and events—plus the referrals each interaction generated.</p></article>
          <article><span>04</span><div className="capability-icon"><BarChart3 /></div><h3>See the relationships that work</h3><p>Compare activity, coverage, referrals, task execution, and user performance with filterable and exportable reporting.</p></article>
          <article><span>05</span><div className="capability-icon"><Users /></div><h3>Coordinate without collisions</h3><p>Mention teammates, share source notes, assign ownership, track event attendance, and preserve a complete audit trail.</p></article>
        </div>
      </section>

      <section className="marketing-security">
        <div><p className="marketing-kicker"><ShieldCheck /> Designed for accountable teams</p><h2>Your organization’s work stays in your organization.</h2><p>Every operational record is scoped to a private tenant workspace. Role-based access, authenticated files, audit history, and platform-level administration are built into the foundation.</p></div>
        <aside><article><ShieldCheck /><span><strong>Tenant isolation</strong><small>Organization-scoped data access</small></span></article><article><FileText /><span><strong>Private attachments</strong><small>Documents stored in Cloudflare R2</small></span></article><article><Activity /><span><strong>Auditable changes</strong><small>Accountability without guesswork</small></span></article></aside>
      </section>

      <section className="marketing-plans" id="plans">
        <header><p className="marketing-kicker">Simple capacity plans</p><h2>Start in beta. Grow when your team does.</h2><p>Every beta workspace includes the full product with no monthly fee and no card required.</p></header>
        <div>{Object.values(PLAN_CATALOG).map((plan) => <article className={plan.code === "team" ? "featured" : ""} key={plan.code}>{plan.code === "team" && <em>Most popular</em>}<small>{plan.name}</small><h3>{plan.seatLabel}</h3><p>{plan.description}</p><strong>$0 <span>/ month during beta</span></strong><a href={`/?signup=1&plan=${plan.code}`}>Choose {plan.name} <ArrowRight /></a></article>)}</div>
      </section>

      <section className="marketing-final"><div><p className="marketing-kicker">The next referral starts with the last follow-up.</p><h2>Give every valuable relationship a clear next step.</h2></div><a href="/?signup=1">Start your free beta workspace <ArrowRight /></a></section>
      <footer className="marketing-footer"><AppMark /><p>Referral Tracking is a relationship operations platform for teams that grow through trust.</p><div><a href="/?login=1">Sign in</a><a href="/?signup=1">Create workspace</a><span>© {new Date().getFullYear()} Referral Tracking · A product of <a href="https://foundrypeak.com">Foundry Peak, LLC</a></span></div></footer>
    </main>
  );
}

function Signup() {
  const parameters = new URLSearchParams(window.location.search);
  const requestedPlan = parameters.get("plan") ?? "team";
  const [organizationName, setOrganizationName] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [planCode, setPlanCode] = useState<PlanCode>(requestedPlan in PLAN_CATALOG ? requestedPlan as PlanCode : "team");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const response = await fetch("/api/signup", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ organizationName, name, email, password, planCode, acceptedTerms }),
      });
      const result = await response.json().catch(() => ({})) as { error?: string };
      if (!response.ok) throw new Error(result.error ?? "Your workspace could not be created.");
      const signInResponse = await fetch("/api/auth/sign-in/email", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password, callbackURL: "/" }),
      });
      if (!signInResponse.ok) throw new Error("Your workspace is ready. Please sign in to continue.");
      window.location.assign("/");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Your workspace could not be created.");
    } finally {
      setBusy(false);
    }
  }

  return <main className="signup-shell">
    <a className="signup-back" href="/"><ArrowLeft />Back to Referral Tracking</a>
    <section className="signup-copy"><AppMark /><p className="marketing-kicker">Free beta access</p><h1>Build a referral habit your whole team can follow.</h1><p>Create a private organization workspace in less than a minute. No credit card is collected during beta.</p><div><span><Check />Full source and contact tracking</span><span><Check />Tasks, events, notes, files, and reporting</span><span><Check />Role-based team access</span><span><Check />No patient or consumer data required</span></div></section>
    <form className="signup-form" onSubmit={submit}>
      <header><small>CREATE YOUR WORKSPACE</small><h2>Tell us about your team.</h2><p>You’ll be the first organization administrator.</p></header>
      <label>Organization name<input required minLength={2} maxLength={120} value={organizationName} onChange={(event) => setOrganizationName(event.target.value)} placeholder="Northstar Partners" autoFocus /></label>
      <div className="signup-form-row"><label>Your name<input required minLength={2} maxLength={120} value={name} onChange={(event) => setName(event.target.value)} placeholder="Jordan Lee" /></label><label>Work email<input required type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="jordan@company.com" /></label></div>
      <label>Password<input required type="password" autoComplete="new-password" minLength={MIN_PASSWORD_LENGTH} maxLength={128} value={password} onChange={(event) => setPassword(event.target.value)} placeholder={`${MIN_PASSWORD_LENGTH}+ characters`} /></label>
      <fieldset><legend>Team capacity</legend><div className="signup-plan-grid">{Object.values(PLAN_CATALOG).map((plan) => <label className={planCode === plan.code ? "selected" : ""} key={plan.code}><input type="radio" name="plan" value={plan.code} checked={planCode === plan.code} onChange={() => setPlanCode(plan.code)} /><span><strong>{plan.name}</strong><small>{plan.seatLabel}</small></span><b>$0 beta</b></label>)}</div><p>{planForCode(planCode).description}</p></fieldset>
      <label className="signup-terms"><input required type="checkbox" checked={acceptedTerms} onChange={(event) => setAcceptedTerms(event.target.checked)} /><span>I agree to use Referral Tracking for appropriate business relationship information and accept the beta service terms.</span></label>
      {error && <div className="form-error"><CircleAlert />{error}</div>}
      <button className="marketing-primary" disabled={busy}>{busy ? "Creating workspace…" : "Create free beta workspace"}<ArrowRight /></button>
      <p className="signup-signin">Already have an account? <a href="/?login=1">Sign in</a></p>
    </form>
  </main>;
}

function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const response = await fetch("/api/auth/sign-in/email", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, password, callbackURL: "/" }),
      });
      if (!response.ok) {
        const result = (await response.json()) as { message?: string };
        throw new Error(result.message ?? "We couldn’t sign you in.");
      }
      window.location.reload();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "We couldn’t sign you in.");
    } finally {
      setBusy(false);
    }
  }

  async function requestReset() {
    if (!email) {
      setError("Enter your email address first.");
      return;
    }
    setError("");
    setResetBusy(true);
    try {
      const response = await fetch("/api/auth/request-password-reset", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          redirectTo: `${window.location.origin}/?reset=1`,
        }),
      });
      if (!response.ok) {
        setError(response.status === 429 ? "Too many reset requests. Please wait a few minutes and try again." : "We couldn’t send the password email. Please try again.");
        return;
      }
      setResetSent(true);
    } catch {
      setError("We couldn’t send the password email. Please try again.");
    } finally {
      setResetBusy(false);
    }
  }

  return (
    <main className="signin-shell">
      <section className="signin-panel">
        <form className="signin-form" onSubmit={submit}>
          <div className="signin-brand"><AppMark /></div>
          <p className="eyebrow">Welcome back</p>
          <h2>Keep your sources close.</h2>
          <p className="muted">Track who you know, when you last connected, and who needs a follow-up.</p>
          <label>
            Work email
            <input required autoComplete="email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@organization.org" />
          </label>
          <label>
            Password
            <input required minLength={MIN_PASSWORD_LENGTH} autoComplete="current-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder={`${MIN_PASSWORD_LENGTH} characters minimum`} />
          </label>
          {error && <div className="form-error"><CircleAlert size={16} />{error}</div>}
          <button className="button primary wide" disabled={busy}>
            {busy ? "Opening workspace…" : "Sign in"} <ArrowRight size={17} />
          </button>
          <button className="text-button" type="button" onClick={requestReset} disabled={resetBusy}>{resetBusy ? "Sending secure link…" : "Forgot your password?"}</button>
          {resetSent && <div className="form-success"><CheckCircle2 size={16} />Check your email for a secure password link.</div>}
        </form>
      </section>
    </main>
  );
}

function SetPassword({ token, errorCode }: { token: string | null; errorCode: string | null }) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState(errorCode ? "This password link is invalid or has expired." : "");
  const [complete, setComplete] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!token) {
      setError("This password link is invalid or has expired.");
      return;
    }
    if (password !== confirmation) {
      setError("The passwords do not match.");
      return;
    }
    setBusy(true);
    setError("");
    const response = await fetch("/api/auth/reset-password", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ newPassword: password, token }),
    });
    setBusy(false);
    if (!response.ok) {
      setError("This password link is invalid or has expired.");
      return;
    }
    window.history.replaceState({}, "", "/");
    setComplete(true);
  }

  return (
    <main className="signin-shell">
      <section className="signin-panel">
        <form className="signin-form" onSubmit={submit}>
          <div className="signin-brand"><AppMark /></div>
          <p className="eyebrow">Secure account setup</p>
          <h2>{complete ? "Password saved." : "Choose your password"}</h2>
          {complete ? (
            <>
              <p className="muted">Your Referral Source Tracker admin account is ready.</p>
              <button className="button primary wide" type="button" onClick={() => window.location.reload()}>
                Continue to sign in <ArrowRight size={17} />
              </button>
            </>
          ) : (
            <>
              <p className="muted">Use at least {MIN_PASSWORD_LENGTH} characters. A short phrase is easier to remember and harder to guess.</p>
              <label>New password<input required minLength={MIN_PASSWORD_LENGTH} autoComplete="new-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
              <label>Confirm password<input required minLength={MIN_PASSWORD_LENGTH} autoComplete="new-password" type="password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></label>
              {error && <div className="form-error"><CircleAlert size={16} />{error}</div>}
              <button className="button primary wide" disabled={busy || Boolean(errorCode)}>
                {busy ? "Saving…" : "Set password"} <ArrowRight size={17} />
              </button>
            </>
          )}
        </form>
      </section>
    </main>
  );
}

function Sidebar({
  view,
  onView,
  mobileOpen,
  onClose,
  memberRole,
  memberName,
  memberAvatarUrl,
  platformRole,
  brandName,
  brandLogoUrl,
  leadTrackingEnabled,
  sourceLabelPlural,
  leadLabelPlural,
  organizationIndependent,
  onLogout,
  logoutBusy,
}: {
  view: View;
  onView: (view: View) => void;
  mobileOpen: boolean;
  onClose: () => void;
  memberRole: WorkspaceRole;
  memberName: string;
  memberAvatarUrl?: string | null;
  platformRole: SessionMember["platformRole"];
  brandName: string;
  brandLogoUrl: string | null;
  leadTrackingEnabled: boolean;
  sourceLabelPlural: string;
  leadLabelPlural: string;
  organizationIndependent: boolean;
  onLogout: () => void;
  logoutBusy: boolean;
}) {
  return (
    <aside className={`sidebar ${mobileOpen ? "open" : ""}`}>
      <div className="sidebar-head">
        <TenantMark name={brandName} logoUrl={brandLogoUrl} />
        <button className="icon-button mobile-only" onClick={onClose} aria-label="Close menu"><X /></button>
      </div>
      <nav aria-label="Primary navigation">
        <p className="nav-label">{organizationIndependent ? "Platform operations" : "Source relationships"}</p>
        {nav.filter((item) => item.id !== "help" && canViewAppSection(memberRole, item.id, platformRole, leadTrackingEnabled, organizationIndependent)).map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              className={view === item.id ? "active" : ""}
              onClick={() => {
                onView(item.id);
                onClose();
              }}
            >
              <Icon size={18} strokeWidth={1.8} />
              <span>{item.id === "sources" ? sourceLabelPlural : item.id === "leads" ? leadLabelPlural : item.label}</span>
            </button>
          );
        })}
        <p className="nav-label support-label">Support</p>
        {nav.filter((item) => item.id === "help").map((item) => {
          const Icon = item.icon;
          return <button key={item.id} className={view === item.id ? "active" : ""} onClick={() => { onView(item.id); onClose(); }}><Icon size={18} strokeWidth={1.8} /><span>{item.label}</span></button>;
        })}
      </nav>
      <div className="sidebar-foot">
        <div className="workspace-chip">
          <UserAvatar name={memberName} avatarUrl={memberAvatarUrl} className="avatar clay" />
          <span><strong>{memberName}</strong><small>{organizationIndependent ? "Platform superadmin" : workspaceRoleLabel(memberRole)}</small></span>
        </div>
        <button className="logout-button" type="button" onClick={onLogout} disabled={logoutBusy}>
          <LogOut size={16} />
          <span>{logoutBusy ? "Signing out…" : "Sign out"}</span>
        </button>
        <p><span className="status-dot" /> All changes saved</p>
      </div>
    </aside>
  );
}

function Topbar({
  view,
  onMenu,
  onCreateContact,
  onCreateSource,
  onNotice,
  notifications,
  onOpenNotification,
  canCreateContact,
  canCreateSource,
  onHelp,
  brandName,
  sourceLabelSingular,
  sourceLabelPlural,
  leadLabelPlural,
  freshness,
}: {
  view: View;
  onMenu: () => void;
  onCreateContact: () => void;
  onCreateSource: () => void;
  onNotice: (message: string) => void;
  notifications: AppNotification[];
  onOpenNotification: (notification: AppNotification) => void;
  canCreateContact: boolean;
  canCreateSource: boolean;
  onHelp: () => void;
  brandName: string;
  sourceLabelSingular: string;
  sourceLabelPlural: string;
  leadLabelPlural: string;
  freshness: "syncing" | "current" | "reconnecting";
}) {
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const [notificationState, setNotificationState] = useState<"unsupported" | "off" | "on" | "blocked">("off");
  const [showNotifications, setShowNotifications] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const addMenuRef = useRef<HTMLDivElement | null>(null);
  const unreadNotifications = notifications.filter((notification) => !notification.readAt).length;

  useEffect(() => {
    const handleInstall = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handleInstall);
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      setNotificationState("unsupported");
    } else if (Notification.permission === "denied") {
      setNotificationState("blocked");
    } else {
      void navigator.serviceWorker.ready.then((registration) => registration.pushManager.getSubscription())
        .then((subscription) => setNotificationState(subscription ? "on" : "off"));
    }
    return () => window.removeEventListener("beforeinstallprompt", handleInstall);
  }, []);

  useEffect(() => {
    setShowAddMenu(false);
  }, [view]);

  useEffect(() => {
    if (!showAddMenu) return;
    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (!addMenuRef.current?.contains(event.target as Node)) setShowAddMenu(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setShowAddMenu(false);
    };
    document.addEventListener("pointerdown", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [showAddMenu]);

  function applicationServerKey(value: string) {
    const padding = "=".repeat((4 - value.length % 4) % 4);
    const raw = atob((value + padding).replaceAll("-", "+").replaceAll("_", "/"));
    return Uint8Array.from(raw, (character) => character.charCodeAt(0));
  }

  async function toggleNotifications() {
    if (notificationState === "unsupported") return onNotice("Notifications are not supported in this browser.");
    if (notificationState === "blocked") return onNotice("Allow notifications for Referral Source Tracker in your browser settings first.");
    try {
      const registration = await navigator.serviceWorker.ready;
      const current = await registration.pushManager.getSubscription();
      if (current) {
        await fetch("/api/push/subscriptions", { method: "DELETE", headers: { "content-type": "application/json" }, body: JSON.stringify({ endpoint: current.endpoint }) });
        await current.unsubscribe();
        setNotificationState("off");
        onNotice("Weekday notifications turned off.");
        return;
      }
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setNotificationState(permission === "denied" ? "blocked" : "off");
        return;
      }
      const keyResponse = await fetch("/api/push/vapid-public-key");
      if (!keyResponse.ok) throw new Error("Notifications are not configured yet.");
      const keyResult = await keyResponse.json() as { data: { publicKey: string } };
      const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: applicationServerKey(keyResult.data.publicKey) });
      const response = await fetch("/api/push/subscriptions", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(subscription.toJSON()) });
      if (!response.ok) throw new Error("The notification subscription could not be saved.");
      setNotificationState("on");
      onNotice("Weekday follow-up notifications are on.");
    } catch (error) {
      onNotice(error instanceof Error ? error.message : "Notifications could not be changed.");
    }
  }
  const names: Record<View, string> = {
    sources: sourceLabelPlural,
    contacts: "Contacts",
    leads: leadLabelPlural,
    today: "Tasks",
    events: "Events",
    growth: "Growth Operations",
    reports: "Source reports",
    admin: "Administration",
    platform: "Platform operations",
    help: "Help Center",
  };
  return (
    <header className="topbar">
      <button className="icon-button mobile-only" onClick={onMenu} aria-label="Open menu"><Menu /></button>
      <div><small className="topbar-tenant-name">{brandName}</small><h2>{names[view]}</h2></div>
      <div className="topbar-actions">
        <span className={`live-data-status ${freshness}`} title={freshness === "current" ? "Workspace data is current" : freshness === "syncing" ? "Loading newer workspace data" : "Waiting to reconnect and check for newer data"}><i />{freshness === "current" ? "Live data" : freshness === "syncing" ? "Refreshing" : "Reconnecting"}</span>
        {installPrompt && <button className="button ghost pwa-action" onClick={() => void installPrompt.prompt().then(() => setInstallPrompt(null))}><Download size={16} />Install</button>}
        <button className={`button ghost pwa-action ${notificationState === "on" ? "enabled" : ""}`} onClick={() => void toggleNotifications()} title="Weekday follow-up notifications">
          {notificationState === "on" ? <Bell size={16} /> : <BellOff size={16} />}<span>{notificationState === "on" ? "Alerts on" : "Alerts"}</span>
        </button>
        <div className="mention-notifications">
          <button type="button" className={`button ghost icon-only${unreadNotifications ? " has-unread" : ""}`} onClick={() => { setShowAddMenu(false); setShowNotifications((current) => !current); }} aria-label={`${unreadNotifications} unread notifications`} aria-expanded={showNotifications}>
            <Bell size={17} />{unreadNotifications > 0 && <em>{unreadNotifications > 9 ? "9+" : unreadNotifications}</em>}
          </button>
          {showNotifications && <div className="mention-popover"><header><strong>Notifications</strong><small>{unreadNotifications ? `${unreadNotifications} unread` : "You're caught up"}</small></header>{notifications.length ? notifications.slice(0, 10).map((notification) => <button type="button" key={notification.id} className={notification.readAt ? "read" : ""} onClick={() => { setShowNotifications(false); onOpenNotification(notification); }}><span>{notification.taskId ? <ClipboardList /> : <AtSign />}</span><div><strong>{notification.actorName}</strong><p>{notification.message}</p>{notification.taskDueAt && <small className="notification-task-meta">Due {formatSourceDate(notification.taskDueAt)} · {notification.taskPriority}</small>}<small>{formatSourceDate(notification.createdAt)}</small></div></button>) : <div className="mention-empty"><MessageSquareText /><strong>No notifications yet</strong><small>Task assignments and team mentions will appear here.</small></div>}</div>}
        </div>
        {view !== "help" && <button type="button" className="button ghost icon-only topbar-help" onClick={onHelp} aria-label={`Help with ${names[view]}`} title={`Help with ${names[view]}`}><HelpCircle size={18} /></button>}
        {(canCreateContact || canCreateSource) && <div className="topbar-add" ref={addMenuRef}>
          <button type="button" className="button primary topbar-add-trigger" onClick={() => { setShowNotifications(false); setShowAddMenu((current) => !current); }} aria-label="Add" aria-haspopup="menu" aria-expanded={showAddMenu}>
            <Plus size={17} /><span>Add</span><ChevronDown className="topbar-add-chevron" />
          </button>
          {showAddMenu && <div className="topbar-add-menu" role="menu" aria-label="Add new">
            {canCreateContact && <button type="button" role="menuitem" onClick={() => { setShowAddMenu(false); onCreateContact(); }}><span><Contact /></span><span><strong>Contact</strong><small>Add a person to your address book</small></span></button>}
            {canCreateSource && <button type="button" role="menuitem" onClick={() => { setShowAddMenu(false); onCreateSource(); }}><span><Building2 /></span><span><strong>{sourceLabelSingular}</strong><small>Add an organization and its contacts</small></span></button>}
          </div>}
        </div>}
      </div>
    </header>
  );
}

const helpCategories: HelpTopic["category"][] = ["Start here", "Daily work", "Reporting", "Account", "Administration"];

function HelpCenter({ selectedTopicId, onSelectTopic }: { selectedTopicId: string; onSelectTopic: (topicId: string) => void }) {
  const [query, setQuery] = useState("");
  const selectedTopic = helpTopicById.get(selectedTopicId) ?? helpTopics[0];
  const normalizedQuery = query.trim().toLowerCase();
  const results = normalizedQuery
    ? helpTopics.filter((topic) => helpTopicSearchText(topic).includes(normalizedQuery))
    : helpTopics;

  function selectTopic(topicId: string) {
    setQuery("");
    onSelectTopic(topicId);
    window.requestAnimationFrame(() => document.querySelector(".help-article")?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }

  return (
    <div className="page help-page">
      <section className="help-hero">
        <div className="help-hero-copy"><span><BookOpen /></span><div><p className="eyebrow">Referral Source Tracker guide</p><h1>How can we help?</h1><p>Search by a button, field, workflow, or question.</p></div></div>
        <label className="help-search"><Search /><input autoFocus={false} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Try “days since contact,” “avatar,” or “PDF”" aria-label="Search help" />{query && <button type="button" onClick={() => setQuery("")} aria-label="Clear help search"><X /></button>}</label>
        <div className="help-quick-links" aria-label="Popular help"><span>Popular:</span><button onClick={() => selectTopic("sources")}>Source list</button><button onClick={() => selectTopic("log-contact")}>Log contact</button><button onClick={() => selectTopic("reports")}>Reports</button><button onClick={() => selectTopic("roles-privacy")}>Privacy</button></div>
      </section>

      <div className="help-layout">
        <aside className="help-directory" aria-label="Help topics">
          <header><span>Guidebook</span><strong>{helpTopics.length} topics</strong></header>
          {helpCategories.map((category) => {
            const topics = results.filter((topic) => topic.category === category);
            if (!topics.length) return null;
            return <section key={category}><h2>{category}</h2>{topics.map((topic) => <button type="button" className={!normalizedQuery && topic.id === selectedTopic.id ? "active" : ""} key={topic.id} onClick={() => selectTopic(topic.id)}><span>{topic.title}</span><ChevronRight /></button>)}</section>;
          })}
          {normalizedQuery && !results.length && <div className="help-no-results"><Search /><strong>No matching topic</strong><p>Try a button label or shorter phrase.</p></div>}
        </aside>

        {normalizedQuery ? (
          <main className="help-search-results" aria-live="polite">
            <header><p className="eyebrow">Search results</p><h1>{results.length} {results.length === 1 ? "topic" : "topics"} for “{query.trim()}”</h1><p>Select a result to see every related field and action.</p></header>
            <div>{results.map((topic) => <button type="button" key={topic.id} onClick={() => selectTopic(topic.id)}><span>{topic.category}</span><h2>{topic.title}</h2><p>{topic.summary}</p><footer>Read topic <ArrowRight /></footer></button>)}</div>
            {!results.length && <section className="help-empty-result"><HelpCircle /><h2>We couldn’t find that wording.</h2><p>Try “task,” “contact,” “event,” “attachment,” “report,” “password,” or “role.”</p><button className="button ghost" onClick={() => setQuery("")}>Browse all topics</button></section>}
          </main>
        ) : (
          <main className="help-article" tabIndex={-1}>
            <header className="help-article-head"><span>{selectedTopic.category}</span><h1>{selectedTopic.title}</h1><p>{selectedTopic.summary}</p></header>
            <nav className="help-on-page" aria-label="On this page"><strong>On this page</strong>{selectedTopic.sections.map((section, index) => <a href={`#help-section-${selectedTopic.id}-${index}`} key={section.title}>{section.title}</a>)}</nav>
            <div className="help-article-body">
              {selectedTopic.sections.map((section, index) => (
                <section id={`help-section-${selectedTopic.id}-${index}`} key={section.title}>
                  <span className="help-section-number">{String(index + 1).padStart(2, "0")}</span>
                  <div><h2>{section.title}</h2>{section.body && <p className="help-section-body">{section.body}</p>}
                    {section.items && <dl>{section.items.map((item) => <div key={item.label}><dt>{item.label}</dt><dd>{item.detail}</dd></div>)}</dl>}
                    {section.steps && <ol>{section.steps.map((step) => <li key={step}>{step}</li>)}</ol>}
                    {section.callout && <aside className="help-callout"><CircleAlert /><p>{section.callout}</p></aside>}
                  </div>
                </section>
              ))}
            </div>
            <section className="help-related"><header><span>Keep learning</span><h2>Related topics</h2></header><div>{selectedTopic.related.map((topicId) => { const topic = helpTopicById.get(topicId); return topic ? <button type="button" key={topic.id} onClick={() => selectTopic(topic.id)}><BookOpen /><span><strong>{topic.title}</strong><small>{topic.category}</small></span><ChevronRight /></button> : null; })}</div></section>
          </main>
        )}
      </div>
    </div>
  );
}

function SectionTitle({ title, action }: { title: string; action?: ReactNode }) {
  return <div className="section-title"><h3>{title}</h3>{action}</div>;
}

function FollowUps({
  tasks,
  sources,
  activity,
  dataState,
  onContacted,
  onCompleteTask,
  onSourceContacted,
  onOpenSource,
  onSnooze,
  onCreateTask,
  onEditTask,
  onMoveTask,
  onViewSources,
  onRetry,
  canCreateTask,
}: {
  tasks: DemoTask[];
  sources: DemoSource[];
  activity: SourceActivity[];
  dataState: "loading" | "ready" | "error";
  onContacted: (task: DemoTask) => void;
  onCompleteTask: (task: DemoTask) => void;
  onSourceContacted: (source: DemoSource) => void;
  onOpenSource: (id: string) => void;
  onSnooze: (item: FollowUpItem) => void;
  onCreateTask: () => void;
  onEditTask: (task: DemoTask) => void;
  onMoveTask: (task: DemoTask, dueAt: string) => Promise<void>;
  onViewSources: () => void;
  onRetry: () => void;
  canCreateTask: boolean;
}) {
  const [mode, setMode] = useState<"agenda" | "calendar">("agenda");
  const [calendarMonth, setCalendarMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const followUps = sourceFollowUps(tasks, sources, true);
  const overdue = followUps.filter((item) => item.timing === "overdue").length;
  const dueToday = followUps.filter((item) => item.timing === "today").length;
  const calendarDays = useMemo(() => {
    const start = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
    start.setDate(start.getDate() - start.getDay());
    return Array.from({ length: 42 }, (_, index) => {
      const day = new Date(start);
      day.setDate(start.getDate() + index);
      return day;
    });
  }, [calendarMonth]);
  const tasksForDay = (day: Date) => tasks.filter((task) => new Date(task.dueAtIso).toDateString() === day.toDateString());
  async function dropTask(taskId: string, day: Date) {
    const task = tasks.find((item) => item.id === taskId);
    if (!task) return;
    const currentDue = new Date(task.dueAtIso);
    const nextDue = new Date(day);
    nextDue.setHours(currentDue.getHours(), currentDue.getMinutes(), 0, 0);
    await onMoveTask(task, nextDue.toISOString());
  }
  return (
    <div className="page simple-page today-page">
      <div className="simple-heading">
        <div><h1>Tasks</h1><p>Manage assigned work and referral-source follow-ups in one place.</p></div>
        {canCreateTask && <button className="button primary" onClick={onCreateTask}><CalendarPlus size={16} />New task</button>}
      </div>
      {dataState === "loading" ? (
        <div className="simple-card followups-state"><span className="loading-line" /><strong>Loading live tasks…</strong></div>
      ) : dataState === "error" ? (
        <div className="simple-card followups-state error"><CircleAlert /><strong>Tasks couldn’t be loaded.</strong><p>No placeholder records are being shown.</p><button className="button primary" onClick={onRetry}>Try again</button></div>
      ) : (
        <>
          <section className="compact-stats">
            <article className={overdue ? "urgent" : ""}><span><CircleAlert /></span><div><strong>{overdue}</strong><small>Overdue</small></div></article>
            <article><span><CalendarDays /></span><div><strong>{dueToday}</strong><small>Due today</small></div></article>
            <article><span><CheckCircle2 /></span><div><strong>{tasks.length}</strong><small>Open tasks</small></div></article>
          </section>
          <section className="simple-split">
            <div className="simple-card queue-card">
              <SectionTitle title={mode === "agenda" ? "Task agenda" : calendarMonth.toLocaleDateString([], { month: "long", year: "numeric" })} action={(
                <div className="followup-view-actions">
                  {mode === "calendar" && <><button className="icon-button" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))} aria-label="Previous month"><ArrowLeft /></button><button className="icon-button" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))} aria-label="Next month"><ArrowRight /></button></>}
                  <div className="view-toggle compact" role="group" aria-label="Follow-up view"><button className={mode === "agenda" ? "active" : ""} onClick={() => setMode("agenda")}><Table2 />Agenda</button><button className={mode === "calendar" ? "active" : ""} onClick={() => setMode("calendar")}><CalendarDays />Calendar</button></div>
                </div>
              )} />
              {mode === "agenda" ? <div className="task-list">
                {followUps.map((item) => (
                  <article className={`task-row ${item.timing} ${item.task ? `priority-${item.task.priority}` : ""}`} key={item.id}>
                    <button
                      className="check-button"
                      onClick={() => item.task ? (item.task.entityType === "general" ? onCompleteTask(item.task) : onContacted(item.task)) : item.source && onSourceContacted(item.source)}
                      aria-label={item.task?.entityType === "general" ? `Complete ${item.title}` : `Log contact with ${item.entity}`}
                    ><Check size={15} /></button>
                    <div><h4>{item.title}</h4><p><strong>{item.entity}</strong>{item.task?.assignee ? ` · ${item.task.assignee}` : item.source?.ownerName ? ` · ${item.source.ownerName}` : ""}</p></div>
                    <span className="task-time">{item.timing === "overdue" && <CircleAlert size={14} />}{item.dueAt}</span>
                    {item.task && <button className="icon-button" onClick={() => onEditTask(item.task as DemoTask)} aria-label={`Edit ${item.title}`}><PencilLine size={16} /></button>}
                    <button className="icon-button snooze-icon" onClick={() => onSnooze(item)} aria-label={item.source ? `Reschedule follow-up with ${item.entity}` : `Reschedule ${item.title}`}><Clock3 size={17} /></button>
                    <button className="icon-button" onClick={() => item.source && onOpenSource(item.source.id)} disabled={!item.source} aria-label={`View ${item.entity}`}><ChevronRight size={18} /></button>
                  </article>
                ))}
                {!followUps.length && <div className="empty-state"><CheckCircle2 /><strong>You’re caught up</strong><p>No tasks or source follow-ups are due today.</p></div>}
              </div> : <div className="followup-calendar">
                {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((label) => <span className="calendar-weekday" key={label}>{label}</span>)}
                {calendarDays.map((day) => {
                  const dayTasks = tasksForDay(day);
                  const outside = day.getMonth() !== calendarMonth.getMonth();
                  const today = day.toDateString() === new Date().toDateString();
                  return <div className={`calendar-day${outside ? " outside" : ""}${today ? " today" : ""}`} key={day.toISOString()} onDragOver={(event) => event.preventDefault()} onDrop={(event) => void dropTask(event.dataTransfer.getData("text/task-id"), day)}>
                    <span>{day.getDate()}</span>
                    {dayTasks.slice(0, 3).map((task) => <button draggable onDragStart={(event) => event.dataTransfer.setData("text/task-id", task.id)} onClick={() => onEditTask(task)} className={`calendar-task priority-${task.priority}`} key={task.id}><strong>{task.entity}</strong><small>{new Date(task.dueAtIso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</small></button>)}
                    {dayTasks.length > 3 && <small className="calendar-more">+{dayTasks.length - 3} more</small>}
                  </div>;
                })}
              </div>}
            </div>
            <div className="simple-card activity-card">
              <SectionTitle title="Recent source activity" />
              <div className="activity-list">
                {activity.map((item, index) => (
                  <article key={item.id}>
                    <span className={`avatar ${["sage", "blue", "ochre", "clay"][index % 4]}`}>{item.actor.split(/\s+/).map((word) => word[0]).join("").slice(0, 2).toUpperCase()}</span>
                    <p><strong>{item.actor}</strong> {item.summary} <b>{item.entity}</b><small>{formatSourceDate(item.occurredAt)}</small></p>
                  </article>
                ))}
                {!activity.length && <div className="empty-state compact"><Activity /><strong>No source activity yet</strong><p>Logged contacts will appear here.</p></div>}
              </div>
            </div>
          </section>
          <button className="inline-link" onClick={onViewSources}>Browse {sources.length} referral sources <ArrowRight size={14} /></button>
        </>
      )}
    </div>
  );
}

function eventDateLabel(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Date unavailable" : date.toLocaleString([], {
    weekday: "short", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });
}

function contactStatusLabel(status: DirectoryContact["contactStatus"]): string {
  if (status === "do-not-contact") return "Do not contact";
  if (status === "unresponsive") return "Unresponsive";
  if (status === "former") return "Former contact";
  return "Active";
}

function preferredMethodLabel(method: DirectoryContact["preferredContactMethod"]): string {
  if (method === "any") return "Any method";
  if (method === "text") return "Text message";
  if (method === "visit") return "In-person visit";
  return method[0].toUpperCase() + method.slice(1);
}

function contactOrganizationNames(contact: DirectoryContact): string {
  return contact.organizations.map((organization) => organization.sourceName).join(" · ");
}

function canEditDirectoryContact(role: WorkspaceRole, userId: string, contact: DirectoryContact): boolean {
  return contact.organizations.length > 0
    && contact.organizations.every((organization) => canAccessSource(role, userId, organization.ownerUserId ?? null));
}

type DuplicateReasonView = { code: string; label: string };
type DuplicateContactRecord = {
  id: string;
  sourceId: string;
  name: string;
  title: string | null;
  phone: string | null;
  mobilePhone: string | null;
  fax: string | null;
  email: string | null;
  address: string | null;
  preferredContactMethod: DirectoryContact["preferredContactMethod"];
  bestContactTime: string | null;
  contactStatus: DirectoryContact["contactStatus"];
  lastVerifiedAt: string | null;
  version: number;
  organizations: Array<{ sourceId: string; sourceName: string }>;
  noteCount: number;
  photoCount: number;
  interactionCount: number;
};
type DuplicateSourceRecord = {
  id: string;
  name: string;
  categoryCode: string | null;
  referralPriority: "daily" | "weekly" | "monthly" | "other";
  ownerUserId: string | null;
  ownerName: string | null;
  phone: string | null;
  website: string | null;
  address: string | null;
  description: string | null;
  descriptionColor: "gold" | "purple" | "teal" | "red";
  notes: string | null;
  cadenceType: "daily" | "weekly" | "monthly" | "custom";
  cadenceDays: number | null;
  version: number;
  contactCount: number;
  activityCount: number;
  referralCount: number;
  taskCount: number;
};
type DuplicatePair<T> = { id: string; confidence: number; reasons: DuplicateReasonView[]; left: T; right: T };
type DuplicateReviewData = {
  contacts: Array<DuplicatePair<DuplicateContactRecord>>;
  sources: Array<DuplicatePair<DuplicateSourceRecord>>;
};
type MergeSide = "left" | "right";

function mergeDisplayValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "Not provided";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) return formatSourceDate(value);
  return String(value).replace(/-/g, " ");
}

function MergeFieldChoice({ label, left, right, choice, onChoice }: {
  label: string;
  left: unknown;
  right: unknown;
  choice: MergeSide;
  onChoice: (side: MergeSide) => void;
}) {
  return <div className="duplicate-field-choice">
    <strong>{label}</strong>
    <button type="button" className={choice === "left" ? "selected" : ""} aria-pressed={choice === "left"} onClick={() => onChoice("left")}><span>{mergeDisplayValue(left)}</span>{choice === "left" && <Check />}</button>
    <button type="button" className={choice === "right" ? "selected" : ""} aria-pressed={choice === "right"} onClick={() => onChoice("right")}><span>{mergeDisplayValue(right)}</span>{choice === "right" && <Check />}</button>
  </div>;
}

function DuplicateReviewModal({ onClose, onMerged, onNotice }: {
  onClose: () => void;
  onMerged: () => Promise<void>;
  onNotice: (message: string) => void;
}) {
  const [data, setData] = useState<DuplicateReviewData | null>(null);
  const [section, setSection] = useState<"contacts" | "sources">("contacts");
  const [contactPair, setContactPair] = useState<DuplicatePair<DuplicateContactRecord> | null>(null);
  const [sourcePair, setSourcePair] = useState<DuplicatePair<DuplicateSourceRecord> | null>(null);
  const [survivorSide, setSurvivorSide] = useState<MergeSide>("left");
  const [choices, setChoices] = useState<Record<string, MergeSide>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    setError("");
    const response = await fetch("/api/duplicates");
    const result = await response.json().catch(() => ({})) as { data?: DuplicateReviewData; error?: string };
    if (!response.ok || !result.data) {
      setError(result.error ?? "Duplicate candidates could not be loaded.");
      return;
    }
    setData(result.data);
  }

  useEffect(() => { void load(); }, []);

  function initialChoices(left: Record<string, unknown>, fields: string[]) {
    return Object.fromEntries(fields.map((field) => [field, left[field] !== null && left[field] !== "" && left[field] !== undefined ? "left" : "right"])) as Record<string, MergeSide>;
  }

  const contactFields: Array<{ key: keyof DuplicateContactRecord; label: string }> = [
    { key: "name", label: "Name" }, { key: "title", label: "Title or role" },
    { key: "phone", label: "Work / direct phone" }, { key: "mobilePhone", label: "Mobile / cell" },
    { key: "fax", label: "Fax" }, { key: "email", label: "Email" },
    { key: "address", label: "Address" }, { key: "preferredContactMethod", label: "Preferred method" },
    { key: "bestContactTime", label: "Best time" }, { key: "contactStatus", label: "Status" },
    { key: "lastVerifiedAt", label: "Last verified" },
  ];
  const sourceFields: Array<{ key: keyof DuplicateSourceRecord; label: string }> = [
    { key: "name", label: "Organization name" }, { key: "categoryCode", label: "Category" },
    { key: "ownerUserId", label: "Owner" }, { key: "phone", label: "Phone" },
    { key: "website", label: "Website" }, { key: "address", label: "Address" },
    { key: "referralPriority", label: "Referral priority" }, { key: "cadenceType", label: "Contact cadence" },
    { key: "cadenceDays", label: "Custom cadence days" }, { key: "description", label: "Pinned note" },
    { key: "descriptionColor", label: "Pinned note color" }, { key: "notes", label: "Relationship notes" },
  ];

  function reviewContact(pair: DuplicatePair<DuplicateContactRecord>) {
    setSourcePair(null);
    setContactPair(pair);
    setSurvivorSide("left");
    setChoices(initialChoices(pair.left as unknown as Record<string, unknown>, contactFields.map((field) => field.key)));
    setError("");
  }

  function reviewSource(pair: DuplicatePair<DuplicateSourceRecord>) {
    setContactPair(null);
    setSourcePair(pair);
    setSurvivorSide("left");
    setChoices(initialChoices(pair.left as unknown as Record<string, unknown>, sourceFields.map((field) => field.key)));
    setError("");
  }

  async function mergeContact() {
    if (!contactPair) return;
    setBusy(true);
    setError("");
    const survivor = contactPair[survivorSide];
    const duplicate = contactPair[survivorSide === "left" ? "right" : "left"];
    const selected = (key: keyof DuplicateContactRecord) => contactPair[choices[key] ?? survivorSide][key];
    const response = await fetch(`/api/contacts/${encodeURIComponent(survivor.id)}/merge`, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({
        duplicateId: duplicate.id, survivorVersion: survivor.version, duplicateVersion: duplicate.version,
        values: {
          name: selected("name"), title: selected("title"), phone: selected("phone"),
          mobilePhone: selected("mobilePhone"), fax: selected("fax"), email: selected("email"),
          address: selected("address"), preferredContactMethod: selected("preferredContactMethod"),
          bestContactTime: selected("bestContactTime"), contactStatus: selected("contactStatus"),
          lastVerifiedAt: selected("lastVerifiedAt"),
        },
      }),
    });
    const result = await response.json().catch(() => ({})) as { error?: string };
    if (!response.ok) {
      setError(result.error ?? "These contacts could not be merged.");
      setBusy(false);
      return;
    }
    await onMerged();
    onNotice(`${duplicate.name} was merged into ${String(selected("name"))}`);
    setContactPair(null);
    setBusy(false);
    await load();
  }

  async function mergeSource() {
    if (!sourcePair) return;
    setBusy(true);
    setError("");
    const survivor = sourcePair[survivorSide];
    const duplicate = sourcePair[survivorSide === "left" ? "right" : "left"];
    const selected = (key: keyof DuplicateSourceRecord) => sourcePair[choices[key] ?? survivorSide][key];
    const response = await fetch(`/api/sources/${encodeURIComponent(survivor.id)}/merge`, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({
        duplicateId: duplicate.id, survivorVersion: survivor.version, duplicateVersion: duplicate.version,
        values: {
          name: selected("name"), categoryCode: selected("categoryCode"), referralPriority: selected("referralPriority"),
          ownerUserId: selected("ownerUserId"), phone: selected("phone"),
          website: selected("website") ? normalizeWebsite(String(selected("website"))) : null,
          address: selected("address"), description: selected("description"), descriptionColor: selected("descriptionColor"),
          notes: selected("notes"), cadenceType: selected("cadenceType"), cadenceDays: selected("cadenceDays"),
        },
      }),
    });
    const result = await response.json().catch(() => ({})) as { error?: string };
    if (!response.ok) {
      setError(result.error ?? "These referral sources could not be merged.");
      setBusy(false);
      return;
    }
    await onMerged();
    onNotice(`${duplicate.name} was merged into ${String(selected("name"))}`);
    setSourcePair(null);
    setBusy(false);
    await load();
  }

  const activePair = contactPair ?? sourcePair;
  const activeLeft = activePair?.left;
  const activeRight = activePair?.right;
  const sourceDisplay = (record: DuplicateSourceRecord, key: keyof DuplicateSourceRecord) => key === "ownerUserId" ? record.ownerName : record[key];

  return <div className="modal-layer duplicate-review-layer" role="dialog" aria-modal="true" aria-labelledby="duplicate-review-title">
    <button type="button" className="modal-scrim" onClick={onClose} aria-label="Close duplicate review" />
    <section className="modal duplicate-review-modal">
      <header><div><p className="eyebrow">Data quality</p><h2 id="duplicate-review-title">Review possible duplicates</h2><p>Compare records before combining them. Linked history, photos, tasks, events, and referrals move to the record you keep.</p></div><button type="button" className="icon-button" onClick={onClose} aria-label="Close duplicate review"><X /></button></header>
      {!activePair && <>
        <nav className="duplicate-review-tabs" aria-label="Duplicate record type"><button type="button" className={section === "contacts" ? "active" : ""} onClick={() => setSection("contacts")}><Contact />Contacts <span>{data?.contacts.length ?? 0}</span></button><button type="button" className={section === "sources" ? "active" : ""} onClick={() => setSection("sources")}><Building2 />Referral sources <span>{data?.sources.length ?? 0}</span></button></nav>
        <div className="duplicate-review-list">
          {!data && !error && <div className="duplicate-review-loading"><span className="loading-line" /><strong>Checking for duplicates…</strong></div>}
          {data && section === "contacts" && data.contacts.map((pair) => <article key={pair.id}><div className="duplicate-confidence"><strong>{Math.round(pair.confidence * 100)}%</strong><small>match</small></div><div><h3>{pair.left.name}<span>and</span>{pair.right.name}</h3><p>{pair.reasons.map((reason) => reason.label).join(" · ")}</p><small>{pair.left.organizations.map((item) => item.sourceName).join(", ")} ↔ {pair.right.organizations.map((item) => item.sourceName).join(", ")}</small></div><button type="button" className="button ghost" onClick={() => reviewContact(pair)}>Review <ChevronRight /></button></article>)}
          {data && section === "sources" && data.sources.map((pair) => <article key={pair.id}><div className="duplicate-confidence"><strong>{Math.round(pair.confidence * 100)}%</strong><small>match</small></div><div><h3>{pair.left.name}<span>and</span>{pair.right.name}</h3><p>{pair.reasons.map((reason) => reason.label).join(" · ")}</p><small>{pair.left.contactCount + pair.right.contactCount} contacts · {pair.left.activityCount + pair.right.activityCount} interactions · {pair.left.referralCount + pair.right.referralCount} referrals</small></div><button type="button" className="button ghost" onClick={() => reviewSource(pair)}>Review <ChevronRight /></button></article>)}
          {data && !(section === "contacts" ? data.contacts : data.sources).length && <div className="duplicate-review-empty"><CheckCircle2 /><h3>No likely duplicate {section}</h3><p>The current records do not share enough matching details to need review.</p></div>}
        </div>
      </>}
      {activePair && activeLeft && activeRight && <div className="duplicate-merge-review">
        <button type="button" className="duplicate-review-back" onClick={() => { setContactPair(null); setSourcePair(null); setError(""); }}><ArrowLeft />Back to candidates</button>
        <section className="duplicate-survivor-choice"><div><p className="eyebrow">Step 1</p><h3>Which record should remain?</h3><p>The other record will be archived with a permanent redirect in the audit history.</p></div><div><button type="button" className={survivorSide === "left" ? "selected" : ""} onClick={() => setSurvivorSide("left")}><span>{activeLeft.name}</span><small>{survivorSide === "left" ? "Main record" : "Make main record"}</small></button><button type="button" className={survivorSide === "right" ? "selected" : ""} onClick={() => setSurvivorSide("right")}><span>{activeRight.name}</span><small>{survivorSide === "right" ? "Main record" : "Make main record"}</small></button></div></section>
        <section className="duplicate-field-review"><div><p className="eyebrow">Step 2</p><h3>Choose the value to keep for each field</h3><p>Blank values are prefilled from the other record. You can change every choice.</p></div><div className="duplicate-field-headings"><span>Field</span><strong>{activeLeft.name}</strong><strong>{activeRight.name}</strong></div>
          {(contactPair ? contactFields : sourceFields).map((field) => <MergeFieldChoice key={field.key} label={field.label} left={contactPair ? contactPair.left[field.key as keyof DuplicateContactRecord] : sourceDisplay(sourcePair!.left, field.key as keyof DuplicateSourceRecord)} right={contactPair ? contactPair.right[field.key as keyof DuplicateContactRecord] : sourceDisplay(sourcePair!.right, field.key as keyof DuplicateSourceRecord)} choice={choices[field.key] ?? survivorSide} onChoice={(side) => setChoices((current) => ({ ...current, [field.key]: side }))} />)}
        </section>
        <div className="duplicate-preservation-note"><ShieldCheck /><div><strong>Linked information is preserved</strong><p>{contactPair ? "Notes, referral receipts, photos, business cards, interactions, event attendance, and networking groups will move to the main contact." : "Contacts, referrals, interactions, tasks, notifications, events, files, photos, and note history will move to the main referral source."}</p></div></div>
      </div>}
      {error && <p className="duplicate-review-error"><CircleAlert />{error}</p>}
      <footer><button type="button" className="button ghost" onClick={onClose}>Close</button>{contactPair && <button type="button" className="button primary" disabled={busy} onClick={() => void mergeContact()}><GitMerge />{busy ? "Merging contacts…" : "Merge contacts"}</button>}{sourcePair && <button type="button" className="button primary" disabled={busy} onClick={() => void mergeSource()}><GitMerge />{busy ? "Merging sources…" : "Merge referral sources"}</button>}</footer>
    </section>
  </div>;
}

function ContactsPage({ contacts, canAdd, canScan, canReviewDuplicates, canEdit, onAdd, onScan, onReviewDuplicates, onEdit, onOpenContact, onOpenSource, onNotice }: {
  contacts: DirectoryContact[];
  canAdd: boolean;
  canScan: boolean;
  canReviewDuplicates: boolean;
  canEdit: (contact: DirectoryContact) => boolean;
  onAdd: () => void;
  onScan: () => void;
  onReviewDuplicates: () => void;
  onEdit: (contact: DirectoryContact) => void;
  onOpenContact: (contactId: string) => void;
  onOpenSource: (sourceId: string) => void;
  onNotice: (message: string) => void;
}) {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("active");
  const [exporting, setExporting] = useState(false);
  const categories = [...new Set(contacts.flatMap((contact) => contact.organizations.map((organization) => organization.sourceCategory)))].sort((a, b) => a.localeCompare(b));
  const query = search.trim().toLowerCase();
  const filteredContacts = contacts.filter((contact) => {
    if (category !== "all" && !contact.organizations.some((organization) => organization.sourceCategory === category)) return false;
    if (status !== "all" && contact.contactStatus !== status) return false;
    return !query || `${contact.name} ${contact.title ?? ""} ${contactOrganizationNames(contact)} ${contact.email ?? ""} ${contact.contactMethods.map((method) => `${method.label} ${method.value}`).join(" ")} ${contact.phone ?? ""} ${contact.mobilePhone ?? ""} ${contact.fax ?? ""}`.toLowerCase().includes(query);
  });
  type ContactTableSort = "contact" | "organization" | "phone" | "email" | "status";
  const { sortedRows: visibleContacts, sortKey: contactSortKey, sortDirection: contactSortDirection, changeSort: changeContactSort } = useSortableTable<DirectoryContact, ContactTableSort>(filteredContacts, "contact", (contact, key) => ({
    contact: contact.name,
    organization: contactOrganizationNames(contact),
    phone: contact.phone ?? contact.mobilePhone,
    email: contact.email,
    status: contactStatusLabel(contact.contactStatus),
  })[key]);
  const organizations = new Set(visibleContacts.flatMap((contact) => contact.organizations.map((organization) => organization.sourceId))).size;
  const reachable = visibleContacts.filter((contact) => contact.phone || contact.mobilePhone || contact.email).length;

  async function exportContacts() {
    setExporting(true);
    try {
      const { downloadContactWorkbook } = await import("./contact-xlsx");
      downloadContactWorkbook(visibleContacts.map((contact) => ({
        name: contact.name,
        title: contact.title ?? "",
        organization: contact.organizations.map((organization) => organization.sourceName).join(" | "),
        category: contact.organizations.map((organization) => organization.sourceCategory).join(" | "),
        phone: contact.phone ?? "",
        mobilePhone: contact.mobilePhone ?? "",
        fax: contact.fax ?? "",
        phoneNumbers: contact.contactMethods.map((method) => `${method.label}: ${method.value}${method.isPrimary ? " (primary)" : ""}${method.canText ? " (text)" : ""}`).join(" | "),
        email: contact.email ?? "",
        address: contact.address ?? "",
        status: contactStatusLabel(contact.contactStatus),
        primary: contact.isPrimary ? "Yes" : "No",
        preferredMethod: preferredMethodLabel(contact.preferredContactMethod),
        bestTime: contact.bestContactTime ?? "",
        lastVerified: contact.lastVerifiedAt?.slice(0, 10) ?? "",
        owner: contact.ownerName ?? "",
      })), `contacts-${new Date().toISOString().slice(0, 10)}.xlsx`);
      onNotice(`${visibleContacts.length} contact${visibleContacts.length === 1 ? "" : "s"} exported to Excel`);
    } catch {
      onNotice("The Excel workbook could not be created.");
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="page simple-page contacts-page">
      <div className="contacts-heading">
        <div><p className="eyebrow">People across relationships</p><h1>Address book</h1><p>Find every person connected to your sources, with the context you need to reach them.</p></div>
        <div>{canReviewDuplicates && <button type="button" className="button ghost" onClick={onReviewDuplicates}><GitMerge />Review duplicates</button>}<button type="button" className="button ghost" disabled={exporting || !visibleContacts.length} onClick={() => void exportContacts()}><FileDown />{exporting ? "Building workbook…" : `Export ${visibleContacts.length || ""} to Excel`}</button>{canAdd && <button type="button" className="button primary" onClick={onAdd}><UserPlus />Add contact</button>}</div>
      </div>
      {canScan && <section className="contact-card-import-banner" aria-label="Business card photo import"><span><ScanLine /></span><div><strong>Have business cards to enter?</strong><p>Take one photo now or select several saved card photos at once. AI reads each card, then pauses for your review before anything is saved.</p></div><button type="button" className="button scan-card-button" onClick={onScan}><ImagePlus />Import card photo</button></section>}
      <section className="contact-directory-summary" aria-label="Contact directory summary">
        <article><span><Contact /></span><div><strong>{visibleContacts.length}</strong><small>People in view</small></div></article>
        <article><span><Building2 /></span><div><strong>{organizations}</strong><small>Organizations</small></div></article>
        <article><span><AtSign /></span><div><strong>{reachable}</strong><small>With phone or email</small></div></article>
      </section>
      <section className="contact-directory-toolbar" aria-label="Contact filters">
        <label className="contact-directory-search"><Search /><input aria-label="Search contacts" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search a name, title, organization, email, or phone" />{search && <button type="button" onClick={() => setSearch("")} aria-label="Clear contact search"><X /></button>}</label>
        <label><span>Category</span><select aria-label="Contact category" value={category} onChange={(event) => setCategory(event.target.value)}><option value="all">All categories</option>{categories.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
        <label><span>Status</span><select aria-label="Contact status" value={status} onChange={(event) => setStatus(event.target.value)}><option value="active">Active contacts</option><option value="all">All statuses</option><option value="former">Former contacts</option><option value="unresponsive">Unresponsive</option><option value="do-not-contact">Do not contact</option></select></label>
      </section>
      {visibleContacts.length ? <>
        <section className="contact-address-grid" aria-label="Contact address book">
          {visibleContacts.map((contact) => <article className={`contact-address-card status-${contact.contactStatus}`} key={contact.id}>
            <header><ContactAvatar name={contact.name} photoId={contact.primaryPhotoId} className="contact-address-avatar" /><button type="button" className="contact-profile-link" onClick={() => onOpenContact(contact.id)}><h2>{contact.name}</h2><p>{contact.title || "Contact"}</p></button><span className={`contact-status-pill ${contact.contactStatus}`}>{contactStatusLabel(contact.contactStatus)}</span></header>
            <div className="contact-source-stack">{contact.organizations.map((organization) => <button type="button" className="contact-source-link" onClick={() => onOpenSource(organization.sourceId)} key={organization.sourceId}><Building2 /><span><strong>{organization.sourceName}</strong><small>{organization.sourceCategory}{organization.isPrimary ? " · Primary contact" : ""}</small></span><ChevronRight /></button>)}</div>
            <div className="contact-address-actions">
              {primaryCallableMethod(contact) ? <a href={`tel:${primaryCallableMethod(contact)!.value}`}><Phone /><span><small>{primaryCallableMethod(contact)!.label}{primaryCallableMethod(contact)!.canText ? " · textable" : ""}</small><strong>{primaryCallableMethod(contact)!.value}</strong></span></a> : <span className="contact-action-empty"><Phone /><small>No phone</small></span>}
              {contact.email ? <a href={`mailto:${contact.email}`}><Mail /><span><small>Email</small><strong>{contact.email}</strong></span></a> : <span className="contact-action-empty"><Mail /><small>No email</small></span>}
            </div>
            {contact.address && <p className="contact-address-line"><MapPin />{contact.address}</p>}
            <footer><span><small>Prefers</small><strong>{preferredMethodLabel(contact.preferredContactMethod)}</strong></span>{contact.bestContactTime && <span><small>Best time</small><strong>{contact.bestContactTime}</strong></span>}<button type="button" className="button ghost" onClick={() => canEdit(contact) ? onEdit(contact) : onOpenSource(contact.sourceId)}>{canEdit(contact) ? <><PencilLine />Edit contact</> : <>View source<ChevronRight /></>}</button></footer>
          </article>)}
        </section>
        <div className="contact-directory-table-wrap"><table className="contact-directory-table"><thead><tr><SortableTableHeader column="contact" label="Contact" sortKey={contactSortKey} sortDirection={contactSortDirection} onSort={changeContactSort} /><SortableTableHeader column="organization" label="Organization" sortKey={contactSortKey} sortDirection={contactSortDirection} onSort={changeContactSort} /><SortableTableHeader column="phone" label="Phone" sortKey={contactSortKey} sortDirection={contactSortDirection} onSort={changeContactSort} /><SortableTableHeader column="email" label="Email" sortKey={contactSortKey} sortDirection={contactSortDirection} onSort={changeContactSort} /><SortableTableHeader column="status" label="Status" sortKey={contactSortKey} sortDirection={contactSortDirection} onSort={changeContactSort} /><th><span className="sr-only">Actions</span></th></tr></thead><tbody>{visibleContacts.map((contact) => <tr key={contact.id}><td><button type="button" className="contact-table-profile-link" onClick={() => onOpenContact(contact.id)}><strong>{contact.name}</strong><small>{contact.title || "Contact"}{contact.isPrimary ? " · Primary" : ""}</small></button></td><td><div className="contact-table-organizations">{contact.organizations.map((organization) => <button type="button" onClick={() => onOpenSource(organization.sourceId)} key={organization.sourceId}><strong>{organization.sourceName}</strong><small>{organization.sourceCategory}{organization.sourceId === contact.sourceId ? " · Current" : " · Linked"}</small></button>)}</div></td><td><span className="contact-table-phone-list">{contact.contactMethods.filter((method) => method.value).map((method) => method.type === "fax" ? <span key={method.id}><small>{method.label}</small>{method.value}</span> : <a href={`tel:${method.value}`} key={method.id}><small>{method.label}{method.isPrimary ? " · Primary" : ""}</small>{method.value}</a>)}{!contact.contactMethods.length && "—"}</span></td><td>{contact.email ? <a href={`mailto:${contact.email}`}>{contact.email}</a> : "—"}</td><td><span className={`contact-status-pill ${contact.contactStatus}`}>{contactStatusLabel(contact.contactStatus)}</span></td><td>{canEdit(contact) && <button type="button" className="icon-button" aria-label={`Edit ${contact.name}`} onClick={() => onEdit(contact)}><PencilLine /></button>}</td></tr>)}</tbody></table></div>
      </> : <div className="simple-card contact-directory-empty"><Contact /><h2>{contacts.length ? "No contacts match these filters" : "Your address book is ready"}</h2><p>{contacts.length ? "Try a broader search or include more contact statuses." : "Add a contact to a source and they’ll appear here automatically."}</p>{contacts.length ? <button type="button" className="button ghost" onClick={() => { setSearch(""); setCategory("all"); setStatus("all"); }}>Clear filters</button> : <div className="contact-empty-actions">{canAdd && <button type="button" className="button primary" onClick={onAdd}><UserPlus />Add first contact</button>}</div>}</div>}
    </div>
  );
}

function ContactProfilePage({ profile, canEdit, canLog, canAddNote, canRecordEvent, onBack, onEdit, onOpenSource, onOutreach, onLogInteraction, onAddNote, onRecordEvent, onUploadPhotos, onDeletePhoto }: {
  profile: ContactProfile;
  canEdit: boolean;
  canLog: boolean;
  canAddNote: boolean;
  canRecordEvent: boolean;
  onBack: () => void;
  onEdit: () => void;
  onOpenSource: (sourceId: string) => void;
  onOutreach: () => void;
  onLogInteraction: () => void;
  onAddNote: () => void;
  onRecordEvent: () => void;
  onUploadPhotos: (files: File[]) => Promise<boolean>;
  onDeletePhoto: (photo: ContactPhoto) => Promise<boolean>;
}) {
  const [photoBusy, setPhotoBusy] = useState(false);
  const [deletingPhotoId, setDeletingPhotoId] = useState<string | null>(null);
  const avatarPhotoId = profile.photos.find((photo) => photo.kind === "photo")?.id ?? profile.primaryPhotoId;
  const primaryNumber = primaryCallableMethod(profile);
  const timeline = [
    ...profile.notes.map((note) => ({ id: `note:${note.id}`, kind: "note" as const, occurredAt: note.updatedAt, title: note.pinned ? "Pinned contact note" : "Contact note", summary: note.noteText, meta: note.updatedBy ?? note.createdBy ?? "Team member" })),
    ...profile.referralReceipts.map((receipt) => ({ id: `referral:${receipt.id}`, kind: "referral" as const, occurredAt: receipt.receivedOn, title: `${receipt.quantity} referral${receipt.quantity === 1 ? "" : "s"} received`, summary: `Recorded for ${profile.name}`, meta: receipt.recordedBy ?? "Team member" })),
    ...profile.events.map((event) => ({ id: `event:${event.id}`, kind: "event" as const, occurredAt: event.occurredAt, title: event.title, summary: event.description || event.location, meta: event.networkingGroupName ? `${event.location} · ${event.networkingGroupName}` : event.sourceName ? `${event.location} · ${event.sourceName}` : event.location })),
    ...profile.contactInteractions.map((activity) => ({ id: `interaction:${activity.id}`, kind: "interaction" as const, occurredAt: activity.occurredAt, title: activityTypeLabel(activity.activityTypeCode), summary: activity.notes ? `${activity.summary} — ${activity.notes}` : activity.summary, meta: `Direct interaction with ${profile.name}${activity.eventTitle ? ` at ${activity.eventTitle}` : activity.contextLabel ? ` · ${activity.contextLabel}` : ""}${activity.actorName ? ` · ${activity.actorName}` : ""}` })),
    ...profile.organizationActivities.map((activity) => ({ id: `activity:${activity.id}`, kind: "activity" as const, occurredAt: activity.occurredAt, title: activityTypeLabel(activity.activityTypeCode), summary: activity.summary, meta: `${activity.sourceName ?? profile.sourceName} relationship activity${activity.eventTitle ? ` at ${activity.eventTitle}` : activity.contextLabel ? ` · ${activity.contextLabel}` : ""}${activity.actorName ? ` · ${activity.actorName}` : ""}` })),
  ].sort((left, right) => new Date(right.occurredAt).getTime() - new Date(left.occurredAt).getTime());
  return <div className="page simple-page contact-profile-page">
    <button type="button" className="contact-profile-back" onClick={onBack}><ArrowLeft />Back to address book</button>
    <section className="contact-profile-hero" id="contact-overview">
      <ContactAvatar name={profile.name} photoId={avatarPhotoId} className="contact-profile-avatar" />
      <div><p className="eyebrow">Contact profile</p><h1>{profile.name}</h1><p>{profile.title || "Contact"} · {contactOrganizationNames(profile)}</p><span className={`contact-status-pill ${profile.contactStatus}`}>{contactStatusLabel(profile.contactStatus)}</span></div>
      <div className="contact-profile-actions">{canLog && <button type="button" className="button ghost" onClick={onOutreach}><Sparkles />Outreach</button>}{primaryNumber && <a className="button ghost" href={`tel:${primaryNumber.value}`}><Phone />Call {primaryNumber.label.toLowerCase()}</a>}{primaryNumber?.canText && <a className="button ghost" href={`sms:${primaryNumber.value}`}><MessageSquareText />Text</a>}{profile.email && <a className="button ghost" href={`mailto:${profile.email}`}><Mail />Email</a>}{canEdit && <label className="button ghost contact-photo-upload"><Camera />{photoBusy ? "Uploading…" : "Add photos"}<input type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" disabled={photoBusy} onChange={(event) => { const files = Array.from(event.target.files ?? []); event.currentTarget.value = ""; if (!files.length) return; setPhotoBusy(true); void onUploadPhotos(files).finally(() => setPhotoBusy(false)); }} /></label>}{canEdit && <button type="button" className="button primary" onClick={onEdit}><PencilLine />Edit profile</button>}</div>
    </section>
    {(canLog || canAddNote || canRecordEvent) && <section className="contact-profile-command-bar" aria-label={`Log activity with ${profile.name}`}>
      <div><p className="eyebrow">Keep the relationship current</p><strong>Log something with {profile.name.split(" ")[0]}</strong><small>Each record stays attached to this individual.</small></div>
      <nav>{canLog && <button type="button" className="interaction" onClick={onLogInteraction}><span><MessageSquareText /></span><strong>Log interaction</strong><small>Call, email, visit, or meeting</small></button>}{canAddNote && <button type="button" className="note" onClick={onAddNote}><span><StickyNote /></span><strong>Add note</strong><small>Context, mention, or follow-up</small></button>}{canRecordEvent && <button type="button" className="event" onClick={onRecordEvent}><span><CalendarPlus /></span><strong>Record event</strong><small>Add them as an attendee</small></button>}</nav>
    </section>}
    <div className="contact-profile-layout">
      <aside className="contact-profile-sidebar">
        <p>Contact record</p>
        <nav aria-label="Contact profile sections"><a href="#contact-overview"><Contact />Profile</a><a href="#contact-photos"><Camera />Photos <span>{profile.photos.length}</span></a><a href="#contact-timeline"><Activity />Interactions <span>{timeline.length}</span></a><a href="#contact-events"><CalendarDays />Events <span>{profile.events.length}</span></a><a href="#contact-notes"><StickyNote />Notes <span>{profile.notes.length}</span></a></nav>
        <div className="contact-profile-organizations"><p>{profile.organizations.length === 1 ? "Organization" : "Organizations"}</p>{profile.organizations.map((organization) => <button type="button" onClick={() => onOpenSource(organization.sourceId)} key={organization.sourceId}><Building2 /><span><strong>{organization.sourceName}</strong><em>{organization.sourceCategory}{organization.sourceId === profile.sourceId ? " · Current" : " · Linked"}</em></span><ChevronRight /></button>)}</div>
      </aside>
      <main className="contact-profile-main">
        <section className="contact-profile-metrics" aria-label="Contact profile summary"><article><strong>{timeline.length}</strong><small>Timeline records</small></article><article><strong>{profile.contactInteractions.length}</strong><small>Direct interactions</small></article><article><strong>{profile.events.length}</strong><small>Events attended</small></article><article><strong>{profile.notes.length}</strong><small>Direct notes</small></article></section>
        <section className="simple-card contact-profile-facts"><header><div><p className="eyebrow">Profile</p><h2>Contact details</h2></div><Contact /></header><div>
          {profile.contactMethods.map((method) => <article key={method.id}><small>{method.label}{method.isPrimary ? " · Primary" : ""}{method.canText ? " · Textable" : ""}</small>{method.type === "fax" ? <strong>{method.value}</strong> : <a href={`tel:${method.value}`}>{method.value}</a>}</article>)}
          {!profile.contactMethods.length && <article><small>Phone numbers</small><strong>Not recorded</strong></article>}
          <article><small>Email</small>{profile.email ? <a href={`mailto:${profile.email}`}>{profile.email}</a> : <strong>Not recorded</strong>}</article>
          <article><small>Preferred contact</small><strong>{preferredMethodLabel(profile.preferredContactMethod)}</strong></article>
          <article><small>Best time to reach</small><strong>{profile.bestContactTime || "Not recorded"}</strong></article>
          <article><small>Last verified</small><strong>{profile.lastVerifiedAt ? formatSourceDate(profile.lastVerifiedAt) : "Not recorded"}</strong></article>
          <article><small>Relationship owner</small><strong>{profile.ownerName || "Unassigned"}</strong></article>
          {profile.address && <article className="wide"><small>Address</small><strong><MapPin />{profile.address}</strong></article>}
        </div></section>
        <section className="simple-card contact-photo-gallery" id="contact-photos"><header><div><p className="eyebrow">Photo record</p><h2>Contact photos</h2><p>Shared wherever {profile.name} appears in this relationship.</p></div>{canEdit ? <label className="button ghost contact-photo-upload"><Camera />{photoBusy ? "Uploading…" : "Upload pictures"}<input type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" disabled={photoBusy} onChange={(event) => { const files = Array.from(event.target.files ?? []); event.currentTarget.value = ""; if (!files.length) return; setPhotoBusy(true); void onUploadPhotos(files).finally(() => setPhotoBusy(false)); }} /></label> : <Camera />}</header>{profile.photos.length ? <div>{profile.photos.map((photo) => <article className={photo.id === avatarPhotoId ? "featured" : photo.kind === "business_card" ? "business-card" : ""} key={photo.id}><a href={`/api/contact-photos/${photo.id}/content`} target="_blank" rel="noreferrer"><img src={`/api/contact-photos/${photo.id}/content`} alt={photo.caption || `${profile.name} — ${photo.fileName}`} /><span><ExternalLink />Open optimized photo</span></a><footer><div><strong>{photo.caption || photo.fileName}</strong><small>{photo.createdBy} · {formatSourceDate(photo.createdAt)}</small></div><span className="contact-photo-file-actions"><a className="icon-button" href={`/api/contact-photos/${photo.id}/content?original=1`} download aria-label={`Download original ${photo.fileName}`}><Download /></a>{canEdit && <button type="button" className="icon-button" disabled={deletingPhotoId === photo.id} aria-label={`Delete ${photo.fileName}`} onClick={() => { if (!window.confirm(`Delete this photo of ${profile.name}?`)) return; setDeletingPhotoId(photo.id); void onDeletePhoto(photo).finally(() => setDeletingPhotoId(null)); }}><Trash2 /></button>}</span></footer></article>)}</div> : <div className="empty-state compact"><Camera /><strong>No contact photos yet</strong><p>{canEdit ? "Upload a headshot or relationship photo so the team can recognize this contact." : "No photos have been added for this contact."}</p></div>}</section>
        <section className="simple-card contact-profile-timeline" id="contact-timeline"><header><div><p className="eyebrow">Complete history</p><h2>Interactions and relationship activity</h2><p>Direct interactions, notes, referrals, and events, plus unassigned activity across {contactOrganizationNames(profile)}.</p></div><Activity /></header>
          {timeline.length ? <div>{timeline.map((item) => { const Icon = item.kind === "event" ? CalendarDays : item.kind === "note" ? StickyNote : item.kind === "referral" ? ArrowRight : Activity; return <article className={item.kind} key={item.id}><span><Icon /></span><div><small>{formatSourceDate(item.occurredAt)} · {item.meta}</small><strong>{item.title}</strong><p>{item.summary}</p></div></article>; })}</div> : <div className="empty-state compact"><Activity /><strong>No interactions recorded yet</strong><p>Notes, referrals, events, and organization outreach will appear here.</p></div>}
        </section>
        <section className="simple-card contact-profile-events" id="contact-events"><header><div><p className="eyebrow">Attendance</p><h2>Events attended</h2></div><CalendarDays /></header>{profile.events.length ? <div>{profile.events.map((event) => <article key={event.id}><time>{new Date(event.occurredAt).toLocaleDateString([], { month: "short", day: "2-digit", year: "numeric" })}</time><div><strong>{event.title}</strong><p><MapPin />{event.location}</p>{event.description && <small>{event.description}</small>}</div><span>{event.networkingGroupName || event.sourceName || "Networking / community event"}</span></article>)}</div> : <div className="empty-state compact"><CalendarDays /><strong>No event attendance yet</strong><p>Select this contact when recording an event and it will appear here.</p></div>}</section>
        <section className="simple-card contact-profile-notes" id="contact-notes"><header><div><p className="eyebrow">Direct context</p><h2>Contact notes</h2></div><StickyNote /></header>{profile.notes.length ? <div>{profile.notes.map((note) => <article className={note.pinned ? "pinned" : ""} key={note.id}>{note.pinned && <span><StickyNote />Pinned</span>}<p>{note.noteText}</p><small>{note.updatedBy ?? note.createdBy ?? "Team member"} · {formatSourceDate(note.updatedAt)}</small></article>)}</div> : <div className="empty-state compact"><StickyNote /><strong>No direct notes yet</strong><p>Add contact-specific context from this profile.</p></div>}</section>
      </main>
    </div>
  </div>;
}

function networkingCadenceLabel(group: Pick<NetworkingGroup, "cadenceType" | "cadenceDetails">) {
  const cadence = group.cadenceType === "irregular" ? "Irregular schedule" : `${group.cadenceType.slice(0, 1).toUpperCase()}${group.cadenceType.slice(1)} meetings`;
  return group.cadenceDetails ? `${cadence} · ${group.cadenceDetails}` : cadence;
}

function EventDetailPage({ event, canManage, onBack, onEdit, onManageContacts, onOpenSource, onOpenGroup, onOpenContact }: {
  event: WorkspaceEvent;
  canManage: boolean;
  onBack: () => void;
  onEdit: () => void;
  onManageContacts: () => void;
  onOpenSource: (sourceId: string) => void;
  onOpenGroup: (groupId: string) => void;
  onOpenContact: (contactId: string) => void;
}) {
  const [photos, setPhotos] = useState<EventPhoto[]>([]);
  const [photosLoading, setPhotosLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setPhotosLoading(true);
    fetch(`/api/events/${encodeURIComponent(event.id)}/photos`)
      .then(async (response) => response.ok ? response.json() as Promise<{ data?: EventPhoto[] }> : { data: [] })
      .then((result) => { if (!cancelled) setPhotos(Array.isArray(result.data) ? result.data : []); })
      .catch(() => { if (!cancelled) setPhotos([]); })
      .finally(() => { if (!cancelled) setPhotosLoading(false); });
    return () => { cancelled = true; };
  }, [event.id, event.attachmentCount]);
  const eventDate = new Date(event.occurredAt);
  const isFuture = eventDate.getTime() > Date.now();
  const totalAttendees = event.attendees.length + event.sourceAttendees.length + event.contactAttendees.length;
  return <div className="page simple-page event-detail-page">
    <button type="button" className="contact-profile-back" onClick={onBack}><ArrowLeft />Back to events</button>
    <section className="event-detail-hero">
      <time><strong>{eventDate.toLocaleDateString([], { day: "2-digit" })}</strong><span>{eventDate.toLocaleDateString([], { month: "short" })}</span><small>{eventDate.toLocaleDateString([], { year: "numeric" })}</small></time>
      <div><p className="eyebrow">{isFuture ? "Scheduled event" : "Event record"}</p><h1>{event.title}</h1><p><MapPin />{event.location}</p><span><Clock3 />{eventDate.toLocaleString([], { dateStyle: "full", timeStyle: "short" })}</span></div>
      {canManage && <button type="button" className="button primary" onClick={onEdit}><PencilLine />Edit event</button>}
    </section>
    <section className="event-detail-metrics" aria-label="Event summary"><article><strong>{totalAttendees}</strong><small>People represented</small></article><article><strong>{event.contactAttendees.length}</strong><small>Individual contacts</small></article><article><strong>{event.sourceAttendees.length}</strong><small>Referral sources</small></article><article><strong>{event.attachmentCount}</strong><small>Files and photos</small></article></section>
    <div className="event-detail-layout">
      <main>
        <section className="simple-card event-detail-overview"><header><div><p className="eyebrow">Event details</p><h2>About this event</h2></div><CalendarDays /></header><div className="event-detail-facts"><article><small>Location</small><strong><MapPin />{event.location}</strong></article><article><small>Date and time</small><strong><Clock3 />{eventDate.toLocaleString([], { dateStyle: "long", timeStyle: "short" })}</strong></article>{event.networkingGroupName && event.networkingGroupId ? <button type="button" onClick={() => onOpenGroup(event.networkingGroupId!)}><small>Networking group</small><strong><Users />{event.networkingGroupName}<ChevronRight /></strong></button> : event.sourceName && event.sourceId ? <button type="button" onClick={() => onOpenSource(event.sourceId!)}><small>Primary referral source</small><strong><Building2 />{event.sourceName}<ChevronRight /></strong></button> : <article><small>Group or source</small><strong>Not linked</strong></article>}<article><small>Recorded by</small><strong><Contact />{event.createdBy}</strong></article></div>{event.description ? <div className="event-detail-notes"><p className="eyebrow">Event notes</p><p>{event.description}</p></div> : <div className="empty-state compact"><StickyNote /><strong>No event notes</strong><p>{canManage ? "Edit this event to add an outcome, context, or follow-up notes." : "No notes were recorded for this event."}</p></div>}</section>
        <section className="simple-card event-detail-attendees"><header><div><p className="eyebrow">Attendance</p><h2>Everyone at this event</h2></div>{canManage ? <button type="button" className="button ghost event-detail-add-contacts" onClick={onManageContacts}><UserPlus />Add contacts</button> : <Users />}</header><div>
          <section><header><h3>Contacts</h3><span>{event.contactAttendees.length}</span></header>{event.contactAttendees.length ? event.contactAttendees.map((contact) => <button type="button" key={contact.id} onClick={() => onOpenContact(contact.id)}><span>{contact.name.slice(0, 1).toUpperCase()}</span><div><strong>{contact.name}</strong><small>{[contact.title, contact.sourceName].filter(Boolean).join(" · ") || "Address-book contact"}</small></div><ChevronRight /></button>) : <p>No individual contacts recorded.</p>}</section>
          <section><header><h3>Referral sources</h3><span>{event.sourceAttendees.length}</span></header>{event.sourceAttendees.length ? event.sourceAttendees.map((source) => <button type="button" key={source.id} onClick={() => onOpenSource(source.id)}><Building2 /><div><strong>{source.name}</strong><small>{source.category || "Referral source"}</small></div><ChevronRight /></button>) : <p>No referral sources recorded.</p>}</section>
          <section><header><h3>Team members</h3><span>{event.attendees.length}</span></header>{event.attendees.length ? event.attendees.map((attendee) => <article key={attendee.id}><Contact /><div><strong>{attendee.name}</strong><small>Staff attendee</small></div></article>) : <p>No team members recorded.</p>}</section>
        </div></section>
      </main>
      <aside className="simple-card event-detail-photos"><header><div><p className="eyebrow">Photo record</p><h2>Event photos</h2></div><Camera /></header>{photosLoading ? <div className="networking-photo-loading"><span className="loading-line" />Loading photos…</div> : photos.length ? <div>{photos.map((photo, index) => <a className={index === 0 ? "featured" : ""} key={photo.id} href={`/api/event-photos/${photo.id}/content`} target="_blank" rel="noreferrer"><img src={`/api/event-photos/${photo.id}/content`} alt={photo.caption || `${event.title} — ${photo.fileName}`} /><span><strong>{photo.caption || photo.fileName}</strong><small>{photo.createdBy}</small></span><ExternalLink /></a>)}</div> : <div className="empty-state compact"><Camera /><strong>No photos yet</strong><p>{canManage ? "Use Edit event to attach a photo." : "No photos were uploaded for this event."}</p></div>}</aside>
    </div>
  </div>;
}

function EventContactAttendeesModal({ event, contacts, newlyCreatedContactId, canAddContacts, onAddContact, onClose, onSave }: {
  event: WorkspaceEvent;
  contacts: DirectoryContact[];
  newlyCreatedContactId?: string;
  canAddContacts: boolean;
  onAddContact: () => void;
  onClose: () => void;
  onSave: (contactIds: string[]) => Promise<boolean>;
}) {
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>(event.contactAttendees.map((contact) => contact.id));
  const [busy, setBusy] = useState(false);
  const contactOptions = useMemo(() => {
    const options = [...contacts];
    for (const attendee of event.contactAttendees) {
      if (options.some((contact) => contact.id === attendee.id)) continue;
      options.push({
        id: attendee.id,
        sourceId: attendee.sourceId,
        sourceName: attendee.sourceName ?? "Previous organization",
        sourceCategory: "",
        organizations: [],
        contactMethods: [],
        name: attendee.name,
        title: attendee.title,
        isPrimary: false,
        preferredContactMethod: "any",
        contactStatus: "active",
        version: 1,
        updatedAt: event.occurredAt,
      });
    }
    return options.sort((left, right) => left.name.localeCompare(right.name));
  }, [contacts, event]);
  const visibleContacts = contactOptions.filter((contact) => {
    const organizations = contact.organizations.map((organization) => organization.sourceName).join(" ");
    return `${contact.name} ${contact.title ?? ""} ${contact.sourceName} ${organizations}`.toLowerCase().includes(search.trim().toLowerCase());
  });
  useEffect(() => {
    if (!newlyCreatedContactId) return;
    setSelectedIds((current) => current.includes(newlyCreatedContactId) ? current : [...current, newlyCreatedContactId]);
    setSearch("");
  }, [newlyCreatedContactId]);
  function toggle(contactId: string) {
    setSelectedIds((current) => current.includes(contactId) ? current.filter((id) => id !== contactId) : [...current, contactId]);
  }
  async function submit(submitEvent: FormEvent) {
    submitEvent.preventDefault();
    setBusy(true);
    if (!await onSave(selectedIds)) setBusy(false);
  }
  return <div className="modal-layer" role="dialog" aria-modal="true" aria-label={`Add contacts to ${event.title}`}>
    <button type="button" className="modal-scrim" onClick={onClose} aria-label="Close contact attendance" />
    <form className="modal event-contact-manager" onSubmit={submit}>
      <header><div><p className="eyebrow">Event attendance</p><h2>Add contacts who attended</h2><p>Select anyone from the address book, regardless of which referral source or organization they belong to.</p></div><button type="button" className="icon-button" onClick={onClose} aria-label="Close contact attendance"><X /></button></header>
      <div className="event-contact-manager-body">
        <div className="event-contact-manager-toolbar"><label><Search /><input autoFocus aria-label="Search event contacts" value={search} onChange={(changeEvent) => setSearch(changeEvent.target.value)} placeholder="Search by name, role, or organization" /></label><span>{selectedIds.length} selected</span>{canAddContacts && <button type="button" onClick={onAddContact}><UserPlus />Create new contact</button>}</div>
        <div className="event-attendee-actions"><button type="button" onClick={() => setSelectedIds([...new Set([...selectedIds, ...visibleContacts.map((contact) => contact.id)])])}>Select all shown</button><button type="button" onClick={() => setSelectedIds([])}>Clear</button></div>
        {visibleContacts.length ? <div className="event-attendee-options contact-options event-contact-manager-results">{visibleContacts.map((contact) => <label className={selectedIds.includes(contact.id) ? "selected" : ""} key={contact.id}><input type="checkbox" checked={selectedIds.includes(contact.id)} onChange={() => toggle(contact.id)} /><ContactAvatar name={contact.name} photoId={contact.primaryPhotoId} className="record-avatar contact-avatar" /><span><strong>{contact.name}</strong><small>{[contact.title, contact.sourceName, contact.contactStatus !== "active" ? contactStatusLabel(contact.contactStatus) : ""].filter(Boolean).join(" · ") || "Address-book contact"}</small></span><Check /></label>)}</div> : <div className="event-contact-empty"><Search /><span><strong>No matching contacts</strong><small>Create this person now or try another name or organization.</small></span>{canAddContacts && <button type="button" className="event-empty-add" onClick={onAddContact}><UserPlus />Create new contact</button>}</div>}
      </div>
      <footer><button type="button" className="button ghost" onClick={onClose}>Cancel</button><button type="submit" className="button primary" disabled={busy}>{busy ? "Saving attendance…" : "Save attendance"}<Check /></button></footer>
    </form>
  </div>;
}

function NetworkingMeetingDetailsModal({ event, canManage, onClose, onEdit, onOpenContact }: {
  event: WorkspaceEvent;
  canManage: boolean;
  onClose: () => void;
  onEdit: () => void;
  onOpenContact: (contactId: string) => void;
}) {
  const [photos, setPhotos] = useState<EventPhoto[]>([]);
  const [photosLoading, setPhotosLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setPhotosLoading(true);
    fetch(`/api/events/${encodeURIComponent(event.id)}/photos`)
      .then(async (response) => response.ok ? response.json() as Promise<{ data?: EventPhoto[] }> : { data: [] })
      .then((result) => { if (!cancelled) setPhotos(Array.isArray(result.data) ? result.data : []); })
      .catch(() => { if (!cancelled) setPhotos([]); })
      .finally(() => { if (!cancelled) setPhotosLoading(false); });
    return () => { cancelled = true; };
  }, [event.id]);
  return <div className="modal-layer" role="dialog" aria-modal="true" aria-label={`Meeting details: ${event.title}`}>
    <button className="modal-scrim" onClick={onClose} aria-label="Close" />
    <article className="modal networking-meeting-details">
      <header><div><p className="eyebrow">Meeting history</p><h2>{event.title}</h2><p>{eventDateLabel(event.occurredAt)}</p></div><button type="button" className="icon-button" onClick={onClose} aria-label="Close meeting details"><X /></button></header>
      <div className="networking-meeting-details-body">
        <section className="networking-meeting-facts"><article><MapPin /><span><small>Location</small><strong>{event.location}</strong></span></article><article><CalendarDays /><span><small>Date and time</small><strong>{new Date(event.occurredAt).toLocaleString([], { dateStyle: "long", timeStyle: "short" })}</strong></span></article></section>
        {event.description && <section className="networking-meeting-description"><p className="eyebrow">Meeting notes</p><p>{event.description}</p></section>}
        <section className="networking-meeting-attendance"><header><div><p className="eyebrow">Attendance</p><h3>Who was there</h3></div><Users /></header><div>
          <article><strong>Contacts <span>{event.contactAttendees.length}</span></strong>{event.contactAttendees.length ? event.contactAttendees.map((contact) => <button type="button" key={contact.id} onClick={() => onOpenContact(contact.id)}><span>{contact.name.slice(0, 1).toUpperCase()}</span><div><strong>{contact.name}</strong><small>{[contact.title, contact.sourceName].filter(Boolean).join(" · ")}</small></div><ChevronRight /></button>) : <p>No contacts recorded.</p>}</article>
          <article><strong>Referral sources <span>{event.sourceAttendees.length}</span></strong>{event.sourceAttendees.length ? event.sourceAttendees.map((source) => <div className="networking-attendee-row" key={source.id}><Building2 /><span><strong>{source.name}</strong><small>{source.category || "Referral source"}</small></span></div>) : <p>No referral sources recorded.</p>}</article>
          <article><strong>Team members <span>{event.attendees.length}</span></strong>{event.attendees.length ? event.attendees.map((attendee) => <div className="networking-attendee-row" key={attendee.id}><Contact /><span><strong>{attendee.name}</strong><small>Staff attendee</small></span></div>) : <p>No team members recorded.</p>}</article>
        </div></section>
        <section className="networking-meeting-gallery"><header><div><p className="eyebrow">Photos</p><h3>From this meeting</h3></div><Camera /></header>{photosLoading ? <div className="networking-photo-loading"><span className="loading-line" />Loading photos…</div> : photos.length ? <div>{photos.map((photo) => <a key={photo.id} href={`/api/event-photos/${photo.id}/content`} target="_blank" rel="noreferrer"><img src={`/api/event-photos/${photo.id}/content`} alt={photo.caption || `${event.title} — ${photo.fileName}`} /><span>{photo.caption || photo.fileName}<ExternalLink /></span></a>)}</div> : <div className="empty-state compact"><Camera /><strong>No photos added</strong><p>Photos uploaded to this meeting will appear here.</p></div>}</section>
      </div>
      <footer><button type="button" className="button ghost" onClick={onClose}>Close</button>{canManage && <button type="button" className="button primary" onClick={onEdit}><PencilLine />Edit meeting</button>}</footer>
    </article>
  </div>;
}

function NetworkingGroupContactPicker({ group, contacts, onClose, onAdd }: {
  group: NetworkingGroupDetail;
  contacts: DirectoryContact[];
  onClose: () => void;
  onAdd: (contactId: string) => Promise<boolean>;
}) {
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const memberIds = new Set(group.contacts.map((contact) => contact.id));
  const available = contacts.filter((contact) => !memberIds.has(contact.id) && `${contact.name} ${contact.title ?? ""} ${contact.sourceName}`.toLowerCase().includes(search.trim().toLowerCase()));
  async function add(contactId: string) {
    setBusyId(contactId);
    const saved = await onAdd(contactId);
    if (!saved) setBusyId(null);
  }
  return <div className="modal-layer" role="dialog" aria-modal="true" aria-label={`Add contact to ${group.name}`}>
    <button className="modal-scrim" onClick={onClose} aria-label="Close" />
    <article className="modal networking-contact-picker"><header><div><p className="eyebrow">Group membership</p><h2>Add a contact</h2><p>Choose anyone from the address book to add to {group.name}.</p></div><button type="button" className="icon-button" onClick={onClose}><X /></button></header><div className="networking-contact-picker-body"><label className="search-field"><Search /><input autoFocus value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search contacts or organizations" aria-label="Search contacts to add" /></label>{available.length ? <div>{available.map((contact) => <article key={contact.id}><span>{contact.name.slice(0, 1).toUpperCase()}</span><div><strong>{contact.name}</strong><small>{[contact.title, contact.sourceName].filter(Boolean).join(" · ")}</small></div><button type="button" className="button ghost" disabled={Boolean(busyId)} onClick={() => void add(contact.id)}>{busyId === contact.id ? "Adding…" : "Add"}<Plus /></button></article>)}</div> : <div className="empty-state compact"><Contact /><strong>{search ? "No available contacts match" : "Everyone is already in this group"}</strong><p>{search ? "Try another name or organization." : "All address-book contacts have been added."}</p></div>}</div><footer><button type="button" className="button ghost" onClick={onClose}>Done</button></footer></article>
  </div>;
}

function NetworkingGroupProfilePage({ group, contacts, canManage, onBack, onEdit, onSchedule, onEditEvent, onOpenContact, onAddContact, onSaveNotes }: {
  group: NetworkingGroupDetail;
  contacts: DirectoryContact[];
  canManage: boolean;
  onBack: () => void;
  onEdit: () => void;
  onSchedule: () => void;
  onEditEvent: (event: WorkspaceEvent) => void;
  onOpenContact: (contactId: string) => void;
  onAddContact: (contactId: string) => Promise<boolean>;
  onSaveNotes: (notes: string) => Promise<boolean>;
}) {
  const [selectedMeeting, setSelectedMeeting] = useState<WorkspaceEvent | null>(null);
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notes, setNotes] = useState(group.notes ?? "");
  const [notesBusy, setNotesBusy] = useState(false);
  useEffect(() => { setNotes(group.notes ?? ""); }, [group.id, group.notes]);
  const now = Date.now();
  const upcoming = group.events.filter((event) => new Date(event.occurredAt).getTime() > now)
    .sort((left, right) => new Date(left.occurredAt).getTime() - new Date(right.occurredAt).getTime());
  const past = group.events.filter((event) => new Date(event.occurredAt).getTime() <= now)
    .sort((left, right) => new Date(right.occurredAt).getTime() - new Date(left.occurredAt).getTime());
  const groupContacts = [...group.contacts].sort((left, right) => left.name.localeCompare(right.name));
  async function saveNotes() {
    setNotesBusy(true);
    const saved = await onSaveNotes(notes.trim());
    setNotesBusy(false);
    if (saved) setEditingNotes(false);
  }
  return <div className="page simple-page networking-profile-page">
    <button type="button" className="contact-profile-back" onClick={onBack}><ArrowLeft />Back to networking groups</button>
    <section className="networking-profile-hero">
      <div className="networking-profile-mark"><Users /><span>{group.name.trim().slice(0, 3).toUpperCase()}</span></div>
      <div><p className="eyebrow">Networking group</p><h1>{group.name}</h1><p>{group.description || "A shared home for this group’s meeting rhythm, dates, people, and relationship notes."}</p><span><Clock3 />{networkingCadenceLabel(group)}</span></div>
      {canManage && <div className="networking-profile-actions"><button type="button" className="button ghost" onClick={onEdit}><PencilLine />Edit details</button><button type="button" className="button primary" onClick={onSchedule}><CalendarPlus />Schedule meeting</button></div>}
    </section>
    <div className="networking-profile-layout">
      <aside className="networking-profile-nav">
        <p>Group details</p>
        <nav aria-label="Networking group profile sections"><a href="#group-overview"><Building2 />Overview</a><a href="#group-meetings"><CalendarDays />Meetings <span>{group.events.length}</span></a><a href="#group-photos"><Camera />Photos <span>{group.photos.length}</span></a><a href="#group-contacts"><Contact />Contacts <span>{groupContacts.length}</span></a><a href="#group-notes"><StickyNote />Notes</a></nav>
        {group.website && <a className="networking-website-link" href={group.website} target="_blank" rel="noreferrer"><Globe2 />Visit group website<ExternalLink /></a>}
      </aside>
      <main className="networking-profile-main">
        <section className="networking-profile-metrics" aria-label="Networking group summary"><article><strong>{upcoming.length}</strong><small>Future dates</small></article><article><strong>{past.length}</strong><small>Past meetings</small></article><article><strong>{groupContacts.length}</strong><small>Contacts in group</small></article><article><strong>{group.photos.length}</strong><small>Event photos</small></article></section>
        <section className="simple-card networking-overview" id="group-overview"><header><div><p className="eyebrow">At a glance</p><h2>Group details</h2></div><Building2 /></header><div><article><small>Meeting cadence</small><strong>{networkingCadenceLabel(group)}</strong></article><article><small>Primary location</small><strong>{group.primaryLocation || "Varies by meeting"}</strong></article><article><small>Next meeting</small><strong>{group.nextMeetingAt ? eventDateLabel(group.nextMeetingAt) : "Not scheduled"}</strong></article><article><small>Last meeting</small><strong>{group.lastMeetingAt ? eventDateLabel(group.lastMeetingAt) : "No past meeting"}</strong></article></div></section>
        <section className="simple-card networking-meetings" id="group-meetings"><header><div><p className="eyebrow">Schedule and history</p><h2>Meetings</h2></div>{canManage && <button type="button" className="button ghost" onClick={onSchedule}><Plus />Add future date</button>}</header>
          {upcoming.length > 0 && <div className="networking-meeting-section"><h3>Coming up</h3>{upcoming.map((event) => <article className="future" key={event.id}><time><strong>{new Date(event.occurredAt).toLocaleDateString([], { day: "2-digit" })}</strong><span>{new Date(event.occurredAt).toLocaleDateString([], { month: "short", year: "numeric" })}</span></time><div><span>Scheduled</span><h4>{event.title}</h4><p><MapPin />{event.location}</p>{event.description && <small>{event.description}</small>}</div>{canManage && <button type="button" aria-label={`Edit ${event.title}`} onClick={() => onEditEvent(event)}><PencilLine /></button>}</article>)}</div>}
          <div className="networking-meeting-section"><h3>Meeting history</h3>{past.length ? past.map((event) => <article className="history" key={event.id}><button type="button" className="networking-meeting-open" onClick={() => setSelectedMeeting(event)} aria-label={`View meeting details for ${event.title}`}><time><strong>{new Date(event.occurredAt).toLocaleDateString([], { day: "2-digit" })}</strong><span>{new Date(event.occurredAt).toLocaleDateString([], { month: "short", year: "numeric" })}</span></time>{event.coverPhotoId && <span className="networking-meeting-photo"><img src={`/api/event-photos/${event.coverPhotoId}/content`} alt={`${event.title} event`} /></span>}<span className="networking-meeting-copy"><span>Occurred</span><h4>{event.title}</h4><p><MapPin />{event.location}</p>{event.description && <small>{event.description}</small>}<em>{event.sourceAttendees.length} source{event.sourceAttendees.length === 1 ? "" : "s"} · {event.contactAttendees.length} contact{event.contactAttendees.length === 1 ? "" : "s"} · {event.attendees.length} staff</em></span><span className="networking-meeting-view">View details<ChevronRight /></span></button>{canManage && <button type="button" className="networking-meeting-edit" aria-label={`Edit ${event.title}`} onClick={() => onEditEvent(event)}><PencilLine /></button>}</article>) : <div className="empty-state compact"><CalendarDays /><strong>No past meetings yet</strong><p>Completed meetings will build the group’s history here.</p></div>}</div>
        </section>
        <section className="simple-card networking-group-photos" id="group-photos"><header><div><p className="eyebrow">Shared photo archive</p><h2>Photos from this group</h2></div><span><strong>{group.photos.length}</strong><Camera /></span></header>{group.photos.length ? <div>{group.photos.map((photo, index) => {
          const meeting = group.events.find((event) => event.id === photo.eventId);
          return <article className={index === 0 ? "featured" : ""} key={photo.id}><a href={`/api/event-photos/${photo.id}/content`} target="_blank" rel="noreferrer"><img src={`/api/event-photos/${photo.id}/content`} alt={photo.caption || `${photo.eventTitle} — ${photo.fileName}`} /><span className="networking-group-photo-open"><ExternalLink />Open full photo</span></a><div><span><small>{formatSourceDate(photo.eventOccurredAt)}</small><strong>{photo.caption || photo.eventTitle}</strong><p><MapPin />{photo.eventLocation}</p></span>{meeting ? <button type="button" onClick={() => setSelectedMeeting(meeting)}>View meeting<ChevronRight /></button> : <small>{photo.eventTitle}</small>}</div></article>;
        })}</div> : <div className="empty-state compact"><Camera /><strong>No group photos yet</strong><p>Photos attached to this group’s events will automatically appear here.</p></div>}</section>
        <section className="simple-card networking-contacts" id="group-contacts"><header><div><p className="eyebrow">Relationship network</p><h2>Contacts in this group</h2></div>{canManage ? <button type="button" className="button ghost" onClick={() => setContactPickerOpen(true)}><UserPlus />Add contact</button> : <Contact />}</header>{groupContacts.length ? <div>{groupContacts.map((contact) => <button type="button" onClick={() => onOpenContact(contact.id)} key={contact.id}><span>{contact.name.trim().slice(0, 1).toUpperCase()}</span><strong>{contact.name}</strong><small>{[contact.title, contact.sourceName].filter(Boolean).join(" · ") || "Address-book contact"}</small><em>{contact.eventCount ? `${contact.eventCount} meeting${contact.eventCount === 1 ? "" : "s"}` : "Group member"}</em><ChevronRight /></button>)}</div> : <div className="empty-state compact"><Users /><strong>No contacts in this group yet</strong><p>{canManage ? "Add someone from the address book or record them at a meeting." : "Contacts added to this group will appear here."}</p>{canManage && <button type="button" className="button ghost" onClick={() => setContactPickerOpen(true)}><UserPlus />Add first contact</button>}</div>}</section>
        <section className="simple-card networking-notes" id="group-notes"><header><div><p className="eyebrow">Shared intelligence</p><h2>Group notes</h2></div>{canManage ? <button type="button" className="button ghost" onClick={() => setEditingNotes(true)}><StickyNote />{group.notes ? "Edit note" : "Add note"}</button> : <StickyNote />}</header>{editingNotes ? <div className="networking-notes-editor"><label htmlFor={`networking-notes-${group.id}`}>Shared group note</label><textarea id={`networking-notes-${group.id}`} autoFocus maxLength={8000} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Membership details, introductions, recurring agenda items, or follow-up context…" /><div><button type="button" className="button ghost" disabled={notesBusy} onClick={() => { setNotes(group.notes ?? ""); setEditingNotes(false); }}>Cancel</button><button type="button" className="button primary" disabled={notesBusy} onClick={() => void saveNotes()}>{notesBusy ? "Saving…" : "Save note"}<Check /></button></div></div> : group.notes ? <p>{group.notes}</p> : <div className="empty-state compact"><StickyNote /><strong>No group notes yet</strong><p>{canManage ? "Add membership guidance, introductions, and follow-up context here." : "Managers can add shared context about this group."}</p>{canManage && <button type="button" className="button ghost" onClick={() => setEditingNotes(true)}><Plus />Add a note</button>}</div>}</section>
      </main>
    </div>
    {selectedMeeting && <NetworkingMeetingDetailsModal event={selectedMeeting} canManage={canManage} onClose={() => setSelectedMeeting(null)} onEdit={() => { const event = selectedMeeting; setSelectedMeeting(null); onEditEvent(event); }} onOpenContact={onOpenContact} />}
    {contactPickerOpen && <NetworkingGroupContactPicker group={group} contacts={contacts} onClose={() => setContactPickerOpen(false)} onAdd={async (contactId) => { const saved = await onAddContact(contactId); if (saved) setContactPickerOpen(false); return saved; }} />}
  </div>;
}

function EventsPage({ events, groups, canManage, section, onSectionChange, onCreate, onOpenEvent, onEdit, onOpenSource, onOpenGroup, onCreateGroup }: {
  events: WorkspaceEvent[];
  groups: NetworkingGroup[];
  canManage: boolean;
  section: "events" | "groups";
  onSectionChange: (section: "events" | "groups") => void;
  onCreate: () => void;
  onOpenEvent: (event: WorkspaceEvent) => void;
  onEdit: (event: WorkspaceEvent) => void;
  onOpenSource: (sourceId: string) => void;
  onOpenGroup: (groupId: string) => void;
  onCreateGroup: () => void;
}) {
  const [collectionView, setCollectionView] = useCollectionView("events-layout");
  type EventTableSort = "event" | "date" | "location" | "group" | "files" | "attendees";
  const { sortedRows: sortedEvents, sortKey: eventSortKey, sortDirection: eventSortDirection, changeSort: changeEventSort } = useSortableTable<WorkspaceEvent, EventTableSort>(events, "date", (event, key) => ({
    event: event.title,
    date: new Date(event.occurredAt).getTime(),
    location: event.location,
    group: event.networkingGroupName ?? event.sourceName,
    files: event.attachmentCount,
    attendees: [...event.attendees, ...event.sourceAttendees, ...event.contactAttendees].map((attendee) => attendee.name).join(", "),
  })[key], "descending");
  const now = new Date();
  const people = new Set(events.flatMap((event) => [
    ...event.attendees.map((attendee) => `staff:${attendee.id}`),
    ...event.contactAttendees.map((attendee) => `contact:${attendee.id}`),
  ])).size;
  const futureEvents = events.filter((event) => new Date(event.occurredAt).getTime() > now.getTime()).length;
  const openEvent = (event: WorkspaceEvent) => onOpenEvent(event);
  return (
    <div className="page simple-page events-page">
      <div className="simple-heading events-heading">
        <div><h1>{section === "events" ? "Events" : "Networking groups"}</h1><p>{section === "events" ? "Track past and future meetings, their locations, and everyone involved." : "Keep recurring groups, meeting patterns, notes, future dates, and relationship history together."}</p></div>
        <div className="events-heading-actions">
          {section === "events" && <div className="view-toggle compact" role="group" aria-label="Event view"><button type="button" className={collectionView === "table" ? "active" : ""} aria-pressed={collectionView === "table"} onClick={() => setCollectionView("table")}><Table2 />Table</button><button type="button" className={collectionView === "cards" ? "active" : ""} aria-pressed={collectionView === "cards"} onClick={() => setCollectionView("cards")}><LayoutGrid />Cards</button></div>}
          {canManage && <button className="button primary" onClick={section === "events" ? onCreate : onCreateGroup}><Plus size={16} />{section === "events" ? "New event" : "New group"}</button>}
        </div>
      </div>
      <div className="event-section-tabs" role="tablist" aria-label="Events sections"><button type="button" role="tab" aria-selected={section === "events"} className={section === "events" ? "active" : ""} onClick={() => onSectionChange("events")}><CalendarDays />Events <span>{events.length}</span></button><button type="button" role="tab" aria-selected={section === "groups"} className={section === "groups" ? "active" : ""} onClick={() => onSectionChange("groups")}><Users />Networking groups <span>{groups.length}</span></button></div>
      <section className="compact-stats events-stats" aria-label={section === "events" ? "Event overview" : "Networking group overview"}>
        {section === "events" ? <><article><span><CalendarDays /></span><div><strong>{events.length}</strong><small>Events recorded</small></div></article><article><span><CalendarPlus /></span><div><strong>{futureEvents}</strong><small>Future dates</small></div></article><article><span><Users /></span><div><strong>{people}</strong><small>People represented</small></div></article></> : <><article><span><Users /></span><div><strong>{groups.length}</strong><small>Active groups</small></div></article><article><span><CalendarPlus /></span><div><strong>{groups.reduce((total, group) => total + group.upcomingEventCount, 0)}</strong><small>Future meetings</small></div></article><article><span><Contact /></span><div><strong>{groups.reduce((total, group) => total + group.contactCount, 0)}</strong><small>Contact relationships</small></div></article></>}
      </section>
      {section === "groups" ? <section className="networking-group-grid" aria-label="Networking groups">{groups.map((group) => <button type="button" className="networking-group-card" onClick={() => onOpenGroup(group.id)} key={group.id}><header><span>{group.name.trim().slice(0, 3).toUpperCase()}</span><div><small>Networking group</small><h2>{group.name}</h2></div><ChevronRight /></header><p>{group.description || "Add a description so the team knows this group’s purpose and audience."}</p><div><span><Clock3 />{networkingCadenceLabel(group)}</span><span><MapPin />{group.primaryLocation || "Location varies"}</span></div><footer><span><CalendarPlus /><strong>{group.nextMeetingAt ? eventDateLabel(group.nextMeetingAt) : "No future date"}</strong><small>Next meeting</small></span><span><Users /><strong>{group.contactCount}</strong><small>Contacts met</small></span></footer></button>)}{!groups.length && <div className="empty-state event-empty"><Users /><strong>No networking groups yet</strong><p>{canManage ? "Create a group, then link past and future events to its profile." : "Networking groups connected to your events will appear here."}</p></div>}</section> : collectionView === "cards" ? (
        <section className="event-card-grid" aria-label="Events">
          {sortedEvents.map((event) => {
            const date = new Date(event.occurredAt);
            const eventPeople = [
              ...event.attendees.map((attendee) => ({ ...attendee, kind: "staff" as const })),
              ...event.sourceAttendees.map((attendee) => ({ ...attendee, kind: "source" as const })),
              ...event.contactAttendees.map((attendee) => ({ ...attendee, kind: "contact" as const })),
            ];
            return <article className="event-card clickable" role="button" tabIndex={0} onClick={() => openEvent(event)} onKeyDown={(keyEvent) => { if (keyEvent.key === "Enter" || keyEvent.key === " ") openEvent(event); }} key={event.id}>
              <div className="event-date-block"><strong>{date.toLocaleDateString([], { day: "2-digit" })}</strong><span>{date.toLocaleDateString([], { month: "short" })}</span><small>{date.toLocaleDateString([], { year: "numeric" })}</small></div>
              <div className="event-card-body">
                <header><span className={`event-status ${date.getTime() > now.getTime() ? "scheduled" : "complete"}`}>{date.getTime() > now.getTime() ? "Scheduled" : "Occurred"}</span>{event.networkingGroupName && <button type="button" onClick={(clickEvent) => { clickEvent.stopPropagation(); if (event.networkingGroupId) onOpenGroup(event.networkingGroupId); }}><Users />{event.networkingGroupName}</button>}{!event.networkingGroupName && event.sourceName && <button type="button" onClick={(clickEvent) => { clickEvent.stopPropagation(); if (event.sourceId) onOpenSource(event.sourceId); }}><Building2 />{event.sourceName}</button>}{event.attachmentCount > 0 && <span className="event-file-count"><Paperclip />{event.attachmentCount}</span>}{canManage && <button type="button" className="event-edit-button" onClick={(clickEvent) => { clickEvent.stopPropagation(); onEdit(event); }} aria-label={`Edit ${event.title}`}><PencilLine /></button>}</header>
                {event.coverPhotoId && <span className="event-cover-photo"><img src={`/api/event-photos/${event.coverPhotoId}/content`} alt={`${event.title} event`} /></span>}
                <h2>{event.title}</h2>
                <p className="event-place"><MapPin />{event.location}</p>
                <time>{eventDateLabel(event.occurredAt)}</time>
                {event.description && <p className="event-description">{event.description}</p>}
                <footer>{eventPeople.length ? <><div className="event-attendee-stack">{eventPeople.slice(0, 5).map((attendee, index) => <span className={attendee.kind} style={{ zIndex: 6 - index }} title={`${attendee.name} · ${attendee.kind === "staff" ? "Staff" : attendee.kind === "source" ? "Referral source" : "Contact"}`} key={`${attendee.kind}:${attendee.id}`}>{attendee.name.trim().slice(0, 1).toUpperCase()}</span>)}{eventPeople.length > 5 && <span>+{eventPeople.length - 5}</span>}</div><strong>{eventPeople.map((attendee) => attendee.name).join(", ")}</strong></> : <strong>Attendance will be recorded after the meeting.</strong>}</footer>
              </div>
            </article>;
          })}
          {!events.length && <div className="empty-state event-empty"><CalendarDays /><strong>No events recorded yet</strong><p>{canManage ? "Create an event and select the staff, referral sources, and contacts who attended." : "Events you attend will appear here."}</p></div>}
        </section>
      ) : (
        <div className="event-table-shell">
          <table className="event-table"><thead><tr><SortableTableHeader column="event" label="Event" sortKey={eventSortKey} sortDirection={eventSortDirection} onSort={changeEventSort} /><SortableTableHeader column="date" label="Date" sortKey={eventSortKey} sortDirection={eventSortDirection} onSort={changeEventSort} /><SortableTableHeader column="location" label="Location" sortKey={eventSortKey} sortDirection={eventSortDirection} onSort={changeEventSort} /><SortableTableHeader column="group" label="Group / source" sortKey={eventSortKey} sortDirection={eventSortDirection} onSort={changeEventSort} /><SortableTableHeader column="files" label="Files" sortKey={eventSortKey} sortDirection={eventSortDirection} onSort={changeEventSort} /><SortableTableHeader column="attendees" label="Attendees" sortKey={eventSortKey} sortDirection={eventSortDirection} onSort={changeEventSort} /></tr></thead><tbody>{sortedEvents.map((event) => <tr key={event.id} className="clickable" onClick={() => openEvent(event)} onKeyDown={(keyEvent) => { if (keyEvent.key === "Enter" || keyEvent.key === " ") openEvent(event); }} tabIndex={0}><td><strong>{event.title}</strong>{event.description && <small>{event.description}</small>}</td><td><time>{eventDateLabel(event.occurredAt)}</time></td><td><span className="event-table-location"><MapPin />{event.location}</span></td><td>{event.networkingGroupName ? <button type="button" onClick={(clickEvent) => { clickEvent.stopPropagation(); if (event.networkingGroupId) onOpenGroup(event.networkingGroupId); }}>{event.networkingGroupName}</button> : event.sourceName ? <button type="button" onClick={(clickEvent) => { clickEvent.stopPropagation(); if (event.sourceId) onOpenSource(event.sourceId); }}>{event.sourceName}</button> : <span>Not linked</span>}</td><td><span className="event-table-files"><Paperclip />{event.attachmentCount}</span></td><td>{[...event.attendees, ...event.sourceAttendees, ...event.contactAttendees].map((attendee) => attendee.name).join(", ") || "Not recorded"}</td></tr>)}</tbody></table>
          {!events.length && <div className="empty-state"><CalendarDays /><strong>No events recorded yet</strong></div>}
        </div>
      )}
    </div>
  );
}

function SourcesPage({
  sourceRecords,
  tasks,
  currentUserId,
  role,
  assignees,
  onOpen,
  onLog,
  onContacted,
  onSourceContacted,
  onSnooze,
  onBulkUpdate,
  onViewTasks,
  sourceLabelSingular,
  sourceLabelPlural,
}: {
  sourceRecords: DemoSource[];
  tasks: DemoTask[];
  currentUserId: string;
  role: WorkspaceRole;
  assignees: SourceAssignee[];
  onOpen: (id: string) => void;
  onLog: (id: string) => void;
  onContacted: (task: DemoTask) => void;
  onSourceContacted: (source: DemoSource) => void;
  onSnooze: (item: FollowUpItem) => void;
  onBulkUpdate: (ids: string[], changes: BulkSourceChange) => Promise<boolean>;
  onViewTasks: () => void;
  sourceLabelSingular: string;
  sourceLabelPlural: string;
}) {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [hospitalFilter, setHospitalFilter] = useState<HospitalFilter>("all");
  const [owner, setOwner] = useState("all");
  const [qualityOnly, setQualityOnly] = useState(false);
  const [sortKey, setSortKey] = useState<SourceSortKey>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("ascending");
  const [savedViews, setSavedViews] = useState<SourceSavedView[]>(readSavedSourceViews);
  const [activeSavedView, setActiveSavedView] = useState("");
  const [savingView, setSavingView] = useState(false);
  const [viewName, setViewName] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkAction, setBulkAction] = useState<"category" | "owner" | "hospital">("category");
  const [bulkValue, setBulkValue] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [collectionView, setCollectionView] = useCollectionView("referral-source-layout");
  const categories = useMemo(
    () => [...new Set(sourceRecords.map((source) => source.category).filter(Boolean))].sort(),
    [sourceRecords],
  );
  const isAdmin = role === "admin";
  const canViewEverySource = canViewAllSources(role);
  const qualitySources = sourceRecords.filter((source) => sourceQualityIssues(source).length > 0);
  const baseFiltered = sourceRecords.filter(
    (source) =>
      (category === "all" || source.category === category) &&
      (hospitalFilter === "all" || (hospitalFilter === "hospitals" ? source.isHospital : !source.isHospital)) &&
      (!canViewEverySource || owner === "all" || (owner === "me" ? source.ownerUserId === currentUserId : source.ownerUserId === owner)) &&
      (!qualityOnly || sourceQualityIssues(source).length > 0) &&
      [source.name, source.contact, source.category, source.description, source.notes].join(" ").toLowerCase().includes(search.toLowerCase()),
  );
  const filtered = [...baseFiltered].sort((left, right) => {
    const leftHealth = relationshipHealth(left);
    const rightHealth = relationshipHealth(right);
    const values: Record<SourceSortKey, [TableSortValue, TableSortValue]> = {
      name: [left.name, right.name],
      hospital: [left.isHospital ? 1 : 0, right.isHospital ? 1 : 0],
      category: [left.category, right.category],
      contact: [left.contact, right.contact],
      owner: [left.ownerName, right.ownerName],
      "last-contact": [left.lastInteractionAtIso ? new Date(left.lastInteractionAtIso).getTime() : null, right.lastInteractionAtIso ? new Date(right.lastInteractionAtIso).getTime() : null],
      "contact-age": [sourceContactAge(left).days, sourceContactAge(right).days],
      "next-contact": [left.nextContactAt ? new Date(left.nextContactAt).getTime() : null, right.nextContactAt ? new Date(right.nextContactAt).getTime() : null],
      health: [leftHealth.rank, rightHealth.rank],
      referrals: [left.referralTotal, right.referralTotal],
      priority: [["daily", "weekly", "monthly", "other"].indexOf(left.referralPriority), ["daily", "weekly", "monthly", "other"].indexOf(right.referralPriority)],
    };
    const [leftValue, rightValue] = values[sortKey];
    return compareTableValues(leftValue, rightValue, sortDirection) || left.name.localeCompare(right.name);
  });
  const canManageSource = (source: DemoSource) => canAccessSource(role, currentUserId, source.ownerUserId ?? null);
  const canLogSource = (source: DemoSource) => canLogSourceActivity(role, currentUserId, source.ownerUserId ?? null);
  const assignedSourceCount = sourceRecords.filter((source) => source.ownerUserId === currentUserId).length;
  const currentTasks = tasks
    .filter((task) => task.assigneeUserId === currentUserId)
    .sort((left, right) => new Date(left.dueAtIso).getTime() - new Date(right.dueAtIso).getTime());
  const manageableFiltered = filtered.filter(canManageSource);
  const allVisibleSelected = manageableFiltered.length > 0 && manageableFiltered.every((source) => selectedIds.has(source.id));

  function changeSort(next: SourceSortKey) {
    if (sortKey === next) setSortDirection((current) => current === "ascending" ? "descending" : "ascending");
    else {
      setSortKey(next);
      setSortDirection(next === "hospital" ? "descending" : "ascending");
    }
    setActiveSavedView("");
  }

  function selectSavedView(viewId: string) {
    setActiveSavedView(viewId);
    const saved = savedViews.find((item) => item.id === viewId);
    if (!saved) return;
    setSearch(saved.search);
    setCategory(saved.category);
    setHospitalFilter(saved.hospitalFilter);
    setOwner(saved.owner);
    setQualityOnly(saved.qualityOnly);
    setSortKey(saved.sortKey);
    setSortDirection(saved.sortDirection);
  }

  function saveCurrentView(event: FormEvent) {
    event.preventDefault();
    const name = viewName.trim();
    if (!name) return;
    const saved: SourceSavedView = {
      id: crypto.randomUUID(), name, search, category, hospitalFilter, owner, qualityOnly, sortKey, sortDirection,
    };
    const next = [...savedViews, saved];
    setSavedViews(next);
    window.localStorage.setItem("referral-source-saved-views", JSON.stringify(next));
    setActiveSavedView(saved.id);
    setViewName("");
    setSavingView(false);
  }

  function deleteSavedView() {
    if (!activeSavedView) return;
    const next = savedViews.filter((item) => item.id !== activeSavedView);
    setSavedViews(next);
    window.localStorage.setItem("referral-source-saved-views", JSON.stringify(next));
    setActiveSavedView("");
  }

  async function applyBulkUpdate(event: FormEvent) {
    event.preventDefault();
    if (!bulkValue || !selectedIds.size) return;
    setBulkBusy(true);
    const saved = await onBulkUpdate([...selectedIds], bulkAction === "category"
      ? { category: bulkValue }
      : bulkAction === "hospital"
        ? { isHospital: bulkValue === "yes" }
        : { ownerUserId: bulkValue });
    setBulkBusy(false);
    if (saved) {
      setSelectedIds(new Set());
      setBulkValue("");
    }
  }
  const todaySources = role === "task_user"
    ? []
    : canViewEverySource
      ? sourceRecords
      : sourceRecords.filter((source) => !source.ownerUserId || source.ownerUserId === currentUserId);
  return (
    <div className="page simple-page sources-page">
      <div className="simple-heading">
        <div><h1>All {sourceLabelPlural.toLowerCase()}</h1><p>{filtered.length} relationships in view{role === "reports" ? " · organization-wide read-only access" : !isAdmin ? ` · ${assignedSourceCount} assigned to you` : ""}</p></div>
      </div>
      {role !== "reports" && <section className="dashboard-task-block" aria-labelledby="dashboard-task-title">
        <header><span><ClipboardList /></span><div><p className="eyebrow">Your work queue</p><h2 id="dashboard-task-title">My current tasks</h2><p>{currentTasks.length ? `${currentTasks.length} open task${currentTasks.length === 1 ? "" : "s"} assigned to you` : "No open tasks are currently assigned to you"}</p></div><button type="button" onClick={onViewTasks}>View all tasks <ArrowRight /></button></header>
        {currentTasks.length ? <div className="dashboard-task-list">{currentTasks.slice(0, 4).map((task) => <button type="button" className={`dashboard-task-item ${task.timing} priority-${task.priority}`} key={task.id} onClick={onViewTasks}>
          <span className="dashboard-task-check"><Check /></span>
          <span><strong>{task.title}</strong><small>{task.entity}{task.description ? ` · ${task.description}` : ""}</small></span>
          <span className="dashboard-task-due"><em>{task.priority}</em><time>{task.timing === "overdue" ? "Overdue · " : ""}{task.dueAt}</time></span>
          <ChevronRight />
        </button>)}</div> : <div className="dashboard-task-empty"><CheckCircle2 /><span><strong>You’re caught up</strong><small>New assignments will appear here as soon as they are created.</small></span></div>}
        {currentTasks.length > 4 && <button type="button" className="dashboard-task-more" onClick={onViewTasks}>+{currentTasks.length - 4} more assigned task{currentTasks.length - 4 === 1 ? "" : "s"}</button>}
      </section>}
      {role !== "reports" && <TodaysContacts tasks={tasks} sources={todaySources} showAssignee={isAdmin} onOpen={onOpen} onContacted={onContacted} onSourceContacted={onSourceContacted} onSnooze={onSnooze} />}
      <section className="relationship-overview" aria-label="Relationship health overview">
        <article><span className="health-dot healthy" /><div><strong>{sourceRecords.filter((source) => relationshipHealth(source).code === "healthy").length}</strong><small>On track</small></div></article>
        <article><span className="health-dot attention" /><div><strong>{sourceRecords.filter((source) => relationshipHealth(source).code === "attention").length}</strong><small>Need attention</small></div></article>
        <article><span className="health-dot stale" /><div><strong>{sourceRecords.filter((source) => relationshipHealth(source).code === "stale").length}</strong><small>Stale</small></div></article>
        <button className={qualityOnly ? "active" : ""} onClick={() => { setQualityOnly((current) => !current); setActiveSavedView(""); }}><CircleAlert /><span><strong>{qualitySources.length}</strong><small>Need data cleanup</small></span><ChevronRight /></button>
      </section>
      <div className="simple-toolbar source-toolbar">
        <label className="search-field"><Search size={17} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search source or contact" /></label>
        <label className="category-filter">
          <Filter size={15} />
          <select aria-label="Filter by source category" value={category} onChange={(event) => setCategory(event.target.value)}>
            <option value="all">All categories</option>
            {categories.map((item) => <option key={item}>{item}</option>)}
          </select>
        </label>
        <label className="category-filter hospital-filter">
          <Hospital size={15} />
          <select aria-label="Filter by hospital status" value={hospitalFilter} onChange={(event) => { setHospitalFilter(event.target.value as HospitalFilter); setActiveSavedView(""); }}>
            <option value="all">All source types</option>
            <option value="hospitals">Hospitals only</option>
            <option value="non-hospitals">Non-hospitals</option>
          </select>
        </label>
        {canViewEverySource && (
          <label className="category-filter owner-filter">
            <Users size={15} />
            <select aria-label="Filter by assigned user" value={owner} onChange={(event) => setOwner(event.target.value)}>
              <option value="all">All assigned users</option>
              <option value="me">Assigned to me</option>
              {assignees.filter((assignee) => assignee.id !== currentUserId).map((assignee) => <option key={assignee.id} value={assignee.id}>{assignee.name}</option>)}
            </select>
          </label>
        )}
        <label className="category-filter source-sort-control">
          <ArrowUpDown size={15} />
          <select aria-label={`Sort ${sourceLabelPlural.toLowerCase()}`} value={sortKey} onChange={(event) => changeSort(event.target.value as SourceSortKey)}>
            <option value="name">Name</option><option value="hospital">Hospitals first</option><option value="category">Category</option><option value="contact">Primary contact</option><option value="last-contact">Last contacted</option><option value="contact-age">Days since contact</option><option value="next-contact">Contact next</option><option value="owner">Owner</option><option value="health">Relationship health</option><option value="referrals">Referrals</option><option value="priority">Referral priority</option>
          </select>
        </label>
        <button className="sort-direction-button" type="button" onClick={() => setSortDirection((current) => current === "ascending" ? "descending" : "ascending")} aria-label={`Sort ${sortDirection === "ascending" ? "descending" : "ascending"}`}>{sortDirection === "ascending" ? "A–Z" : "Z–A"}</button>
        <ViewToggle value={collectionView} onChange={setCollectionView} label={`${sourceLabelSingular} view`} />
      </div>
      <div className="saved-view-bar">
        <label>Saved view<select aria-label="Saved source view" value={activeSavedView} onChange={(event) => selectSavedView(event.target.value)}><option value="">Current filters</option>{savedViews.map((saved) => <option key={saved.id} value={saved.id}>{saved.name}</option>)}</select></label>
        <button className="text-button" type="button" onClick={() => setSavingView((current) => !current)}><Save />Save current view</button>
        {activeSavedView && <button className="text-button danger" type="button" onClick={deleteSavedView}><Trash2 />Delete view</button>}
        {savingView && <form className="save-view-form" onSubmit={saveCurrentView}><label>View name<input autoFocus required maxLength={60} value={viewName} onChange={(event) => setViewName(event.target.value)} placeholder="Example: My overdue hospitals" /></label><button className="button primary">Save</button><button type="button" className="button ghost" onClick={() => setSavingView(false)}>Cancel</button></form>}
      </div>
      {qualityOnly && <div className="quality-queue-banner"><CircleAlert /><div><strong>Data-quality queue</strong><p>Showing sources missing a contact, communication method, category, owner, or relationship notes.</p></div><button className="button ghost" onClick={() => setQualityOnly(false)}>Show all sources</button></div>}
      {collectionView === "table" && selectedIds.size > 0 && <form className="bulk-source-bar" onSubmit={applyBulkUpdate}>
        <strong>{selectedIds.size} selected</strong>
        <label>Change<select aria-label="Bulk source action" value={bulkAction} onChange={(event) => { setBulkAction(event.target.value as "category" | "owner" | "hospital"); setBulkValue(""); }}><option value="category">Category</option><option value="hospital">Hospital designation</option>{isAdmin && <option value="owner">Assigned person</option>}</select></label>
        {bulkAction === "category" ? <label>To<select required aria-label="Bulk category" value={bulkValue} onChange={(event) => setBulkValue(event.target.value)}><option value="">Choose category</option>{categories.map((item) => <option key={item}>{item}</option>)}</select></label> : bulkAction === "hospital" ? <label>To<select required aria-label="Bulk hospital designation" value={bulkValue} onChange={(event) => setBulkValue(event.target.value)}><option value="">Choose designation</option><option value="yes">Hospital</option><option value="no">Not a hospital</option></select></label> : <label>To<select required aria-label="Bulk assigned person" value={bulkValue} onChange={(event) => setBulkValue(event.target.value)}><option value="">Choose person</option>{assignees.map((assignee) => <option value={assignee.id} key={assignee.id}>{assignee.name}</option>)}</select></label>}
        <button className="button primary" disabled={bulkBusy}>{bulkBusy ? "Applying…" : "Apply"}</button><button type="button" className="button ghost" onClick={() => setSelectedIds(new Set())}>Clear</button>
      </form>}
      {collectionView === "cards" ? <div className="source-card-grid">
        {filtered.map((source) => (
          <article className="source-card" key={source.id}>
            <button className="source-card-title" onClick={() => onOpen(source.id)}>
              <span className={`source-icon${source.isHospital ? " hospital" : ""}`}>{source.isHospital ? <Hospital size={18} /> : <Building2 size={18} />}</span>
              <span><strong>{source.name}</strong><small>{source.category} · {source.contact}</small>{source.isHospital && <em className="hospital-badge"><Hospital />Hospital</em>}</span>
              <span className="source-title-status"><i className={`relationship-health ${relationshipHealth(source).code}`}>{relationshipHealth(source).label}</i><ChevronRight size={18} /></span>
            </button>
            <div className="source-contact-dates">
              <div><small>Last contacted</small><strong>{source.lastInteractionAt}</strong></div>
              <ArrowRight size={15} />
              <div className={source.nextTaskAt === "Today" ? "due" : ""}><small>Contact next</small><strong>{source.nextTaskAt}</strong></div>
            </div>
            {(source.lastActivityNotes || source.description || source.notes) && (
              <button className="source-card-note" onClick={() => onOpen(source.id)} aria-label={`Open ${source.name} details`}>
                <span>{source.lastActivityNotes ? activityTypeLabel(source.lastActivityTypeCode) : source.description ? "Pinned note" : "Source note"}</span>
                <p>{source.lastActivityNotes || source.description || source.notes}</p><ChevronRight size={15} />
              </button>
            )}
            <footer>
              <span>Owner <b>{source.ownerName}</b></span>
              <span className="source-referral-total"><BarChart3 /> <b>{source.referralTotal}</b> referrals</span>
              {sourceQualityIssues(source).length > 0 && <span className="quality-count"><CircleAlert />{sourceQualityIssues(source).length} missing</span>}
              <span className={`cadence priority-${source.referralPriority}`}>{referralPriorityLabel(source.referralPriority)} priority</span>
              {canLogSource(source) && <button className="button source-log-button" onClick={() => onLog(source.id)} aria-label={`Log contact with ${source.name}`}><Phone size={13} />Log contact</button>}
            </footer>
          </article>
        ))}
        {!filtered.length && <div className="empty-state"><Building2 /><strong>No sources found</strong><p>Try a different search or category.</p></div>}
      </div> : (
        <div className="data-table-shell source-table-shell">
          <table className="data-table source-data-table">
            <thead><tr><th className="select-column"><input type="checkbox" aria-label="Select all manageable sources" disabled={!manageableFiltered.length} checked={allVisibleSelected} onChange={(event) => setSelectedIds(event.target.checked ? new Set(manageableFiltered.map((source) => source.id)) : new Set())} /></th><SortableTableHeader column="name" label="Referral source" sortKey={sortKey} sortDirection={sortDirection} onSort={changeSort} /><SortableTableHeader column="hospital" label="Hospital" sortKey={sortKey} sortDirection={sortDirection} onSort={changeSort} /><SortableTableHeader column="contact" label="Primary contact" sortKey={sortKey} sortDirection={sortDirection} onSort={changeSort} /><SortableTableHeader column="last-contact" label="Latest interaction" sortKey={sortKey} sortDirection={sortDirection} onSort={changeSort} /><SortableTableHeader column="contact-age" label="Days since contact" sortKey={sortKey} sortDirection={sortDirection} onSort={changeSort} /><SortableTableHeader column="next-contact" label="Contact next" sortKey={sortKey} sortDirection={sortDirection} onSort={changeSort} /><SortableTableHeader column="owner" label="Owner" sortKey={sortKey} sortDirection={sortDirection} onSort={changeSort} /><SortableTableHeader column="health" label="Health" sortKey={sortKey} sortDirection={sortDirection} onSort={changeSort} /><SortableTableHeader column="referrals" label="Referrals" sortKey={sortKey} sortDirection={sortDirection} onSort={changeSort} /><SortableTableHeader column="priority" label="Referral priority" sortKey={sortKey} sortDirection={sortDirection} onSort={changeSort} /><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>
              {filtered.map((source) => {
                const contactAge = sourceContactAge(source);
                return <tr
                  key={source.id}
                  className="clickable-table-row"
                  tabIndex={0}
                  aria-label={`Open ${source.name}`}
                  onClick={(event) => {
                    if ((event.target as HTMLElement).closest("button, a, input, select, textarea")) return;
                    onOpen(source.id);
                  }}
                  onKeyDown={(event) => {
                    if (event.target !== event.currentTarget || !["Enter", " "].includes(event.key)) return;
                    event.preventDefault();
                    onOpen(source.id);
                  }}
                >
                  <td className="select-column"><input type="checkbox" aria-label={canManageSource(source) ? `Select ${source.name}` : `${source.name} is managed by ${source.ownerName}`} disabled={!canManageSource(source)} checked={selectedIds.has(source.id)} onChange={(event) => setSelectedIds((current) => { const next = new Set(current); if (event.target.checked) next.add(source.id); else next.delete(source.id); return next; })} /></td>
                  <td><button className="table-primary-link" onClick={() => onOpen(source.id)}>{source.name}</button><small>{source.category}</small>{source.description && <small className="table-pinned-note"><StickyNote />{source.description}</small>}</td>
                  <td>{source.isHospital ? <span className="hospital-badge table"><Hospital />Hospital</span> : <span className="hospital-none">—</span>}</td>
                  <td><strong>{source.contact}</strong>{source.contactPhone && <a href={`tel:${source.contactPhone}`}>{source.contactPhone}</a>}{source.contactEmail && <a href={`mailto:${source.contactEmail}`}>{source.contactEmail}</a>}</td>
                  <td><span className="table-activity-type">{activityTypeLabel(source.lastActivityTypeCode)}</span><strong>{source.lastInteractionAt}</strong>{source.lastActivityNotes && <small>{source.lastActivityNotes}</small>}</td>
                  <td><span className={`table-contact-age ${contactAge.status}`}><strong>{contactAge.label}</strong><small>{contactAge.days === null ? "No contact logged" : "Since last contact"}</small></span></td>
                  <td className={source.nextTaskAt === "Today" ? "table-due" : ""}><strong>{source.nextTaskAt}</strong></td>
                  <td><strong>{source.ownerName}</strong></td>
                  <td><span className={`relationship-health ${relationshipHealth(source).code}`}>{relationshipHealth(source).label}</span>{sourceQualityIssues(source).length > 0 && <small>{sourceQualityIssues(source).join(" · ")}</small>}</td>
                  <td><strong>{source.referralTotal}</strong><small>All time</small></td>
                  <td><span className={`table-cadence priority-${source.referralPriority}`}><strong>{referralPriorityLabel(source.referralPriority)}</strong><small>{source.cadence}</small></span></td>
                  <td><div className="table-actions">{canLogSource(source) && <button className="icon-button" onClick={() => onLog(source.id)} aria-label={`Log contact with ${source.name}`}><Phone /></button>}<button className="icon-button" onClick={() => onOpen(source.id)} aria-label={`View ${source.name}`}><ChevronRight /></button></div></td>
                </tr>;
              })}
            </tbody>
          </table>
          {!filtered.length && <div className="empty-state"><Building2 /><strong>No sources found</strong><p>Try a different search or category.</p></div>}
        </div>
      )}
    </div>
  );
}

type LeadFeatureSet = Pick<SessionMember,
  "leadPipelineEnabled" | "leadContactDetailsEnabled" | "leadActivityTrackingEnabled"
>;

type LeadCreateInput = {
  prospectName: string;
  inquiryDate: string;
  statusCode: LeadStatus;
  payerCode: string | null;
  acuityCode: string | null;
  referralChannelCode: string | null;
  sourceId: string | null;
  ownerUserId: string | null;
  currentResidence: string | null;
  contacts: Array<{ name?: string; relationship?: string; phone?: string; email?: string; isPrimary: boolean }>;
};

const LEAD_STATUSES: Array<{ code: LeadStatus; label: string }> = [
  { code: "active", label: "Active" },
  { code: "screening", label: "Screening" },
  { code: "approved-pending", label: "Approved · pending" },
  { code: "waitlist", label: "Waitlist" },
  { code: "moved-in", label: "Moved in" },
  { code: "approved-no-move", label: "Approved · no move" },
  { code: "inactive", label: "Inactive" },
  { code: "denied", label: "Denied" },
];

function leadStatusLabel(status: LeadStatus): string {
  return LEAD_STATUSES.find((option) => option.code === status)?.label ?? status;
}

function leadSummaryFromApi(row: Record<string, unknown>): LeadSummary {
  return {
    id: String(row.id),
    prospectName: String(row.prospectName ?? row.prospect_name ?? "Unnamed lead"),
    payerCode: row.payerCode || row.payer_code ? String(row.payerCode ?? row.payer_code) : undefined,
    acuityCode: row.acuityCode || row.acuity_code ? String(row.acuityCode ?? row.acuity_code) : undefined,
    statusCode: String(row.statusCode ?? row.status_code ?? "active") as LeadStatus,
    inquiryDate: String(row.inquiryDate ?? row.inquiry_date ?? ""),
    needsReview: Boolean(row.needsReview ?? row.needs_review),
    version: Number(row.version ?? 1),
    updatedAt: String(row.updatedAt ?? row.updated_at ?? ""),
    sourceName: row.sourceName || row.source_name ? String(row.sourceName ?? row.source_name) : undefined,
    ownerName: row.ownerName || row.owner_name ? String(row.ownerName ?? row.owner_name) : undefined,
    lastActivityAt: row.lastActivityAt ? String(row.lastActivityAt) : undefined,
    nextFollowUpAt: row.nextFollowUpAt ? String(row.nextFollowUpAt) : undefined,
  };
}

function leadDetailFromApi(row: Record<string, unknown>): LeadDetail {
  const summary = leadSummaryFromApi(row);
  const rawContacts = Array.isArray(row.contacts) ? row.contacts as Array<Record<string, unknown>> : [];
  const rawActivities = Array.isArray(row.activities) ? row.activities as Array<Record<string, unknown>> : [];
  return {
    ...summary,
    referralChannelCode: row.referral_channel_code ? String(row.referral_channel_code) : undefined,
    waiverStatus: row.waiver_status ? String(row.waiver_status) : undefined,
    currentResidence: row.current_residence ? String(row.current_residence) : undefined,
    sourceId: row.source_id ? String(row.source_id) : undefined,
    sourceDescription: row.source_description ? String(row.source_description) : undefined,
    ownerUserId: row.owner_user_id ? String(row.owner_user_id) : undefined,
    contacts: rawContacts.map((contact) => ({
      id: String(contact.id),
      name: contact.name ? String(contact.name) : undefined,
      relationship: contact.relationship ? String(contact.relationship) : undefined,
      phone: contact.phone ? String(contact.phone) : undefined,
      email: contact.email ? String(contact.email) : undefined,
      isPrimary: Boolean(contact.is_primary),
    })),
    activities: rawActivities.map((activity): LeadActivity => ({
      id: String(activity.id),
      activityTypeCode: String(activity.activity_type_code ?? "note"),
      occurredAt: String(activity.occurred_at ?? ""),
      summary: String(activity.summary ?? "Lead update"),
      notes: activity.notes ? String(activity.notes) : undefined,
      actorName: activity.actor_name ? String(activity.actor_name) : undefined,
    })),
  };
}

function LeadsPage({
  leads,
  features,
  canEdit,
  loading,
  onCreate,
  onOpen,
  leadLabelSingular,
  leadLabelPlural,
}: {
  leads: LeadSummary[];
  features: LeadFeatureSet;
  canEdit: boolean;
  loading: boolean;
  onCreate: () => void;
  onOpen: (lead: LeadSummary) => void;
  leadLabelSingular: string;
  leadLabelPlural: string;
}) {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<LeadStatus | "all">("all");
  const filteredLeads = leads.filter((lead) => {
    const matchesSearch = `${lead.prospectName} ${lead.sourceName ?? ""} ${lead.ownerName ?? ""}`.toLowerCase().includes(search.toLowerCase());
    return matchesSearch && (status === "all" || lead.statusCode === status);
  });
  type LeadTableSort = "lead" | "status" | "source" | "owner" | "inquiry" | "activity";
  const { sortedRows: visible, sortKey: leadSortKey, sortDirection: leadSortDirection, changeSort: changeLeadSort } = useSortableTable<LeadSummary, LeadTableSort>(filteredLeads, "inquiry", (lead, key) => ({
    lead: lead.prospectName,
    status: leadStatusLabel(lead.statusCode),
    source: lead.sourceName,
    owner: lead.ownerName,
    inquiry: lead.inquiryDate ? new Date(lead.inquiryDate).getTime() : null,
    activity: lead.lastActivityAt ? new Date(lead.lastActivityAt).getTime() : null,
  })[key], "descending");
  const open = leads.filter((lead) => ["active", "screening", "approved-pending", "waitlist"].includes(lead.statusCode)).length;
  const movedIn = leads.filter((lead) => lead.statusCode === "moved-in").length;

  return <div className="page list-page leads-page">
    <div className="page-heading">
      <div><p className="eyebrow">Optional workspace</p><h1>{leadLabelPlural}</h1><p>Follow each {leadLabelSingular.toLowerCase()} from first inquiry through the next outcome.</p></div>
      {canEdit && <button className="button primary" onClick={onCreate}><Plus />Add {leadLabelSingular.toLowerCase()}</button>}
    </div>
    <section className="lead-capability-strip" aria-label="Enabled individual lead features">
      <span><ClipboardList /></span>
      <div><strong>Tracking is tailored to this workspace</strong><small>{[
        features.leadPipelineEnabled && "Pipeline",
        features.leadContactDetailsEnabled && "Contact details",
        features.leadActivityTrackingEnabled && "Activity history",
      ].filter(Boolean).join(" · ")}</small></div>
      <em>{leads.length} {leads.length === 1 ? leadLabelSingular.toLowerCase() : leadLabelPlural.toLowerCase()}</em>
    </section>
    {features.leadPipelineEnabled && <section className="lead-metric-grid">
      <article><span className="metric-icon sage"><ClipboardList /></span><div><small>OPEN PIPELINE</small><strong>{open}</strong></div></article>
      <article><span className="metric-icon ochre"><Activity /></span><div><small>IN SCREENING</small><strong>{leads.filter((lead) => lead.statusCode === "screening").length}</strong></div></article>
      <article><span className="metric-icon blue"><CheckCircle2 /></span><div><small>MOVED IN</small><strong>{movedIn}</strong></div></article>
    </section>}
    <div className="filterbar lead-filterbar">
      <label className="search-field"><Search /><input aria-label="Search individual leads" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search name, source, or owner" /></label>
      {features.leadPipelineEnabled && <select aria-label="Filter leads by status" value={status} onChange={(event) => setStatus(event.target.value as LeadStatus | "all")}><option value="all">All statuses</option>{LEAD_STATUSES.map((option) => <option value={option.code} key={option.code}>{option.label}</option>)}</select>}
      <span>{visible.length} in view</span>
    </div>
    <div className="data-table-card lead-table-card">
      <table className="data-table leads-table">
        <thead><tr><SortableTableHeader column="lead" label={leadLabelSingular} sortKey={leadSortKey} sortDirection={leadSortDirection} onSort={changeLeadSort} />{features.leadPipelineEnabled && <><SortableTableHeader column="status" label="Status" sortKey={leadSortKey} sortDirection={leadSortDirection} onSort={changeLeadSort} /><SortableTableHeader column="source" label="Source" sortKey={leadSortKey} sortDirection={leadSortDirection} onSort={changeLeadSort} /><SortableTableHeader column="owner" label="Owner" sortKey={leadSortKey} sortDirection={leadSortDirection} onSort={changeLeadSort} /></>}<SortableTableHeader column="inquiry" label="Inquiry" sortKey={leadSortKey} sortDirection={leadSortDirection} onSort={changeLeadSort} />{features.leadActivityTrackingEnabled && <SortableTableHeader column="activity" label="Last activity" sortKey={leadSortKey} sortDirection={leadSortDirection} onSort={changeLeadSort} />}<th><span className="sr-only">Open</span></th></tr></thead>
        <tbody>{visible.map((lead) => <tr className="clickable-table-row" tabIndex={0} onClick={() => onOpen(lead)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") onOpen(lead); }} key={lead.id}>
          <td><span className="lead-initials">{lead.prospectName.split(/\s+/).map((part) => part[0]).join("").slice(0, 2)}</span><span><strong>{lead.prospectName}</strong>{lead.needsReview && <small className="lead-review-note"><CircleAlert />Needs review</small>}</span></td>
          {features.leadPipelineEnabled && <><td><span className={`status-pill ${lead.statusCode}`}><i />{leadStatusLabel(lead.statusCode)}</span></td><td><strong>{lead.sourceName ?? "Not linked"}</strong></td><td><strong>{lead.ownerName ?? "Unassigned"}</strong></td></>}
          <td><strong>{formatSourceDate(lead.inquiryDate)}</strong></td>
          {features.leadActivityTrackingEnabled && <td><strong>{lead.lastActivityAt ? formatSourceDate(lead.lastActivityAt) : "No activity"}</strong></td>}
          <td><ChevronRight /></td>
        </tr>)}</tbody>
      </table>
      {loading && <div className="settings-loading"><span className="loading-line" /><p>Loading individual leads…</p></div>}
      {!loading && !visible.length && <div className="empty-state"><ClipboardList /><strong>{leads.length ? "No leads match this view" : "No individual leads yet"}</strong><p>{leads.length ? "Try a different search or status." : "Add a lead when your team chooses to track one by name."}</p>{canEdit && !leads.length && <button className="button primary" onClick={onCreate}><Plus />Add first lead</button>}</div>}
    </div>
  </div>;
}

function LeadEditorModal({
  features,
  sources,
  assignees,
  lookups,
  onClose,
  onSave,
}: {
  features: LeadFeatureSet;
  sources: DemoSource[];
  assignees: SourceAssignee[];
  lookups: ActiveLookupOption[];
  onClose: () => void;
  onSave: (input: LeadCreateInput) => Promise<boolean>;
}) {
  const [prospectName, setProspectName] = useState("");
  const [inquiryDate, setInquiryDate] = useState(new Date().toISOString().slice(0, 10));
  const [statusCode, setStatusCode] = useState<LeadStatus>("active");
  const [payerCode, setPayerCode] = useState("");
  const [acuityCode, setAcuityCode] = useState("");
  const [referralChannelCode, setReferralChannelCode] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [ownerUserId, setOwnerUserId] = useState("");
  const [currentResidence, setCurrentResidence] = useState("");
  const [contactName, setContactName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const lookup = (type: ActiveLookupOption["type"]) => lookups.filter((option) => option.type === type);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (features.leadContactDetailsEnabled && (contactName || relationship) && !phone && !email) {
      setError("Add a phone number or email address for the contact details provided.");
      return;
    }
    setBusy(true); setError("");
    const includeContact = features.leadContactDetailsEnabled && Boolean(phone || email);
    const saved = await onSave({
      prospectName: prospectName.trim(), inquiryDate, statusCode,
      payerCode: features.leadPipelineEnabled && payerCode ? payerCode : null,
      acuityCode: features.leadPipelineEnabled && acuityCode ? acuityCode : null,
      referralChannelCode: features.leadPipelineEnabled && referralChannelCode ? referralChannelCode : null,
      sourceId: sourceId || null, ownerUserId: ownerUserId || null,
      currentResidence: currentResidence.trim() || null,
      contacts: includeContact ? [{ name: contactName.trim() || undefined, relationship: relationship.trim() || undefined, phone: phone.trim() || undefined, email: email.trim() || undefined, isPrimary: true }] : [],
    });
    if (!saved) setBusy(false);
  }
  return <div className="modal-layer" role="dialog" aria-modal="true" aria-label="Add individual lead">
    <button className="modal-scrim" onClick={onClose} aria-label="Close" />
    <form className="modal lead-editor-modal" onSubmit={submit}>
      <header><div><p className="eyebrow">Individual lead</p><h2>Add a lead</h2><p>Start with what you know. Optional tracking items can be completed later.</p></div><button type="button" className="icon-button" onClick={onClose}><X /></button></header>
      <div className="form-row"><label>Lead name<input required autoFocus maxLength={160} value={prospectName} onChange={(event) => setProspectName(event.target.value)} placeholder="Full name" /></label><label>Inquiry date<input required type="date" value={inquiryDate} onChange={(event) => setInquiryDate(event.target.value)} /></label></div>
      <div className="form-row"><label>Referral source <small>(optional)</small><select value={sourceId} onChange={(event) => setSourceId(event.target.value)}><option value="">Not linked</option>{sources.map((source) => <option value={source.id} key={source.id}>{source.name}</option>)}</select></label><label>Lead owner <small>(optional)</small><select value={ownerUserId} onChange={(event) => setOwnerUserId(event.target.value)}><option value="">Assign to me</option>{assignees.map((person) => <option value={person.id} key={person.id}>{person.name}</option>)}</select></label></div>
      {features.leadPipelineEnabled && <fieldset className="lead-editor-section"><legend><LayoutGrid />Pipeline & profile</legend><div className="form-row"><label>Status<select value={statusCode} onChange={(event) => setStatusCode(event.target.value as LeadStatus)}>{LEAD_STATUSES.map((option) => <option value={option.code} key={option.code}>{option.label}</option>)}</select></label><label>Current residence<input maxLength={200} value={currentResidence} onChange={(event) => setCurrentResidence(event.target.value)} placeholder="Optional" /></label></div><div className="form-row lead-three-fields"><label>Payer<input list="lead-payers" value={payerCode} onChange={(event) => setPayerCode(event.target.value)} placeholder="Not specified" /><datalist id="lead-payers">{lookup("payer").map((option) => <option value={option.code} key={option.code}>{option.label}</option>)}</datalist></label><label>Acuity<input list="lead-acuity" value={acuityCode} onChange={(event) => setAcuityCode(event.target.value)} placeholder="Not specified" /><datalist id="lead-acuity">{lookup("acuity").map((option) => <option value={option.code} key={option.code}>{option.label}</option>)}</datalist></label><label>Channel<input list="lead-channels" value={referralChannelCode} onChange={(event) => setReferralChannelCode(event.target.value)} placeholder="Not specified" /><datalist id="lead-channels">{lookup("channel").map((option) => <option value={option.code} key={option.code}>{option.label}</option>)}</datalist></label></div></fieldset>}
      {features.leadContactDetailsEnabled && <fieldset className="lead-editor-section"><legend><Users />Primary contact</legend><div className="form-row"><label>Contact name<input maxLength={120} value={contactName} onChange={(event) => setContactName(event.target.value)} placeholder="Lead or representative" /></label><label>Relationship<input maxLength={80} value={relationship} onChange={(event) => setRelationship(event.target.value)} placeholder="Self, family, advocate…" /></label></div><div className="form-row"><label>Phone<input maxLength={40} value={phone} onChange={(event) => setPhone(event.target.value)} /></label><label>Email<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label></div></fieldset>}
      {error && <div className="form-error"><CircleAlert />{error}</div>}
      <footer><button type="button" className="button ghost" onClick={onClose}>Cancel</button><button className="button primary" disabled={busy}>{busy ? "Adding…" : "Add lead"}<ArrowRight /></button></footer>
    </form>
  </div>;
}

function LeadActivityModal({ lead, activityTypes, onClose, onSave }: { lead: LeadDetail; activityTypes: ActiveLookupOption[]; onClose: () => void; onSave: (activity: { activityTypeCode: string; summary: string; notes: string }) => Promise<boolean> }) {
  const options = activityTypes.length ? activityTypes : [{ type: "activity_type" as const, code: "note", label: "Note" }, { type: "activity_type" as const, code: "call", label: "Phone call" }, { type: "activity_type" as const, code: "email", label: "Email" }];
  const [activityTypeCode, setActivityTypeCode] = useState(options[0].code);
  const [summary, setSummary] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent) { event.preventDefault(); setBusy(true); if (!await onSave({ activityTypeCode, summary, notes })) setBusy(false); }
  return <div className="modal-layer" role="dialog" aria-modal="true" aria-label={`Log activity for ${lead.prospectName}`}><button className="modal-scrim" onClick={onClose} aria-label="Close" /><form className="modal lead-activity-modal" onSubmit={submit}><header><div><p className="eyebrow">Lead activity</p><h2>Log an update</h2><p>{lead.prospectName}</p></div><button type="button" className="icon-button" onClick={onClose}><X /></button></header><label>Activity type<select value={activityTypeCode} onChange={(event) => setActivityTypeCode(event.target.value)}>{options.map((option) => <option value={option.code} key={option.code}>{option.label}</option>)}</select></label><label>What happened?<input required maxLength={240} autoFocus value={summary} onChange={(event) => setSummary(event.target.value)} placeholder="Brief outcome" /></label><label>Notes<textarea maxLength={4000} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Optional context or next step" /></label><footer><button type="button" className="button ghost" onClick={onClose}>Cancel</button><button className="button primary" disabled={busy}>{busy ? "Saving…" : "Save activity"}<Check /></button></footer></form></div>;
}

function LeadDrawer({ lead, features, canEdit, onClose, onStatusChange, onLogActivity }: { lead: LeadDetail; features: LeadFeatureSet; canEdit: boolean; onClose: () => void; onStatusChange: (status: LeadStatus) => Promise<void>; onLogActivity: () => void }) {
  return <div className="drawer-layer"><button className="drawer-scrim" onClick={onClose} aria-label="Close lead" /><aside className="drawer lead-drawer" aria-label={`${lead.prospectName} details`}><header className="drawer-head"><span>Individual lead</span><button className="icon-button" onClick={onClose} aria-label="Close lead details"><X /></button></header><section className="lead-record-hero"><span className="record-initials">{lead.prospectName.split(/\s+/).map((part) => part[0]).join("").slice(0, 2)}</span><div><p className="eyebrow">Inquiry · {formatSourceDate(lead.inquiryDate)}</p><h2>{lead.prospectName}</h2><p>{lead.sourceName ? `Referred by ${lead.sourceName}` : "No referral source linked"}</p></div></section>
    {features.leadPipelineEnabled && <section className="lead-pipeline-control"><label>Pipeline status<select disabled={!canEdit} value={lead.statusCode} onChange={(event) => void onStatusChange(event.target.value as LeadStatus)}>{LEAD_STATUSES.map((option) => <option value={option.code} key={option.code}>{option.label}</option>)}</select></label><div><span><small>Owner</small><strong>{lead.ownerName ?? "Unassigned"}</strong></span><span><small>Payer</small><strong>{lead.payerCode || "Not specified"}</strong></span><span><small>Acuity</small><strong>{lead.acuityCode || "Not specified"}</strong></span><span><small>Channel</small><strong>{lead.referralChannelCode || "Not specified"}</strong></span></div></section>}
    {features.leadContactDetailsEnabled && <section className="lead-drawer-section"><header><div><h3>Contact details</h3><p>People connected with this lead.</p></div><Users /></header>{lead.contacts.length ? <div className="lead-contact-list">{lead.contacts.map((contact) => <article key={contact.id}><span className="record-avatar">{(contact.name ?? lead.prospectName).slice(0, 1)}</span><div><strong>{contact.name ?? lead.prospectName}</strong><small>{contact.relationship ?? (contact.isPrimary ? "Primary contact" : "Contact")}</small></div><span>{contact.phone && <a href={`tel:${contact.phone}`}><Phone />{contact.phone}</a>}{contact.email && <a href={`mailto:${contact.email}`}><Mail />{contact.email}</a>}</span></article>)}</div> : <div className="empty-state compact"><Users /><strong>No contact details</strong><p>This lead can still be tracked without personal contact information.</p></div>}</section>}
    {features.leadActivityTrackingEnabled && <section className="lead-drawer-section lead-timeline"><header><div><h3>Activity history</h3><p>Calls, messages, and progress notes.</p></div>{canEdit && <button className="button ghost" onClick={onLogActivity}><Plus />Log activity</button>}</header>{lead.activities.length ? <div>{lead.activities.map((activity) => <article key={activity.id}><span><Activity /></span><div><small>{activityTypeLabel(activity.activityTypeCode)} · {formatSourceDate(activity.occurredAt)}</small><strong>{activity.summary}</strong>{activity.notes && <p>{activity.notes}</p>}{activity.actorName && <em>{activity.actorName}</em>}</div></article>)}</div> : <div className="empty-state compact"><Activity /><strong>No activity yet</strong><p>Log the first update to begin the timeline.</p></div>}</section>}
  </aside></div>;
}

function TodaysContacts({
  tasks,
  sources,
  onOpen,
  onContacted,
  onSourceContacted,
  onSnooze,
  showAssignee,
}: {
  tasks: DemoTask[];
  sources: DemoSource[];
  onOpen: (id: string) => void;
  onContacted: (task: DemoTask) => void;
  onSourceContacted: (source: DemoSource) => void;
  onSnooze: (item: FollowUpItem) => void;
  showAssignee: boolean;
}) {
  const due = sourceFollowUps(tasks, sources);
  const today = new Intl.DateTimeFormat([], { weekday: "long", month: "long", day: "numeric" }).format(new Date());
  return (
    <section className="today-contact-widget" aria-labelledby="today-contact-title">
      <header>
        <div><p className="eyebrow">Today · {today}</p><h2 id="today-contact-title">Who to contact today</h2><p>{due.length ? `${due.length} relationship${due.length === 1 ? "" : "s"} ready for outreach` : "Today’s outreach is complete"}</p></div>
        {due.length > 1 && <span className="swipe-hint">Swipe for more <ArrowRight size={14} /></span>}
      </header>
      <div className="today-contact-track">
        {due.map((item) => {
          const source = item.source;
          return (
            <article className={`today-contact-card ${item.timing}`} key={item.id}>
              <div className="today-card-meta"><span>{item.timing === "overdue" ? "Needs attention" : "Due today"}</span><time>{item.dueAt}</time></div>
              <button className="today-source-name" onClick={() => source && onOpen(source.id)} disabled={!source}>{item.entity}<ChevronRight size={17} /></button>
              <p>{item.title}</p>
              <div className="today-person"><span className="record-avatar">{(source?.contact ?? item.entity).split(" ").map((word) => word[0]).join("").slice(0, 2)}</span><span><small>Contact</small><strong>{source?.contact ?? "Open source for details"}</strong></span></div>
              {showAssignee && source?.ownerName && <div className="today-assignee"><span>Assigned to</span><strong>{source.ownerName}</strong></div>}
              <footer>
                {source?.contactPhone ? <a href={`tel:${source.contactPhone}`}><Phone size={15} />Call</a> : <button type="button" className="today-details" onClick={() => source && onOpen(source.id)} disabled={!source}>View details</button>}
                <button type="button" className="today-snooze" onClick={() => onSnooze(item)}><Clock3 size={15} />Later</button>
                <button className="button contacted-button" onClick={() => item.task ? onContacted(item.task) : source && onSourceContacted(source)}><Check size={16} />Contacted</button>
              </footer>
            </article>
          );
        })}
        {!due.length && <div className="today-contact-complete"><span><CheckCircle2 /></span><div><strong>All caught up</strong><p>Nothing else needs contact today.</p></div></div>}
      </div>
    </section>
  );
}

type SourceReportRow = {
  id: string;
  name: string;
  category: string;
  isHospital: boolean;
  activityCount: number;
  referralsReceived: number;
  lastInteractionAt: string;
  openTaskCount: number;
  overdueTaskCount: number;
  contactCount: number;
};

type ReportInsights = {
  activityTypes: Array<{ code: string; count: number }>;
  activityTrend: Array<{ day: string; count: number }>;
  teamActivity: Array<{ name: string; count: number }>;
  tasks: { completed: number; open: number; overdue: number };
};

type ReportView = "sources" | "hospitals" | "goals" | "users" | "source-users" | "referral-attribution";

function ReportsNavigation({ value, onChange, showUserPerformance }: { value: ReportView; onChange: (view: ReportView) => void; showUserPerformance: boolean }) {
  return (
    <nav className={`report-navigation${showUserPerformance ? " full" : ""}`} aria-label="Available reports" role="tablist">
      <button type="button" role="tab" aria-selected={value === "sources"} className={value === "sources" ? "active" : ""} onClick={() => onChange("sources")}><Activity /><span><strong>Source activity</strong><small>Outreach and relationship health</small></span></button>
      <button type="button" role="tab" aria-selected={value === "hospitals"} className={value === "hospitals" ? "active" : ""} onClick={() => onChange("hospitals")}><Hospital /><span><strong>Hospital referrals</strong><small>Referral volume by hospital</small></span></button>
      {showUserPerformance && <button type="button" role="tab" aria-selected={value === "goals"} className={value === "goals" ? "active" : ""} onClick={() => onChange("goals")}><BarChart3 /><span><strong>Goals</strong><small>Targets and live progress</small></span></button>}
      {showUserPerformance && <button type="button" role="tab" aria-selected={value === "users"} className={value === "users" ? "active" : ""} onClick={() => onChange("users")}><Users /><span><strong>User performance</strong><small>Individual and team KPIs</small></span></button>}
      {showUserPerformance && <button type="button" role="tab" aria-selected={value === "source-users"} className={value === "source-users" ? "active" : ""} onClick={() => onChange("source-users")}><Building2 /><span><strong>Source by user</strong><small>Who reached each relationship</small></span></button>}
      {showUserPerformance && <button type="button" role="tab" aria-selected={value === "referral-attribution"} className={value === "referral-attribution" ? "active" : ""} onClick={() => onChange("referral-attribution")}><ArrowUpDown /><span><strong>Referral attribution</strong><small>Source, contact, and credit</small></span></button>}
    </nav>
  );
}

type WorkspaceGoal = {
  id: string;
  metric: "outreach" | "referrals" | "conversions" | "followups";
  period: "monthly" | "quarterly" | "annual";
  target: number;
  startsOn: string;
  endsOn: string;
  progress: number;
  percentage: number;
  version: number;
};

const goalMetricLabels: Record<WorkspaceGoal["metric"], { name: string; unit: string; description: string }> = {
  outreach: { name: "Outreach logged", unit: "interactions", description: "Calls, visits, meetings, emails, and other source activity." },
  referrals: { name: "Referrals received", unit: "referrals", description: "Referral receipts and referrals recorded during outreach." },
  conversions: { name: "Move-ins", unit: "move-ins", description: "Individual leads converted to moved-in status." },
  followups: { name: "Follow-ups completed", unit: "tasks", description: "Completed relationship and workspace follow-up tasks." },
};

function goalPeriodDates(period: WorkspaceGoal["period"], date = new Date()): { startsOn: string; endsOn: string } {
  const year = date.getFullYear();
  const month = date.getMonth();
  const startMonth = period === "quarterly" ? Math.floor(month / 3) * 3 : period === "annual" ? 0 : month;
  const endMonth = period === "quarterly" ? startMonth + 2 : period === "annual" ? 11 : month;
  const localDate = (value: Date) => `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
  return { startsOn: localDate(new Date(year, startMonth, 1)), endsOn: localDate(new Date(year, endMonth + 1, 0)) };
}

function GoalsReportsPanel({ navigation }: { navigation: ReactNode }) {
  const initialDates = goalPeriodDates("monthly");
  const [goals, setGoals] = useState<WorkspaceGoal[] | null>(null);
  const [metric, setMetric] = useState<WorkspaceGoal["metric"]>("outreach");
  const [period, setPeriod] = useState<WorkspaceGoal["period"]>("monthly");
  const [target, setTarget] = useState("50");
  const [startsOn, setStartsOn] = useState(initialDates.startsOn);
  const [endsOn, setEndsOn] = useState(initialDates.endsOn);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  function load() {
    fetch("/api/reports/goals")
      .then(async (response) => {
        const result = await response.json().catch(() => ({})) as { data?: WorkspaceGoal[]; error?: string };
        if (!response.ok || !result.data) throw new Error(result.error ?? "Goals could not be loaded.");
        setGoals(result.data);
      })
      .catch((caught: Error) => setError(caught.message));
  }
  useEffect(load, []);

  function changePeriod(next: WorkspaceGoal["period"]) {
    const dates = goalPeriodDates(next);
    setPeriod(next);
    setStartsOn(dates.startsOn);
    setEndsOn(dates.endsOn);
  }

  async function saveGoal(event: FormEvent) {
    event.preventDefault();
    setBusy("save");
    setError("");
    const response = await fetch("/api/reports/goals", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ metric, period, target: Number(target), startsOn, endsOn }),
    });
    const result = await response.json().catch(() => ({})) as { error?: string };
    setBusy("");
    if (!response.ok) {
      setError(result.error ?? "The goal could not be saved.");
      return;
    }
    load();
  }

  async function removeGoal(goal: WorkspaceGoal) {
    setBusy(goal.id);
    const response = await fetch(`/api/reports/goals/${encodeURIComponent(goal.id)}?version=${goal.version}`, { method: "DELETE" });
    setBusy("");
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      setError(result.error ?? "The goal could not be removed.");
      return;
    }
    setGoals((current) => current?.filter((candidate) => candidate.id !== goal.id) ?? null);
  }

  return <div className="page simple-page reports-page goals-page">
    {navigation}
    <div className="simple-heading"><div><h1>Workspace goals</h1><p>Set concrete targets and see live progress from recorded work.</p></div></div>
    {error && <AdminError message={error} />}
    <form className="goal-composer" onSubmit={saveGoal}>
      <div><p className="eyebrow">New target</p><h2>Define what success looks like</h2><p>Saving the same metric and date range updates its target.</p></div>
      <label>Metric<select value={metric} onChange={(event) => setMetric(event.target.value as WorkspaceGoal["metric"])}>{Object.entries(goalMetricLabels).map(([value, label]) => <option value={value} key={value}>{label.name}</option>)}</select></label>
      <label>Period<select value={period} onChange={(event) => changePeriod(event.target.value as WorkspaceGoal["period"])}><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="annual">Annual</option></select></label>
      <label>Target<input required type="number" min="1" max="1000000" value={target} onChange={(event) => setTarget(event.target.value)} /></label>
      <label>Starts<input required type="date" value={startsOn} onChange={(event) => setStartsOn(event.target.value)} /></label>
      <label>Ends<input required type="date" min={startsOn} value={endsOn} onChange={(event) => setEndsOn(event.target.value)} /></label>
      <button className="button primary" disabled={busy === "save"}>{busy === "save" ? "Saving…" : "Save goal"}<Plus /></button>
    </form>
    {!goals && !error && <AdminLoading />}
    {goals && <section className="goal-grid">
      {!goals.length && <div className="admin-empty"><BarChart3 /><strong>No goals yet</strong><p>Create the first target above. Progress updates from live workspace records.</p></div>}
      {goals.map((goal) => {
        const definition = goalMetricLabels[goal.metric];
        const percentage = Math.min(100, Math.max(0, goal.percentage));
        return <article className={percentage >= 100 ? "complete" : ""} key={goal.id}>
          <header><span><BarChart3 /></span><div><strong>{definition.name}</strong><small>{goal.period} · {goal.startsOn} to {goal.endsOn}</small></div><button type="button" aria-label={`Remove ${definition.name} goal`} disabled={busy === goal.id} onClick={() => void removeGoal(goal)}><Trash2 /></button></header>
          <div className="goal-progress-copy"><strong>{goal.progress.toLocaleString()}</strong><span>of {goal.target.toLocaleString()} {definition.unit}</span><b>{percentage}%</b></div>
          <div className="goal-progress-track"><i style={{ width: `${percentage}%` }} /></div>
          <p>{percentage >= 100 ? "Goal reached. Keep the momentum going." : definition.description}</p>
        </article>;
      })}
    </section>}
  </div>;
}

function ReportsPage({ sourceRecords, tasks, onOpenSource, onOpenContact, showTeamReports }: { sourceRecords: DemoSource[]; tasks: DemoTask[]; onOpenSource: (id: string) => void; onOpenContact: (id: string) => void; showTeamReports: boolean }) {
  const [reportView, setReportView] = useState<ReportView>("sources");
  const [range, setRange] = useState("all");
  const hospitalReport = reportView === "hospitals";
  const previewRows = useMemo(
    () =>
      sourceRecords.map((source) => ({
        id: source.id,
        name: source.name,
        category: source.category,
        isHospital: source.isHospital,
        activityCount: source.activityCount,
        referralsReceived: source.referralTotal,
        lastInteractionAt: source.lastInteractionAt,
        openTaskCount: tasks.filter((task) => task.entity === source.name).length,
        overdueTaskCount: tasks.filter((task) => task.entity === source.name && task.timing === "overdue").length,
        contactCount: source.contact === "No contact added" ? 0 : 1,
      })),
    [sourceRecords, tasks],
  );
  const [rows, setRows] = useState<SourceReportRow[]>(previewRows);
  const [insights, setInsights] = useState<ReportInsights>({ activityTypes: [], activityTrend: [], teamActivity: [], tasks: { completed: 0, open: 0, overdue: 0 } });

  useEffect(() => {
    const controller = new AbortController();
    setRows(hospitalReport ? previewRows.filter((row) => row.isHospital) : previewRows);
    fetch(`/api/reports/sources?range=${range}&scope=${hospitalReport ? "hospitals" : "all"}`, { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : Promise.reject()))
      .then((result: { data: SourceReportRow[]; insights?: ReportInsights }) => { setRows(result.data.map((row) => ({ ...row, isHospital: Boolean(row.isHospital) }))); if (result.insights) setInsights(result.insights); })
      .catch(() => undefined);
    return () => controller.abort();
  }, [previewRows, range, hospitalReport]);

  const visibleRows = hospitalReport ? rows.filter((row) => row.isHospital) : rows;
  const sorted = [...visibleRows].sort((a, b) => hospitalReport
    ? b.referralsReceived - a.referralsReceived || b.activityCount - a.activityCount || a.name.localeCompare(b.name)
    : b.activityCount - a.activityCount || a.name.localeCompare(b.name));
  const totalActivity = visibleRows.reduce((total, row) => total + row.activityCount, 0);
  const totalReferrals = visibleRows.reduce((total, row) => total + Number(row.referralsReceived || 0), 0);
  const overdueTasks = visibleRows.reduce((total, row) => total + row.overdueTaskCount, 0);
  const maximum = Math.max(...visibleRows.map((row) => hospitalReport ? Number(row.referralsReceived || 0) : row.activityCount), 1);
  const activityTypeMaximum = Math.max(...insights.activityTypes.map((item) => Number(item.count)), 1);
  const trendMaximum = Math.max(...insights.activityTrend.map((item) => Number(item.count)), 1);
  const atRisk = [...visibleRows].filter((row) => row.overdueTaskCount > 0 || row.contactCount === 0 || row.activityCount === 0).sort((left, right) => right.overdueTaskCount - left.overdueTaskCount || left.activityCount - right.activityCount).slice(0, 8);

  function downloadReport() {
    const csvCell = (value: string | number) => {
      const text = String(value);
      const safe = /^[=+\-@]/.test(text) ? `'${text}` : text;
      return `"${safe.replaceAll('"', '""')}"`;
    };
    const csv = [
      ["Referral Source", "Hospital", "Referral Category", "Contacts Logged", "Referrals Received", "Last Contacted", "Open Follow-ups", "Overdue Follow-ups", "People"],
      ...sorted.map((row) => [row.name, row.isHospital ? "Yes" : "No", row.category, row.activityCount, row.referralsReceived, row.lastInteractionAt, row.openTaskCount, row.overdueTaskCount, row.contactCount]),
    ].map((line) => line.map(csvCell).join(",")).join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `referral-tracking-${hospitalReport ? "hospital-referrals" : "source-outreach"}-${range === "all" ? "all-time" : `${range}-days`}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  const navigation = <ReportsNavigation value={reportView} onChange={setReportView} showUserPerformance={showTeamReports} />;
  if (showTeamReports && reportView === "goals") return <GoalsReportsPanel navigation={navigation} />;
  if (showTeamReports && reportView === "users") return <UserActivityReportsPanel navigation={navigation} />;
  if (showTeamReports && reportView === "source-users") return <SourceUserActivityReportsPanel navigation={navigation} onOpenSource={onOpenSource} />;
  if (showTeamReports && reportView === "referral-attribution") return <ReferralAttributionReportsPanel navigation={navigation} onOpenSource={onOpenSource} onOpenContact={onOpenContact} />;

  return (
    <div className="page simple-page reports-page">
      {navigation}
      <div className="simple-heading">
        <div><h1>{hospitalReport ? "Hospital referrals" : "Source activity"}</h1><p>{hospitalReport ? "See how many referrals each designated hospital gives your team." : "A clear view of outreach and follow-up health."}</p></div>
        <div className="report-actions">
          <label>Period<select value={range} onChange={(event) => setRange(event.target.value)}><option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option><option value="365">Last year</option><option value="all">All time</option></select></label>
          <button className="button primary" onClick={downloadReport}><Download size={15} />Download CSV</button>
        </div>
      </div>
      <section className={`report-summary${hospitalReport ? " hospital-report-summary" : ""}`}>
        <article><small>{hospitalReport ? "Hospital sources" : "Contacts logged"}</small><strong>{hospitalReport ? visibleRows.length : totalActivity}</strong></article>
        <article><small>{hospitalReport ? "Hospitals referring" : "Sources contacted"}</small><strong>{visibleRows.filter((row) => hospitalReport ? row.referralsReceived > 0 : row.activityCount > 0).length}</strong></article>
        <article><small>Referrals received</small><strong>{totalReferrals}</strong></article>
        <article className={!hospitalReport && overdueTasks ? "urgent-report" : ""}><small>{hospitalReport ? "Hospital outreach" : "Overdue"}</small><strong>{hospitalReport ? totalActivity : overdueTasks}</strong></article>
      </section>
      <section className="report-layout">
        <div className="simple-card source-performance">
          <SectionTitle title={hospitalReport ? "Referrals by hospital" : "Outreach by source"} action={<span className="subtle-count">{range === "all" ? "All time" : `${range} days`}</span>} />
          <div className="performance-list">
            {sorted.slice(0, 12).map((row, index) => (
              <button type="button" className="performance-row" key={row.id} onClick={() => onOpenSource(row.id)}>
                <span className="rank">{index + 1}</span>
                <div><strong>{row.name}</strong><small>{row.category}</small><i><b style={{ width: `${((hospitalReport ? Number(row.referralsReceived || 0) : row.activityCount) / maximum) * 100}%` }} /></i></div>
                <span><strong>{row.activityCount}</strong><small>contacts</small></span>
                <span><strong>{Number(row.referralsReceived || 0)}</strong><small>referrals</small></span>
              </button>
            ))}
            {!sorted.length && <p className="report-empty">{hospitalReport ? "No hospitals are designated yet. Edit a referral source and mark it as a hospital." : "No source activity is available for this period."}</p>}
          </div>
        </div>
        <div className="simple-card report-insight">
          <span className="report-icon">{hospitalReport ? <Hospital /> : <Activity />}</span>
          <h3>{hospitalReport ? "Top referring hospital" : "Most contact"}</h3>
          <strong>{sorted[0]?.name ?? "No activity yet"}</strong>
          <p>{sorted[0] ? hospitalReport ? `${sorted[0].referralsReceived} referrals came from this hospital in the selected period.` : `${sorted[0].activityCount} contacts have been logged for this relationship in the selected period.` : hospitalReport ? "Mark sources as hospitals and record referrals to build this report." : "Log contact with a source to build this report."}</p>
          <div><small>Last contacted</small><b>{sorted[0]?.lastInteractionAt ?? "Not recorded"}</b></div>
        </div>
      </section>
      <section className="actionable-report-grid">
        <div className="simple-card report-breakdown"><SectionTitle title={hospitalReport ? "How the team is reaching hospitals" : "How the team is reaching sources"} /><div className="breakdown-list">{insights.activityTypes.map((item) => <article key={item.code}><span>{activityTypeLabel(item.code)}</span><i><b style={{ width: `${Number(item.count) / activityTypeMaximum * 100}%` }} /></i><strong>{Number(item.count)}</strong></article>)}{!insights.activityTypes.length && <p className="report-empty">No outreach logged in this period.</p>}</div></div>
        <div className="simple-card report-trend"><SectionTitle title="Outreach rhythm" /><div className="trend-bars" aria-label="Outreach activity over time">{insights.activityTrend.slice(-30).map((item) => <i key={item.day} style={{ height: `${Math.max(8, Number(item.count) / trendMaximum * 100)}%` }} title={`${formatSourceDate(item.day)}: ${item.count}`} />)}</div><p>{insights.activityTrend.length ? `${insights.activityTrend.reduce((sum, item) => sum + Number(item.count), 0)} contacts across ${insights.activityTrend.length} active days.` : "Activity will appear as outreach is logged."}</p></div>
      </section>
      <section className="simple-card at-risk-report"><SectionTitle title={hospitalReport ? "Hospitals needing action" : "Relationships needing action"} action={<span className="subtle-count">{atRisk.length} shown</span>} /><div>{atRisk.map((row) => <button type="button" onClick={() => onOpenSource(row.id)} key={row.id}><span><strong>{row.name}</strong><small>{row.category}</small></span><span className="risk-reasons">{row.overdueTaskCount > 0 && <b>{row.overdueTaskCount} overdue</b>}{row.contactCount === 0 && <b>No contact person</b>}{row.activityCount === 0 && <b>No outreach in period</b>}</span><ChevronRight /></button>)}{!atRisk.length && <div className="empty-state compact"><CheckCircle2 /><strong>{hospitalReport ? "Hospital relationships look healthy" : "Relationships look healthy"}</strong><p>No obvious gaps in the selected period.</p></div>}</div></section>
    </div>
  );
}

type AdminSectionKey = "people" | "lookups" | "imports" | "quality" | "archives" | "audit" | "billing" | "workspace";

function formatAdminDate(value: string | null | undefined) {
  if (!value) return "Not recorded";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function AdminPanelShell({
  title,
  description,
  onBack,
  action,
  children,
}: {
  title: string;
  description: string;
  onBack: () => void;
  action?: ReactNode;
  children: ReactNode;
}) {
  const helpTopic = title === "People & access" ? "admin-people" : title === "Workspace settings" ? "admin-data" : "admin-data";
  return (
    <div className="page admin-page admin-subpage">
      <button className="admin-back" onClick={onBack}><ArrowLeft size={16} />All admin settings</button>
      <div className="page-heading">
        <div><p className="eyebrow">Administration</p><h1>{title}</h1><p>{description}</p></div>
        <div className="admin-panel-heading-actions">{action}<ContextHelp topic={helpTopic} label="Page help" /></div>
      </div>
      {children}
    </div>
  );
}

function AdminLoading() {
  return <div className="settings-loading"><span className="loading-line" /><p>Loading…</p></div>;
}

function AdminError({ message }: { message: string }) {
  return <div className="settings-message error"><CircleAlert />{message}</div>;
}

type AdminUser = {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  emailVerified: boolean | number;
  role: WorkspaceRole;
  active: boolean | number;
  digestEnabled: boolean | number;
  createdAt: string;
};

type AdminUserDeletionImpact = {
  sources: number;
  leads: number;
  openTasks: number;
  upcomingEvents: number;
  calendarFeeds: number;
};

type AdminCalendarFeed = {
  id: string;
  userId: string;
  name: string;
  urlPreview: string;
  enabled: boolean | number;
  lastSyncedAt: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
  lastImportCount: number;
  importedEventCount: number;
  version: number;
  createdAt: string;
};

function CalendarFeedsModal({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const [feeds, setFeeds] = useState<AdminCalendarFeed[] | null>(null);
  const [form, setForm] = useState({ name: "Primary calendar", url: "" });
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  async function load() {
    setError("");
    const response = await fetch(`/api/admin/users/${user.id}/calendar-feeds`);
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      setError(result.error ?? "Calendar feeds could not be loaded.");
      return;
    }
    const result = await response.json() as { data: AdminCalendarFeed[] };
    setFeeds(result.data);
  }

  useEffect(() => { void load(); }, [user.id]);

  async function addFeed(event: FormEvent) {
    event.preventDefault();
    setBusy("add");
    setError("");
    setNotice("");
    const response = await fetch(`/api/admin/users/${user.id}/calendar-feeds`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ ...form, enabled: true }),
    });
    setBusy("");
    const result = await response.json().catch(() => ({})) as {
      error?: string;
      data?: { sync?: { ok: boolean; importedCount: number; error?: string } };
    };
    if (!response.ok) return setError(result.error ?? "The calendar feed could not be added.");
    setForm({ name: "Primary calendar", url: "" });
    if (result.data?.sync?.ok) {
      setNotice(`Calendar added and ${result.data.sync.importedCount} event${result.data.sync.importedCount === 1 ? "" : "s"} imported.`);
    } else {
      setNotice("Calendar added. Review the sync message below before leaving it enabled.");
    }
    await load();
  }

  async function toggleFeed(feed: AdminCalendarFeed) {
    setBusy(`toggle:${feed.id}`);
    setError("");
    setNotice("");
    const enabled = !Boolean(feed.enabled);
    setFeeds((current) => current?.map((item) => item.id === feed.id ? { ...item, enabled } : item) ?? null);
    const response = await fetch(`/api/admin/calendar-feeds/${feed.id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ enabled, version: feed.version }),
    });
    setBusy("");
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      setError(result.error ?? "The calendar feed could not be updated.");
      await load();
      return;
    }
    setNotice(`${feed.name} is now ${enabled ? "refreshing automatically" : "paused"}.`);
    await load();
  }

  async function syncFeed(feed: AdminCalendarFeed) {
    setBusy(`sync:${feed.id}`);
    setError("");
    setNotice("");
    const response = await fetch(`/api/admin/calendar-feeds/${feed.id}/sync`, { method: "POST" });
    setBusy("");
    const result = await response.json().catch(() => ({})) as {
      error?: string;
      data?: { sync?: { ok: boolean; importedCount: number; unchanged?: boolean; error?: string } };
    };
    if (!response.ok) return setError(result.error ?? "The calendar feed could not be refreshed.");
    if (result.data?.sync?.ok) {
      setNotice(result.data.sync.unchanged
        ? `${feed.name} is already up to date.`
        : `${feed.name} refreshed with ${result.data.sync.importedCount} event${result.data.sync.importedCount === 1 ? "" : "s"}.`);
    } else {
      setError(result.data?.sync?.error ?? "The calendar feed could not be refreshed.");
    }
    await load();
  }

  async function removeFeed(feed: AdminCalendarFeed) {
    if (!window.confirm(`Remove ${feed.name}? Events already imported will remain in the workspace.`)) return;
    setBusy(`delete:${feed.id}`);
    setError("");
    setNotice("");
    const response = await fetch(`/api/admin/calendar-feeds/${feed.id}`, { method: "DELETE" });
    setBusy("");
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      return setError(result.error ?? "The calendar feed could not be removed.");
    }
    setNotice(`${feed.name} was removed. Imported events were kept.`);
    await load();
  }

  return (
    <div className="modal-layer" role="dialog" aria-modal="true" aria-label={`Calendar feeds for ${user.name}`}>
      <button className="modal-scrim" onClick={onClose} aria-label="Close" />
      <section className="modal calendar-feeds-modal">
        <header>
          <div><p className="eyebrow">People & access</p><h2>{user.name}’s calendars</h2><p>Subscription feeds refresh automatically and add this user as an event attendee.</p></div>
          <button type="button" className="icon-button" onClick={onClose}><X /></button>
        </header>
        <div className="modal-body calendar-feeds-body">
          {error && <div className="settings-message error"><CircleAlert />{error}</div>}
          {notice && <div className="admin-notice"><CheckCircle2 />{notice}</div>}
          <form className="calendar-feed-form" onSubmit={addFeed}>
            <div>
              <h3>Add a calendar subscription</h3>
              <p>Paste an iCalendar link ending in .ics, or a private webcal subscription link.</p>
            </div>
            <div className="calendar-feed-fields">
              <label>Calendar name<input required maxLength={80} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
              <label>Calendar URL<input required type="url" inputMode="url" autoComplete="off" placeholder="https://calendar.example.com/private.ics" value={form.url} onChange={(event) => setForm({ ...form, url: event.target.value })} /></label>
            </div>
            <p className="calendar-privacy-note"><ShieldCheck />The full subscription URL stays private and is only available to the import service.</p>
            <button className="button primary" disabled={busy === "add" || (feeds?.length ?? 0) >= 5}>{busy === "add" ? "Adding & syncing…" : "Add calendar"}</button>
          </form>

          <section className="calendar-feed-list" aria-label="Configured calendar feeds">
            <header><div><h3>Configured feeds</h3><p>Up to five feeds per user · automatic refresh runs hourly.</p></div></header>
            {!feeds && !error && <AdminLoading />}
            {feeds?.length === 0 && <div className="calendar-feed-empty"><CalendarDays /><strong>No calendar feeds yet</strong><p>Add a subscription URL above to begin importing events.</p></div>}
            {feeds?.map((feed) => (
              <article key={feed.id} className={!Boolean(feed.enabled) ? "paused" : ""}>
                <div className="calendar-feed-icon"><CalendarDays /></div>
                <div className="calendar-feed-info">
                  <div><strong>{feed.name}</strong><span className={Boolean(feed.enabled) ? "enabled" : "paused"}>{Boolean(feed.enabled) ? "Auto-import on" : "Paused"}</span></div>
                  <code>{feed.urlPreview}</code>
                  <p>{feed.importedEventCount} linked event{feed.importedEventCount === 1 ? "" : "s"} · Last checked {feed.lastSyncedAt ? formatAdminDate(feed.lastSyncedAt) : "not yet"}</p>
                  {feed.lastError && <div className="calendar-feed-error"><CircleAlert />{feed.lastError}</div>}
                </div>
                <div className="calendar-feed-actions">
                  <button type="button" className="button ghost" disabled={busy === `sync:${feed.id}`} onClick={() => void syncFeed(feed)}>{busy === `sync:${feed.id}` ? "Syncing…" : "Sync now"}</button>
                  <label className="row-toggle"><input type="checkbox" checked={Boolean(feed.enabled)} disabled={busy === `toggle:${feed.id}`} onChange={() => void toggleFeed(feed)} /><span>Automatic</span></label>
                  <button type="button" className="icon-button danger" aria-label={`Remove ${feed.name}`} disabled={busy === `delete:${feed.id}`} onClick={() => void removeFeed(feed)}><Trash2 /></button>
                </div>
              </article>
            ))}
          </section>
        </div>
        <footer><button type="button" className="button ghost" onClick={onClose}>Done</button></footer>
      </section>
    </div>
  );
}

function PeopleAccessPanel({ onBack, currentUserId, onCurrentAvatarChange }: { onBack: () => void; currentUserId: string; onCurrentAvatarChange: (avatarUrl: string | null) => void }) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [inviting, setInviting] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [passwordUser, setPasswordUser] = useState<AdminUser | null>(null);
  const [calendarUser, setCalendarUser] = useState<AdminUser | null>(null);
  const [deletingUser, setDeletingUser] = useState<AdminUser | null>(null);
  const [deletionImpact, setDeletionImpact] = useState<AdminUserDeletionImpact | null>(null);
  const [reassignToUserId, setReassignToUserId] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [invite, setInvite] = useState({ name: "", email: "", role: "staff" as WorkspaceRole, digestEnabled: true });

  async function load() {
    setError("");
    const response = await fetch("/api/admin/users");
    if (!response.ok) {
      setError("People and access could not be loaded.");
      return;
    }
    const result = (await response.json()) as { data: AdminUser[] };
    setUsers(result.data);
  }

  useEffect(() => { void load(); }, []);

  async function updateUser(user: AdminUser, changes: Partial<Pick<AdminUser, "role" | "active" | "digestEnabled">>) {
    setBusyId(user.id);
    setError("");
    const response = await fetch(`/api/admin/users/${user.id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(changes),
    });
    setBusyId("");
    if (!response.ok) {
      const result = (await response.json().catch(() => ({}))) as { error?: string };
      setError(result.error ?? "This user could not be updated.");
      return false;
    }
    setUsers((current) => current?.map((item) => item.id === user.id ? { ...item, ...changes } : item) ?? null);
    setNotice(changes.active === false
      ? `${user.name} was suspended and signed out.`
      : changes.active === true
        ? `${user.name} was reactivated.`
        : `${user.name} was updated.`);
    return true;
  }

  async function toggleUserStatus(user: AdminUser) {
    if (Boolean(user.active) && !window.confirm(
      `Suspend ${user.name}? They’ll be signed out and unable to access this workspace until reactivated.`,
    )) return;
    await updateUser(user, { active: !Boolean(user.active) });
  }

  async function beginDeleteUser(user: AdminUser) {
    const replacement = users?.find((candidate) => candidate.id === currentUserId && Boolean(candidate.active) && candidate.id !== user.id)
      ?? users?.find((candidate) => candidate.id !== user.id && Boolean(candidate.active));
    setDeletingUser(user);
    setDeletionImpact(null);
    setReassignToUserId(replacement?.id ?? "");
    setError("");
    setNotice("");
    setBusyId(`impact:${user.id}`);
    const response = await fetch(`/api/admin/users/${user.id}/deletion-impact`);
    setBusyId("");
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      setDeletingUser(null);
      setError(result.error ?? "The user’s reassignment details could not be loaded.");
      return;
    }
    const result = await response.json() as { data: AdminUserDeletionImpact };
    setDeletionImpact(result.data);
  }

  async function deleteAndReassignUser(event: FormEvent) {
    event.preventDefault();
    if (!deletingUser || !reassignToUserId) return;
    setBusyId(`delete:${deletingUser.id}`);
    setError("");
    const response = await fetch(`/api/admin/users/${deletingUser.id}`, {
      method: "DELETE",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ reassignToUserId }),
    });
    setBusyId("");
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      setError(result.error ?? "The user could not be deleted and reassigned.");
      return;
    }
    const replacement = users?.find((user) => user.id === reassignToUserId);
    setUsers((current) => current?.filter((user) => user.id !== deletingUser.id) ?? null);
    setNotice(`${deletingUser.name} was deleted from this workspace. Their active work now belongs to ${replacement?.name ?? "the selected user"}.`);
    setDeletingUser(null);
    setDeletionImpact(null);
    setReassignToUserId("");
  }

  async function sendInvite(event: FormEvent) {
    event.preventDefault();
    setBusyId("invite");
    setError("");
    setNotice("");
    const response = await fetch("/api/admin/users", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(invite),
    });
    setBusyId("");
    if (!response.ok) {
      const result = (await response.json().catch(() => ({}))) as { error?: string };
      setError(result.error ?? "The invitation could not be sent.");
      return;
    }
    const result = (await response.json()) as { data: { invitationSent: boolean } };
    setInvite({ name: "", email: "", role: "staff", digestEnabled: true });
    setInviting(false);
    setNotice(result.data.invitationSent ? "Invitation email sent." : "User created, but the invitation email needs to be resent.");
    await load();
  }

  async function setUserPassword(event: FormEvent) {
    event.preventDefault();
    if (!passwordUser) return;
    if (password !== passwordConfirm) return setError("The passwords do not match.");
    setBusyId(passwordUser.id);
    setError("");
    const response = await fetch(`/api/admin/users/${passwordUser.id}/password`, {
      method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ password }),
    });
    setBusyId("");
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      return setError(result.error ?? "The password could not be set.");
    }
    setPasswordUser(null);
    setPassword("");
    setPasswordConfirm("");
    setNotice(`${passwordUser.name}’s password was changed and their other sessions were signed out.`);
  }

  async function resendActivation(user: AdminUser) {
    setBusyId(`activation:${user.id}`);
    setError("");
    setNotice("");
    const response = await fetch(`/api/admin/users/${user.id}/resend-activation`, { method: "POST" });
    setBusyId("");
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      setError(result.error ?? "The activation email could not be sent.");
      return;
    }
    setNotice(`A new activation email was sent to ${user.email}.`);
  }

  async function uploadAvatar(user: AdminUser, file: File | undefined) {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return setError("Profile photos must be 5 MB or smaller.");
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      return setError("Choose a JPEG, PNG, or WebP image.");
    }
    setBusyId(`avatar:${user.id}`);
    setError("");
    setNotice("");
    const body = new FormData();
    body.set("file", file);
    const response = await fetch(`/api/admin/users/${user.id}/avatar`, { method: "POST", body });
    setBusyId("");
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      return setError(result.error ?? "The profile photo could not be saved.");
    }
    const result = await response.json() as { data: { avatarUrl: string } };
    setUsers((current) => current?.map((item) => item.id === user.id ? { ...item, avatarUrl: result.data.avatarUrl } : item) ?? null);
    if (user.id === currentUserId) onCurrentAvatarChange(result.data.avatarUrl);
    setNotice(`${user.name}’s profile photo was updated.`);
  }

  async function removeAvatar(user: AdminUser) {
    if (!window.confirm(`Remove ${user.name}’s profile photo?`)) return;
    setBusyId(`avatar:${user.id}`);
    setError("");
    setNotice("");
    const response = await fetch(`/api/admin/users/${user.id}/avatar`, { method: "DELETE" });
    setBusyId("");
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      return setError(result.error ?? "The profile photo could not be removed.");
    }
    setUsers((current) => current?.map((item) => item.id === user.id ? { ...item, avatarUrl: null } : item) ?? null);
    if (user.id === currentUserId) onCurrentAvatarChange(null);
    setNotice(`${user.name}’s profile photo was removed.`);
  }

  async function impersonate(user: AdminUser) {
    if (!window.confirm(`Continue as ${user.name}? You can return to your admin account from the banner at the top.`)) return;
    setBusyId(user.id);
    const response = await fetch(`/api/admin/users/${user.id}/impersonate`, { method: "POST" });
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      setBusyId("");
      return setError(result.error ?? "This user could not be impersonated.");
    }
    window.location.assign("/?view=sources");
  }

  return (
    <AdminPanelShell
      title="People & access"
      description="Invite teammates, set access, suspend accounts, or delete a user and safely transfer their active work."
      onBack={onBack}
      action={<button className="button primary" onClick={() => setInviting((value) => !value)}><Plus />Invite person</button>}
    >
      {error && <AdminError message={error} />}
      {notice && <div className="admin-notice"><CheckCircle2 />{notice}</div>}
      {inviting && (
        <form className="admin-inline-form invite-form" onSubmit={sendInvite}>
          <header><div><h2>Invite a teammate</h2><p>They’ll receive a secure email to choose their password.</p></div><button type="button" className="icon-button" onClick={() => setInviting(false)}><X /></button></header>
          <div className="admin-form-grid">
            <label>Name<input required minLength={2} value={invite.name} onChange={(event) => setInvite({ ...invite, name: event.target.value })} placeholder="Full name" /></label>
            <label>Email<input required type="email" value={invite.email} onChange={(event) => setInvite({ ...invite, email: event.target.value })} placeholder="name@example.com" /></label>
            <label>Role<select value={invite.role} onChange={(event) => setInvite({ ...invite, role: event.target.value as WorkspaceRole })}><option value="staff">Staff · source outreach</option><option value="task_user">Assigned tasks only</option><option value="reports">Reports · read only</option><option value="manager">Manager</option><option value="admin">Admin</option></select></label>
            <label className="admin-check"><input type="checkbox" checked={invite.digestEnabled} onChange={(event) => setInvite({ ...invite, digestEnabled: event.target.checked })} /><span>Weekday digest</span></label>
          </div>
          <footer><button type="button" className="button ghost" onClick={() => setInviting(false)}>Cancel</button><button className="button primary" disabled={busyId === "invite"}>{busyId === "invite" ? "Sending…" : "Send invitation"}</button></footer>
        </form>
      )}
      {!users && !error && <AdminLoading />}
      {users && (
        <section className="admin-list-card">
          <header><div><h2>Workspace people</h2><p>{users.filter((user) => Boolean(user.active)).length} active · {users.length} total</p></div></header>
          <div className="people-list">
            {users.map((user) => (
              <article className={!Boolean(user.active) ? "inactive" : ""} key={user.id}>
                <div className="person-avatar-editor">
                  <UserAvatar name={user.name} avatarUrl={user.avatarUrl} />
                  <label className="avatar-edit-button" title={`Upload profile photo for ${user.name}`} aria-label={`Upload profile photo for ${user.name}`}>
                    {busyId === `avatar:${user.id}` ? <span className="button-spinner" /> : <Camera />}
                    <input type="file" accept="image/jpeg,image/png,image/webp" disabled={busyId === `avatar:${user.id}`} onChange={(event) => { const file = event.target.files?.[0]; event.currentTarget.value = ""; void uploadAvatar(user, file); }} />
                  </label>
                  {user.avatarUrl && <button type="button" className="avatar-remove-button" title={`Remove profile photo for ${user.name}`} aria-label={`Remove profile photo for ${user.name}`} disabled={busyId === `avatar:${user.id}`} onClick={() => void removeAvatar(user)}><X /></button>}
                </div>
                <div className="person-identity"><strong>{user.name}</strong><small>{user.email}</small><i>{Boolean(user.emailVerified) ? "Email verified" : "Invitation pending"}</i></div>
                <label className="compact-field">Role<select value={user.role} disabled={busyId === user.id || !Boolean(user.active)} onChange={(event) => void updateUser(user, { role: event.target.value as WorkspaceRole })}><option value="staff">Staff · source outreach</option><option value="task_user">Assigned tasks only</option><option value="reports">Reports · read only</option><option value="manager">Manager</option><option value="admin">Admin</option></select></label>
                <label className="row-toggle"><input type="checkbox" checked={Boolean(user.digestEnabled)} disabled={busyId === user.id || !Boolean(user.active)} onChange={(event) => void updateUser(user, { digestEnabled: event.target.checked })} /><span>Digest</span></label>
                <div className="person-security-actions">
                  <button className="icon-button" title="Calendar auto-import" aria-label={`Manage calendar feeds for ${user.name}`} disabled={!Boolean(user.active)} onClick={() => setCalendarUser(user)}><CalendarDays /></button>
                  <button className="icon-button" title="Set password" aria-label={`Set password for ${user.name}`} disabled={user.id === currentUserId || busyId === user.id || !Boolean(user.active)} onClick={() => { setPasswordUser(user); setError(""); }}><KeyRound /></button>
                  {Boolean(user.emailVerified)
                    ? <button className="icon-button" title="Continue as user" aria-label={`Continue as ${user.name}`} disabled={user.id === currentUserId || busyId === user.id || !Boolean(user.active)} onClick={() => void impersonate(user)}><LogIn /></button>
                    : <button className="icon-button activation-button" title="Resend activation email" aria-label={`Resend activation email to ${user.name}`} disabled={busyId === `activation:${user.id}` || !Boolean(user.active)} onClick={() => void resendActivation(user)}>{busyId === `activation:${user.id}` ? <span className="button-spinner" /> : <Mail />}</button>}
                  <button type="button" className="icon-button danger" title="Delete user" aria-label={`Delete ${user.name}`} disabled={user.id === currentUserId || busyId !== ""} onClick={() => void beginDeleteUser(user)}><Trash2 /></button>
                </div>
                <button className={`status-action ${Boolean(user.active) ? "suspend" : "reactivate"}`} disabled={user.id === currentUserId || busyId !== ""} onClick={() => void toggleUserStatus(user)}>{Boolean(user.active) ? "Suspend" : "Reactivate"}</button>
              </article>
            ))}
          </div>
        </section>
      )}
      {passwordUser && (
        <div className="modal-layer" role="dialog" aria-modal="true" aria-label={`Set password for ${passwordUser.name}`}>
          <button className="modal-scrim" onClick={() => setPasswordUser(null)} aria-label="Close" />
          <form className="modal compact-modal password-modal" onSubmit={setUserPassword}>
            <header><div><p className="eyebrow">People & access</p><h2>Set {passwordUser.name}’s password</h2><p>They’ll be signed out everywhere else after this change.</p></div><button type="button" className="icon-button" onClick={() => setPasswordUser(null)}><X /></button></header>
            <div className="modal-body">
              <label>New password<input autoFocus required type="password" autoComplete="new-password" minLength={MIN_PASSWORD_LENGTH} maxLength={128} value={password} onChange={(event) => setPassword(event.target.value)} /><small>At least {MIN_PASSWORD_LENGTH} characters.</small></label>
              <label>Confirm password<input required type="password" autoComplete="new-password" minLength={MIN_PASSWORD_LENGTH} maxLength={128} value={passwordConfirm} onChange={(event) => setPasswordConfirm(event.target.value)} /></label>
            </div>
            <footer><button type="button" className="button ghost" onClick={() => setPasswordUser(null)}>Cancel</button><button className="button primary" disabled={busyId === passwordUser.id}>{busyId === passwordUser.id ? "Saving…" : "Set password"}</button></footer>
          </form>
        </div>
      )}
      {deletingUser && (
        <div className="modal-layer" role="dialog" aria-modal="true" aria-labelledby="delete-user-title">
          <button className="modal-scrim" disabled={busyId === `delete:${deletingUser.id}`} onClick={() => setDeletingUser(null)} aria-label="Close" />
          <form className="modal user-delete-modal" onSubmit={deleteAndReassignUser}>
            <header>
              <div><p className="eyebrow">People & access</p><h2 id="delete-user-title">Delete {deletingUser.name}?</h2><p>This removes their workspace access and signs them out. Choose who should take over before continuing.</p></div>
              <button type="button" className="icon-button" disabled={busyId === `delete:${deletingUser.id}`} onClick={() => setDeletingUser(null)}><X /></button>
            </header>
            <div className="modal-body user-delete-body">
              {error && <AdminError message={error} />}
              {!deletionImpact && <AdminLoading />}
              {deletionImpact && (
                <>
                  <section className="user-transfer-summary" aria-label="Work to transfer">
                    <h3>Work that will transfer</h3>
                    <div>
                      <span><strong>{deletionImpact.sources}</strong> referral source{deletionImpact.sources === 1 ? "" : "s"}</span>
                      <span><strong>{deletionImpact.leads}</strong> lead{deletionImpact.leads === 1 ? "" : "s"}</span>
                      <span><strong>{deletionImpact.openTasks}</strong> open task{deletionImpact.openTasks === 1 ? "" : "s"}</span>
                      <span><strong>{deletionImpact.upcomingEvents}</strong> upcoming event{deletionImpact.upcomingEvents === 1 ? "" : "s"}</span>
                    </div>
                  </section>
                  <label className="user-reassignment-field">Transfer active work to
                    <select required autoFocus value={reassignToUserId} onChange={(event) => setReassignToUserId(event.target.value)}>
                      <option value="" disabled>Choose an active user</option>
                      {users?.filter((user) => user.id !== deletingUser.id && Boolean(user.active)).map((user) => (
                        <option value={user.id} key={user.id}>{user.name} · {user.role}</option>
                      ))}
                    </select>
                  </label>
                  <div className="user-delete-notes">
                    <p><CheckCircle2 />Owned sources, leads, unfinished tasks, and upcoming event assignments transfer to the selected user.</p>
                    <p><ShieldCheck />Past activities and completed work keep {deletingUser.name}’s name so reporting remains accurate.</p>
                    {deletionImpact.calendarFeeds > 0 && <p><CalendarDays />{deletionImpact.calendarFeeds} private calendar feed{deletionImpact.calendarFeeds === 1 ? "" : "s"} will be removed. Events already imported stay in the workspace.</p>}
                  </div>
                </>
              )}
            </div>
            <footer>
              <button type="button" className="button ghost" disabled={busyId === `delete:${deletingUser.id}`} onClick={() => setDeletingUser(null)}>Cancel</button>
              <button className="button danger" disabled={!deletionImpact || !reassignToUserId || busyId === `delete:${deletingUser.id}`}>{busyId === `delete:${deletingUser.id}` ? "Deleting & transferring…" : "Delete & transfer work"}</button>
            </footer>
          </form>
        </div>
      )}
      {calendarUser && <CalendarFeedsModal user={calendarUser} onClose={() => setCalendarUser(null)} />}
    </AdminPanelShell>
  );
}

type LookupOption = {
  id: string;
  type: string;
  code: string;
  label: string;
  color: string | null;
  sortOrder: number;
  active: boolean | number;
};

function LookupOptionsPanel({ onBack }: { onBack: () => void }) {
  const [options, setOptions] = useState<LookupOption[] | null>(null);
  const [type, setType] = useState<"source_category" | "activity_type">("source_category");
  const [label, setLabel] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  async function load() {
    const response = await fetch("/api/admin/lookups");
    if (!response.ok) {
      setError("Source options could not be loaded.");
      return;
    }
    const result = (await response.json()) as { data: LookupOption[] };
    setOptions(result.data);
  }

  useEffect(() => { void load(); }, []);

  async function addOption(event: FormEvent) {
    event.preventDefault();
    const code = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    if (!code) return;
    setBusy("new");
    setError("");
    const response = await fetch("/api/admin/lookups", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        type,
        code,
        label,
        color: type === "source_category" ? "#079A98" : "#F26474",
        sortOrder: options?.filter((option) => option.type === type).length ?? 0,
      }),
    });
    setBusy("");
    if (!response.ok) {
      const result = (await response.json().catch(() => ({}))) as { error?: string };
      setError(result.error ?? "The option could not be added.");
      return;
    }
    setLabel("");
    await load();
  }

  async function toggleOption(option: LookupOption) {
    setBusy(option.id);
    setError("");
    const active = !Boolean(option.active);
    const response = await fetch(`/api/admin/lookups/${option.id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ active }),
    });
    setBusy("");
    if (!response.ok) {
      setError("The option could not be updated.");
      return;
    }
    setOptions((current) => current?.map((item) => item.id === option.id ? { ...item, active } : item) ?? null);
  }

  const visible = options?.filter((option) => option.type === type) ?? [];
  return (
    <AdminPanelShell title="Source categories" description="Keep organization categories and contact activity labels consistent." onBack={onBack}>
      {error && <AdminError message={error} />}
      <div className="admin-tabs" role="tablist">
        <button className={type === "source_category" ? "active" : ""} onClick={() => setType("source_category")}>Source categories</button>
        <button className={type === "activity_type" ? "active" : ""} onClick={() => setType("activity_type")}>Contact activity types</button>
      </div>
      <form className="lookup-add" onSubmit={addOption}>
        <label>New {type === "source_category" ? "category" : "activity type"}<input required maxLength={120} value={label} onChange={(event) => setLabel(event.target.value)} placeholder={type === "source_category" ? "Example: Senior living" : "Example: Lunch meeting"} /></label>
        <button className="button primary" disabled={busy === "new"}><Plus />{busy === "new" ? "Adding…" : "Add option"}</button>
      </form>
      {!options && !error && <AdminLoading />}
      {options && (
        <section className="admin-list-card lookup-list">
          <header><div><h2>{type === "source_category" ? "Categories" : "Activity types"}</h2><p>{visible.filter((option) => Boolean(option.active)).length} active options</p></div></header>
          {visible.length === 0 ? <div className="admin-empty"><SlidersHorizontal /><strong>No options yet</strong><p>Add the first option above.</p></div> : visible.map((option) => (
            <article className={!Boolean(option.active) ? "inactive" : ""} key={option.id}>
              <i style={{ background: option.color ?? "#079A98" }} />
              <div><strong>{option.label}</strong><small>{option.code}</small></div>
              <button disabled={busy === option.id} onClick={() => void toggleOption(option)}>{Boolean(option.active) ? "Active" : "Inactive"}</button>
            </article>
          ))}
        </section>
      )}
    </AdminPanelShell>
  );
}

type ImportRun = {
  id: string;
  filename: string;
  mode: string;
  status: string;
  report: string | null;
  startedAt: string;
  completedAt: string | null;
  revertedAt: string | null;
  openIssues: number;
};

type ImportPreviewRow = {
  rowNumber: number;
  organizationName: string;
  contactName: string | null;
  action: "create-source" | "add-contact" | "skip-contact" | "needs-review";
  matchedSourceId: string | null;
  matchedSourceName: string | null;
  issues: string[];
};

type ImportPreview = {
  data: ImportPreviewRow[];
  summary: { rows: number; createSources: number; addContacts: number; skippedContacts: number; needsReview: number };
};

function ImportsExportsPanel({ onBack }: { onBack: () => void }) {
  const [runs, setRuns] = useState<ImportRun[] | null>(null);
  const [error, setError] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<ImportColumnMapping>({});
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [preparedRows, setPreparedRows] = useState<ImportRow[]>([]);
  const [fingerprint, setFingerprint] = useState("");
  const [busy, setBusy] = useState("");

  function loadRuns() {
    fetch("/api/admin/imports")
      .then(async (response) => {
        if (!response.ok) throw new Error("Import history could not be loaded.");
        return response.json() as Promise<{ data: ImportRun[] }>;
      })
      .then((result) => setRuns(result.data))
      .catch((caught: Error) => setError(caught.message));
  }

  useEffect(loadRuns, []);

  async function chooseFile(nextFile: File | null) {
    setError("");
    setPreview(null);
    setPreparedRows([]);
    setFingerprint("");
    if (!nextFile) {
      setFile(null);
      setHeaders([]);
      setRawRows([]);
      setMapping({});
      return;
    }
    if (!/\.(csv|xlsx)$/i.test(nextFile.name)) {
      setError("Choose a .csv or .xlsx file.");
      return;
    }
    if (nextFile.size > 25 * 1024 * 1024) {
      setError("Choose a file smaller than 25 MB.");
      return;
    }
    setBusy("read");
    try {
      const grid = await readImportGrid(nextFile);
      if (grid.length < 2) throw new Error("The file needs a header row and at least one data row.");
      const fileHeaders = prepareImportHeaders(grid[0]);
      setFile(nextFile);
      setHeaders(fileHeaders);
      setRawRows(grid.slice(1));
      setMapping(autoMapImportColumns(fileHeaders));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The file could not be read.");
      setFile(null);
    } finally {
      setBusy("");
    }
  }

  async function previewImport() {
    if (!file || !mapping.organizationName) {
      setError("Map the required organization name column first.");
      return;
    }
    const rows = mapImportRows(headers, rawRows, mapping);
    if (!rows.length) {
      setError("No data rows were found in this file.");
      return;
    }
    if (rows.length > 1000) {
      setError("Import up to 1,000 rows at a time. Split this file and try again.");
      return;
    }
    const firstMissingOrganization = rows.find((row) => !row.organizationName);
    if (firstMissingOrganization) {
      setError(`Row ${firstMissingOrganization.rowNumber} is missing an organization name.`);
      return;
    }
    setBusy("preview");
    setError("");
    try {
      const nextFingerprint = await fileFingerprint(file);
      const response = await fetch("/api/admin/imports/preview", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ filename: file.name, fingerprint: nextFingerprint, rows }),
      });
      const result = await response.json().catch(() => ({})) as ImportPreview & { error?: string };
      if (!response.ok) throw new Error(result.error ?? "The import preview could not be created.");
      setPreparedRows(rows);
      setFingerprint(nextFingerprint);
      setPreview(result);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The import preview could not be created.");
    } finally {
      setBusy("");
    }
  }

  async function runImport() {
    if (!file || !preview || preview.summary.needsReview || !preparedRows.length || !fingerprint) return;
    setBusy("import");
    setError("");
    try {
      const response = await fetch("/api/admin/imports", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ filename: file.name, fingerprint, rows: preparedRows }),
      });
      const result = await response.json().catch(() => ({})) as { error?: string };
      if (!response.ok) throw new Error(result.error ?? "The import could not be completed.");
      await chooseFile(null);
      loadRuns();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The import could not be completed.");
    } finally {
      setBusy("");
    }
  }

  async function revertImport(run: ImportRun) {
    if (!window.confirm(`Roll back everything created by “${run.filename}”? This is only allowed when none of those records has been used or edited.`)) return;
    setBusy(run.id);
    setError("");
    const response = await fetch(`/api/admin/imports/${encodeURIComponent(run.id)}/revert`, { method: "POST" });
    const result = await response.json().catch(() => ({})) as { error?: string };
    setBusy("");
    if (!response.ok) {
      setError(result.error ?? "The import could not be rolled back.");
      return;
    }
    loadRuns();
  }

  function reportFor(run: ImportRun): { rows?: number; createdSources?: number; createdContacts?: number; skippedContacts?: number } {
    try { return run.report ? JSON.parse(run.report) as ReturnType<typeof reportFor> : {}; }
    catch { return {}; }
  }

  return (
    <AdminPanelShell title="Imports & exports" description="Bring in source and contact records with a safe preview, or download clean workspace data." onBack={onBack}>
      {error && <AdminError message={error} />}
      <section className="admin-import-workbench">
        <header><span className="admin-icon coral"><Upload /></span><div><h2>Import sources and contacts</h2><p>Upload CSV or Excel, map its columns, and review every action before anything is created.</p></div></header>
        <label className="admin-import-dropzone">
          <input type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(event) => void chooseFile(event.target.files?.[0] ?? null)} />
          <Upload />
          <span><strong>{busy === "read" ? "Reading file…" : file?.name ?? "Choose a CSV or Excel file"}</strong><small>{file ? `${rawRows.length} data rows found` : "The first row must contain column names · maximum 1,000 rows"}</small></span>
        </label>
        {file && headers.length > 0 && <>
          <div className="admin-import-mapping-heading"><div><h3>Map columns</h3><p>Organization name is required. Unused fields can stay unmapped.</p></div><span>{Object.keys(mapping).length} mapped</span></div>
          <div className="admin-import-mapping">
            {importFieldDefinitions.map((field) => <label key={field.key}>{field.label}{field.required && <b>Required</b>}<select value={mapping[field.key] ?? ""} onChange={(event) => {
              setMapping((current) => ({ ...current, [field.key]: event.target.value || undefined }));
              setPreview(null);
            }}><option value="">Not mapped</option>{headers.map((header) => <option value={header} key={header}>{header}</option>)}</select></label>)}
          </div>
          <div className="admin-import-actions"><button className="button primary" type="button" disabled={Boolean(busy)} onClick={() => void previewImport()}><ScanLine />{busy === "preview" ? "Checking…" : preview ? "Refresh preview" : "Preview import"}</button></div>
        </>}
        {preview && <div className="admin-import-preview">
          <div className="import-preview-summary">
            <article><small>Rows</small><strong>{preview.summary.rows}</strong></article>
            <article><small>New sources</small><strong>{preview.summary.createSources}</strong></article>
            <article><small>Contacts added</small><strong>{preview.summary.addContacts}</strong></article>
            <article><small>Duplicates skipped</small><strong>{preview.summary.skippedContacts}</strong></article>
            <article className={preview.summary.needsReview ? "warning" : "ready"}><small>Need review</small><strong>{preview.summary.needsReview}</strong></article>
          </div>
          <div className="admin-import-preview-table"><table><thead><tr><th>Row</th><th>Organization</th><th>Contact</th><th>Planned action</th></tr></thead><tbody>{preview.data.slice(0, 50).map((row) => <tr key={row.rowNumber}><td>{row.rowNumber}</td><td><strong>{row.organizationName}</strong>{row.matchedSourceName && <small>Matched {row.matchedSourceName}</small>}</td><td>{row.contactName || "—"}</td><td><span className={`import-action ${row.action}`}>{row.action.replaceAll("-", " ")}</span>{row.issues.map((issue) => <small className="import-issue" key={issue}>{issue}</small>)}</td></tr>)}</tbody></table>{preview.data.length > 50 && <p>Showing the first 50 of {preview.data.length} rows.</p>}</div>
          <footer><div><ShieldCheck /><span><strong>Duplicate protection is on</strong><small>Repeated organizations are consolidated and contacts with an existing email are skipped.</small></span></div><button className="button primary" disabled={Boolean(busy) || preview.summary.needsReview > 0} onClick={() => void runImport()}>{busy === "import" ? "Importing…" : `Import ${preview.summary.rows} rows`}<Check /></button></footer>
        </div>}
      </section>
      <section className="export-grid">
        <article><span className="admin-icon"><Building2 /></span><div><h2>Referral source directory</h2><p>Organizations, contacts, cadence, last contact, and open follow-ups.</p></div><a className="button primary" href="/api/admin/export/sources.csv" download><Download />Download CSV</a></article>
        <article><span className="admin-icon coral"><FileDown /></span><div><h2>Legacy referral records</h2><p>The referral rows retained from the original workbook migration.</p></div><a className="button ghost" href="/api/admin/export/referrals.csv" download><Download />Download CSV</a></article>
      </section>
      {!runs && !error && <AdminLoading />}
      {runs && (
        <section className="admin-list-card import-history">
          <header><div><h2>Workbook import history</h2><p>{runs.length} recorded {runs.length === 1 ? "run" : "runs"}</p></div></header>
          {runs.length === 0 ? <div className="admin-empty"><FileDown /><strong>No imports recorded</strong><p>Imported workbooks will appear here.</p></div> : runs.map((run) => {
            const report = reportFor(run);
            return (
            <article key={run.id}>
              <span className={`import-status ${run.revertedAt ? "reverted" : run.status}`}>{run.revertedAt ? <Archive /> : run.status === "failed" ? <CircleAlert /> : <Check />}</span>
              <div><strong>{run.filename}</strong><small>{run.mode} · {report.createdSources ?? 0} sources · {report.createdContacts ?? 0} contacts · {report.skippedContacts ?? 0} skipped</small><small>Started {formatAdminDate(run.startedAt)}</small></div>
              <div><b>{run.revertedAt ? "rolled back" : run.status}</b><small>{run.openIssues} open issues</small>{["completed", "failed"].includes(run.status) && !run.revertedAt && <button type="button" disabled={busy === run.id} onClick={() => void revertImport(run)}>{busy === run.id ? "Checking…" : "Roll back"}</button>}</div>
            </article>
          );})}
        </section>
      )}
    </AdminPanelShell>
  );
}

type DataQualityIssue = {
  key: string;
  kind: string;
  severity: "high" | "medium";
  entityType: "contact" | "source";
  entityId: string;
  entityName: string;
  sourceId: string;
  sourceName: string;
  detail: string;
};

function DataQualityPanel({ onBack, onOpenSource, onOpenContact, onReviewDuplicates }: {
  onBack: () => void;
  onOpenSource: (id: string) => void;
  onOpenContact: (id: string) => void;
  onReviewDuplicates: () => void;
}) {
  const [issues, setIssues] = useState<DataQualityIssue[] | null>(null);
  const [summary, setSummary] = useState({ total: 0, high: 0, duplicateContactGroups: 0, duplicateSourceGroups: 0 });
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  function load() {
    setError("");
    fetch("/api/data-quality")
      .then(async (response) => {
        const result = await response.json().catch(() => ({})) as { data?: DataQualityIssue[]; summary?: typeof summary; error?: string };
        if (!response.ok || !result.data || !result.summary) throw new Error(result.error ?? "The data-quality queue could not be loaded.");
        setIssues(result.data);
        setSummary(result.summary);
      })
      .catch((caught: Error) => setError(caught.message));
  }

  useEffect(load, []);

  async function dismiss(issue: DataQualityIssue) {
    setBusy(issue.key);
    const response = await fetch("/api/data-quality/dismiss", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ issueKey: issue.key, days: 30 }),
    });
    setBusy("");
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      setError(result.error ?? "The issue could not be dismissed.");
      return;
    }
    setIssues((current) => current?.filter((candidate) => candidate.key !== issue.key) ?? null);
    setSummary((current) => ({ ...current, total: Math.max(0, current.total - 1), high: Math.max(0, current.high - (issue.severity === "high" ? 1 : 0)) }));
  }

  return <AdminPanelShell title="Data quality" description="A prioritized cleanup inbox for incomplete, stale, and duplicate relationship records." onBack={onBack}>
    {error && <AdminError message={error} />}
    <section className="quality-dashboard-summary">
      <article className={summary.high ? "urgent" : "clear"}><span><CircleAlert /></span><div><strong>{summary.high}</strong><small>High priority</small></div></article>
      <article><span><ClipboardList /></span><div><strong>{summary.total}</strong><small>Open checks</small></div></article>
      <article><span><Users /></span><div><strong>{summary.duplicateContactGroups}</strong><small>Duplicate contact groups</small></div><button type="button" onClick={onReviewDuplicates}>Review</button></article>
      <article><span><Building2 /></span><div><strong>{summary.duplicateSourceGroups}</strong><small>Duplicate source groups</small></div><button type="button" onClick={onReviewDuplicates}>Review</button></article>
    </section>
    {!issues && !error && <AdminLoading />}
    {issues && <section className="quality-inbox">
      <header><div><h2>Cleanup inbox</h2><p>High-priority items appear first. Dismissed checks return in 30 days.</p></div><button className="button ghost" type="button" onClick={load}><RefreshCw />Refresh</button></header>
      {!issues.length ? <div className="admin-empty quality-clear"><CheckCircle2 /><strong>Your workspace data is in good shape</strong><p>No stale or incomplete relationship records need attention.</p></div> : issues.map((issue) => <article key={issue.key}>
        <span className={`quality-severity ${issue.severity}`}><i />{issue.severity}</span>
        <div><strong>{issue.entityName}</strong><small>{issue.entityType === "contact" && issue.sourceName ? `${issue.sourceName} · ` : ""}{issue.detail}</small></div>
        <div className="quality-row-actions"><button className="button primary" type="button" onClick={() => issue.entityType === "contact" ? onOpenContact(issue.entityId) : onOpenSource(issue.sourceId)}>{issue.entityType === "contact" ? "Open contact" : "Open source"}<ChevronRight /></button><button className="button ghost" type="button" disabled={busy === issue.key} onClick={() => void dismiss(issue)}>{busy === issue.key ? "Dismissing…" : "Dismiss 30 days"}</button></div>
      </article>)}
    </section>}
  </AdminPanelShell>;
}

type ArchivedRecord = {
  id: string;
  name: string;
  type: "source" | "referral";
  archivedAt: string;
  version: number;
};

function ArchivesPanel({ onBack }: { onBack: () => void }) {
  const [records, setRecords] = useState<ArchivedRecord[] | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => {
    fetch("/api/admin/archives")
      .then(async (response) => {
        if (!response.ok) throw new Error("Archived records could not be loaded.");
        return response.json() as Promise<{ data: ArchivedRecord[] }>;
      })
      .then((result) => setRecords(result.data))
      .catch((caught: Error) => setError(caught.message));
  }, []);

  async function restore(record: ArchivedRecord) {
    setBusy(record.id);
    setError("");
    const response = await fetch(`/api/admin/archives/${record.type}/${record.id}/restore`, { method: "POST" });
    setBusy("");
    if (!response.ok) {
      setError("The record could not be restored.");
      return;
    }
    setRecords((current) => current?.filter((item) => item.id !== record.id) ?? null);
  }
  return (
    <AdminPanelShell title="Archived sources" description="Review removed records and return them to the active workspace." onBack={onBack}>
      {error && <AdminError message={error} />}
      {!records && !error && <AdminLoading />}
      {records && (
        <section className="admin-list-card archive-list">
          <header><div><h2>Archived records</h2><p>{records.length} records</p></div></header>
          {records.length === 0 ? <div className="admin-empty"><Archive /><strong>Nothing is archived</strong><p>Archived referral sources will be kept safely here.</p></div> : records.map((record) => (
            <article key={`${record.type}_${record.id}`}>
              <span className="admin-icon"><Archive /></span>
              <div><strong>{record.name}</strong><small>{record.type === "source" ? "Referral source" : "Legacy referral"} · Archived {formatAdminDate(record.archivedAt)}</small></div>
              <button className="button ghost" disabled={busy === record.id} onClick={() => void restore(record)}>{busy === record.id ? "Restoring…" : "Restore"}</button>
            </article>
          ))}
        </section>
      )}
    </AdminPanelShell>
  );
}

type AuditEvent = {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  metadata: string;
  requestId: string;
  createdAt: string;
  actorName: string | null;
  entityName: string | null;
};

type AuditPage = { page: number; pageSize: number; total: number; totalPages: number };
type AuditFilterOptions = {
  actors: Array<{ id: string; name: string }>;
  actions: string[];
  entityTypes: string[];
};

type AuditDetail = { key: string; label: string; value: string };

const auditFieldLabels: Record<string, string> = {
  active: "Account active",
  actorUserId: "Acting user",
  address: "Address",
  assigneeUserId: "Assigned to",
  assignmentNotificationId: "Assignment notification",
  attendeeContactIds: "Contact attendees",
  attendeeSourceIds: "Referral source attendees",
  attendeeUserIds: "Staff attendees",
  bestContactTime: "Best contact time",
  brandAccentColor: "Brand accent color",
  brandName: "Brand name",
  brandPrimaryColor: "Brand primary color",
  cadenceDays: "Contact cadence (days)",
  cadenceType: "Contact cadence",
  categoryCode: "Referral category",
  contactCount: "Number of contacts",
  contactId: "Contact",
  contactNames: "Contacts",
  contactStatus: "Contact status",
  createdFrom: "Created from",
  defaultSourceContactDays: "Default contact interval",
  description: "Description",
  digestEnabled: "Email digest enabled",
  digestHourLocal: "Digest delivery hour",
  digestsEnabled: "Workspace digests enabled",
  displayContentType: "Display image type",
  dueAt: "Due date",
  email: "Email",
  eventId: "Event",
  fileName: "File name",
  invitationSent: "Invitation sent",
  isPrimary: "Primary contact",
  lastVerifiedAt: "Last verified",
  leadActivityTrackingEnabled: "Lead activity tracking",
  leadContactDetailsEnabled: "Lead contact details",
  leadPipelineEnabled: "Lead pipeline",
  location: "Location",
  name: "Name",
  networkingGroupId: "Networking group",
  notes: "Notes",
  occurredAt: "Date and time",
  ownerUserId: "Assigned owner",
  phone: "Phone",
  preferredContactMethod: "Preferred contact method",
  previousOwnerUserId: "Previous owner",
  priority: "Priority",
  referralId: "Lead",
  referralPriority: "Referral priority",
  role: "Role",
  sessionsRevoked: "Other sessions signed out",
  sizeBytes: "File size",
  sourceContactId: "Contact",
  sourceId: "Referral source",
  sourceName: "Referral source",
  staffAttendees: "Staff attendees",
  sourceAttendees: "Referral sources attending",
  contactAttendees: "Contacts attending",
  summary: "Summary",
  title: "Title",
  timezone: "Timezone",
  verticalCode: "Organization vertical",
  website: "Website",
};

function auditFieldLabel(key: string): string {
  const label = auditFieldLabels[key]
    ?? key.replaceAll("_", " ").replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/\bids?\b/gi, "").replace(/^./, (letter) => letter.toUpperCase());
  return label.replace(/\s+/g, " ").trim();
}

const auditTechnicalFields = new Set(["version", "assignmentNotificationId"]);
const auditIdentifierPattern = /^(?:source|source_contact|networking_group|event|task|referral|lookup|user|activity|audit|request)_[a-z0-9_-]+$/i;
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function auditValueLabel(key: string, value: unknown, references: Record<string, string>): string {
  if (value === null || value === undefined || value === "") return "None";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (key.toLowerCase().includes("sizebytes") && typeof value === "number") return attachmentSizeLabel(value);
  if (Array.isArray(value)) return value.length ? value.map((item) => auditValueLabel(key, item, references)).join(", ") : "None";
  if (typeof value === "object") return JSON.stringify(value);
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) return formatAdminDate(value);
  if (typeof value === "string" && references[value]) return references[value];
  if (typeof value === "string" && (auditIdentifierPattern.test(value) || uuidPattern.test(value))) return "Deleted or unavailable record";
  return String(value);
}

function auditDetails(metadata: string, references: Record<string, string>): AuditDetail[] {
  let parsed: unknown;
  try { parsed = metadata ? JSON.parse(metadata) : {}; } catch { parsed = { recordedDetails: metadata }; }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return [];
  const details: AuditDetail[] = [];
  const visit = (record: Record<string, unknown>, prefix = "") => {
    for (const [key, value] of Object.entries(record)) {
      if (auditTechnicalFields.has(key)) continue;
      if (value && typeof value === "object" && !Array.isArray(value)) {
        visit(value as Record<string, unknown>, prefix ? `${prefix} · ${auditFieldLabel(key)}` : auditFieldLabel(key));
        continue;
      }
      details.push({
        key: prefix ? `${prefix}.${key}` : key,
        label: prefix ? `${prefix} · ${auditFieldLabel(key)}` : auditFieldLabel(key),
        value: auditValueLabel(key, value, references),
      });
    }
  };
  visit(parsed as Record<string, unknown>);
  return details;
}

function auditEntityLabel(entityType: string): string {
  const labels: Record<string, string> = {
    source: "Referral source",
    source_contact: "Contact",
    source_contact_affiliation: "Contact organization link",
    contact_interaction: "Contact interaction",
    source_activity: "Referral source interaction",
    source_contact_note: "Contact note",
    source_attachment: "Attachment",
    event: "Event",
    event_photo: "Event photo",
    contact_photo: "Contact photo",
    "networking-group": "Networking group",
    task: "Task",
    referral: "Lead",
    referral_activity: "Lead interaction",
    referral_receipt: "Referral count",
    lookup: "Admin option",
    user: "User account",
    workspace: "Workspace settings",
    "calendar-feed": "Calendar import",
  };
  return labels[entityType] ?? auditFieldLabel(entityType);
}

function auditActionLabel(action: string): string {
  const labels: Record<string, string> = {
    create: "created",
    update: "updated",
    archive: "archived",
    restore: "restored",
    delete: "deleted",
    complete: "completed",
    export: "exported",
    sync: "synchronized",
    link: "linked",
    transfer: "transferred",
    "add-contact": "added a contact to",
    "update-avatar": "changed the photo for",
    "delete-avatar": "removed the photo from",
    "resend-activation": "resent activation for",
    "set-password": "set a new password for",
    impersonate: "started viewing as",
  };
  return labels[action] ?? action.replaceAll("-", " ");
}

function AuditHistoryPanel({ onBack }: { onBack: () => void }) {
  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  const [references, setReferences] = useState<Record<string, string>>({});
  const [pageInfo, setPageInfo] = useState<AuditPage>({ page: 1, pageSize: 25, total: 0, totalPages: 1 });
  const [filterOptions, setFilterOptions] = useState<AuditFilterOptions>({ actors: [], actions: [], entityTypes: [] });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [actorUserId, setActorUserId] = useState("");
  const [action, setAction] = useState("");
  const [entityType, setEntityType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    const parameters = new URLSearchParams({ page: String(page), pageSize: String(pageSize), sortBy, sortDirection });
    if (search) parameters.set("search", search);
    if (actorUserId) parameters.set("actorUserId", actorUserId);
    if (action) parameters.set("action", action);
    if (entityType) parameters.set("entityType", entityType);
    if (dateFrom) parameters.set("dateFrom", dateFrom);
    if (dateTo) parameters.set("dateTo", dateTo);
    setError("");
    setEvents(null);
    fetch(`/api/admin/audit?${parameters}`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("Audit history could not be loaded.");
        return response.json() as Promise<{ data: AuditEvent[]; references?: Record<string, string>; page?: AuditPage; filters?: AuditFilterOptions }>;
      })
      .then((result) => {
        setEvents(result.data);
        setReferences(result.references ?? {});
        setPageInfo(result.page ?? { page, pageSize, total: result.data.length, totalPages: 1 });
        if (result.filters) setFilterOptions(result.filters);
      })
      .catch((caught: Error) => {
        if (!(caught instanceof DOMException && caught.name === "AbortError")) setError(caught.message);
      });
    return () => controller.abort();
  }, [page, pageSize, search, actorUserId, action, entityType, dateFrom, dateTo, sortBy, sortDirection]);
  const activeFilterCount = [search, actorUserId, action, entityType, dateFrom, dateTo].filter(Boolean).length;
  const firstResult = pageInfo.total ? (pageInfo.page - 1) * pageInfo.pageSize + 1 : 0;
  const lastResult = Math.min(pageInfo.page * pageInfo.pageSize, pageInfo.total);
  function updateFilter(setter: (value: string) => void, value: string) {
    setter(value);
    setPage(1);
  }
  function clearFilters() {
    setSearchDraft("");
    setSearch("");
    setActorUserId("");
    setAction("");
    setEntityType("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  }
  return (
    <AdminPanelShell title="Audit history" description="See the complete action, affected record, and every detail recorded for each workspace change." onBack={onBack}>
      <section className="audit-controls" aria-label="Filter and sort audit history">
        <form className="audit-search" onSubmit={(submitEvent) => { submitEvent.preventDefault(); updateFilter(setSearch, searchDraft.trim()); }}>
          <label><Search /><input aria-label="Search audit history" value={searchDraft} onChange={(changeEvent) => setSearchDraft(changeEvent.target.value)} placeholder="Search people, records, actions, or details" /></label>
          <button type="submit">Search</button>
        </form>
        <div className="audit-filter-grid">
          <label><span>Person</span><select aria-label="Filter audit history by person" value={actorUserId} onChange={(changeEvent) => updateFilter(setActorUserId, changeEvent.target.value)}><option value="">Everyone</option>{filterOptions.actors.map((actor) => <option value={actor.id} key={actor.id}>{actor.name}</option>)}</select></label>
          <label><span>Action</span><select aria-label="Filter audit history by action" value={action} onChange={(changeEvent) => updateFilter(setAction, changeEvent.target.value)}><option value="">All actions</option>{filterOptions.actions.map((option) => <option value={option} key={option}>{auditActionLabel(option).replace(/^./, (letter) => letter.toUpperCase())}</option>)}</select></label>
          <label><span>Record type</span><select aria-label="Filter audit history by record type" value={entityType} onChange={(changeEvent) => updateFilter(setEntityType, changeEvent.target.value)}><option value="">All record types</option>{filterOptions.entityTypes.map((option) => <option value={option} key={option}>{auditEntityLabel(option)}</option>)}</select></label>
          <label><span>From</span><input aria-label="Audit history start date" type="date" value={dateFrom} max={dateTo || undefined} onChange={(changeEvent) => updateFilter(setDateFrom, changeEvent.target.value)} /></label>
          <label><span>Through</span><input aria-label="Audit history end date" type="date" value={dateTo} min={dateFrom || undefined} onChange={(changeEvent) => updateFilter(setDateTo, changeEvent.target.value)} /></label>
          <label><span>Sort by</span><select aria-label="Sort audit history" value={sortBy} onChange={(changeEvent) => { setSortBy(changeEvent.target.value); setPage(1); }}><option value="date">Date and time</option><option value="actor">Person</option><option value="record">Record name</option><option value="action">Action</option><option value="type">Record type</option></select></label>
          <button type="button" className="audit-sort-direction" onClick={() => { setSortDirection((current) => current === "asc" ? "desc" : "asc"); setPage(1); }}><ArrowUpDown />{sortDirection === "asc" ? "Ascending" : "Descending"}</button>
        </div>
        <footer><span><Filter />{activeFilterCount ? `${activeFilterCount} filter${activeFilterCount === 1 ? "" : "s"} applied` : "Showing all changes"}</span>{activeFilterCount > 0 && <button type="button" onClick={clearFilters}><X />Clear filters</button>}</footer>
      </section>
      {error && <AdminError message={error} />}
      {!events && !error && <AdminLoading />}
      {events && (
        <section className="admin-list-card audit-list">
          <header><div><h2>Workspace changes</h2><p>{pageInfo.total.toLocaleString()} matching event{pageInfo.total === 1 ? "" : "s"}</p></div><label className="audit-page-size"><span>Rows</span><select aria-label="Audit events per page" value={pageSize} onChange={(changeEvent) => { setPageSize(Number(changeEvent.target.value)); setPage(1); }}><option value="10">10</option><option value="25">25</option><option value="50">50</option></select></label></header>
          {events.length === 0 ? <div className="admin-empty"><Activity /><strong>{activeFilterCount ? "No changes match these filters" : "No changes recorded"}</strong><p>{activeFilterCount ? "Adjust or clear the filters to see more history." : "Workspace changes will appear here."}</p></div> : events.map((event) => {
            const details = auditDetails(event.metadata, references);
            const entityLabel = auditEntityLabel(event.entityType);
            return (
              <article className="audit-entry" key={event.id}>
                <span className={`audit-action ${event.action}`} aria-hidden="true"><Activity /></span>
                <div className="audit-entry-body">
                  <div className="audit-entry-summary">
                    <div>
                      <strong><b>{event.actorName ?? "System"}</b> {auditActionLabel(event.action)} {entityLabel.toLowerCase()}</strong>
                      <small>{formatAdminDate(event.createdAt)}</small>
                    </div>
                    <span className="audit-target"><i>{entityLabel}</i><b>{event.entityName || "Record details unavailable"}</b></span>
                  </div>
                  <details className="audit-full-action">
                    <summary><span>View full action</span><ChevronRight /></summary>
                    <div className="audit-detail-panel">
                      {details.length ? (
                        <dl>{details.map((detail) => <div key={detail.key}><dt>{detail.label}</dt><dd>{detail.value}</dd></div>)}</dl>
                      ) : <p>No additional field details were recorded for this older action.</p>}
                      <details className="audit-technical-details">
                        <summary><span>Technical details</span><ChevronRight /></summary>
                        <footer>
                          <span><small>Record ID</small><code>{event.entityId}</code></span>
                          <span><small>Audit event ID</small><code>{event.id}</code></span>
                          {event.requestId && <span><small>Request ID</small><code>{event.requestId}</code></span>}
                        </footer>
                      </details>
                    </div>
                  </details>
                </div>
              </article>
            );
          })}
          {pageInfo.total > 0 && <nav className="audit-pagination" aria-label="Audit history pages"><span>Showing {firstResult.toLocaleString()}–{lastResult.toLocaleString()} of {pageInfo.total.toLocaleString()}</span><div><button type="button" disabled={pageInfo.page <= 1 || !events} onClick={() => setPage((current) => Math.max(1, current - 1))}><ArrowLeft />Previous</button><strong>Page {pageInfo.page} of {pageInfo.totalPages}</strong><button type="button" disabled={pageInfo.page >= pageInfo.totalPages || !events} onClick={() => setPage((current) => Math.min(pageInfo.totalPages, current + 1))}>Next<ArrowRight /></button></div></nav>}
        </section>
      )}
    </AdminPanelShell>
  );
}

type UserActivityReport = {
  id: string;
  name: string;
  email: string;
  role: WorkspaceRole;
  active: boolean | number;
  sourcesOwned: number;
  ownedSourcesReached: number;
  contactsLogged: number;
  previousContactsLogged: number;
  uniqueSourcesReached: number;
  activeDays: number;
  callsLogged: number;
  emailsLogged: number;
  visitsLogged: number;
  otherOutreach: number;
  referralsReceived: number;
  tasksCompleted: number;
  tasksDue: number;
  tasksCompletedDue: number;
  tasksCompletedOnTime: number;
  openTasks: number;
  overdueTasks: number;
  changesMade: number;
  relationshipCoverageRate: number | null;
  taskCompletionRate: number | null;
  onTimeRate: number | null;
  outreachTrend: number | null;
  lastActivityAt: string | null;
};

type UserKpiSummary = {
  activeUsers: number;
  contactsLogged: number;
  referralsReceived: number;
  previousContactsLogged: number;
  sourcesOwned: number;
  ownedSourcesReached: number;
  tasksDue: number;
  tasksCompletedDue: number;
  tasksCompletedOnTime: number;
  openTasks: number;
  overdueTasks: number;
  relationshipCoverageRate: number | null;
  taskCompletionRate: number | null;
  onTimeRate: number | null;
  outreachTrend: number | null;
};

function userKpiPercent(value: number | null): string {
  return value === null ? "—" : `${value}%`;
}

function userOutreachTrend(row: Pick<UserActivityReport, "contactsLogged" | "previousContactsLogged" | "outreachTrend">, range: string): string {
  if (range === "all") return "All recorded activity";
  if (row.outreachTrend === null) return row.contactsLogged > 0 && row.previousContactsLogged === 0 ? "New activity this period" : "No prior-period baseline";
  if (row.outreachTrend === 0) return "Even with prior period";
  return `${row.outreachTrend > 0 ? "+" : ""}${row.outreachTrend}% vs prior period`;
}

function UserActivityReportsPanel({ navigation }: { navigation: ReactNode }) {
  const [range, setRange] = useState("30");
  const [selectedUserId, setSelectedUserId] = useState("all");
  const [userOptions, setUserOptions] = useState<Array<Pick<UserActivityReport, "id" | "name" | "active">>>([]);
  const [collectionView, setCollectionView] = useCollectionView("user-activity-report-layout");
  const [report, setReport] = useState<{ data: UserActivityReport[]; summary: UserKpiSummary } | null>(null);
  const [error, setError] = useState("");
  const [exportingPdf, setExportingPdf] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    setReport(null);
    setError("");
    const query = new URLSearchParams({ range });
    if (selectedUserId !== "all") query.set("userId", selectedUserId);
    fetch(`/api/admin/reports/users?${query}`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("User KPI reports could not be loaded.");
        return response.json() as Promise<{ data: UserActivityReport[]; summary: UserKpiSummary }>;
      })
      .then((result) => {
        setReport(result);
        if (selectedUserId === "all") setUserOptions(result.data.map(({ id, name, active }) => ({ id, name, active })));
      })
      .catch((caught: Error) => {
        if (caught.name !== "AbortError") setError(caught.message);
      });
    return () => controller.abort();
  }, [range, selectedUserId]);

  const rows = report?.data ?? null;
  const summary = report?.summary ?? null;
  type UserKpiTableSort = "person" | "outreach" | "referrals" | "coverage" | "follow-through" | "on-time" | "workload" | "mix" | "last-activity";
  const { sortedRows: sortedUserRows, sortKey: userSortKey, sortDirection: userSortDirection, changeSort: changeUserSort } = useSortableTable<UserActivityReport, UserKpiTableSort>(rows ?? [], "outreach", (row, key) => ({
    person: row.name,
    outreach: row.contactsLogged,
    referrals: row.referralsReceived,
    coverage: row.relationshipCoverageRate,
    "follow-through": row.taskCompletionRate,
    "on-time": row.onTimeRate,
    workload: row.overdueTasks * 1_000_000 + row.openTasks,
    mix: row.callsLogged + row.emailsLogged + row.visitsLogged + row.otherOutreach,
    "last-activity": row.lastActivityAt ? new Date(row.lastActivityAt).getTime() : null,
  })[key], "descending");
  const selectedUser = userOptions.find((user) => user.id === selectedUserId);
  const reportSubject = selectedUser?.name ?? "All users";
  const periodLabel = range === "all" ? "All time" : `Last ${range} days`;
  const userFilenameSuffix = selectedUser ? `-${selectedUser.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}` : "";

  function downloadUserKpiReport() {
    if (!rows) return;
    const csvCell = (value: string | number | null) => {
      const text = value === null ? "" : String(value);
      const safe = /^[=+\-@]/.test(text) ? `'${text}` : text;
      return `"${safe.replaceAll('"', '""')}"`;
    };
    const csv = [
      ["Person", "Email", "Role", "Status", "Contacts logged", "Referrals received", "Prior-period contacts", "Outreach trend", "Relationships reached", "Assigned sources reached", "Sources assigned", "Relationship coverage", "Active outreach days", "Tasks completed", "Due tasks completed", "Tasks due", "Completion rate", "Completed on time", "On-time rate", "Open tasks", "Overdue tasks", "Calls/texts", "Emails", "Visits/meetings", "Other outreach", "Workspace changes", "Last activity"],
      ...rows.map((row) => [row.name, row.email, row.role, Boolean(row.active) ? "Active" : "Inactive", row.contactsLogged, row.referralsReceived, row.previousContactsLogged, row.outreachTrend, row.uniqueSourcesReached, row.ownedSourcesReached, row.sourcesOwned, row.relationshipCoverageRate, row.activeDays, row.tasksCompleted, row.tasksCompletedDue, row.tasksDue, row.taskCompletionRate, row.tasksCompletedOnTime, row.onTimeRate, row.openTasks, row.overdueTasks, row.callsLogged, row.emailsLogged, row.visitsLogged, row.otherOutreach, row.changesMade, row.lastActivityAt]),
    ].map((line) => line.map(csvCell).join(",")).join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `referral-source-user-kpis${userFilenameSuffix}-${range === "all" ? "all-time" : `${range}-days`}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  async function downloadUserKpiPdf() {
    if (!rows || !summary) return;
    setExportingPdf(true);
    setError("");
    try {
      const { exportUserKpiPdf } = await import("./user-kpi-pdf");
      exportUserKpiPdf({
        rows,
        summary,
        subject: reportSubject,
        period: periodLabel,
        filename: `referral-source-user-kpis${userFilenameSuffix || "-all-users"}-${range === "all" ? "all-time" : `${range}-days`}.pdf`,
      });
    } catch {
      setError("The PDF report could not be created. Please try again.");
    } finally {
      setExportingPdf(false);
    }
  }

  return (
    <div className="page admin-page admin-subpage reports-page user-performance-report-page">
      {navigation}
      <div className="page-heading">
        <div><p className="eyebrow">Reports</p><h1>User performance KPIs</h1><p>Understand outreach consistency, relationship coverage, follow-through, and current workload by person.</p></div>
        <div className="admin-view-actions user-kpi-actions">
        <label className="report-range user-report-person">Person<select value={selectedUserId} onChange={(event) => setSelectedUserId(event.target.value)}><option value="all">All users</option>{userOptions.map((user) => <option key={user.id} value={user.id}>{user.name}{Boolean(user.active) ? "" : " (inactive)"}</option>)}</select></label>
        <label className="report-range">Period<select value={range} onChange={(event) => setRange(event.target.value)}><option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option><option value="365">Last year</option><option value="all">All time</option></select></label>
        <div className="user-kpi-export-actions"><button type="button" className="button ghost" disabled={!rows || exportingPdf} onClick={downloadUserKpiPdf}><FileDown />{exportingPdf ? "Creating PDF…" : "Export PDF"}</button><button type="button" className="button ghost" disabled={!rows} onClick={downloadUserKpiReport}><Download />Export CSV</button></div>
        <ViewToggle value={collectionView} onChange={setCollectionView} label="User KPI report view" />
        </div>
      </div>
      {error && <AdminError message={error} />}
      {!rows && !error && <AdminLoading />}
      {rows && <div className="user-report-scope" aria-live="polite"><span>{selectedUserId === "all" ? "Team report" : "Individual report"}</span><strong>{reportSubject}</strong><small>{periodLabel}</small></div>}
      {summary && <section className="user-kpi-summary" aria-label={selectedUserId === "all" ? "Team KPI summary" : "User KPI summary"}>
        <article><span><Activity /></span><small>{selectedUserId === "all" ? "Team outreach" : "User outreach"}</small><strong>{summary.contactsLogged}</strong><p>{userOutreachTrend(summary, range)}</p></article>
        <article><span><Users /></span><small>Relationship coverage</small><strong>{userKpiPercent(summary.relationshipCoverageRate)}</strong><p>{summary.ownedSourcesReached} of {summary.sourcesOwned} assigned sources reached</p></article>
        <article><span><CheckCircle2 /></span><small>Follow-through</small><strong>{userKpiPercent(summary.taskCompletionRate)}</strong><p>{summary.tasksCompletedDue} of {summary.tasksDue} due follow-ups completed</p></article>
        <article className={summary.overdueTasks > 0 ? "attention" : ""}><span><CircleAlert /></span><small>Current overdue</small><strong>{summary.overdueTasks}</strong><p>{selectedUserId === "all" ? `${summary.openTasks} open across ${summary.activeUsers} active users` : `${summary.openTasks} open follow-ups`}</p></article>
      </section>}
      {rows && <section className="user-kpi-guide" aria-label="KPI definitions">
        <BarChart3 />
        <div><strong>How these KPIs work</strong><p><b>Coverage</b> is the share of assigned sources personally reached during the period. <b>Follow-through</b> is the share of assigned follow-ups due in the period that were completed. <b>On-time</b> measures whether those follow-ups were finished by their due date.</p></div>
      </section>}
      {rows && collectionView === "cards" && <section className="user-report-grid">
        {sortedUserRows.map((row) => <article key={row.id} className={!Boolean(row.active) ? "inactive" : ""}>
          <header><span className="admin-avatar">{row.name.split(" ").map((part) => part[0]).join("").slice(0, 2)}</span><div><h2>{row.name}</h2><p>{row.role} · {Boolean(row.active) ? "Active" : "Inactive"}</p></div></header>
          <div className="user-kpi-hero">
            <div><small>Outreach logged</small><strong>{row.contactsLogged}</strong><p>{row.uniqueSourcesReached} relationships · {row.activeDays} active days</p><em className={row.outreachTrend !== null && row.outreachTrend < 0 ? "down" : ""}>{userOutreachTrend(row, range)}</em></div>
            <div><small>Relationship coverage</small><strong>{userKpiPercent(row.relationshipCoverageRate)}</strong><p>{row.ownedSourcesReached} of {row.sourcesOwned} assigned</p><i><b style={{ width: `${row.relationshipCoverageRate ?? 0}%` }} /></i></div>
          </div>
          <div className="user-report-metrics user-kpi-metrics"><span><strong>{row.tasksCompleted}</strong><small>Tasks completed</small></span><span><strong>{userKpiPercent(row.taskCompletionRate)}</strong><small>Follow-through</small></span><span><strong>{userKpiPercent(row.onTimeRate)}</strong><small>Completed on time</small></span><span className={row.overdueTasks > 0 ? "attention" : ""}><strong>{row.overdueTasks}</strong><small>Overdue · {row.openTasks} open</small></span></div>
          <div className="user-kpi-mix" aria-label={`${row.name} outreach mix`}><span className="referral-kpi-chip"><ArrowRight />{row.referralsReceived} referrals received</span><span><Phone />{row.callsLogged} calls/texts</span><span><Mail />{row.emailsLogged} emails</span><span><Building2 />{row.visitsLogged} visits/meetings</span>{row.otherOutreach > 0 && <span><Activity />{row.otherOutreach} other</span>}</div>
          <footer><span>Last activity <strong>{formatAdminDate(row.lastActivityAt)}</strong></span><span>{row.changesMade} workspace changes</span></footer>
        </article>)}
      </section>}
      {rows && collectionView === "table" && <div className="data-table-shell user-report-table-shell">
        <table className="data-table user-report-table">
          <thead><tr><SortableTableHeader column="person" label="Person" sortKey={userSortKey} sortDirection={userSortDirection} onSort={changeUserSort} /><SortableTableHeader column="outreach" label="Outreach" sortKey={userSortKey} sortDirection={userSortDirection} onSort={changeUserSort} /><SortableTableHeader column="referrals" label="Referrals received" sortKey={userSortKey} sortDirection={userSortDirection} onSort={changeUserSort} /><SortableTableHeader column="coverage" label="Relationship coverage" sortKey={userSortKey} sortDirection={userSortDirection} onSort={changeUserSort} /><SortableTableHeader column="follow-through" label="Follow-through" sortKey={userSortKey} sortDirection={userSortDirection} onSort={changeUserSort} /><SortableTableHeader column="on-time" label="On time" sortKey={userSortKey} sortDirection={userSortDirection} onSort={changeUserSort} /><SortableTableHeader column="workload" label="Current workload" sortKey={userSortKey} sortDirection={userSortDirection} onSort={changeUserSort} /><SortableTableHeader column="mix" label="Outreach mix" sortKey={userSortKey} sortDirection={userSortDirection} onSort={changeUserSort} /><SortableTableHeader column="last-activity" label="Last activity" sortKey={userSortKey} sortDirection={userSortDirection} onSort={changeUserSort} /></tr></thead>
          <tbody>{sortedUserRows.map((row) => <tr key={row.id} className={!Boolean(row.active) ? "inactive" : ""}>
            <td><strong>{row.name}</strong><small>{row.email}</small><span className="table-status">{row.role} · {Boolean(row.active) ? "Active" : "Inactive"}</span></td>
            <td><strong className="table-kpi-value">{row.contactsLogged}</strong><small>{row.uniqueSourcesReached} relationships · {row.activeDays} days</small><em>{userOutreachTrend(row, range)}</em></td>
            <td><strong className="table-kpi-value">{row.referralsReceived}</strong><small>credited to this user</small></td>
            <td><strong className="table-kpi-value">{userKpiPercent(row.relationshipCoverageRate)}</strong><small>{row.ownedSourcesReached} of {row.sourcesOwned} assigned</small></td>
            <td><strong className="table-kpi-value">{userKpiPercent(row.taskCompletionRate)}</strong><small>{row.tasksCompletedDue} of {row.tasksDue} due · {row.tasksCompleted} completed</small></td>
            <td><strong className="table-kpi-value">{userKpiPercent(row.onTimeRate)}</strong><small>{row.tasksCompletedOnTime} completed by due date</small></td>
            <td className={row.overdueTasks > 0 ? "table-due" : ""}><strong>{row.overdueTasks} overdue</strong><small>{row.openTasks} open</small></td>
            <td><strong>{row.callsLogged} calls · {row.emailsLogged} emails</strong><small>{row.visitsLogged} visits/meetings · {row.otherOutreach} other</small></td>
            <td><strong>{formatAdminDate(row.lastActivityAt)}</strong><small>{row.changesMade} changes</small></td>
          </tr>)}</tbody>
        </table>
        {!rows.length && <div className="admin-empty"><BarChart3 /><strong>No users found</strong><p>Add a workspace user to begin tracking KPIs.</p></div>}
      </div>}
    </div>
  );
}

type SourceUserActivityReport = {
  sourceId: string;
  sourceName: string;
  sourceCategory: string;
  userId: string;
  userName: string;
  userActive: boolean | number;
  totalContacts: number;
  calls: number;
  emails: number;
  visits: number;
  meetings: number;
  events: number;
  marketing: number;
  other: number;
  referralsReceived: number;
  lastActivityAt: string;
};

type SourceActivityUserOption = { id: string; name: string; active: boolean | number };

function SourceUserActivityReportsPanel({ navigation, onOpenSource }: { navigation: ReactNode; onOpenSource: (sourceId: string) => void }) {
  const [range, setRange] = useState("30");
  const [selectedUserId, setSelectedUserId] = useState("all");
  const [search, setSearch] = useState("");
  const [collectionView, setCollectionView] = useCollectionView("source-user-activity-report-layout");
  const [rows, setRows] = useState<SourceUserActivityReport[] | null>(null);
  const [users, setUsers] = useState<SourceActivityUserOption[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setRows(null);
    setError("");
    const query = new URLSearchParams({ range });
    if (selectedUserId !== "all") query.set("userId", selectedUserId);
    fetch(`/api/admin/reports/source-activity?${query}`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("The source-by-user report could not be loaded.");
        return response.json() as Promise<{ data: SourceUserActivityReport[]; users: SourceActivityUserOption[] }>;
      })
      .then((result) => {
        setRows(result.data.map((row) => ({
          ...row,
          totalContacts: Number(row.totalContacts), calls: Number(row.calls), emails: Number(row.emails),
          visits: Number(row.visits), meetings: Number(row.meetings), events: Number(row.events),
          marketing: Number(row.marketing), other: Number(row.other),
          referralsReceived: Number(row.referralsReceived),
        })));
        setUsers(result.users);
      })
      .catch((caught: Error) => { if (caught.name !== "AbortError") setError(caught.message); });
    return () => controller.abort();
  }, [range, selectedUserId]);

  const normalizedSearch = search.trim().toLowerCase();
  const filteredRows = (rows ?? []).filter((row) => !normalizedSearch || [row.sourceName, row.sourceCategory, row.userName].some((value) => value.toLowerCase().includes(normalizedSearch)));
  type SourceUserTableSort = "source" | "user" | "total" | "referrals" | "calls" | "emails" | "visits" | "meetings" | "events" | "marketing" | "other" | "last-activity";
  const { sortedRows: visibleRows, sortKey: sourceUserSortKey, sortDirection: sourceUserSortDirection, changeSort: changeSourceUserSort } = useSortableTable<SourceUserActivityReport, SourceUserTableSort>(filteredRows, "total", (row, key) => ({
    source: row.sourceName,
    user: row.userName,
    total: row.totalContacts,
    referrals: row.referralsReceived,
    calls: row.calls,
    emails: row.emails,
    visits: row.visits,
    meetings: row.meetings,
    events: row.events,
    marketing: row.marketing,
    other: row.other,
    "last-activity": row.lastActivityAt ? new Date(row.lastActivityAt).getTime() : null,
  })[key], "descending");
  const totalContacts = visibleRows.reduce((total, row) => total + row.totalContacts, 0);
  const totalVisits = visibleRows.reduce((total, row) => total + row.visits, 0);
  const totalReferrals = visibleRows.reduce((total, row) => total + row.referralsReceived, 0);
  const uniqueSources = new Set(visibleRows.map((row) => row.sourceId)).size;
  const selectedUser = users.find((user) => user.id === selectedUserId);
  const periodLabel = range === "all" ? "All time" : `Last ${range} days`;

  function downloadSourceUserReport() {
    if (!rows) return;
    const csvCell = (value: string | number) => {
      const text = String(value);
      const safe = /^[=+\-@]/.test(text) ? `'${text}` : text;
      return `"${safe.replaceAll('"', '""')}"`;
    };
    const csv = [
      ["Referral source", "Referral category", "User", "Total contacts", "Referrals received", "Calls/texts", "Emails", "Visits", "Meetings", "Events", "Marketing", "Other", "Last activity"],
      ...visibleRows.map((row) => [row.sourceName, row.sourceCategory, row.userName, row.totalContacts, row.referralsReceived, row.calls, row.emails, row.visits, row.meetings, row.events, row.marketing, row.other, row.lastActivityAt]),
    ].map((line) => line.map(csvCell).join(",")).join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    const userSuffix = selectedUser ? `-${selectedUser.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}` : "-all-users";
    link.download = `referral-source-activity-by-user${userSuffix}-${range === "all" ? "all-time" : `${range}-days`}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return <div className="page admin-page admin-subpage reports-page source-user-report-page">
    {navigation}
    <div className="page-heading source-user-report-heading">
      <div><p className="eyebrow">Reports</p><h1>Referral source activity by user</h1><p>See exactly how often each person called, emailed, visited, met with, or attended an event at every referral source.</p></div>
      <div className="admin-view-actions source-user-report-actions">
        <label className="report-range user-report-person">Person<select value={selectedUserId} onChange={(event) => setSelectedUserId(event.target.value)}><option value="all">All users</option>{users.map((user) => <option value={user.id} key={user.id}>{user.name}{Boolean(user.active) ? "" : " (inactive)"}</option>)}</select></label>
        <label className="report-range">Period<select value={range} onChange={(event) => setRange(event.target.value)}><option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option><option value="365">Last year</option><option value="all">All time</option></select></label>
        <button type="button" className="button ghost" disabled={!rows} onClick={downloadSourceUserReport}><Download />Export CSV</button>
        <ViewToggle value={collectionView} onChange={setCollectionView} label="Source by user report view" />
      </div>
    </div>
    {error && <AdminError message={error} />}
    {!rows && !error && <AdminLoading />}
    {rows && <>
      <div className="source-user-report-scope"><span>{selectedUser?.name ?? "All users"}</span><small>{periodLabel}</small></div>
      <section className="compact-stats source-user-report-summary" aria-label="Source activity by user summary">
        <article><span><Activity /></span><div><strong>{totalContacts}</strong><small>Total contacts</small></div></article>
        <article><span><Building2 /></span><div><strong>{uniqueSources}</strong><small>Sources reached</small></div></article>
        <article><span><MapPin /></span><div><strong>{totalVisits}</strong><small>Visits</small></div></article>
        <article><span><ArrowRight /></span><div><strong>{totalReferrals}</strong><small>Referrals received</small></div></article>
      </section>
      <div className="source-user-report-toolbar"><label className="search-field"><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search source, category, or user" aria-label="Search source activity by user" /></label><span>{visibleRows.length} source–user {visibleRows.length === 1 ? "record" : "records"}</span></div>
      {collectionView === "cards" ? <section className="source-user-report-grid" aria-label="Source activity by user cards">
        {visibleRows.map((row) => {
          const metrics = [
            { label: "Calls/texts", value: row.calls, icon: Phone }, { label: "Emails", value: row.emails, icon: Mail },
            { label: "Visits", value: row.visits, icon: MapPin }, { label: "Meetings", value: row.meetings, icon: Users },
            { label: "Events", value: row.events, icon: CalendarDays }, { label: "Marketing", value: row.marketing, icon: Building2 },
            { label: "Other", value: row.other, icon: Activity },
            { label: "Referrals", value: row.referralsReceived, icon: ArrowRight },
          ];
          return <button type="button" className="source-user-report-card" onClick={() => onOpenSource(row.sourceId)} key={`${row.sourceId}:${row.userId}`}>
            <header><span className="source-user-report-icon"><Building2 /></span><div><small>{row.sourceCategory}</small><h2>{row.sourceName}</h2></div><ChevronRight /></header>
            <div className="source-user-report-person"><span className="admin-avatar">{row.userName.split(" ").map((part) => part[0]).join("").slice(0, 2)}</span><span><small>Contacted by</small><strong>{row.userName}</strong></span><b>{row.totalContacts}<small>total</small></b></div>
            <div className="source-user-activity-mix">{metrics.filter((metric) => metric.value > 0).map((metric) => <span key={metric.label}><metric.icon /><strong>{metric.value}</strong><small>{metric.label}</small></span>)}</div>
            <footer>Last activity <strong>{formatAdminDate(row.lastActivityAt)}</strong></footer>
          </button>;
        })}
        {!visibleRows.length && <div className="empty-state source-user-report-empty"><BarChart3 /><strong>No matching activity</strong><p>Try another person, period, or search.</p></div>}
      </section> : <div className="data-table-shell source-user-report-table-shell">
        <table className="data-table source-user-report-table"><thead><tr><SortableTableHeader column="source" label="Referral source" sortKey={sourceUserSortKey} sortDirection={sourceUserSortDirection} onSort={changeSourceUserSort} /><SortableTableHeader column="user" label="User" sortKey={sourceUserSortKey} sortDirection={sourceUserSortDirection} onSort={changeSourceUserSort} /><SortableTableHeader column="total" label="Total" sortKey={sourceUserSortKey} sortDirection={sourceUserSortDirection} onSort={changeSourceUserSort} /><SortableTableHeader column="referrals" label="Referrals" sortKey={sourceUserSortKey} sortDirection={sourceUserSortDirection} onSort={changeSourceUserSort} /><SortableTableHeader column="calls" label="Calls/texts" sortKey={sourceUserSortKey} sortDirection={sourceUserSortDirection} onSort={changeSourceUserSort} /><SortableTableHeader column="emails" label="Emails" sortKey={sourceUserSortKey} sortDirection={sourceUserSortDirection} onSort={changeSourceUserSort} /><SortableTableHeader column="visits" label="Visits" sortKey={sourceUserSortKey} sortDirection={sourceUserSortDirection} onSort={changeSourceUserSort} /><SortableTableHeader column="meetings" label="Meetings" sortKey={sourceUserSortKey} sortDirection={sourceUserSortDirection} onSort={changeSourceUserSort} /><SortableTableHeader column="events" label="Events" sortKey={sourceUserSortKey} sortDirection={sourceUserSortDirection} onSort={changeSourceUserSort} /><SortableTableHeader column="marketing" label="Marketing" sortKey={sourceUserSortKey} sortDirection={sourceUserSortDirection} onSort={changeSourceUserSort} /><SortableTableHeader column="other" label="Other" sortKey={sourceUserSortKey} sortDirection={sourceUserSortDirection} onSort={changeSourceUserSort} /><SortableTableHeader column="last-activity" label="Last activity" sortKey={sourceUserSortKey} sortDirection={sourceUserSortDirection} onSort={changeSourceUserSort} /></tr></thead>
          <tbody>{visibleRows.map((row) => <tr className="clickable" tabIndex={0} onClick={() => onOpenSource(row.sourceId)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") onOpenSource(row.sourceId); }} key={`${row.sourceId}:${row.userId}`}><td><strong>{row.sourceName}</strong><small>{row.sourceCategory}</small></td><td><strong>{row.userName}</strong><small>{Boolean(row.userActive) ? "Active user" : "Inactive user"}</small></td><td><strong className="table-kpi-value">{row.totalContacts}</strong></td><td><strong className="table-referral-value">{row.referralsReceived}</strong></td><td>{row.calls}</td><td>{row.emails}</td><td>{row.visits}</td><td>{row.meetings}</td><td>{row.events}</td><td>{row.marketing}</td><td>{row.other}</td><td>{formatAdminDate(row.lastActivityAt)}</td></tr>)}</tbody>
        </table>
        {!visibleRows.length && <div className="admin-empty"><BarChart3 /><strong>No matching activity</strong><p>Try another person, period, or search.</p></div>}
      </div>}
    </>}
  </div>;
}

type ReferralAttributionRecord = {
  id: string;
  recordKind: "individual" | "count";
  quantity: number;
  receivedOn: string;
  createdAt: string;
  sourceId: string;
  sourceName: string;
  sourceCategory: string;
  contactId: string | null;
  contactName: string | null;
  contactTitle: string | null;
  creditedUserId: string;
  creditedUserName: string;
  recordedByUserId: string;
  recordedByUserName: string;
  creditMethod: "selected" | "recorder";
};

type ReferralAttributionLeader = { id: string; label: string; detail: string; total: number } | null;

function referralCountLabel(value: number) {
  return `${value} referral${value === 1 ? "" : "s"}`;
}

function ReferralAttributionReportsPanel({ navigation, onOpenSource, onOpenContact }: { navigation: ReactNode; onOpenSource: (sourceId: string) => void; onOpenContact: (contactId: string) => void }) {
  const [range, setRange] = useState("30");
  const [sourceId, setSourceId] = useState("all");
  const [contactId, setContactId] = useState("all");
  const [creditedUserId, setCreditedUserId] = useState("all");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<ReferralAttributionRecord[] | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setRows(null);
    setError("");
    fetch(`/api/admin/reports/referral-attribution?range=${range}`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("The referral attribution report could not be loaded.");
        return response.json() as Promise<{ data: ReferralAttributionRecord[] }>;
      })
      .then((result) => setRows(result.data.map((row) => ({ ...row, quantity: Number(row.quantity) }))))
      .catch((caught: Error) => { if (caught.name !== "AbortError") setError(caught.message); });
    return () => controller.abort();
  }, [range]);

  const sourceOptions = useMemo(() => Array.from(new Map((rows ?? []).map((row) => [row.sourceId, { id: row.sourceId, name: row.sourceName }])).values()).sort((left, right) => left.name.localeCompare(right.name)), [rows]);
  const contactOptions = useMemo(() => Array.from(new Map((rows ?? []).filter((row) => row.contactId && (sourceId === "all" || row.sourceId === sourceId)).map((row) => [row.contactId as string, { id: row.contactId as string, name: row.contactName ?? "Unknown contact", sourceName: row.sourceName }])).values()).sort((left, right) => left.name.localeCompare(right.name) || left.sourceName.localeCompare(right.sourceName)), [rows, sourceId]);
  const userOptions = useMemo(() => Array.from(new Map((rows ?? []).map((row) => [row.creditedUserId, { id: row.creditedUserId, name: row.creditedUserName }])).values()).sort((left, right) => left.name.localeCompare(right.name)), [rows]);
  const normalizedSearch = search.trim().toLowerCase();
  const filteredRows = (rows ?? []).filter((row) => {
    if (sourceId !== "all" && row.sourceId !== sourceId) return false;
    if (contactId !== "all" && row.contactId !== contactId) return false;
    if (creditedUserId !== "all" && row.creditedUserId !== creditedUserId) return false;
    return !normalizedSearch || [row.sourceName, row.sourceCategory, row.contactName ?? "", row.contactTitle ?? "", row.creditedUserName, row.recordedByUserName].some((value) => value.toLowerCase().includes(normalizedSearch));
  });
  type ReferralAttributionTableSort = "date" | "referrals" | "source" | "contact" | "credit" | "recorded-by";
  const { sortedRows: visibleRows, sortKey: referralSortKey, sortDirection: referralSortDirection, changeSort: changeReferralSort } = useSortableTable<ReferralAttributionRecord, ReferralAttributionTableSort>(filteredRows, "date", (row, key) => ({
    date: row.receivedOn ? new Date(`${row.receivedOn}T12:00:00`).getTime() : null,
    referrals: row.quantity,
    source: row.sourceName,
    contact: row.contactName,
    credit: row.creditedUserName,
    "recorded-by": row.recordedByUserName,
  })[key], "descending");

  function leaderFor(getValue: (row: ReferralAttributionRecord) => { id: string; label: string; detail: string } | null): ReferralAttributionLeader {
    const totals = new Map<string, ReferralAttributionLeader & {}>();
    for (const row of visibleRows) {
      const value = getValue(row);
      if (!value) continue;
      const current = totals.get(value.id);
      totals.set(value.id, { ...value, total: (current?.total ?? 0) + row.quantity });
    }
    return [...totals.values()].sort((left, right) => right.total - left.total || left.label.localeCompare(right.label))[0] ?? null;
  }

  const totalReferrals = visibleRows.reduce((total, row) => total + row.quantity, 0);
  const uniqueSources = new Set(visibleRows.map((row) => row.sourceId)).size;
  const uniqueContacts = new Set(visibleRows.flatMap((row) => row.contactId ? [row.contactId] : [])).size;
  const uniqueCreditUsers = new Set(visibleRows.map((row) => row.creditedUserId)).size;
  const topSource = leaderFor((row) => ({ id: row.sourceId, label: row.sourceName, detail: row.sourceCategory }));
  const topContact = leaderFor((row) => row.contactId ? { id: row.contactId, label: row.contactName ?? "Unknown contact", detail: row.sourceName } : null);
  const topCredit = leaderFor((row) => ({ id: row.creditedUserId, label: row.creditedUserName, detail: "Referral credit" }));
  const periodLabel = range === "all" ? "All time" : `Last ${range} days`;

  function downloadReferralAttribution() {
    if (!rows) return;
    const csvCell = (value: string | number | null) => {
      const text = value === null ? "" : String(value);
      const safe = /^[=+\-@]/.test(text) ? `'${text}` : text;
      return `"${safe.replaceAll('"', '""')}"`;
    };
    const csv = [
      ["Date received", "Referral count", "Referral source", "Source category", "Source contact", "Contact title", "Credited to", "Credit basis", "Recorded by"],
      ...visibleRows.map((row) => [row.receivedOn, row.quantity, row.sourceName, row.sourceCategory, row.contactName, row.contactTitle, row.creditedUserName, row.creditMethod === "selected" ? "Selected when logged" : "Recorder of referral count", row.recordedByUserName]),
    ].map((line) => line.map(csvCell).join(",")).join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `referral-attribution-${range === "all" ? "all-time" : `${range}-days`}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return <div className="page admin-page admin-subpage reports-page referral-attribution-page">
    {navigation}
    <div className="page-heading referral-attribution-heading">
      <div><p className="eyebrow">Reports</p><h1>Referral attribution</h1><p>See which source and individual contact produced each referral, and who received credit.</p></div>
      <div className="admin-view-actions referral-attribution-actions">
        <label className="report-range">Period<select value={range} onChange={(event) => setRange(event.target.value)}><option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option><option value="365">Last year</option><option value="all">All time</option></select></label>
        <button type="button" className="button ghost" disabled={!rows} onClick={downloadReferralAttribution}><Download />Export CSV</button>
      </div>
    </div>
    {error && <AdminError message={error} />}
    {!rows && !error && <AdminLoading />}
    {rows && <>
      <div className="source-user-report-scope"><span>Attribution report</span><small>{periodLabel} · {visibleRows.length} {visibleRows.length === 1 ? "record" : "records"}</small></div>
      <section className="compact-stats referral-attribution-summary" aria-label="Referral attribution summary">
        <article><span><ArrowRight /></span><div><strong>{totalReferrals}</strong><small>Total referrals</small></div></article>
        <article><span><Building2 /></span><div><strong>{uniqueSources}</strong><small>Contributing sources</small></div></article>
        <article><span><Contact /></span><div><strong>{uniqueContacts}</strong><small>Source contacts</small></div></article>
        <article><span><Users /></span><div><strong>{uniqueCreditUsers}</strong><small>People credited</small></div></article>
      </section>
      <section className="referral-attribution-leaders" aria-label="Referral attribution leaders">
        <article><span><Building2 /></span><div><small>Top referral source</small><strong>{topSource?.label ?? "No referrals yet"}</strong><p>{topSource ? `${referralCountLabel(topSource.total)} · ${topSource.detail}` : "No source attribution in this period."}</p></div>{topSource && <button type="button" onClick={() => onOpenSource(topSource.id)}>Open source<ChevronRight /></button>}</article>
        <article><span><Contact /></span><div><small>Top source contact</small><strong>{topContact?.label ?? "Not attributed"}</strong><p>{topContact ? `${referralCountLabel(topContact.total)} · ${topContact.detail}` : "No contact attribution in this period."}</p></div>{topContact && <button type="button" onClick={() => onOpenContact(topContact.id)}>Open contact<ChevronRight /></button>}</article>
        <article><span><Users /></span><div><small>Most referral credit</small><strong>{topCredit?.label ?? "No credit yet"}</strong><p>{topCredit ? `${referralCountLabel(topCredit.total)} credited` : "No credit attribution in this period."}</p></div></article>
      </section>
      <section className="simple-card referral-attribution-filters" aria-label="Filter referral attribution report">
        <label>Referral source<select value={sourceId} onChange={(event) => { setSourceId(event.target.value); setContactId("all"); }}><option value="all">All sources</option>{sourceOptions.map((option) => <option value={option.id} key={option.id}>{option.name}</option>)}</select></label>
        <label>Source contact<select value={contactId} onChange={(event) => setContactId(event.target.value)}><option value="all">All contacts</option>{contactOptions.map((option) => <option value={option.id} key={option.id}>{option.name} · {option.sourceName}</option>)}</select></label>
        <label>Credited to<select value={creditedUserId} onChange={(event) => setCreditedUserId(event.target.value)}><option value="all">Everyone</option>{userOptions.map((option) => <option value={option.id} key={option.id}>{option.name}</option>)}</select></label>
        <label className="referral-attribution-search">Search<span><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Source, contact, or teammate" /></span></label>
      </section>
      <section className="simple-card referral-attribution-detail">
        <header><div><p className="eyebrow">Attribution ledger</p><h2>Referral detail</h2></div><span>{referralCountLabel(totalReferrals)} across {visibleRows.length} {visibleRows.length === 1 ? "entry" : "entries"}</span></header>
        <div className="data-table-shell referral-attribution-table-shell">
          <table className="data-table referral-attribution-table"><thead><tr><SortableTableHeader column="date" label="Date" sortKey={referralSortKey} sortDirection={referralSortDirection} onSort={changeReferralSort} /><SortableTableHeader column="referrals" label="Referrals" sortKey={referralSortKey} sortDirection={referralSortDirection} onSort={changeReferralSort} /><SortableTableHeader column="source" label="Referral source" sortKey={referralSortKey} sortDirection={referralSortDirection} onSort={changeReferralSort} /><SortableTableHeader column="contact" label="Source contact" sortKey={referralSortKey} sortDirection={referralSortDirection} onSort={changeReferralSort} /><SortableTableHeader column="credit" label="Credit" sortKey={referralSortKey} sortDirection={referralSortDirection} onSort={changeReferralSort} /><SortableTableHeader column="recorded-by" label="Recorded by" sortKey={referralSortKey} sortDirection={referralSortDirection} onSort={changeReferralSort} /></tr></thead>
            <tbody>{visibleRows.map((row) => <tr key={`${row.recordKind}:${row.id}`}><td><strong>{formatSourceDate(`${row.receivedOn}T12:00:00`)}</strong><small>{row.recordKind === "individual" ? "Individual referral" : "Referral count"}</small></td><td><strong className="table-referral-value">{row.quantity}</strong></td><td><button type="button" onClick={() => onOpenSource(row.sourceId)}><strong>{row.sourceName}</strong><small>{row.sourceCategory}</small></button></td><td>{row.contactId ? <button type="button" onClick={() => onOpenContact(row.contactId!)}><strong>{row.contactName}</strong><small>{row.contactTitle || "Contact at source"}</small></button> : <span className="unattributed-cell">Not specified</span>}</td><td><span className="referral-credit-person"><i>{row.creditedUserName.split(" ").map((part) => part[0]).join("").slice(0, 2)}</i><span><strong>{row.creditedUserName}</strong><small>{row.creditMethod === "selected" ? "Selected when logged" : "Recorder of count"}</small></span></span></td><td><strong>{row.recordedByUserName}</strong><small>{formatAdminDate(row.createdAt)}</small></td></tr>)}</tbody>
          </table>
          {!visibleRows.length && <div className="admin-empty"><ArrowUpDown /><strong>No matching referrals</strong><p>Try another period or clear one of the attribution filters.</p></div>}
        </div>
        <footer><ShieldCheck /><span>This report shows counts and business attribution only; referral details are not included.</span></footer>
      </section>
    </>}
  </div>;
}

type WorkspaceSettings = {
  id: string;
  name: string;
  timezone: string;
  digestHourLocal: number;
  defaultSourceContactDays: number;
  digestsEnabled: boolean | number;
  updatedAt: string;
  brandName: string;
  brandLogoUrl: string | null;
  brandPrimaryColor: string;
  brandAccentColor: string;
  verticalCode: VerticalCode;
  sourceLabelSingular: string;
  sourceLabelPlural: string;
  leadLabelSingular: string;
  leadLabelPlural: string;
  leadPipelineEnabled: boolean;
  leadContactDetailsEnabled: boolean;
  leadActivityTrackingEnabled: boolean;
  individualLeadTrackingEnabled: boolean;
};

type BillingSummary = {
  planCode: PlanCode;
  status: "beta" | "trialing" | "active" | "past_due" | "canceled";
  billingProvider: "helcim";
  monthlyAmountCents: number;
  currency: string;
  seatLimit: number;
  activeSeats: number;
  beta: boolean;
  paymentCollectionEnabled: boolean;
};

function BillingPanel({ onBack }: { onBack: () => void }) {
  const [billing, setBilling] = useState<BillingSummary | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    fetch("/api/admin/billing").then(async (response) => {
      if (!response.ok) throw new Error("Plan details could not be loaded.");
      return response.json() as Promise<{ data: BillingSummary }>;
    }).then((result) => setBilling(result.data)).catch((caught: Error) => setError(caught.message));
  }, []);
  const plan = billing ? planForCode(billing.planCode) : null;
  return <AdminPanelShell title="Plan & billing" description="Review organization capacity and beta billing status." onBack={onBack}>
    {error && <div className="settings-message error"><CircleAlert />{error}</div>}
    {!billing && !error && <div className="settings-loading"><span className="loading-line" /><p>Loading plan details…</p></div>}
    {billing && plan && <div className="billing-panel-grid">
      <section className="billing-plan-card"><header><span><CreditCard /></span><div><p className="eyebrow">Current plan</p><h2>{plan.name}</h2></div><em>{billing.beta ? "Free beta" : billing.status}</em></header><strong>{plan.seatLabel}</strong><p>{plan.description}</p><div className="seat-meter"><span><i style={{ width: `${Math.min(100, billing.activeSeats / billing.seatLimit * 100)}%` }} /></span><small>{billing.activeSeats} of {billing.seatLimit} active seats</small></div></section>
      <section className="billing-beta-card"><Sparkles /><div><p className="eyebrow">Beta billing</p><h2>No monthly fee. No card on file.</h2><p>All product capabilities are included during beta. When paid plans are introduced, an organization administrator will review pricing and authorize payment before any charge occurs.</p></div></section>
      <section className="billing-provider-card"><header><ShieldCheck /><div><h2>Payment processing is Helcim-ready</h2><p>Future card verification and recurring billing will be handled by Helcim. Referral Tracking will not store raw payment card details.</p></div></header><dl><div><dt>Provider</dt><dd>Helcim</dd></div><div><dt>Collection</dt><dd>Disabled during beta</dd></div><div><dt>Current charge</dt><dd>$0.00 USD / month</dd></div></dl></section>
    </div>}
  </AdminPanelShell>;
}

type PlatformTenant = {
  id: string; name: string; slug: string; status: "active" | "suspended" | "closed"; planCode: PlanCode;
  seatLimit: number; subscriptionStatus: string; activeUsers: number; userCount: number; sourceCount: number;
  lastActivityAt?: string; createdAt: string;
};
type PlatformUser = { id: string; name: string; email: string; workspaceId: string; workspaceName: string; role: WorkspaceRole; active: number; platformRole: string; emailVerified: number };

function PlatformDashboard() {
  const [tenants, setTenants] = useState<PlatformTenant[]>([]);
  const [overview, setOverview] = useState<Record<string, Record<string, number>>>({});
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");

  async function load() {
    try {
      const [overviewResponse, tenantResponse, userResponse] = await Promise.all([
        fetch("/api/platform/overview"), fetch("/api/platform/tenants"), fetch("/api/platform/users"),
      ]);
      if (!overviewResponse.ok || !tenantResponse.ok || !userResponse.ok) throw new Error("Platform data could not be loaded.");
      const [overviewResult, tenantResult, userResult] = await Promise.all([overviewResponse.json(), tenantResponse.json(), userResponse.json()]) as [{ data: Record<string, Record<string, number>> }, { data: PlatformTenant[] }, { data: PlatformUser[] }];
      setOverview(overviewResult.data); setTenants(tenantResult.data); setUsers(userResult.data);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Platform data could not be loaded."); }
  }
  useEffect(() => { void load(); }, []);

  async function updateTenant(tenant: PlatformTenant, changes: { planCode?: PlanCode; status?: PlatformTenant["status"] }) {
    setBusyId(tenant.id); setError("");
    const response = await fetch(`/api/platform/tenants/${tenant.id}`, { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(changes) });
    const result = await response.json().catch(() => ({})) as { error?: string };
    setBusyId("");
    if (!response.ok) return setError(result.error ?? "The organization could not be updated.");
    await load();
  }
  async function toggleUser(user: PlatformUser) {
    setBusyId(user.id); setError("");
    const response = await fetch(`/api/platform/users/${user.id}`, { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify({ workspaceId: user.workspaceId, active: !Boolean(user.active) }) });
    const result = await response.json().catch(() => ({})) as { error?: string };
    setBusyId("");
    if (!response.ok) return setError(result.error ?? "The user could not be updated.");
    await load();
  }
  async function impersonate(user: PlatformUser) {
    if (!window.confirm(`View ${user.name} in ${user.workspaceName}? You can return to Platform Operations from the banner at the top.`)) return;
    setBusyId(user.id); setError("");
    const response = await fetch(`/api/platform/users/${user.id}/impersonate`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ workspaceId: user.workspaceId }),
    });
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      setBusyId("");
      return setError(result.error ?? "This user could not be viewed.");
    }
    window.location.assign("/?view=sources");
  }
  const visibleTenants = tenants.filter((tenant) => `${tenant.name} ${tenant.slug}`.toLowerCase().includes(query.toLowerCase()));
  const selectedUsers = users.filter((user) => user.workspaceId === selectedTenantId);
  return <div className="page platform-page">
    <div className="page-heading"><div><p className="eyebrow">Platform operations</p><h1>Organizations</h1><p>Manage every tenant, plan, user, and account status from one place.</p></div><span className="platform-owner-badge"><ShieldCheck />Superuser</span></div>
    {error && <div className="settings-message error"><CircleAlert />{error}</div>}
    <section className="platform-overview"><article><span><Globe2 /></span><div><small>ORGANIZATIONS</small><strong>{overview.organizations?.total ?? tenants.length}</strong><p>{overview.organizations?.active ?? 0} active</p></div></article><article><span><Users /></span><div><small>USERS</small><strong>{overview.users?.total ?? users.length}</strong><p>{overview.users?.active ?? 0} available</p></div></article><article><span><Building2 /></span><div><small>REFERRAL SOURCES</small><strong>{overview.sources?.total ?? 0}</strong><p>Across all tenants</p></div></article><article><span><Activity /></span><div><small>30-DAY ACTIVITY</small><strong>{overview.recentActivities?.total ?? 0}</strong><p>Recorded interactions</p></div></article></section>
    <div className="platform-toolbar"><label><Search /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search organizations" /></label><span>{visibleTenants.length} tenant{visibleTenants.length === 1 ? "" : "s"}</span></div>
    <div className="platform-tenant-list">{visibleTenants.map((tenant) => <article className={selectedTenantId === tenant.id ? "open" : ""} key={tenant.id}>
      <button className="platform-tenant-summary" onClick={() => setSelectedTenantId(selectedTenantId === tenant.id ? null : tenant.id)}><span className="platform-tenant-mark">{tenant.name.split(" ").map((part) => part[0]).join("").slice(0, 2)}</span><span><strong>{tenant.name}</strong><small>{tenant.slug} · joined {new Date(tenant.createdAt).toLocaleDateString()}</small></span><span><b>{tenant.activeUsers}/{tenant.seatLimit}</b><small>active seats</small></span><span><b>{tenant.sourceCount}</b><small>sources</small></span><em className={tenant.status}>{tenant.status}</em><ChevronRight /></button>
      {selectedTenantId === tenant.id && <div className="platform-tenant-detail"><div className="platform-controls"><label>Capacity plan<select disabled={busyId === tenant.id} value={tenant.planCode} onChange={(event) => void updateTenant(tenant, { planCode: event.target.value as PlanCode })}>{Object.values(PLAN_CATALOG).map((plan) => <option value={plan.code} key={plan.code}>{plan.name} · {plan.seatLabel}</option>)}</select></label><label>Organization status<select disabled={busyId === tenant.id} value={tenant.status} onChange={(event) => void updateTenant(tenant, { status: event.target.value as PlatformTenant["status"] })}><option value="active">Active</option><option value="suspended">Suspended</option><option value="closed">Closed</option></select></label><div><small>Billing status</small><strong>{tenant.subscriptionStatus === "beta" ? "Free beta · $0/month" : tenant.subscriptionStatus}</strong></div></div>
        <section className="platform-users"><header><div><h3>Tenant users</h3><p>View the workspace as a user or control their organization access.</p></div><span>{selectedUsers.filter((user) => Boolean(user.active)).length} active</span></header>{selectedUsers.map((user) => <article key={user.id}><UserAvatar name={user.name} /><span><strong>{user.name}</strong><small>{user.email}</small></span><span><b>{workspaceRoleLabel(user.role)}</b><small>{Boolean(user.emailVerified) ? "Email verified" : "Activation pending"}</small></span>{user.platformRole === "superadmin" && <em>Superuser</em>}<div className="platform-user-actions"><button type="button" className="platform-impersonate" aria-label={`View as ${user.name}`} disabled={busyId === user.id || !Boolean(user.active)} onClick={() => void impersonate(user)}><LogIn />View as</button><button type="button" className={Boolean(user.active) ? "danger" : ""} aria-label={`${Boolean(user.active) ? "Suspend" : "Reactivate"} ${user.name}`} disabled={busyId === user.id} onClick={() => void toggleUser(user)}>{busyId === user.id ? "Saving…" : Boolean(user.active) ? "Suspend" : "Reactivate"}</button></div></article>)}</section>
      </div>}
    </article>)}</div>
  </div>;
}

function WorkspaceSettingsPanel({ onBack, onSaved }: { onBack: () => void; onSaved: (settings: WorkspaceSettings) => void }) {
  const [settings, setSettings] = useState<WorkspaceSettings | null>(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);

  useEffect(() => {
    fetch("/api/admin/settings")
      .then(async (response) => {
        if (!response.ok) throw new Error("Workspace settings could not be loaded.");
        return response.json() as Promise<{ data: WorkspaceSettings }>;
      })
      .then((result) => setSettings({
        ...result.data,
        verticalCode: verticalForCode(result.data.verticalCode).code,
        digestsEnabled: Boolean(result.data.digestsEnabled),
        leadPipelineEnabled: Boolean(result.data.leadPipelineEnabled),
        leadContactDetailsEnabled: Boolean(result.data.leadContactDetailsEnabled),
        leadActivityTrackingEnabled: Boolean(result.data.leadActivityTrackingEnabled),
        individualLeadTrackingEnabled: individualLeadTrackingEnabled(result.data),
        brandName: result.data.brandName || result.data.name,
        brandLogoUrl: result.data.brandLogoUrl ?? null,
        brandPrimaryColor: result.data.brandPrimaryColor || "#4D058F",
        brandAccentColor: result.data.brandAccentColor || "#00A99C",
      }))
      .catch((caught: Error) => setError(caught.message));
  }, []);

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!settings) return;
    setBusy(true);
    setSaved(false);
    setError("");
    try {
      const response = await fetch("/api/admin/settings", {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name: settings.name,
          timezone: settings.timezone,
          digestHourLocal: Number(settings.digestHourLocal),
          defaultSourceContactDays: Number(settings.defaultSourceContactDays),
          digestsEnabled: Boolean(settings.digestsEnabled),
          leadPipelineEnabled: Boolean(settings.leadPipelineEnabled),
          leadContactDetailsEnabled: Boolean(settings.leadContactDetailsEnabled),
          leadActivityTrackingEnabled: Boolean(settings.leadActivityTrackingEnabled),
          brandName: settings.brandName,
          brandPrimaryColor: settings.brandPrimaryColor,
          brandAccentColor: settings.brandAccentColor,
          verticalCode: settings.verticalCode,
        }),
      });
      if (!response.ok) {
        const result = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(result.error ?? "Workspace settings could not be saved.");
      }
      const result = (await response.json()) as { data: WorkspaceSettings };
      setSettings({
        ...result.data,
        digestsEnabled: Boolean(result.data.digestsEnabled),
        leadPipelineEnabled: Boolean(result.data.leadPipelineEnabled),
        leadContactDetailsEnabled: Boolean(result.data.leadContactDetailsEnabled),
        leadActivityTrackingEnabled: Boolean(result.data.leadActivityTrackingEnabled),
        individualLeadTrackingEnabled: individualLeadTrackingEnabled(result.data),
      });
      onSaved(result.data);
      setSaved(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Workspace settings could not be saved.");
    } finally {
      setBusy(false);
    }
  }

  async function uploadLogo(file: File | undefined) {
    if (!file || !settings) return;
    if (file.size > 5 * 1024 * 1024) return setError("Workspace logos must be 5 MB or smaller.");
    setLogoBusy(true); setError(""); setSaved(false);
    const body = new FormData(); body.set("file", file);
    try {
      const response = await fetch("/api/admin/branding/logo", { method: "POST", body });
      const result = await response.json().catch(() => ({})) as { data?: { brandLogoUrl: string }; error?: string };
      if (!response.ok || !result.data) throw new Error(result.error ?? "The workspace logo could not be uploaded.");
      const next = { ...settings, brandLogoUrl: result.data.brandLogoUrl };
      setSettings(next); onSaved(next); setSaved(true);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "The workspace logo could not be uploaded."); }
    finally { setLogoBusy(false); }
  }

  async function removeLogo() {
    if (!settings) return;
    setLogoBusy(true); setError(""); setSaved(false);
    try {
      const response = await fetch("/api/admin/branding/logo", { method: "DELETE" });
      const result = await response.json().catch(() => ({})) as { error?: string };
      if (!response.ok) throw new Error(result.error ?? "The workspace logo could not be removed.");
      const next = { ...settings, brandLogoUrl: null };
      setSettings(next); onSaved(next); setSaved(true);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "The workspace logo could not be removed."); }
    finally { setLogoBusy(false); }
  }

  const selectedVertical = verticalForCode(settings?.verticalCode);
  return (
    <div className="page admin-page settings-page">
      <button className="admin-back" onClick={onBack}><ArrowLeft size={16} />All admin settings</button>
      <div className="page-heading">
        <div><p className="eyebrow">Administration</p><h1>Workspace settings</h1><p>Control your organization’s branding, follow-up timing, timezone, and reminders.</p></div>
      </div>
      {error && <div className="settings-message error"><CircleAlert />{error}</div>}
      {!settings && !error && <div className="settings-loading"><span className="loading-line" /><p>Loading workspace settings…</p></div>}
      {settings && (
        <form className="workspace-settings-form" onSubmit={save}>
          <section className="settings-section vertical-settings">
            <header><span className="settings-section-icon"><Globe2 /></span><div><h2>Industry profile</h2><p>Choose the language and starter source categories that fit this organization.</p></div><span className="vertical-current">{selectedVertical.name}</span></header>
            <div className="vertical-profile-grid" role="radiogroup" aria-label="Available industry profiles">
              {VERTICAL_CATALOG.map((profile) => (
                <label className={settings.verticalCode === profile.code ? "selected" : ""} key={profile.code}>
                  <input type="radio" name="verticalCode" value={profile.code} checked={settings.verticalCode === profile.code} onChange={() => setSettings({ ...settings, verticalCode: profile.code })} />
                  <span className="vertical-check"><Check /></span>
                  <strong>{profile.name}</strong>
                  <small>{profile.description}</small>
                  <em>{profile.sourceLabelPlural} · {profile.leadLabelPlural}</em>
                </label>
              ))}
            </div>
            <div className="vertical-category-preview">
              <div><strong>{selectedVertical.name} source template</strong><small>Saving adds any missing defaults. Existing categories and records are never removed or renamed.</small></div>
              <div>{selectedVertical.sourceCategories.map((category) => <span key={category.code}><i style={{ background: category.color }} />{category.label}</span>)}</div>
              <p><SlidersHorizontal />Fine-tune these anytime under <b>Source categories</b> in Admin.</p>
            </div>
          </section>
          <section className="settings-section">
            <header><span className="settings-section-icon"><Building2 /></span><div><h2>Identity & branding</h2><p>Make the signed-in workspace feel like your organization.</p></div></header>
            <div className="branding-settings-layout">
              <div className="branding-preview" style={{ "--preview-primary": settings.brandPrimaryColor, "--preview-accent": settings.brandAccentColor, "--preview-foreground": readableForeground(settings.brandPrimaryColor) } as CSSProperties}>
                <p>LIVE WORKSPACE PREVIEW</p>
                <div><TenantMark name={settings.brandName || settings.name} logoUrl={settings.brandLogoUrl} /><span><i /><i /></span></div>
                <button type="button">Primary action</button>
              </div>
              <div className="settings-fields branding-fields">
                <label>Workspace name<input required minLength={2} maxLength={120} value={settings.name} onChange={(event) => setSettings({ ...settings, name: event.target.value })} /><small>Administrative organization name used in platform records.</small></label>
                <label>Brand display name<input required minLength={2} maxLength={80} value={settings.brandName} onChange={(event) => setSettings({ ...settings, brandName: event.target.value })} /><small>Shown to everyone in this tenant’s sidebar and application header.</small></label>
                <div className="branding-logo-control"><span><strong>Organization logo</strong><small>JPEG, PNG, or WebP · up to 5 MB. Transparent artwork works best.</small></span><div><label className="button ghost branding-upload-button"><Upload />{logoBusy ? "Working…" : settings.brandLogoUrl ? "Replace logo" : "Upload logo"}<input type="file" accept="image/jpeg,image/png,image/webp" disabled={logoBusy} onChange={(event) => { void uploadLogo(event.target.files?.[0]); event.target.value = ""; }} /></label>{settings.brandLogoUrl && <button className="button ghost branding-remove-button" type="button" disabled={logoBusy} onClick={() => void removeLogo()}><Trash2 />Remove</button>}</div></div>
                <div className="branding-color-grid">
                  <label>Primary color<span className="color-field"><input aria-label="Primary brand color picker" type="color" value={settings.brandPrimaryColor} onChange={(event) => setSettings({ ...settings, brandPrimaryColor: event.target.value.toUpperCase() })} /><input aria-label="Primary brand color" required pattern="#[0-9A-Fa-f]{6}" value={settings.brandPrimaryColor} onChange={(event) => setSettings({ ...settings, brandPrimaryColor: event.target.value })} /></span><small>Navigation, buttons, and key actions.</small></label>
                  <label>Accent color<span className="color-field"><input aria-label="Accent brand color picker" type="color" value={settings.brandAccentColor} onChange={(event) => setSettings({ ...settings, brandAccentColor: event.target.value.toUpperCase() })} /><input aria-label="Accent brand color" required pattern="#[0-9A-Fa-f]{6}" value={settings.brandAccentColor} onChange={(event) => setSettings({ ...settings, brandAccentColor: event.target.value })} /></span><small>Highlights, progress, and active details.</small></label>
                </div>
              </div>
            </div>
          </section>
          <section className="settings-section">
            <header><span className="settings-section-icon"><Phone /></span><div><h2>Referral source follow-up</h2><p>The standard interval used unless a source has its own setting.</p></div></header>
            <div className="settings-fields">
              <label className="settings-days-field">
                Default days between contacts
                <span><input required type="number" min="1" max="3650" value={settings.defaultSourceContactDays} onChange={(event) => setSettings({ ...settings, defaultSourceContactDays: Number(event.target.value) })} /><b>days</b></span>
                <small>Sources using the workspace default update automatically when this number changes.</small>
              </label>
            </div>
          </section>
          <section className="settings-section lead-feature-settings">
            <header>
              <span className="settings-section-icon"><ClipboardList /></span>
              <div><h2>Individual lead tracking</h2><p>Optionally follow named leads in addition to aggregate referral-source performance.</p></div>
              <span className={`feature-state ${individualLeadTrackingEnabled(settings) ? "enabled" : "disabled"}`}>
                {individualLeadTrackingEnabled(settings) ? "Feature enabled" : "Feature off"}
              </span>
            </header>
            <div className="lead-feature-intro"><ShieldCheck /><p><strong>The master feature follows these items automatically.</strong><span>Turning on any item adds Individual leads to the workspace. Turning all items off safely hides it without deleting records.</span></p></div>
            <div className="lead-feature-grid">
              <label className={settings.leadPipelineEnabled ? "selected" : ""}>
                <input type="checkbox" checked={settings.leadPipelineEnabled} onChange={(event) => setSettings({ ...settings, leadPipelineEnabled: event.target.checked })} />
                <span><i><LayoutGrid /></i><strong>Pipeline & profile</strong><small>Status, source, owner, payer, channel, and acuity.</small></span>
              </label>
              <label className={settings.leadContactDetailsEnabled ? "selected" : ""}>
                <input type="checkbox" checked={settings.leadContactDetailsEnabled} onChange={(event) => setSettings({ ...settings, leadContactDetailsEnabled: event.target.checked })} />
                <span><i><Users /></i><strong>Contact details</strong><small>Primary contact, phone, email, and relationship.</small></span>
              </label>
              <label className={settings.leadActivityTrackingEnabled ? "selected" : ""}>
                <input type="checkbox" checked={settings.leadActivityTrackingEnabled} onChange={(event) => setSettings({ ...settings, leadActivityTrackingEnabled: event.target.checked })} />
                <span><i><Activity /></i><strong>Activity history</strong><small>Calls, emails, notes, and a chronological lead timeline.</small></span>
              </label>
            </div>
          </section>
          <section className="settings-section">
            <header><span className="settings-section-icon"><CalendarDays /></span><div><h2>Time and reminders</h2><p>Dates and weekday digest delivery use this timezone.</p></div></header>
            <div className="settings-fields two-column">
              <label>
                Timezone
                <select value={settings.timezone} onChange={(event) => setSettings({ ...settings, timezone: event.target.value })}>
                  <option value="America/Chicago">Central · America/Chicago</option>
                  <option value="America/New_York">Eastern · America/New_York</option>
                  <option value="America/Denver">Mountain · America/Denver</option>
                  <option value="America/Los_Angeles">Pacific · America/Los_Angeles</option>
                </select>
              </label>
              <label>
                Digest delivery time
                <select value={settings.digestHourLocal} onChange={(event) => setSettings({ ...settings, digestHourLocal: Number(event.target.value) })}>
                  {Array.from({ length: 24 }, (_, hour) => (
                    <option value={hour} key={hour}>{new Date(2026, 0, 1, hour).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</option>
                  ))}
                </select>
              </label>
            </div>
            <label className="digest-toggle">
              <input type="checkbox" checked={Boolean(settings.digestsEnabled)} onChange={(event) => setSettings({ ...settings, digestsEnabled: event.target.checked })} />
              <span><i /><b>Send weekday follow-up digests</b><small>Each opted-in user receives the referral sources due that day, their contact details, and a link to Today.</small></span>
            </label>
          </section>
          <footer className="settings-actions">
            {saved && <span className="saved-indicator"><CheckCircle2 />Saved</span>}
            <button className="button primary" disabled={busy}>{busy ? "Saving…" : "Save settings"}</button>
          </footer>
        </form>
      )}
    </div>
  );
}

function AdminPage({ onWorkspaceSaved, currentUserId, onCurrentAvatarChange, onOpenSource, onOpenContact, onReviewDuplicates }: { onWorkspaceSaved: (settings: WorkspaceSettings) => void; currentUserId: string; onCurrentAvatarChange: (avatarUrl: string | null) => void; onOpenSource: (id: string) => void; onOpenContact: (id: string) => void; onReviewDuplicates: () => void }) {
  const [section, setSection] = useState<AdminSectionKey | null>(null);
  const adminSections: Array<{
    id: AdminSectionKey;
    icon: typeof Users;
    title: string;
    text: string;
    meta: string;
  }> = [
    { id: "people", icon: Users, title: "People & access", text: "Invite staff, assign roles, suspend access, and safely delete users.", meta: "Manage workspace access" },
    { id: "lookups", icon: SlidersHorizontal, title: "Source categories", text: "Manage source categories and outreach activity labels.", meta: "Categories and labels" },
    { id: "imports", icon: FileDown, title: "Imports & exports", text: "Review source imports and download source records.", meta: "Download clean CSV files" },
    { id: "quality", icon: ShieldCheck, title: "Data quality", text: "Resolve incomplete, stale, and duplicate relationship records.", meta: "Prioritized cleanup inbox" },
    { id: "archives", icon: Archive, title: "Archived sources", text: "Find and restore relationships removed from active work.", meta: "Restore archived records" },
    { id: "audit", icon: Activity, title: "Audit history", text: "Review changes made across source records.", meta: "Recent workspace changes" },
    { id: "billing", icon: CreditCard, title: "Plan & billing", text: "Review team capacity, beta status, and payment configuration.", meta: "Free during beta" },
    { id: "workspace", icon: Settings, title: "Workspace settings", text: "Brand name, logo, colors, timezone, and organization defaults.", meta: "Customize tenant experience" },
  ];
  if (section === "people") return <PeopleAccessPanel onBack={() => setSection(null)} currentUserId={currentUserId} onCurrentAvatarChange={onCurrentAvatarChange} />;
  if (section === "lookups") return <LookupOptionsPanel onBack={() => setSection(null)} />;
  if (section === "imports") return <ImportsExportsPanel onBack={() => setSection(null)} />;
  if (section === "quality") return <DataQualityPanel onBack={() => setSection(null)} onOpenSource={onOpenSource} onOpenContact={onOpenContact} onReviewDuplicates={onReviewDuplicates} />;
  if (section === "archives") return <ArchivesPanel onBack={() => setSection(null)} />;
  if (section === "audit") return <AuditHistoryPanel onBack={() => setSection(null)} />;
  if (section === "billing") return <BillingPanel onBack={() => setSection(null)} />;
  if (section === "workspace") return <WorkspaceSettingsPanel onBack={() => setSection(null)} onSaved={onWorkspaceSaved} />;
  return (
    <div className="page admin-page">
      <div className="page-heading"><div><p className="eyebrow">Administration</p><h1>Admin tools</h1><p>Manage access, source categories, imports, and history.</p></div></div>
      <div className="admin-grid">
        {adminSections.map(({ id, icon: Icon, title, text, meta }) => (
          <button className="admin-card" key={title} onClick={() => setSection(id)}>
            <span className="admin-icon"><Icon /></span>
            <span><h3>{title}</h3><p>{text}</p><small>{meta}</small></span>
            <ChevronRight />
          </button>
        ))}
      </div>
    </div>
  );
}

type ContactNoteInput = {
  noteText: string;
  pinned: boolean;
  mentionedUserIds: string[];
  followUp?: { assigneeUserId: string; dueAt: string };
};

function ContactCollaboration({
  sourceId,
  contact,
  teamMembers,
  currentUserId,
  onAddNote,
  onUpdateNote,
  onAddReceipt,
}: {
  sourceId: string;
  contact: SourceContact;
  teamMembers: SourceAssignee[];
  currentUserId: string;
  onAddNote: (contactId: string, input: ContactNoteInput) => Promise<boolean>;
  onUpdateNote: (contactId: string, note: ContactNote, noteText: string, pinned: boolean) => Promise<boolean>;
  onAddReceipt: (contactId: string, quantity: number, receivedOn: string) => Promise<boolean>;
}) {
  const [mode, setMode] = useState<"closed" | "note" | "receipt">("closed");
  const [noteText, setNoteText] = useState("");
  const [pinned, setPinned] = useState(false);
  const [mentionedUserId, setMentionedUserId] = useState("");
  const [createFollowUp, setCreateFollowUp] = useState(false);
  const [followUpDate, setFollowUpDate] = useState(() => new Date(Date.now() + 86_400_000).toISOString().slice(0, 10));
  const [quantity, setQuantity] = useState("1");
  const [receivedOn, setReceivedOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [editPinned, setEditPinned] = useState(false);
  const [historyNoteId, setHistoryNoteId] = useState<string | null>(null);
  const notes = contact.notes ?? [];
  const receipts = contact.referralReceipts ?? [];
  const recentStart = new Date();
  recentStart.setDate(recentStart.getDate() - 30);
  const recentTotal = receipts
    .filter((receipt) => new Date(`${receipt.receivedOn}T23:59:59`).getTime() >= recentStart.getTime())
    .reduce((sum, receipt) => sum + receipt.quantity, 0);
  const allTimeTotal = receipts.reduce((sum, receipt) => sum + receipt.quantity, 0);
  const mentionOptions = teamMembers.filter((teammate) => teammate.id !== currentUserId);

  async function submitNote(event: FormEvent) {
    event.preventDefault();
    if (!noteText.trim()) return;
    setBusy(true);
    const followUp = createFollowUp && mentionedUserId
      ? { assigneeUserId: mentionedUserId, dueAt: new Date(`${followUpDate}T09:00:00`).toISOString() }
      : undefined;
    const saved = await onAddNote(contact.id, {
      noteText: noteText.trim(),
      pinned,
      mentionedUserIds: mentionedUserId ? [mentionedUserId] : [],
      followUp,
    });
    setBusy(false);
    if (saved) {
      setNoteText(""); setPinned(false); setMentionedUserId(""); setCreateFollowUp(false); setMode("closed");
    }
  }

  async function submitReceipt(event: FormEvent) {
    event.preventDefault();
    const count = Number(quantity);
    if (!Number.isInteger(count) || count < 1) return;
    setBusy(true);
    const saved = await onAddReceipt(contact.id, count, receivedOn);
    setBusy(false);
    if (saved) { setQuantity("1"); setMode("closed"); }
  }

  async function submitEdit(event: FormEvent, note: ContactNote) {
    event.preventDefault();
    setBusy(true);
    const saved = await onUpdateNote(contact.id, note, editText.trim(), editPinned);
    setBusy(false);
    if (saved) setEditingNoteId(null);
  }

  return (
    <div className="contact-collaboration" data-source-id={sourceId}>
      <div className="referral-count-summary">
        <span><small>Referrals received</small><strong>{recentTotal}</strong><em>last 30 days</em></span>
        <span><small>Recorded total</small><strong>{allTimeTotal}</strong><em>{receipts.length} count entr{receipts.length === 1 ? "y" : "ies"}</em></span>
      </div>
      <div className="contact-collaboration-actions">
        <button type="button" onClick={() => setMode(mode === "receipt" ? "closed" : "receipt")}><Plus />Add referral count</button>
        <button type="button" onClick={() => setMode(mode === "note" ? "closed" : "note")}><MessageSquareText />Contact update / @mention</button>
      </div>
      {mode === "receipt" && <form className="referral-receipt-form" onSubmit={submitReceipt}>
        <div className="privacy-banner"><CircleAlert /><span><strong>Aggregate counts only</strong><small>Do not enter names, diagnoses, or any patient information.</small></span></div>
        <div><label>How many received?<input type="number" required min="1" max="9999" inputMode="numeric" value={quantity} onChange={(event) => setQuantity(event.target.value)} /></label><label>Date received<input type="date" required value={receivedOn} onChange={(event) => setReceivedOn(event.target.value)} /></label></div>
        <footer><button type="button" className="button ghost" onClick={() => setMode("closed")}>Cancel</button><button className="button primary" disabled={busy}>{busy ? "Saving…" : "Save count"}</button></footer>
      </form>}
      {mode === "note" && <form className="contact-note-form" onSubmit={submitNote}>
        <label>Contact note<textarea required maxLength={4000} value={noteText} onChange={(event) => setNoteText(event.target.value)} placeholder={`Standing preferences or relationship context for ${contact.name}…`} /></label>
        <small className="privacy-copy">Keep this non-clinical. Never include patient-specific information.</small>
        <div className="contact-note-options"><label className="check-option"><input type="checkbox" checked={pinned} onChange={(event) => setPinned(event.target.checked)} /><StickyNote />Pin as important</label><label>Mention teammate<select value={mentionedUserId} onChange={(event) => { setMentionedUserId(event.target.value); if (!event.target.value) setCreateFollowUp(false); }}><option value="">No mention</option>{mentionOptions.map((teammate) => <option key={teammate.id} value={teammate.id}>@{teammate.name}</option>)}</select></label></div>
        {mentionedUserId && <div className="mention-follow-up"><label className="check-option"><input type="checkbox" checked={createFollowUp} onChange={(event) => setCreateFollowUp(event.target.checked)} /><CalendarPlus />Also create a follow-up</label>{createFollowUp && <label>Due date<input type="date" min={new Date().toISOString().slice(0, 10)} value={followUpDate} onChange={(event) => setFollowUpDate(event.target.value)} /></label>}</div>}
        <footer><button type="button" className="button ghost" onClick={() => setMode("closed")}>Cancel</button><button className="button primary" disabled={busy}>{busy ? "Saving…" : "Save note"}</button></footer>
      </form>}
      {(notes.length > 0 || receipts.length > 0) && <div className="contact-records">
        {notes.map((note) => <article key={note.id} className={`contact-note-record${note.pinned ? " pinned" : ""}`}>
          {editingNoteId === note.id ? <form onSubmit={(event) => void submitEdit(event, note)}><textarea required maxLength={4000} value={editText} onChange={(event) => setEditText(event.target.value)} /><label className="check-option"><input type="checkbox" checked={editPinned} onChange={(event) => setEditPinned(event.target.checked)} />Pinned</label><footer><button type="button" onClick={() => setEditingNoteId(null)}>Cancel</button><button disabled={busy}>Save changes</button></footer></form> : <><header>{note.pinned && <span><StickyNote />Pinned</span>}<button type="button" onClick={() => { setEditingNoteId(note.id); setEditText(note.noteText); setEditPinned(note.pinned); }}><PencilLine />Edit</button></header><p>{note.noteText}</p><small>{note.updatedBy ?? note.createdBy ?? "Team member"} · {formatSourceDate(note.updatedAt)}</small>{note.history.length > 1 && <button type="button" className="note-history-toggle" onClick={() => setHistoryNoteId(historyNoteId === note.id ? null : note.id)}>{historyNoteId === note.id ? "Hide history" : `History (${note.history.length})`}</button>}{historyNoteId === note.id && <div className="contact-note-history">{note.history.slice(1).map((entry) => <div key={entry.id}><p>{entry.noteText}</p><small>{entry.changedBy ?? "Team member"} · {formatSourceDate(entry.createdAt)}</small></div>)}</div>}</>}
        </article>)}
        {receipts.slice(0, 5).map((receipt) => <article key={receipt.id} className="referral-receipt-record"><span>{receipt.quantity}</span><div><strong>{receipt.quantity} referral{receipt.quantity === 1 ? "" : "s"} received</strong><small>{formatSourceDate(receipt.receivedOn)} · recorded by {receipt.recordedBy ?? "Team member"}</small></div></article>)}
      </div>}
    </div>
  );
}

function attachmentSizeLabel(sizeBytes: number): string {
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  if (sizeBytes < 1024 * 1024) return `${Math.round(sizeBytes / 1024)} KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(sizeBytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
}

function SourceAttachments({
  source,
  events,
  canManage,
  onUpload,
  onUpdate,
  onDelete,
}: {
  source: DemoSource;
  events: WorkspaceEvent[];
  canManage: boolean;
  onUpload: (file: File, eventId: string, caption: string) => Promise<boolean>;
  onUpdate: (attachment: SourceAttachment, eventId: string, caption: string) => Promise<boolean>;
  onDelete: (attachment: SourceAttachment) => Promise<boolean>;
}) {
  const attachments = source.attachments ?? [];
  const [showUpload, setShowUpload] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [eventId, setEventId] = useState("");
  const [caption, setCaption] = useState("");
  const [editingId, setEditingId] = useState("");
  const [editEventId, setEditEventId] = useState("");
  const [editCaption, setEditCaption] = useState("");
  const [busy, setBusy] = useState(false);

  async function submitUpload(event: FormEvent) {
    event.preventDefault();
    if (!file) return;
    setBusy(true);
    const saved = await onUpload(file, eventId, caption);
    setBusy(false);
    if (!saved) return;
    setFile(null);
    setEventId("");
    setCaption("");
    setShowUpload(false);
  }

  function startEditing(attachment: SourceAttachment) {
    setEditingId(attachment.id);
    setEditEventId(attachment.eventId ?? "");
    setEditCaption(attachment.caption ?? "");
  }

  async function submitEdit(event: FormEvent, attachment: SourceAttachment) {
    event.preventDefault();
    setBusy(true);
    const saved = await onUpdate(attachment, editEventId, editCaption);
    setBusy(false);
    if (saved) setEditingId("");
  }

  async function remove(attachment: SourceAttachment) {
    if (!window.confirm(`Delete ${attachment.fileName}? This permanently removes the stored file.`)) return;
    setBusy(true);
    await onDelete(attachment);
    setBusy(false);
  }

  return (
    <section className="source-detail-section source-attachments" aria-labelledby={`source-attachments-${source.id}`}>
      <div className="section-title">
        <div><h3 id={`source-attachments-${source.id}`}>Photos & documents{attachments.length ? ` · ${attachments.length}` : ""}</h3><p>Private files stored with this referral source.</p></div>
        <div className="attachment-heading-actions"><ContextHelp topic="events-files" label="File help" />{canManage && <button type="button" className="button ghost attachment-add-button" onClick={() => setShowUpload((current) => !current)}><Upload />{showUpload ? "Cancel" : "Add attachment"}</button>}</div>
      </div>
      {showUpload && <form className="attachment-upload-panel" onSubmit={submitUpload}>
        <label className={`attachment-drop-field${file ? " selected" : ""}`}>
          <input required type="file" accept=".jpg,.jpeg,.png,.webp,.gif,.heic,.heif,.pdf,.txt,.csv,.rtf,.doc,.docx,.xls,.xlsx,.ppt,.pptx" onChange={(event) => setFile(event.target.files?.[0] ?? null)} />
          <span><Paperclip /></span>
          <strong>{file?.name ?? "Choose a photo or document"}</strong>
          <small>{file ? attachmentSizeLabel(file.size) : "JPG, PNG, PDF, Word, Excel and more · up to 25 MB"}</small>
        </label>
        <div className="attachment-upload-fields">
          <label>Assign to event <small>(optional)</small><select value={eventId} onChange={(event) => setEventId(event.target.value)}><option value="">General source attachment</option>{events.map((item) => <option value={item.id} key={item.id}>{item.title} · {eventDateLabel(item.occurredAt)}</option>)}</select></label>
          <label>Caption <small>(optional)</small><input maxLength={500} value={caption} onChange={(event) => setCaption(event.target.value)} placeholder="What this file shows or why it matters" /></label>
        </div>
        <p className="attachment-privacy-note"><CircleAlert />Do not upload patient clinical records or other information that belongs in the clinical system.</p>
        <footer><button type="button" className="button ghost" onClick={() => setShowUpload(false)}>Cancel</button><button className="button primary" disabled={busy || !file}>{busy ? "Uploading…" : "Upload attachment"}<Upload /></button></footer>
      </form>}
      <div className="attachment-gallery">
        {attachments.map((attachment) => {
          const isImage = attachment.contentType.startsWith("image/");
          const contentUrl = `/api/attachments/${attachment.id}/content`;
          return <article className={`attachment-card${isImage ? " image" : " document"}`} key={attachment.id}>
            <a className="attachment-preview" href={contentUrl} target="_blank" rel="noreferrer" aria-label={`Open ${attachment.fileName}`}>
              {isImage ? <img src={contentUrl} alt={attachment.caption || attachment.fileName} loading="lazy" /> : <span><FileText /><small>{attachment.fileName.split(".").at(-1)?.toUpperCase() || "FILE"}</small></span>}
            </a>
            <div className="attachment-card-body">
              <header><div><strong title={attachment.fileName}>{attachment.fileName}</strong><small>{attachmentSizeLabel(attachment.sizeBytes)} · {attachment.createdBy} · {formatSourceDate(attachment.createdAt)}</small></div>{attachment.eventTitle && <span className="attachment-event"><CalendarDays />{attachment.eventTitle}</span>}</header>
              {attachment.caption && <p>{attachment.caption}</p>}
              {editingId === attachment.id ? <form className="attachment-edit-form" onSubmit={(event) => void submitEdit(event, attachment)}>
                <label>Event<select value={editEventId} onChange={(event) => setEditEventId(event.target.value)}><option value="">General source attachment</option>{events.map((item) => <option value={item.id} key={item.id}>{item.title}</option>)}</select></label>
                <label>Caption<input maxLength={500} value={editCaption} onChange={(event) => setEditCaption(event.target.value)} /></label>
                <footer><button type="button" onClick={() => setEditingId("")}>Cancel</button><button disabled={busy}>Save</button></footer>
              </form> : <footer className="attachment-actions">
                <a href={contentUrl} target="_blank" rel="noreferrer"><ExternalLink />Open</a>
                <a href={`${contentUrl}?download=1`}><Download />Download</a>
                {canManage && <button type="button" onClick={() => startEditing(attachment)}><PencilLine />Assign / edit</button>}
                {canManage && <button type="button" className="danger" disabled={busy} onClick={() => void remove(attachment)}><Trash2 />Delete</button>}
              </footer>}
            </div>
          </article>;
        })}
        {!attachments.length && <div className="attachment-empty"><Paperclip /><span><strong>No photos or documents yet</strong><small>{canManage ? "Add files and optionally connect them to an event at this source." : "Files added to this source will appear here."}</small></span></div>}
      </div>
    </section>
  );
}

type ReferralPeriod = "day" | "week" | "month" | "year";
type ReferralPeriodSummary = {
  period: ReferralPeriod;
  current: number;
  previous: number;
  change: number;
  changePercent: number | null;
};

function SourceReferralPerformance({ source }: { source: DemoSource }) {
  const [period, setPeriod] = useState<ReferralPeriod>("month");
  const [summary, setSummary] = useState<ReferralPeriodSummary | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    fetch(`/api/sources/${source.id}/referral-summary?period=${period}`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("Referral summary request failed");
        const result = await response.json() as { data: ReferralPeriodSummary };
        setSummary(result.data);
      })
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) setSummary(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [period, source.id, source.referralTotal]);
  const previousLabel = period === "day" ? "Previous day" : period === "week" ? "Previous week" : period === "month" ? "Previous month" : "Previous year";
  return <section className="source-detail-section referral-performance" aria-label={`Referral performance for ${source.name}`}>
    <div className="referral-performance-heading"><div><span><BarChart3 /></span><div><small>Aggregate performance</small><h3>Referral performance</h3></div></div><strong>{source.referralTotal}<small>all time</small></strong></div>
    <p>Includes referrals logged directly with this source and every contact associated with it.</p>
    <div className="referral-period-switcher" role="group" aria-label="Referral comparison period">{(["day", "week", "month", "year"] as const).map((option) => <button type="button" aria-pressed={period === option} className={period === option ? "active" : ""} onClick={() => setPeriod(option)} key={option}>{option[0].toUpperCase() + option.slice(1)}</button>)}</div>
    {loading ? <div className="referral-performance-loading"><span className="loading-line" />Updating totals…</div> : summary ? <div className="referral-performance-grid">
      <div><small>This {period}</small><strong>{summary.current}</strong></div>
      <div><small>{previousLabel}</small><strong>{summary.previous}</strong></div>
      <div className={summary.change > 0 ? "positive" : summary.change < 0 ? "negative" : "neutral"}><small>Change</small><strong>{summary.change > 0 ? "+" : ""}{summary.change}</strong><span>{summary.changePercent === null ? (summary.current ? "New activity" : "No change") : `${summary.changePercent > 0 ? "+" : ""}${summary.changePercent}%`}</span></div>
    </div> : <div className="referral-performance-error">Referral comparison is temporarily unavailable.</div>}
  </section>;
}

function ExistingContactAffiliationModal({ source, contacts, onClose, onSave }: {
  source: DemoSource;
  contacts: DirectoryContact[];
  onClose: () => void;
  onSave: (contact: DirectoryContact, mode: "transfer" | "link") => Promise<boolean>;
}) {
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [mode, setMode] = useState<"transfer" | "link">("transfer");
  const [busy, setBusy] = useState(false);
  const existingIds = new Set(source.contacts?.map((contact) => contact.id) ?? []);
  const available = contacts.filter((contact) => !existingIds.has(contact.id)
    && `${contact.name} ${contact.title ?? ""} ${contact.organizations.map((organization) => organization.sourceName).join(" ")}`.toLowerCase().includes(search.trim().toLowerCase()));
  const selected = contacts.find((contact) => contact.id === selectedId);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    setBusy(true);
    const saved = await onSave(selected, mode);
    setBusy(false);
    if (saved) onClose();
  }
  return <div className="modal-layer contact-affiliation-layer" role="dialog" aria-modal="true" aria-label={`Add existing contact to ${source.name}`}>
    <button type="button" className="modal-scrim" onClick={onClose} aria-label="Close contact selection" />
    <form className="modal contact-affiliation-modal" onSubmit={submit}>
      <header><div><p className="eyebrow">Existing address-book contact</p><h2>Add someone to {source.name}</h2><p>Keep one contact profile and choose how their employer relationship should change.</p></div><button type="button" className="icon-button" onClick={onClose}><X /></button></header>
      <div className="contact-affiliation-body">
        <label className="search-field"><Search /><input autoFocus value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search people or current employers" aria-label="Search existing contacts" /></label>
        <div className="contact-affiliation-results" aria-label="Existing contacts">{available.length ? available.map((contact) => <button type="button" className={selectedId === contact.id ? "selected" : ""} onClick={() => setSelectedId(contact.id)} key={contact.id}><ContactAvatar name={contact.name} photoId={contact.primaryPhotoId} className="record-avatar" /><span><strong>{contact.name}</strong><small>{contact.title || "Contact"} · {contact.organizations.map((organization) => organization.sourceName).join(" + ")}</small></span><Check /></button>) : <div className="empty-state compact"><Contact /><strong>No available contacts match</strong><p>Try another name or employer.</p></div>}</div>
        {selected && <fieldset className="contact-affiliation-choice"><legend>How should {selected.name} be added?</legend><label className={mode === "transfer" ? "selected" : ""}><input type="radio" name="affiliation-mode" value="transfer" checked={mode === "transfer"} onChange={() => setMode("transfer")} /><span><i><ArrowRight /></i><strong>Transfer to {source.name}</strong><small>Remove current employer links and make this the contact’s current referral source. Their complete history stays on the same profile.</small></span></label><label className={mode === "link" ? "selected" : ""}><input type="radio" name="affiliation-mode" value="link" checked={mode === "link"} onChange={() => setMode("link")} /><span><i><Building2 /></i><strong>Link to both</strong><small>Keep {selected.organizations.map((organization) => organization.sourceName).join(" and ")} and also show this contact under {source.name}.</small></span></label></fieldset>}
      </div>
      <footer><button type="button" className="button ghost" onClick={onClose}>Cancel</button><button type="submit" className="button primary" disabled={!selected || busy}>{busy ? "Saving…" : mode === "transfer" ? "Transfer contact" : "Link contact"}<ArrowRight /></button></footer>
    </form>
  </div>;
}

function SourceDetailPage({
  source,
  onBack,
  onLog,
  onSchedule,
  onEdit,
  onSaveDescription,
  teamMembers,
  currentUserId,
  onAddContactNote,
  onUpdateContactNote,
  onAddReferralReceipt,
  canManage,
  canLog,
  canCollaborate,
  events,
  onUploadAttachment,
  onUpdateAttachment,
  onDeleteAttachment,
  directoryContacts,
  onAssociateContact,
}: {
  source: DemoSource;
  onBack: () => void;
  onLog: () => void;
  onSchedule: () => void;
  onEdit: () => void;
  onSaveDescription: (description: string, color: DemoSource["descriptionColor"]) => Promise<boolean>;
  teamMembers: SourceAssignee[];
  currentUserId: string;
  onAddContactNote: (contactId: string, input: ContactNoteInput) => Promise<boolean>;
  onUpdateContactNote: (contactId: string, note: ContactNote, noteText: string, pinned: boolean) => Promise<boolean>;
  onAddReferralReceipt: (contactId: string, quantity: number, receivedOn: string) => Promise<boolean>;
  canManage: boolean;
  canLog: boolean;
  canCollaborate: boolean;
  events: WorkspaceEvent[];
  onUploadAttachment: (file: File, eventId: string, caption: string) => Promise<boolean>;
  onUpdateAttachment: (attachment: SourceAttachment, eventId: string, caption: string) => Promise<boolean>;
  onDeleteAttachment: (attachment: SourceAttachment) => Promise<boolean>;
  directoryContacts: DirectoryContact[];
  onAssociateContact: (contact: DirectoryContact, mode: "transfer" | "link") => Promise<boolean>;
}) {
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState(source.description ?? "");
  const [descriptionColor, setDescriptionColor] = useState<NonNullable<DemoSource["descriptionColor"]>>(source.descriptionColor ?? "gold");
  const [showNoteHistory, setShowNoteHistory] = useState(false);
  const [descriptionBusy, setDescriptionBusy] = useState(false);
  const [addingExistingContact, setAddingExistingContact] = useState(false);
  const contactRecords = source.contacts
    ? source.contacts
    : source.contact !== "No contact added"
      ? [{
          id: `${source.id}_summary`,
          name: source.contact,
          phone: source.contactPhone,
          email: source.contactEmail,
          isPrimary: true,
          preferredContactMethod: "any" as const,
          contactStatus: "active" as const,
        }]
      : [];

  useEffect(() => {
    if (!editingDescription) setDescriptionDraft(source.description ?? "");
  }, [editingDescription, source.description, source.id]);

  async function saveDescription(event: FormEvent) {
    event.preventDefault();
    setDescriptionBusy(true);
    const saved = await onSaveDescription(descriptionDraft.trim(), descriptionColor);
    setDescriptionBusy(false);
    if (saved) setEditingDescription(false);
  }

  return (
    <div className="page simple-page source-detail-page" aria-label={`Referral source ${source.name}`}>
        <header className="source-detail-page-toolbar">
          <button className="source-detail-back" onClick={onBack}><ArrowLeft />Back to previous screen</button>
          <div className="drawer-actions"><ContextHelp topic="source-record" label="Source help" />{canManage && <button className="button ghost" onClick={onSchedule}><CalendarPlus size={15} />Follow-up</button>}{canManage && <button className="button ghost" onClick={onEdit}><PencilLine size={15} />Edit source</button>}{canLog && <button className="button primary" onClick={onLog}><Phone size={15} />Log contact</button>}</div>
        </header>
        <section className="source-record-hero">
          <span className={`source-record-icon${source.isHospital ? " hospital" : ""}`}>{source.isHospital ? <Hospital /> : <Building2 />}</span>
          <div><p className="eyebrow">{source.category} · Referral source</p><h1>{source.name}</h1><p>Relationship owned by {source.ownerName}{source.isHospital && <span className="hospital-badge"><Hospital />Hospital</span>}{!canManage && <span className="shared-source-label">Shared record</span>}</p></div>
        </section>
        <section className="source-detail-page-metrics" aria-label="Referral source summary">
          <article><span><Users /></span><div><strong>{contactRecords.length}</strong><small>Contacts</small></div></article>
          <article><span><Activity /></span><div><strong>{source.activityCount}</strong><small>Interactions</small></div></article>
          <article><span><ArrowRight /></span><div><strong>{source.referralTotal}</strong><small>Referrals</small></div></article>
          <article><span><Paperclip /></span><div><strong>{source.attachments?.length ?? 0}</strong><small>Files</small></div></article>
        </section>
        <nav className="source-detail-page-nav" aria-label="Referral source details sections"><a href="#source-overview">Overview</a><a href="#source-contacts">Contacts</a><a href="#source-files">Files & photos</a><a href="#source-activity">Activity</a></nav>
        <div className="source-detail-page-content">
        <section className="relationship-status" id="source-overview">
          <div><small>Last contacted</small><strong>{source.lastInteractionAt}</strong></div>
          <ArrowRight />
          <div className={source.nextTaskAt === "Today" ? "due" : ""}><small>Contact next</small><strong>{source.nextTaskAt}</strong></div>
        </section>
        <SourceReferralPerformance source={source} />
        <section className={`source-sticky-note note-${source.descriptionColor ?? "gold"}${source.description ? " has-note" : ""}`} aria-labelledby={`pinned-note-${source.id}`}>
          <header>
            <span className="sticky-note-title"><i><StickyNote /></i><span><small>At-a-glance information</small><h3 id={`pinned-note-${source.id}`}>Pinned source note</h3></span></span>
            {canCollaborate && !editingDescription && <button type="button" className="sticky-note-edit" onClick={() => setEditingDescription(true)}><PencilLine />{source.description ? "Edit" : "Add note"}</button>}
          </header>
          {editingDescription ? (
            <form onSubmit={saveDescription}>
              <label htmlFor={`pinned-note-input-${source.id}`} className="sr-only">Pinned source note</label>
              <textarea id={`pinned-note-input-${source.id}`} autoFocus maxLength={8000} value={descriptionDraft} onChange={(event) => setDescriptionDraft(event.target.value)} placeholder="Add standing context, preferences, access details, or other useful information…" />
              <div className="note-color-picker" role="group" aria-label="Note importance"><span>Note color</span>{(["gold", "purple", "teal", "red"] as const).map((color) => <button type="button" key={color} className={`${color}${descriptionColor === color ? " selected" : ""}`} aria-label={`${color} note`} aria-pressed={descriptionColor === color} onClick={() => setDescriptionColor(color)} />)}</div>
              <small>Keep patient-specific details in the appropriate clinical system.</small>
              <div><button type="button" className="button ghost" onClick={() => { setDescriptionDraft(source.description ?? ""); setEditingDescription(false); }}>Cancel</button><button className="button primary" disabled={descriptionBusy}>{descriptionBusy ? "Saving…" : "Save note"}<Check /></button></div>
            </form>
          ) : source.description ? (
            <><p>{source.description}</p><footer className="sticky-note-meta"><span>{source.descriptionUpdatedBy ? `Updated by ${source.descriptionUpdatedBy}` : "Pinned source information"}{source.descriptionUpdatedAt ? ` · ${formatSourceDate(source.descriptionUpdatedAt)}` : ""}</span>{Boolean(source.noteHistory?.length) && <button type="button" onClick={() => setShowNoteHistory((current) => !current)}>{showNoteHistory ? "Hide history" : `History (${source.noteHistory?.length})`}</button>}</footer>{showNoteHistory && <div className="note-history">{source.noteHistory?.map((entry) => <article key={entry.id}><i className={entry.color} /><div><p>{entry.noteText || "Note cleared"}</p><small>{entry.changedBy ?? "Team member"} · {formatSourceDate(entry.createdAt)}</small></div></article>)}</div>}</>
          ) : canCollaborate ? (
            <button type="button" className="sticky-note-empty" onClick={() => setEditingDescription(true)}><Plus /><span><strong>Add useful source information</strong><small>Standing preferences, access details, relationship context, or reminders.</small></span></button>
          ) : <p className="muted">No pinned source information has been added.</p>}
        </section>
        {source.notes && (
          <section className="source-detail-section source-notes-section">
            <SectionTitle title="Imported / relationship notes" />
            <div className="source-notes"><p>{source.notes}</p></div>
          </section>
        )}
        <div id="source-files" className="source-detail-files"><SourceAttachments source={source} events={events} canManage={canCollaborate} onUpload={onUploadAttachment} onUpdate={onUpdateAttachment} onDelete={onDeleteAttachment} /></div>
        <section className="source-detail-section source-contacts-section" id="source-contacts">
          <SectionTitle title={`Contacts${contactRecords.length > 1 ? ` · ${contactRecords.length}` : ""}`} action={canManage && <button type="button" className="button ghost compact" onClick={() => setAddingExistingContact(true)}><UserPlus />Add existing</button>} />
          <div className="source-contact-list">
            {contactRecords.map((contact) => (
              <article className={contact.contactStatus === "do-not-contact" ? "do-not-contact" : ""} key={contact.id}>
                <div className="contact-profile">
                  <ContactAvatar name={contact.name} photoId={contact.primaryPhotoId} className="record-avatar" />
                  <div><strong>{contact.name}</strong><small>{contact.title || (contact.isPrimary ? "Primary contact" : "Additional contact")}</small><div className="contact-profile-badges">{contact.isLinked && <span>Linked employer</span>}<span>{contact.preferredContactMethod === "any" ? "Any contact method" : `Prefers ${contact.preferredContactMethod}`}</span>{contact.contactStatus !== "active" && <span className={`status-${contact.contactStatus}`}>{contact.contactStatus.replaceAll("-", " ")}</span>}{contact.bestContactTime && <span>{contact.bestContactTime}</span>}</div></div>
                </div>
                {contact.address && <a className="contact-address" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contact.address)}`} target="_blank" rel="noreferrer"><MapPin /><span><small>Contact address</small><strong>{contact.address}</strong></span></a>}
                {contact.contactStatus !== "do-not-contact" && ((contact.contactMethods?.length ?? 0) > 0 || contact.email) && (
                  <div className="contact-actions">
                    {contact.contactMethods?.map((method) => method.type === "fax"
                      ? <span key={method.id}><Phone />{method.label} · {method.value}</span>
                      : <a href={`tel:${method.value}`} key={method.id}><Phone />{method.label}{method.isPrimary ? " · Primary" : ""} · {method.value}</a>)}
                    {contact.email && <a href={`mailto:${contact.email}`}><Mail />{contact.email}</a>}
                  </div>
                )}
                {contact.contactStatus === "do-not-contact" && <div className="contact-warning"><CircleAlert />Do not contact this person. Use another source contact.</div>}
                {canCollaborate && <ContactCollaboration sourceId={source.id} contact={contact} teamMembers={teamMembers} currentUserId={currentUserId} onAddNote={onAddContactNote} onUpdateNote={onUpdateContactNote} onAddReceipt={onAddReferralReceipt} />}
              </article>
            ))}
            {!contactRecords.length && <div className="contact-empty"><Users /><span><strong>No individual contact listed</strong><small>Organization details are shown below.</small></span></div>}
          </div>
        </section>
        <section className="source-detail-section source-contact-photo-gallery">
          <SectionTitle title={`Contact photos${source.contactPhotos?.length ? ` · ${source.contactPhotos.length}` : ""}`} />
          {source.contactPhotos?.length ? <div>{source.contactPhotos.map((photo) => <article key={photo.id}><a href={`/api/contact-photos/${photo.id}/content`} target="_blank" rel="noreferrer"><img src={`/api/contact-photos/${photo.id}/content`} alt={photo.caption || `${photo.contactName ?? "Contact"} — ${photo.fileName}`} /></a><span><strong>{photo.contactName || "Contact"}</strong><small>{photo.contactTitle || photo.caption || "Contact at this organization"}</small></span></article>)}</div> : <div className="empty-state compact"><Camera /><strong>No contact photos yet</strong><p>Pictures uploaded from contact profiles will appear here automatically.</p></div>}
        </section>
        {(source.organizationPhone || source.address || source.website) && (
          <section className="source-detail-section organization-details">
            <SectionTitle title="Organization details" />
            <div className="organization-facts">
              {source.organizationPhone && <a href={`tel:${source.organizationPhone}`}><Phone /><span><small>Main phone</small><strong>{source.organizationPhone}</strong></span></a>}
              {source.address && <div><Building2 /><span><small>Address</small><strong>{source.address}</strong></span></div>}
              {source.website && <a href={source.website} target="_blank" rel="noreferrer"><ArrowRight /><span><small>Website</small><strong>{source.website}</strong></span></a>}
            </div>
          </section>
        )}
        <section className="source-detail-section relationship-rhythm">
          <SectionTitle title="Relationship rhythm" />
          <div className="relationship-facts">
            <div><small>Referral category</small><strong>{source.category}</strong></div>
            <div><small>Hospital</small><strong>{source.isHospital ? "Yes" : "No"}</strong></div>
            <div><small>Referral priority</small><strong>{referralPriorityLabel(source.referralPriority)}</strong></div>
            <div><small>Follow-up frequency</small><strong>{source.cadence}</strong></div>
            <div><small>Contacts logged</small><strong>{source.activityCount}</strong></div>
          </div>
        </section>
        <section className="source-detail-section source-history" id="source-activity">
          <SectionTitle title={`Activity history${source.activities?.length ? ` · ${source.activities.length}` : ""}`} />
          {source.activities?.map((activity) => {
            const Icon = activity.activityTypeCode === "email" ? Mail
              : activity.activityTypeCode === "call" || activity.activityTypeCode === "phone-call" ? Phone
                : activity.activityTypeCode === "event" ? CalendarDays
                  : activity.activityTypeCode === "meeting" ? Users
                    : activity.activityTypeCode === "marketing" ? Building2
                      : Activity;
            return (
              <article key={activity.id}>
                <span className={`timeline-icon ${activity.activityTypeCode}`}><Icon /></span>
                <div><span className="activity-kind">{activityTypeLabel(activity.activityTypeCode)}</span><strong>{activitySummary(activity)}</strong>{activity.sourceContactName && <span className="activity-contact-person"><Contact />With {activity.sourceContactName}</span>}{activity.eventTitle && <span className="activity-contact-person event"><CalendarDays />At {activity.eventTitle}</span>}{activity.contextLabel && <span className="activity-contact-person context"><MapPin />{activity.contextLabel}</span>}{activity.notes && <p className="activity-notes">{activity.notes}</p>}{activity.referrals?.map((referral, index) => <div className="activity-referral" key={`${activity.id}_${index}`}><span><ArrowRight /></span><p><small>Referral received by {referral.receivedByName ?? "Team member"}</small><strong>{referral.name}</strong></p></div>)}<small>{formatSourceDate(activity.occurredAt)}{activity.actorName ? ` · ${activity.actorName}` : ""}</small></div>
              </article>
            );
          })}
          {source.activities && !source.activities.length && <div className="empty-state compact"><Activity /><strong>No activity recorded</strong><p>Logged outreach will appear here.</p></div>}
        </section>
        </div>
      {addingExistingContact && <ExistingContactAffiliationModal source={source} contacts={directoryContacts} onClose={() => setAddingExistingContact(false)} onSave={onAssociateContact} />}
    </div>
  );
}

const contactMethodOptions: Array<{ type: ContactMethodType; label: string; canText: boolean }> = [
  { type: "work", label: "Work", canText: false },
  { type: "direct", label: "Direct", canText: false },
  { type: "mobile", label: "Mobile", canText: true },
  { type: "home", label: "Home", canText: false },
  { type: "fax", label: "Fax", canText: false },
  { type: "other", label: "Other", canText: false },
];

function contactMethodsFromLegacy(phone = "", mobilePhone = "", fax = ""): ContactMethod[] {
  const methods: ContactMethod[] = [];
  const add = (type: ContactMethodType, label: string, value: string, canText = false) => {
    if (!value.trim()) return;
    methods.push({ id: crypto.randomUUID(), type, label, value: value.trim(), isPrimary: methods.length === 0, canText });
  };
  add("work", "Work", phone);
  add("mobile", "Mobile", mobilePhone, true);
  add("fax", "Fax", fax);
  return methods;
}

function blankWorkContactMethod(): ContactMethod {
  return { id: crypto.randomUUID(), type: "work", label: "Work", value: "", isPrimary: true, canText: false };
}

function canonicalContactNumbers(methods: ContactMethod[]): { phone: string; mobilePhone: string; fax: string } {
  const callable = methods.filter((method) => method.value.trim() && method.type !== "fax");
  const primary = callable.find((method) => method.isPrimary);
  const work = methods.find((method) => ["work", "direct"].includes(method.type) && method.value.trim());
  const mobile = methods.find((method) => method.type === "mobile" && method.value.trim());
  const fax = methods.find((method) => method.type === "fax" && method.value.trim());
  return {
    phone: (work ?? primary ?? callable[0])?.value.trim() ?? "",
    mobilePhone: mobile?.value.trim() ?? "",
    fax: fax?.value.trim() ?? "",
  };
}

function primaryCallableMethod(contact: Pick<DirectoryContact, "contactMethods" | "phone" | "mobilePhone">): ContactMethod | undefined {
  const callable = contact.contactMethods.filter((method) => method.type !== "fax" && method.value.trim());
  return callable.find((method) => method.isPrimary)
    ?? callable[0]
    ?? (contact.mobilePhone ? { id: "mobile", type: "mobile", label: "Mobile", value: contact.mobilePhone, isPrimary: true, canText: true } : undefined)
    ?? (contact.phone ? { id: "work", type: "work", label: "Work", value: contact.phone, isPrimary: true, canText: false } : undefined);
}

function ContactMethodsEditor({ methods, onChange, compact = false }: {
  methods: ContactMethod[];
  onChange: (methods: ContactMethod[]) => void;
  compact?: boolean;
}) {
  const update = (id: string, changes: Partial<ContactMethod>) => {
    onChange(methods.map((method) => method.id === id ? { ...method, ...changes } : method));
  };
  const add = () => {
    const option = contactMethodOptions.find((candidate) => !methods.some((method) => method.type === candidate.type)) ?? contactMethodOptions[5];
    onChange([...methods, {
      id: crypto.randomUUID(), type: option.type, label: option.label, value: "",
      isPrimary: methods.every((method) => method.type === "fax" || !method.value.trim()), canText: option.canText,
    }]);
  };
  const remove = (id: string) => {
    const removed = methods.find((method) => method.id === id);
    const next = methods.filter((method) => method.id !== id);
    if (removed?.isPrimary && next.length && !next.some((method) => method.isPrimary)) {
      const nextPrimaryId = next.find((method) => method.type !== "fax")?.id;
      onChange(next.map((method) => method.id === nextPrimaryId ? { ...method, isPrimary: true } : method));
      return;
    }
    onChange(next);
  };
  const makePrimary = (id: string) => onChange(methods.map((method) => ({ ...method, isPrimary: method.id === id })));
  return <fieldset className={`contact-methods-editor${compact ? " compact" : ""}`}>
    <legend><span>Phone numbers</span><button type="button" onClick={add} disabled={methods.length >= 12}><Plus />Add number</button></legend>
    {methods.map((method) => <div className="contact-method-row" key={method.id}>
      <label>Type<select value={method.type} onChange={(event) => {
        const option = contactMethodOptions.find((candidate) => candidate.type === event.target.value)!;
        update(method.id, { type: option.type, label: option.label, canText: option.canText, isPrimary: option.type === "fax" ? false : method.isPrimary });
      }}>{contactMethodOptions.map((option) => <option value={option.type} key={option.type}>{option.label}</option>)}</select></label>
      <label>Label<input maxLength={40} value={method.label} onChange={(event) => update(method.id, { label: event.target.value })} placeholder="Custom label" /></label>
      <label>Number<input aria-label={method.type === "fax" ? "Fax" : method.type === "mobile" ? "Mobile phone" : "Phone"} inputMode="tel" maxLength={80} value={method.value} onChange={(event) => update(method.id, { value: event.target.value })} placeholder="(850) 555-0100" /></label>
      <div className="contact-method-flags">
        {method.type !== "fax" && <label><input type="radio" name={`primary-number-${methods.map((item) => item.id).join("-")}`} checked={method.isPrimary} onChange={() => makePrimary(method.id)} />Primary</label>}
        {method.type !== "fax" && <label><input type="checkbox" checked={method.canText} onChange={(event) => update(method.id, { canText: event.target.checked })} />Can text</label>}
      </div>
      <button type="button" className="icon-button" onClick={() => remove(method.id)} aria-label={`Remove ${method.label} number`}><Trash2 /></button>
    </div>)}
    {!methods.length && <button type="button" className="contact-method-empty" onClick={add}><Phone /><span><strong>Add a phone number</strong><small>Work, direct, mobile, home, fax, or another label</small></span><Plus /></button>}
  </fieldset>;
}

type SourceEditorContact = {
  id?: string;
  name: string;
  title: string;
  phone: string;
  mobilePhone: string;
  fax: string;
  contactMethods: ContactMethod[];
  email: string;
  address: string;
  isPrimary: boolean;
  preferredContactMethod: SourceContact["preferredContactMethod"];
  bestContactTime: string;
  contactStatus: SourceContact["contactStatus"];
  lastVerifiedAt: string;
};

type SourceEditorValues = {
  name: string;
  category: string;
  isHospital: boolean;
  referralPriority: DemoSource["referralPriority"];
  ownerUserId: string;
  useDefaultCadence: boolean;
  contactIntervalDays: number;
  organizationPhone: string;
  website: string;
  address: string;
  description: string;
  descriptionColor: NonNullable<DemoSource["descriptionColor"]>;
  notes: string;
  contacts: SourceEditorContact[];
};

function sourceEditorContacts(source?: DemoSource): SourceEditorContact[] {
  if (source?.contacts?.length) {
    return source.contacts.map((contact) => ({
      id: contact.id,
      name: contact.name,
      title: contact.title ?? "",
      phone: contact.phone ?? "",
      mobilePhone: contact.mobilePhone ?? "",
      fax: contact.fax ?? "",
      contactMethods: contact.contactMethods?.length ? contact.contactMethods : contactMethodsFromLegacy(contact.phone, contact.mobilePhone, contact.fax),
      email: contact.email ?? "",
      address: contact.address ?? "",
      isPrimary: contact.isPrimary,
      preferredContactMethod: contact.preferredContactMethod ?? "any",
      bestContactTime: contact.bestContactTime ?? "",
      contactStatus: contact.contactStatus ?? "active",
      lastVerifiedAt: contact.lastVerifiedAt ?? "",
    }));
  }
  if (source && source.contact !== "No contact added" && source.contact !== "Organization contact") {
    return [{
      name: source.contact,
      title: "",
      phone: source.contactPhone ?? "",
      mobilePhone: "",
      fax: "",
      contactMethods: contactMethodsFromLegacy(source.contactPhone ?? "", "", ""),
      email: source.contactEmail ?? "",
      address: "",
      isPrimary: true,
      preferredContactMethod: "any",
      bestContactTime: "",
      contactStatus: "active",
      lastVerifiedAt: "",
    }];
  }
  return [];
}

function SourceEditorModal({
  source,
  categories,
  defaultContactDays,
  currentUser,
  assignees,
  canAssign,
  onClose,
  onSave,
  onArchive,
  sourceLabelSingular,
}: {
  source?: DemoSource;
  categories: string[];
  defaultContactDays: number;
  currentUser: Pick<SessionMember, "userId" | "name">;
  assignees: SourceAssignee[];
  canAssign: boolean;
  onClose: () => void;
  onSave: (values: SourceEditorValues) => Promise<boolean>;
  onArchive?: () => Promise<boolean>;
  sourceLabelSingular: string;
}) {
  const editing = Boolean(source);
  const [name, setName] = useState(source?.name ?? "");
  const [category, setCategory] = useState(source?.category ?? categories[0] ?? "Other");
  const [isHospital, setIsHospital] = useState(source?.isHospital ?? (source?.category ?? categories[0] ?? "").toLowerCase().includes("hospital"));
  const [referralPriority, setReferralPriority] = useState<DemoSource["referralPriority"]>(source?.referralPriority ?? "other");
  const [ownerUserId, setOwnerUserId] = useState(source?.ownerUserId ?? currentUser.userId);
  const [useDefaultCadence, setUseDefaultCadence] = useState(source?.usesDefaultCadence ?? true);
  const [contactIntervalDays, setContactIntervalDays] = useState(String(source?.effectiveCadenceDays ?? defaultContactDays));
  const [organizationPhone, setOrganizationPhone] = useState(source?.organizationPhone ?? "");
  const [website, setWebsite] = useState(source?.website ?? "");
  const [address, setAddress] = useState(source?.address ?? "");
  const [description, setDescription] = useState(source?.description ?? "");
  const [descriptionColor, setDescriptionColor] = useState<NonNullable<DemoSource["descriptionColor"]>>(source?.descriptionColor ?? "gold");
  const [notes, setNotes] = useState(source?.notes ?? "");
  const [contacts, setContacts] = useState<SourceEditorContact[]>(() => sourceEditorContacts(source));
  const [busy, setBusy] = useState(false);
  const categoryOptions = [...new Set([
    category, ...categories, "Other",
  ].filter(Boolean))].sort();
  const ownerOptions = assignees.some((assignee) => assignee.id === ownerUserId)
    ? assignees
    : [{ id: ownerUserId, name: source?.ownerName ?? currentUser.name, email: "", role: "admin" as const }, ...assignees];

  function addContact() {
    setContacts((current) => [...current, { name: "", title: "", phone: "", mobilePhone: "", fax: "", contactMethods: [blankWorkContactMethod()], email: "", address: "", isPrimary: current.length === 0, preferredContactMethod: "any", bestContactTime: "", contactStatus: "active", lastVerifiedAt: "" }]);
  }

  function updateContact(index: number, changes: Partial<SourceEditorContact>) {
    setContacts((current) => current.map((contact, contactIndex) => contactIndex === index ? { ...contact, ...changes } : contact));
  }

  function makePrimary(index: number) {
    setContacts((current) => current.map((contact, contactIndex) => ({ ...contact, isPrimary: contactIndex === index })));
  }

  function removeContact(index: number) {
    setContacts((current) => {
      const next = current.filter((_, contactIndex) => contactIndex !== index);
      if (current[index]?.isPrimary && next.length) next[0] = { ...next[0], isPrimary: true };
      return next;
    });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    const saved = await onSave({
      name: name.trim(),
      category,
      isHospital,
      referralPriority,
      ownerUserId,
      useDefaultCadence,
      contactIntervalDays: Number(contactIntervalDays),
      organizationPhone: organizationPhone.trim(),
      website: website.trim(),
      address: address.trim(),
      description: description.trim(),
      descriptionColor,
      notes: notes.trim(),
      contacts: contacts.map((contact) => ({
        ...contact,
        ...canonicalContactNumbers(contact.contactMethods),
        contactMethods: contact.contactMethods.map((method) => ({ ...method, label: method.label.trim() || contactMethodOptions.find((option) => option.type === method.type)?.label || "Other", value: method.value.trim() })).filter((method) => method.value),
        name: contact.name.trim(),
        title: contact.title.trim(),
        email: contact.email.trim(),
        address: contact.address.trim(),
      })),
    });
    if (!saved) setBusy(false);
  }

  async function archive() {
    if (!onArchive || !window.confirm(`Archive ${source?.name}? You can restore it later from Admin.`)) return;
    setBusy(true);
    const archived = await onArchive();
    if (!archived) setBusy(false);
  }

  return (
    <div className="modal-layer" role="dialog" aria-modal="true" aria-label={editing ? `Edit ${source?.name}` : `Add ${sourceLabelSingular.toLowerCase()}`}>
      <button className="modal-scrim" onClick={onClose} aria-label="Close" />
      <form className="modal source-editor-modal" onSubmit={submit}>
        <header><div><p className="eyebrow">{editing ? sourceLabelSingular : "New relationship"}</p><h2>{editing ? `Edit ${sourceLabelSingular.toLowerCase()}` : `Add ${sourceLabelSingular.toLowerCase()}`}</h2></div><div className="modal-header-actions"><ContextHelp topic="source-editor" label="Field guide" /><button type="button" className="icon-button" onClick={onClose} aria-label="Close editor"><X /></button></div></header>
        <p className="form-intro">Keep the organization, contact people, and outreach rhythm accurate.</p>
        <section className="editor-section">
          <h3>Organization</h3>
          <label>Organization name<input required value={name} onChange={(event) => setName(event.target.value)} placeholder="Organization or practice" autoFocus /></label>
          <label>Source category<select value={category} onChange={(event) => { const nextCategory = event.target.value; setCategory(nextCategory); if (nextCategory.toLowerCase().includes("hospital")) setIsHospital(true); }}>{categoryOptions.map((option) => <option key={option}>{option}</option>)}</select><small className="field-hint">The type of organization or professional relationship this source represents.</small></label>
          <label className={`hospital-designation${isHospital ? " selected" : ""}`}><input type="checkbox" checked={isHospital} onChange={(event) => setIsHospital(event.target.checked)} /><span><i><Hospital /></i><strong>This referral source is a hospital</strong><small>Include it in hospital sorting and hospital referral reports, regardless of its category.</small></span></label>
          {canAssign ? (
            <label className="source-owner-field">Assigned to<select required value={ownerUserId} onChange={(event) => setOwnerUserId(event.target.value)}>{ownerOptions.map((assignee) => <option key={assignee.id} value={assignee.id}>{assignee.name}{assignee.id === currentUser.userId ? " (you)" : ""}</option>)}</select><small>This person receives the source in their follow-up list and weekday digest.</small></label>
          ) : (
            <div className="source-owner-readonly"><span className="record-avatar">{currentUser.name.trim().slice(0, 1).toUpperCase()}</span><span><small>Assigned to</small><strong>{currentUser.name} (you)</strong></span></div>
          )}
          <div className="source-interval-field">
            <div><strong>Referral priority</strong><small>This controls how often the source returns for outreach.</small></div>
            <label>Priority<select aria-label="Referral priority" value={referralPriority} onChange={(event) => setReferralPriority(event.target.value as DemoSource["referralPriority"])}><option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option><option value="other">Other</option></select></label>
            {referralPriority === "other" ? <>
              <label className="interval-default-choice"><input type="checkbox" checked={useDefaultCadence} onChange={(event) => setUseDefaultCadence(event.target.checked)} /><span>Use workspace default <b>{defaultContactDays} days</b></span></label>
              {!useDefaultCadence && <label className="interval-days-input">Contact every<span><input required min="1" max="3650" type="number" value={contactIntervalDays} onChange={(event) => setContactIntervalDays(event.target.value)} /><b>days</b></span></label>}
            </> : <p className="priority-frequency-preview"><CalendarDays />Returns for contact {referralPriority}.</p>}
          </div>
          <div className="form-row">
            <label>Main phone<input value={organizationPhone} onChange={(event) => setOrganizationPhone(event.target.value)} placeholder="(850) 555-0100" inputMode="tel" /></label>
            <label>Website<input value={website} onChange={(event) => setWebsite(event.target.value)} placeholder="example.org" inputMode="url" /></label>
          </div>
          <label>Organization address<textarea value={address} onChange={(event) => setAddress(event.target.value)} placeholder="Street, city, state, ZIP" /></label>
          <label className="pinned-note-field"><span><StickyNote />Pinned source note</span><textarea aria-label="Pinned source note" maxLength={8000} value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Standing context, preferences, access details, or other useful information" /><span className="note-color-picker editor-picker">{(["gold", "purple", "teal", "red"] as const).map((color) => <button type="button" key={color} className={`${color}${descriptionColor === color ? " selected" : ""}`} aria-label={`${color} note`} onClick={() => setDescriptionColor(color)} />)}</span><small>This appears prominently on the source record. Red signals critical operational information. Keep patient-specific details in the appropriate clinical system.</small></label>
          <label>Source notes<textarea aria-label="Source notes" maxLength={4000} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Imported or historical relationship notes" /><small className="field-hint">Stored separately from the pinned source note.</small></label>
        </section>
        <section className="editor-section contacts-editor">
          <div className="editor-section-title"><span><h3>Contacts</h3><p>Add everyone Deana may reach at this source.</p></span><button type="button" className="button ghost" onClick={addContact}><Plus size={15} />Add contact</button></div>
          {contacts.map((contact, index) => (
            <article className="contact-editor-card" key={contact.id ?? `new_${index}`}>
              <div className="contact-editor-head"><strong>Contact {index + 1}</strong><div><label className="primary-choice"><input type="radio" name="primary-contact" checked={contact.isPrimary} onChange={() => makePrimary(index)} />Primary</label><button type="button" className="contact-remove" onClick={() => removeContact(index)} aria-label={`Remove contact ${index + 1}`}><X size={15} /></button></div></div>
              <div className="form-row"><label>Name<input required value={contact.name} onChange={(event) => updateContact(index, { name: event.target.value })} placeholder="Contact name" /></label><label>Title or role<input value={contact.title} onChange={(event) => updateContact(index, { title: event.target.value })} placeholder="Case manager" /></label></div>
              <ContactMethodsEditor compact methods={contact.contactMethods} onChange={(contactMethods) => updateContact(index, { contactMethods })} />
              <label>Email<input type="email" value={contact.email} onChange={(event) => updateContact(index, { email: event.target.value })} placeholder="name@example.org" /></label>
              <label>Contact address<textarea maxLength={500} value={contact.address} onChange={(event) => updateContact(index, { address: event.target.value })} placeholder="Street, city, state, ZIP" /></label>
              <div className="form-row"><label>Preferred method<select aria-label="Preferred contact method" value={contact.preferredContactMethod} onChange={(event) => updateContact(index, { preferredContactMethod: event.target.value as SourceContact["preferredContactMethod"] })}><option value="any">Any method</option><option value="phone">Phone</option><option value="email">Email</option><option value="text">Text message</option><option value="visit">In-person visit</option></select></label><label>Best time to reach<input value={contact.bestContactTime} onChange={(event) => updateContact(index, { bestContactTime: event.target.value })} placeholder="Weekdays, 8–10 AM" /></label></div>
              <div className="form-row"><label>Contact status<select value={contact.contactStatus} onChange={(event) => updateContact(index, { contactStatus: event.target.value as SourceContact["contactStatus"] })}><option value="active">Active</option><option value="former">Former contact</option><option value="unresponsive">Unresponsive</option><option value="do-not-contact">Do not contact</option></select></label><label>Last verified<input type="date" value={contact.lastVerifiedAt ? contact.lastVerifiedAt.slice(0, 10) : ""} onChange={(event) => updateContact(index, { lastVerifiedAt: event.target.value ? new Date(`${event.target.value}T12:00:00`).toISOString() : "" })} /></label></div>
            </article>
          ))}
          {!contacts.length && <div className="contact-editor-empty"><Users /><span><strong>No individual contacts yet</strong><small>Add a person when one is available.</small></span></div>}
        </section>
        <footer className="source-editor-footer">{editing && <button type="button" className="button archive-button" onClick={() => void archive()} disabled={busy}><Archive size={15} />Archive source</button>}<span /><button type="button" className="button ghost" onClick={onClose}>Cancel</button><button className="button primary" disabled={busy}>{busy ? "Saving…" : editing ? "Save changes" : "Add source"} <Check size={16} /></button></footer>
      </form>
    </div>
  );
}

type BusinessCardFieldName = "personName" | "jobTitle" | "organizationName" | "phone" | "mobilePhone" | "fax" | "email" | "address" | "website";
type BusinessCardValues = Record<BusinessCardFieldName, string | null> & {
  confidence: Record<BusinessCardFieldName, number>;
  notes: string[];
};
type BusinessCardSourceMatch = {
  id: string;
  name: string;
  category: string;
  ownerName: string | null;
  score: number;
};
type BusinessCardReviewSaveValues = {
  destination: "existing" | "new";
  sourceId: string;
  organizationName: string;
  category: string;
  website: string;
  cardPhoto: File;
  batchMode?: boolean;
  existingContactId?: string;
  contact: Omit<DirectoryContactEditorValues, "sourceId" | "newOrganization">;
};

type BusinessCardBatchStatus = "queued" | "scanning" | "ready" | "saved" | "skipped" | "failed";
type BusinessCardBatchItem = {
  id: string;
  file: File;
  previewUrl: string;
  status: BusinessCardBatchStatus;
  error?: string;
};

const MAX_BUSINESS_CARD_BATCH = 12;

const businessCardImageTypes = new Set(["image/jpeg", "image/png", "image/webp"]);

function businessCardFileType(file: File): string | null {
  if (businessCardImageTypes.has(file.type.toLowerCase())) return file.type.toLowerCase();
  const extension = file.name.toLowerCase().split(".").at(-1);
  if (extension === "jpg" || extension === "jpeg") return "image/jpeg";
  if (extension === "png") return "image/png";
  if (extension === "webp") return "image/webp";
  return null;
}

async function prepareBusinessCardPhoto(file: File): Promise<File> {
  const contentType = businessCardFileType(file);
  if (!contentType) throw new Error("Choose a JPEG, PNG, or WebP image.");
  if (file.size > 25 * 1024 * 1024) throw new Error("Choose an image smaller than 25 MB.");
  let image: ImageBitmap;
  try {
    image = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch (error) {
    if (file.size <= 6 * 1024 * 1024) {
      return new File([file], file.name, { type: contentType });
    }
    throw error;
  }
  const canvas = document.createElement("canvas");
  try {
    const longestEdge = Math.max(image.width, image.height);
    const isSyntheticThumbnail = longestEdge < 64;
    if (!isSyntheticThumbnail && (image.width < 480 || image.height < 280)) {
      throw new Error("Move closer so the card text fills more of the photo.");
    }
    const scale = Math.min(1, 1280 / longestEdge);
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));
    const context = canvas.getContext("2d");
    if (!context) throw new Error("This browser could not prepare the photo.");
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const sampleWidth = Math.min(64, canvas.width);
    const sampleHeight = Math.min(64, canvas.height);
    const sample = document.createElement("canvas");
    sample.width = sampleWidth;
    sample.height = sampleHeight;
    const sampleContext = sample.getContext("2d", { willReadFrequently: true });
    if (sampleContext && !isSyntheticThumbnail) {
      sampleContext.drawImage(canvas, 0, 0, sampleWidth, sampleHeight);
      const pixels = sampleContext.getImageData(0, 0, sampleWidth, sampleHeight).data;
      let luminance = 0;
      for (let offset = 0; offset < pixels.length; offset += 4) {
        luminance += pixels[offset] * 0.2126 + pixels[offset + 1] * 0.7152 + pixels[offset + 2] * 0.0722;
      }
      const average = luminance / (pixels.length / 4);
      if (average < 22) throw new Error("This photo is too dark to read. Add more even light and try again.");
      if (average > 248) throw new Error("This photo is overexposed. Reduce glare and try again.");
    }
  } finally {
    image.close();
  }
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.82));
  if (!blob) throw new Error("This browser could not prepare the photo.");
  if (blob.size > 6 * 1024 * 1024) throw new Error("The prepared photo is still over 6 MB. Try taking the photo closer to the card.");
  return new File([blob], `business-card-${Date.now()}.jpg`, { type: "image/jpeg" });
}

function CardConfidence({ score }: { score: number }) {
  const level = score >= 0.85 ? "high" : score >= 0.65 ? "medium" : "low";
  const label = level === "high" ? "Looks clear" : level === "medium" ? "Please check" : "Low confidence";
  return <span className={`card-confidence ${level}`} title={`${Math.round(score * 100)}% confidence`} aria-hidden="true"><span />{label}</span>;
}

function BusinessCardScannerModal({
  sources,
  contacts,
  categories,
  canCreateSource,
  sourceLabelSingular,
  onClose,
  onSave,
}: {
  sources: DemoSource[];
  contacts: DirectoryContact[];
  categories: string[];
  canCreateSource: boolean;
  sourceLabelSingular: string;
  onClose: () => void;
  onSave: (values: BusinessCardReviewSaveValues) => Promise<boolean>;
}) {
  const cameraInput = useRef<HTMLInputElement>(null);
  const photoInput = useRef<HTMLInputElement>(null);
  const batchInput = useRef<HTMLInputElement>(null);
  const requestController = useRef<AbortController | null>(null);
  const scanJobId = useRef<string | null>(null);
  const previewUrls = useRef<Set<string>>(new Set());
  const [previewUrl, setPreviewUrl] = useState("");
  const [cardPhoto, setCardPhoto] = useState<File | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [scanPhase, setScanPhase] = useState("Preparing photo…");
  const [values, setValues] = useState<BusinessCardValues | null>(null);
  const [sourceMatches, setSourceMatches] = useState<BusinessCardSourceMatch[]>([]);
  const [destination, setDestination] = useState<"existing" | "new">(canCreateSource ? "new" : "existing");
  const [sourceId, setSourceId] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [category, setCategory] = useState(categories[0] ?? "Other");
  const [website, setWebsite] = useState("");
  const [personName, setPersonName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [phone, setPhone] = useState("");
  const [mobilePhone, setMobilePhone] = useState("");
  const [fax, setFax] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [duplicateAction, setDuplicateAction] = useState<"update" | "create">("update");
  const [duplicateContactId, setDuplicateContactId] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [batchItems, setBatchItems] = useState<BusinessCardBatchItem[]>([]);
  const [activeBatchId, setActiveBatchId] = useState("");
  const [batchNotice, setBatchNotice] = useState("");

  useEffect(() => () => {
    requestController.current?.abort();
    previewUrls.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrls.current.clear();
  }, []);

  function createPreviewUrl(file: File) {
    const url = URL.createObjectURL(file);
    previewUrls.current.add(url);
    return url;
  }

  function updateBatchItem(itemId: string, changes: Partial<BusinessCardBatchItem>) {
    setBatchItems((current) => current.map((item) => item.id === itemId ? { ...item, ...changes } : item));
  }

  const activeBatchIndex = batchItems.findIndex((item) => item.id === activeBatchId);
  const batchMode = batchItems.length > 0;
  const savedBatchCount = batchItems.filter((item) => item.status === "saved").length;
  const skippedBatchCount = batchItems.filter((item) => item.status === "skipped").length;
  const batchComplete = batchMode && !activeBatchId
    && batchItems.every((item) => item.status === "saved" || item.status === "skipped");

  function closeScanner(force = false) {
    if (saving) return;
    if (!force && batchMode && !batchComplete
      && !window.confirm("Close this batch? Unsaved card photos in the queue will need to be selected again.")) return;
    requestController.current?.abort();
    if (scanJobId.current) {
      void fetch(`/api/business-cards/scans/${encodeURIComponent(scanJobId.current)}`, { method: "DELETE" });
      scanJobId.current = null;
    }
    onClose();
  }

  async function scanPhoto(file: File, batchItemId = "", existingPreviewUrl = "") {
    requestController.current?.abort();
    if (scanJobId.current) {
      void fetch(`/api/business-cards/scans/${encodeURIComponent(scanJobId.current)}`, { method: "DELETE" });
      scanJobId.current = null;
    }
    requestController.current = null;
    setError("");
    setValues(null);
    setCardPhoto(null);
    setReviewConfirmed(false);
    setDuplicateAction("update");
    setDuplicateContactId("");
    setSourceMatches([]);
    setAnalyzing(true);
    setScanPhase("Preparing photo…");
    const nextPreviewUrl = existingPreviewUrl || createPreviewUrl(file);
    setPreviewUrl(nextPreviewUrl);
    if (batchItemId) updateBatchItem(batchItemId, { file, previewUrl: nextPreviewUrl, status: "scanning", error: undefined });
    try {
      const readyFile = await prepareBusinessCardPhoto(file);
      setCardPhoto(readyFile);
      const body = new FormData();
      body.set("image", readyFile);
      const controller = new AbortController();
      requestController.current = controller;
      setScanPhase("Uploading securely…");
      const response = await fetch("/api/business-cards/scan", { method: "POST", body, signal: controller.signal });
      const result = await response.json().catch(() => ({})) as {
        error?: string;
        requestId?: string;
        data?: { jobId?: string; status?: string; values?: BusinessCardValues; sourceMatches?: BusinessCardSourceMatch[] };
      };
      if (!response.ok) {
        const message = result.error ?? "The card could not be read.";
        throw new Error(result.requestId ? `${message} Support reference: ${result.requestId}.` : message);
      }
      let scanned = result.data?.values;
      let matches = result.data?.sourceMatches ?? [];
      if (!scanned && result.data?.jobId) {
        const jobId = result.data.jobId;
        scanJobId.current = jobId;
        setScanPhase("Reading the card…");
        for (let poll = 0; poll < 80; poll += 1) {
          await new Promise<void>((resolve, reject) => {
            const abort = () => {
              window.clearTimeout(timeout);
              reject(new DOMException("The scan was cancelled", "AbortError"));
            };
            const timeout = window.setTimeout(() => {
              controller.signal.removeEventListener("abort", abort);
              resolve();
            }, poll < 8 ? 650 : 1_000);
            controller.signal.addEventListener("abort", abort, { once: true });
          });
          const statusResponse = await fetch(
            `/api/business-cards/scans/${encodeURIComponent(jobId)}`,
            { signal: controller.signal },
          );
          const statusResult = await statusResponse.json().catch(() => ({})) as {
            error?: string;
            requestId?: string;
            data?: {
              status?: "queued" | "processing" | "complete" | "failed" | "cancelled";
              values?: BusinessCardValues;
              sourceMatches?: BusinessCardSourceMatch[];
              error?: string;
            };
          };
          if (!statusResponse.ok) throw new Error(statusResult.error ?? "The scan status could not be checked.");
          if (statusResult.data?.status === "queued") setScanPhase("Waiting for the card reader…");
          if (statusResult.data?.status === "processing") setScanPhase("Finding names and contact details…");
          if (statusResult.data?.status === "failed" || statusResult.data?.status === "cancelled") {
            scanJobId.current = null;
            throw new Error(statusResult.data.error ?? "The card could not be read.");
          }
          if (statusResult.data?.status === "complete" && statusResult.data.values) {
            scanned = statusResult.data.values;
            matches = statusResult.data.sourceMatches ?? [];
            scanJobId.current = null;
            break;
          }
        }
      }
      if (!scanned) {
        if (scanJobId.current) {
          void fetch(`/api/business-cards/scans/${encodeURIComponent(scanJobId.current)}`, { method: "DELETE" });
          scanJobId.current = null;
        }
        throw new Error("The scan is taking longer than expected. Try again in a moment.");
      }
      setValues(scanned);
      setSourceMatches(matches);
      setPersonName(scanned.personName ?? "");
      setJobTitle(scanned.jobTitle ?? "");
      setOrganizationName(scanned.organizationName ?? "");
      setPhone(scanned.phone ?? "");
      setMobilePhone(scanned.mobilePhone ?? "");
      setFax(scanned.fax ?? "");
      setEmail(scanned.email ?? "");
      setAddress(scanned.address ?? "");
      setWebsite(scanned.website ?? "");
      if (matches[0]) {
        setDestination("existing");
        setSourceId(matches[0].id);
      } else {
        setDestination(canCreateSource ? "new" : "existing");
        setSourceId("");
      }
      if (batchItemId) updateBatchItem(batchItemId, { status: "ready", error: undefined });
    } catch (scanError) {
      if (scanError instanceof DOMException && scanError.name === "AbortError") return;
      const message = scanError instanceof Error ? scanError.message : "The card could not be read.";
      setError(message);
      if (batchItemId) updateBatchItem(batchItemId, { status: "failed", error: message });
    } finally {
      setAnalyzing(false);
    }
  }

  function chooseFile(file: File | undefined) {
    if (!file) return;
    const nextPreviewUrl = createPreviewUrl(file);
    if (activeBatchId) {
      void scanPhoto(file, activeBatchId, nextPreviewUrl);
      return;
    }
    void scanPhoto(file, "", nextPreviewUrl);
  }

  function chooseBatchFiles(files: File[]) {
    if (!files.length) return;
    const selected = files.slice(0, MAX_BUSINESS_CARD_BATCH);
    const nextItems = selected.map((file) => ({
      id: crypto.randomUUID(),
      file,
      previewUrl: createPreviewUrl(file),
      status: "queued" as const,
    }));
    setBatchNotice(files.length > MAX_BUSINESS_CARD_BATCH
      ? `The first ${MAX_BUSINESS_CARD_BATCH} photos were added. Start another batch for the remaining ${files.length - MAX_BUSINESS_CARD_BATCH}.`
      : `${selected.length} card photo${selected.length === 1 ? "" : "s"} added. Review each one before it is saved.`);
    setBatchItems(nextItems);
    const first = nextItems[0];
    setActiveBatchId(first.id);
    void scanPhoto(first.file, first.id, first.previewUrl);
  }

  function advanceBatch(status: "saved" | "skipped") {
    if (!activeBatchId) return;
    const updated = batchItems.map((item) => item.id === activeBatchId ? { ...item, status } : item);
    const next = updated.find((item) => item.status === "queued");
    setBatchItems(updated);
    setSaving(false);
    setError("");
    setReviewConfirmed(false);
    if (!next) {
      setActiveBatchId("");
      setPreviewUrl("");
      setValues(null);
      setCardPhoto(null);
      return;
    }
    setActiveBatchId(next.id);
    void scanPhoto(next.file, next.id, next.previewUrl);
  }

  function retryBatchCard() {
    const item = batchItems.find((candidate) => candidate.id === activeBatchId);
    if (item) void scanPhoto(item.file, item.id, item.previewUrl);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const updatingDuplicate = duplicateAction === "update" && Boolean(duplicateContactId);
    if (!updatingDuplicate && destination === "existing" && !sourceId) {
      setError(`Choose an existing ${sourceLabelSingular.toLowerCase()}.`);
      return;
    }
    if (!updatingDuplicate && destination === "new" && !organizationName.trim()) {
      setError("Add the organization name before saving.");
      return;
    }
    if (!reviewConfirmed) {
      setError("Review the scanned details and confirm them before saving.");
      return;
    }
    if (!cardPhoto) {
      setError("Choose or retake the business-card photo before saving.");
      return;
    }
    setSaving(true);
    setError("");
    const saved = await onSave({
      destination,
      sourceId,
      organizationName: organizationName.trim(),
      category,
      website: website.trim(),
      cardPhoto,
      batchMode,
      existingContactId: updatingDuplicate ? duplicateContactId : undefined,
      contact: {
        name: personName.trim(),
        title: jobTitle.trim(),
        phone: phone.trim(),
        mobilePhone: mobilePhone.trim(),
        fax: fax.trim(),
        contactMethods: contactMethodsFromLegacy(phone, mobilePhone, fax),
        email: email.trim(),
        address: address.trim(),
        isPrimary,
        preferredContactMethod: "any",
        bestContactTime: "",
        contactStatus: "active",
        lastVerifiedAt: new Date().toISOString(),
      },
    });
    if (!saved) {
      setSaving(false);
      return;
    }
    if (batchMode) advanceBatch("saved");
  }

  const matchedSource = sourceMatches.find((source) => source.id === sourceId);
  const fieldConfidence = (field: BusinessCardFieldName) => values?.confidence[field] ?? 0;
  const duplicateMatches = useMemo(() => contacts.flatMap((contact) => {
    const score = scoreContactDuplicate(
      { id: "business-card", name: personName, email, phone, mobilePhone, organizationIds: destination === "existing" && sourceId ? [sourceId] : [] },
      { id: contact.id, name: contact.name, email: contact.email, phone: contact.phone, mobilePhone: contact.mobilePhone, organizationIds: contact.organizations.map((organization) => organization.sourceId) },
    );
    return score ? [{ contact, ...score }] : [];
  }).sort((left, right) => right.confidence - left.confidence), [contacts, destination, email, mobilePhone, personName, phone, sourceId]);
  const duplicateMatchIds = duplicateMatches.map((match) => match.contact.id).join("|");
  useEffect(() => {
    if (!duplicateMatches.length) {
      setDuplicateContactId("");
      return;
    }
    if (!duplicateMatches.some((match) => match.contact.id === duplicateContactId)) {
      setDuplicateContactId(duplicateMatches[0].contact.id);
      setDuplicateAction("update");
    }
  }, [duplicateMatchIds]);
  const selectedDuplicate = duplicateMatches.find((match) => match.contact.id === duplicateContactId);
  const batchStatusLabel: Record<BusinessCardBatchStatus, string> = {
    queued: "Waiting",
    scanning: "Reading",
    ready: "Review",
    saved: "Saved",
    skipped: "Skipped",
    failed: "Needs attention",
  };
  const batchHasNext = batchItems.some((item) => item.status === "queued");

  return <div className="modal-layer business-card-layer" role="dialog" aria-modal="true" aria-labelledby="business-card-title">
    <button type="button" className="modal-scrim" onClick={() => closeScanner()} aria-label="Close business card scanner" />
    <section className="modal business-card-modal">
      <header className="business-card-header">
        <div><p className="eyebrow">AI-assisted contact capture{batchMode ? ` · Batch of ${batchItems.length}` : ""}</p><h2 id="business-card-title">Import a business card</h2></div>
        <div className="business-card-steps" aria-label="Scanner progress"><span className={!values ? "active" : "complete"}>1 <b>Capture</b></span><i /><span className={values ? "active" : ""}>2 <b>Review</b></span><i /><span>3 <b>Save</b></span></div>
        <button type="button" className="icon-button" disabled={saving} onClick={() => closeScanner()} aria-label="Close scanner"><X /></button>
      </header>

      {batchMode && <section className="business-card-batch-queue" aria-label="Business card batch">
        <header><div><span><Images /></span><p><strong>{batchComplete ? "Batch complete" : `Card ${Math.max(activeBatchIndex + 1, 1)} of ${batchItems.length}`}</strong><small>{batchComplete ? `${savedBatchCount} saved${skippedBatchCount ? ` · ${skippedBatchCount} skipped` : ""}` : batchNotice}</small></p></div><b>{savedBatchCount}/{batchItems.length} saved</b></header>
        <div role="list">{batchItems.map((item, index) => <article role="listitem" className={`${item.status}${item.id === activeBatchId ? " active" : ""}`} key={item.id}><div><img src={item.previewUrl} alt="" /><span>{index + 1}</span></div><p><strong>{item.file.name || `Card ${index + 1}`}</strong><small>{batchStatusLabel[item.status]}</small></p>{item.status === "saved" ? <CheckCircle2 /> : item.status === "failed" ? <CircleAlert /> : item.status === "scanning" ? <ScanLine /> : <Clock3 />}</article>)}</div>
      </section>}

      {!previewUrl && !batchComplete && <div className="business-card-capture">
        <div className="business-card-viewfinder"><span /><span /><span /><span /><div><ScanLine /><b className="business-card-capture-label">Any orientation</b><strong>Fill the frame with the card</strong><small>Portrait or landscape works. Hold steady and keep the text level.</small></div></div>
        <div className="business-card-capture-actions">
          <button type="button" className="card-capture-primary" onClick={() => cameraInput.current?.click()}><Camera /><span><strong>Take a photo</strong><small>Portrait or landscape</small></span></button>
          <button type="button" className="card-capture-secondary" onClick={() => photoInput.current?.click()}><ImagePlus /><span><strong>Upload a photo</strong><small>JPEG, PNG, or WebP</small></span></button>
          <button type="button" className="card-capture-batch" onClick={() => batchInput.current?.click()}><Images /><span><strong>Upload several</strong><small>Select up to {MAX_BUSINESS_CARD_BATCH} saved card photos</small></span></button>
        </div>
        <input ref={cameraInput} className="business-card-file-input" type="file" accept="image/jpeg,image/png,image/webp" capture="environment" onChange={(event) => { chooseFile(event.currentTarget.files?.[0]); event.currentTarget.value = ""; }} />
        <input ref={photoInput} className="business-card-file-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => { chooseFile(event.currentTarget.files?.[0]); event.currentTarget.value = ""; }} />
        <input ref={batchInput} className="business-card-file-input" type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={(event) => { chooseBatchFiles(Array.from(event.currentTarget.files ?? [])); event.currentTarget.value = ""; }} />
        <p className="business-card-privacy"><ShieldCheck />Your image is sent securely for this scan and is saved only after you confirm the contact.</p>
      </div>}

      {batchComplete && <div className="business-card-batch-complete">
        <span><CheckCircle2 /></span><p className="eyebrow">Batch finished</p><h3>{savedBatchCount} contact{savedBatchCount === 1 ? "" : "s"} saved</h3><p>Each contact was created or updated after review, with its card added to the contact photo gallery when the upload succeeded. {skippedBatchCount ? `${skippedBatchCount} card${skippedBatchCount === 1 ? " was" : "s were"} skipped.` : "No cards were skipped."}</p>
        <div><button type="button" className="button ghost" onClick={() => closeScanner(true)}>Done</button><button type="button" className="button primary" onClick={() => batchInput.current?.click()}><Images />Upload another batch</button></div>
        <input ref={batchInput} className="business-card-file-input" type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={(event) => { chooseBatchFiles(Array.from(event.currentTarget.files ?? [])); event.currentTarget.value = ""; }} />
      </div>}

      {previewUrl && analyzing && <div className="business-card-analyzing">
        <div className="business-card-photo"><img src={previewUrl} alt="Business card being analyzed" /><span className="card-scan-beam" /></div>
        <div className="business-card-thinking" role="status" aria-live="polite"><span><Sparkles /></span><p><strong>{scanPhase}</strong><small>You can leave this open while the secure background scan finishes.</small></p><i /></div>
      </div>}

      {previewUrl && !analyzing && !values && <div className="business-card-error-state">
        <div className="business-card-photo"><img src={previewUrl} alt="Business card that could not be analyzed" /></div>
        <CircleAlert /><h3>We couldn’t finish this scan</h3><p>{error || "Try a brighter, straighter photo with the full card visible."}</p>
        <div>{batchMode && <button type="button" className="button ghost" onClick={() => advanceBatch("skipped")}>Skip card</button>}<button type="button" className="button ghost" onClick={() => photoInput.current?.click()}><ImagePlus />Choose another</button><button type="button" className="button primary" onClick={batchMode ? retryBatchCard : () => cameraInput.current?.click()}>{batchMode ? <RefreshCw /> : <Camera />}{batchMode ? "Try again" : "Retake photo"}</button></div>
        <input ref={cameraInput} className="business-card-file-input" type="file" accept="image/jpeg,image/png,image/webp" capture="environment" onChange={(event) => { chooseFile(event.currentTarget.files?.[0]); event.currentTarget.value = ""; }} />
        <input ref={photoInput} className="business-card-file-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => { chooseFile(event.currentTarget.files?.[0]); event.currentTarget.value = ""; }} />
      </div>}

      {values && <form className="business-card-review" onSubmit={submit}>
        <div className="business-card-review-intro"><span><Sparkles /></span><div><strong>Review what we found</strong><p>Nothing has been saved yet. Correct anything that doesn’t look right.</p></div><button type="button" className="button ghost" disabled={saving} onClick={() => photoInput.current?.click()}><RefreshCw />Rescan</button></div>
        <input ref={photoInput} className="business-card-file-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => { chooseFile(event.currentTarget.files?.[0]); event.currentTarget.value = ""; }} />
        <div className="business-card-review-layout">
          <aside><div className="business-card-photo"><img src={previewUrl} alt="Scanned business card" /></div><p><ShieldCheck />This card will be saved in the contact’s photo gallery, never as their avatar.</p><button type="button" className="button ghost business-card-mobile-rescan" disabled={saving} onClick={() => photoInput.current?.click()}><RefreshCw />Rescan</button>{values.notes.length > 0 && <div className="business-card-notes"><strong>Worth a closer look</strong>{values.notes.map((note) => <span key={note}>{note}</span>)}</div>}</aside>
          <div className="business-card-fields">
            {duplicateMatches.length > 0 && <section className="business-card-duplicate-warning" aria-label="Possible existing contact">
              <header><CircleAlert /><div><strong>This person may already be in the address book</strong><p>{duplicateMatches[0].reasons.map((reason) => reason.label).join(" · ")}. Choose how to save this card.</p></div></header>
              {duplicateMatches.length > 1 && <label>Possible match<select value={duplicateContactId} onChange={(event) => setDuplicateContactId(event.target.value)}>{duplicateMatches.map((match) => <option value={match.contact.id} key={match.contact.id}>{match.contact.name} · {contactOrganizationNames(match.contact)}</option>)}</select></label>}
              {selectedDuplicate && <div className="business-card-duplicate-actions"><label className={duplicateAction === "update" ? "selected" : ""}><input type="radio" name="duplicate-action" checked={duplicateAction === "update"} onChange={() => setDuplicateAction("update")} /><ContactAvatar name={selectedDuplicate.contact.name} photoId={selectedDuplicate.contact.primaryPhotoId} className="business-card-duplicate-avatar" /><span><strong>Update {selectedDuplicate.contact.name}</strong><small>Apply the reviewed details and save this card to their photos</small></span></label><label className={duplicateAction === "create" ? "selected" : ""}><input type="radio" name="duplicate-action" checked={duplicateAction === "create"} onChange={() => setDuplicateAction("create")} /><UserPlus /><span><strong>Create a separate contact</strong><small>Use this only when these are different people</small></span></label></div>}
            </section>}
            <fieldset className="card-destination"><legend>Where should this person go?</legend>
              <label className={destination === "existing" ? "selected" : ""}><input type="radio" name="card-destination" value="existing" checked={destination === "existing"} disabled={!sources.length} onChange={() => setDestination("existing")} /><Building2 /><span><strong>Existing {sourceLabelSingular.toLowerCase()}</strong><small>Add this person as another contact</small></span></label>
              <label className={destination === "new" ? "selected" : ""}><input type="radio" name="card-destination" value="new" checked={destination === "new"} disabled={!canCreateSource} onChange={() => setDestination("new")} /><Plus /><span><strong>New {sourceLabelSingular.toLowerCase()}</strong><small>Create the organization and contact</small></span></label>
            </fieldset>

            {destination === "existing" && <div className="card-source-choice">
              {matchedSource && <p><Sparkles />Suggested match · {Math.round(matchedSource.score * 100)}% name match</p>}
              <label>Referral source<select required value={sourceId} onChange={(event) => setSourceId(event.target.value)}><option value="">Choose an organization</option>{sources.map((source) => <option key={source.id} value={source.id}>{source.name}</option>)}</select></label>
            </div>}

            <section className="card-review-section"><div><h3>Organization</h3><CardConfidence score={fieldConfidence("organizationName")} /></div>
              <label>Organization name<input required={destination === "new"} maxLength={180} value={organizationName} onChange={(event) => setOrganizationName(event.target.value)} placeholder="Organization on the card" /></label>
              {destination === "new" && <label>Source category<select value={category} onChange={(event) => setCategory(event.target.value)}>{[...new Set([...categories, "Other"])].map((option) => <option key={option}>{option}</option>)}</select></label>}
              <label><span>Website <CardConfidence score={fieldConfidence("website")} /></span><input maxLength={300} inputMode="url" value={website} onChange={(event) => setWebsite(event.target.value)} placeholder="Website on the card" /><small>{destination === "existing" ? "Shown for review; the existing source website will not be changed." : "This will be saved on the new source."}</small></label>
            </section>

            <section className="card-review-section"><div><h3>Contact</h3><span className="card-review-required">Review required</span></div>
              <div className="form-row"><label><span>Name <CardConfidence score={fieldConfidence("personName")} /></span><input required maxLength={120} value={personName} onChange={(event) => setPersonName(event.target.value)} placeholder="Full name" /></label><label><span>Title or role <CardConfidence score={fieldConfidence("jobTitle")} /></span><input maxLength={120} value={jobTitle} onChange={(event) => setJobTitle(event.target.value)} placeholder="Title or role" /></label></div>
              <div className="contact-phone-fields"><label><span>Work / direct phone <CardConfidence score={fieldConfidence("phone")} /></span><input maxLength={40} inputMode="tel" value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="Office or direct number" /></label><label><span>Mobile / cell <CardConfidence score={fieldConfidence("mobilePhone")} /></span><input maxLength={40} inputMode="tel" value={mobilePhone} onChange={(event) => setMobilePhone(event.target.value)} placeholder="Mobile number" /></label><label><span>Fax <CardConfidence score={fieldConfidence("fax")} /></span><input maxLength={40} inputMode="tel" value={fax} onChange={(event) => setFax(event.target.value)} placeholder="Fax number" /></label></div>
              <label><span>Email <CardConfidence score={fieldConfidence("email")} /></span><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="name@example.org" /></label>
              <label><span>Address <CardConfidence score={fieldConfidence("address")} /></span><textarea maxLength={500} value={address} onChange={(event) => setAddress(event.target.value)} placeholder="Street, city, state, ZIP" /></label>
              <label className="address-contact-primary"><input type="checkbox" checked={isPrimary} onChange={(event) => setIsPrimary(event.target.checked)} /><span><strong>Make primary contact</strong><small>Show this person first on the organization record.</small></span></label>
              <label className="card-review-confirm"><input type="checkbox" checked={reviewConfirmed} onChange={(event) => setReviewConfirmed(event.target.checked)} /><span><strong>I reviewed these details</strong><small>Save the corrected values shown above, not just the original scan.</small></span></label>
            </section>
          </div>
        </div>
        {error && <p className="business-card-form-error"><CircleAlert />{error}</p>}
        <footer><button type="button" className="button ghost" disabled={saving} onClick={batchMode ? () => advanceBatch("skipped") : () => closeScanner()}>{batchMode ? "Skip card" : "Cancel"}</button><button className="button primary" disabled={saving || !reviewConfirmed || !personName.trim() || (!(duplicateAction === "update" && duplicateContactId) && (destination === "existing" ? !sourceId : !organizationName.trim()))}>{saving ? "Saving contact…" : batchMode ? batchHasNext ? "Save & review next" : "Save final contact" : duplicateAction === "update" && duplicateContactId ? "Update contact + save card" : destination === "existing" ? "Add contact" : `Create ${sourceLabelSingular.toLowerCase()} + contact`}<Check /></button></footer>
      </form>}
    </section>
  </div>;
}

type PostScanAction = { contactId: string; contactName: string; sourceId: string; sourceName: string };

function PostScanNextStepsModal({ saved, onClose, onViewContact, onLogInteraction, onScheduleFollowUp, onAddToEvent }: {
  saved: PostScanAction;
  onClose: () => void;
  onViewContact: () => void;
  onLogInteraction: () => void;
  onScheduleFollowUp: () => void;
  onAddToEvent: () => void;
}) {
  return <div className="modal-layer post-scan-layer" role="dialog" aria-modal="true" aria-labelledby="post-scan-title">
    <button className="modal-scrim" type="button" onClick={onClose} aria-label="Close next steps" />
    <section className="modal post-scan-modal">
      <header><span><Check /></span><div><p className="eyebrow">Contact saved</p><h2 id="post-scan-title">Keep the relationship moving</h2><p><strong>{saved.contactName}</strong> is now connected to {saved.sourceName}.</p></div><button className="icon-button" type="button" onClick={onClose} aria-label="Close"><X /></button></header>
      <div className="post-scan-actions">
        <button type="button" onClick={onViewContact}><Contact /><span><strong>View contact</strong><small>Open the profile and saved card photo</small></span><ChevronRight /></button>
        <button type="button" onClick={onLogInteraction}><Phone /><span><strong>Log an interaction</strong><small>Record the conversation or first outreach</small></span><ChevronRight /></button>
        <button type="button" onClick={onScheduleFollowUp}><CalendarPlus /><span><strong>Schedule a follow-up</strong><small>Create the next relationship task</small></span><ChevronRight /></button>
        <button type="button" onClick={onAddToEvent}><Users /><span><strong>Add to an event</strong><small>Include this contact in event attendance</small></span><ChevronRight /></button>
      </div>
      <footer><button className="button ghost" type="button" onClick={onClose}>Done for now</button></footer>
    </section>
  </div>;
}

type DirectoryContactEditorValues = {
  sourceId: string;
  organizationAction?: "transfer" | "link";
  newOrganization?: {
    name: string;
    category: string;
    website: string;
  };
  name: string;
  title: string;
  phone: string;
  mobilePhone: string;
  fax: string;
  contactMethods: ContactMethod[];
  email: string;
  address: string;
  isPrimary: boolean;
  preferredContactMethod: SourceContact["preferredContactMethod"];
  bestContactTime: string;
  contactStatus: SourceContact["contactStatus"];
  lastVerifiedAt: string;
};

function ContactEditorModal({ contact, context = "directory", sources, categories = [], canCreateSource = false, sourceLabelSingular = "Referral source", onScanBusinessCard, onClose, onSave }: {
  contact?: DirectoryContact;
  context?: "directory" | "event";
  sources: DemoSource[];
  categories?: string[];
  canCreateSource?: boolean;
  sourceLabelSingular?: string;
  onScanBusinessCard?: () => void;
  onClose: () => void;
  onSave: (values: DirectoryContactEditorValues) => Promise<boolean>;
}) {
  const [organizationMode, setOrganizationMode] = useState<"existing" | "new">(sources.length || !canCreateSource ? "existing" : "new");
  const [sourceId, setSourceId] = useState(contact?.sourceId ?? sources[0]?.id ?? "");
  const [organizationAction, setOrganizationAction] = useState<"transfer" | "link">("transfer");
  const [organizationName, setOrganizationName] = useState("");
  const [organizationCategory, setOrganizationCategory] = useState(categories[0] ?? "Other");
  const [organizationWebsite, setOrganizationWebsite] = useState("");
  const [name, setName] = useState(contact?.name ?? "");
  const [title, setTitle] = useState(contact?.title ?? "");
  const [contactMethods, setContactMethods] = useState<ContactMethod[]>(() => {
    if (contact?.contactMethods?.length) return contact.contactMethods;
    const legacy = contactMethodsFromLegacy(contact?.phone, contact?.mobilePhone, contact?.fax);
    return legacy.length ? legacy : [blankWorkContactMethod()];
  });
  const [email, setEmail] = useState(contact?.email ?? "");
  const [address, setAddress] = useState(contact?.address ?? "");
  const [isPrimary, setIsPrimary] = useState(contact?.isPrimary ?? false);
  const [preferredContactMethod, setPreferredContactMethod] = useState<SourceContact["preferredContactMethod"]>(contact?.preferredContactMethod ?? "any");
  const [bestContactTime, setBestContactTime] = useState(contact?.bestContactTime ?? "");
  const [contactStatus, setContactStatus] = useState<SourceContact["contactStatus"]>(contact?.contactStatus ?? "active");
  const [lastVerifiedAt, setLastVerifiedAt] = useState(contact?.lastVerifiedAt?.slice(0, 10) ?? "");
  const [busy, setBusy] = useState(false);
  const organizationChanged = Boolean(contact && sourceId !== contact.sourceId);
  const selectedSource = sources.find((source) => source.id === sourceId);
  const selectedSourceAlreadyLinked = Boolean(contact?.organizations.some((organization) => organization.sourceId === sourceId));

  function changeOrganization(nextSourceId: string) {
    setSourceId(nextSourceId);
    if (contact?.organizations.some((organization) => organization.sourceId === nextSourceId)) {
      setOrganizationAction("transfer");
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    const canonicalNumbers = canonicalContactNumbers(contactMethods);
    const saved = await onSave({
      sourceId: organizationMode === "existing" || contact ? sourceId : "",
      organizationAction: organizationChanged ? organizationAction : undefined,
      newOrganization: !contact && organizationMode === "new" ? {
        name: organizationName.trim(),
        category: organizationCategory,
        website: organizationWebsite.trim(),
      } : undefined,
      name: name.trim(),
      title: title.trim(),
      ...canonicalNumbers,
      contactMethods: contactMethods.map((method) => ({
        ...method,
        label: method.label.trim() || contactMethodOptions.find((option) => option.type === method.type)?.label || "Other",
        value: method.value.trim(),
      })).filter((method) => method.value),
      email: email.trim(),
      address: address.trim(),
      isPrimary: organizationMode === "new" ? true : organizationChanged && contact ? contact.isPrimary : isPrimary,
      preferredContactMethod,
      bestContactTime: bestContactTime.trim(),
      contactStatus,
      lastVerifiedAt: lastVerifiedAt ? new Date(`${lastVerifiedAt}T12:00:00`).toISOString() : "",
    });
    if (!saved) setBusy(false);
  }

  return <div className="modal-layer" role="dialog" aria-modal="true" aria-label={contact ? `Edit contact ${contact.name}` : context === "event" ? "Add event attendee" : "Add contact"}>
    <button className="modal-scrim" onClick={onClose} aria-label="Close" />
    <form className="modal compact-modal address-contact-editor" onSubmit={submit}>
      <header><div><p className="eyebrow">{context === "event" ? "Event attendee" : "Address book"}</p><h2>{contact ? "Edit contact" : context === "event" ? "Add a new attendee" : "Add contact"}</h2><p>{contact ? `Update ${contact.name} or change their referral-source relationship without leaving the directory.` : context === "event" ? "Create the contact, select them for this event, and return to attendance." : "Use an existing organization or create a new one without leaving this form."}</p></div><button type="button" className="icon-button" onClick={onClose} aria-label="Close contact editor"><X /></button></header>
      <div className="modal-body">
        {!contact && onScanBusinessCard && <button type="button" className="contact-card-import-shortcut" onClick={onScanBusinessCard}><span><ImagePlus /></span><span><strong>Import a business card photo</strong><small>Take a photo or upload one. AI fills the details for you to review before saving.</small></span><ChevronRight /></button>}
        {!contact && canCreateSource && <fieldset className="contact-organization-destination"><legend>Where does this person work?</legend><div>
          <label className={organizationMode === "existing" ? "selected" : ""}><input type="radio" name="contact-organization-mode" value="existing" checked={organizationMode === "existing"} disabled={!sources.length} onChange={() => setOrganizationMode("existing")} /><Building2 /><span><strong>Existing organization</strong><small>Choose a {sourceLabelSingular.toLowerCase()} already in the system</small></span></label>
          <label className={organizationMode === "new" ? "selected" : ""}><input type="radio" name="contact-organization-mode" value="new" checked={organizationMode === "new"} onChange={() => setOrganizationMode("new")} /><Plus /><span><strong>New organization</strong><small>Create the organization and this contact together</small></span></label>
        </div></fieldset>}
        {contact ? <section className="contact-editor-organizations" aria-label="Contact organizations">
          <div className="contact-editor-current-organizations"><span>Currently connected to</span><div>{contact.organizations.map((organization) => <span className={organization.sourceId === contact.sourceId ? "current" : "linked"} key={organization.sourceId}><Building2 /><strong>{organization.sourceName}</strong><small>{organization.sourceId === contact.sourceId ? "Current" : "Linked"}</small></span>)}</div></div>
          <label>Organization<select required value={sourceId} onChange={(event) => changeOrganization(event.target.value)}><option value="" disabled>Choose an organization</option>{sources.map((source) => { const relationship = contact.organizations.find((organization) => organization.sourceId === source.id); return <option key={source.id} value={source.id}>{source.name}{source.id === contact.sourceId ? " — Current" : relationship ? " — Linked" : ""}</option>; })}</select><small>Choose a different organization to transfer this contact or link them to both.</small></label>
          {organizationChanged && selectedSource && <fieldset className="contact-affiliation-choice contact-editor-affiliation-choice"><legend>How should {contact.name} be connected?</legend>
            <label className={organizationAction === "transfer" ? "selected" : ""}><input type="radio" name="contact-editor-affiliation-mode" value="transfer" checked={organizationAction === "transfer"} onChange={() => setOrganizationAction("transfer")} /><span><i><ArrowRight /></i><strong>{selectedSourceAlreadyLinked ? `Make ${selectedSource.name} the only organization` : `Transfer to ${selectedSource.name}`}</strong><small>Remove other organization links and make this the current referral source. The complete contact history remains attached.</small></span></label>
            <label className={`${organizationAction === "link" ? "selected" : ""}${selectedSourceAlreadyLinked ? " disabled" : ""}`}><input type="radio" name="contact-editor-affiliation-mode" value="link" checked={organizationAction === "link"} disabled={selectedSourceAlreadyLinked} onChange={() => setOrganizationAction("link")} /><span><i><Building2 /></i><strong>{selectedSourceAlreadyLinked ? `Already linked to ${selectedSource.name}` : "Link to both"}</strong><small>{selectedSourceAlreadyLinked ? "Choose transfer if this should become the contact’s only organization." : `Keep the current organization and also show this contact under ${selectedSource.name}.`}</small></span></label>
          </fieldset>}
        </section> : organizationMode === "existing" && <label>Organization<select required value={sourceId} onChange={(event) => setSourceId(event.target.value)}><option value="" disabled>Choose an organization</option>{sources.map((source) => <option key={source.id} value={source.id}>{source.name}</option>)}</select></label>}
        {!contact && organizationMode === "new" && <section className="contact-new-organization" aria-label="New organization details"><div className="form-row"><label>New organization name<input autoFocus required maxLength={180} value={organizationName} onChange={(event) => setOrganizationName(event.target.value)} placeholder="Organization name" /></label><label>Category<select value={organizationCategory} onChange={(event) => setOrganizationCategory(event.target.value)}>{[...new Set([...categories, "Other"])].map((option) => <option key={option}>{option}</option>)}</select></label></div><label>Organization website <span className="optional-label">Optional</span><input maxLength={300} inputMode="url" value={organizationWebsite} onChange={(event) => setOrganizationWebsite(event.target.value)} placeholder="example.org" /></label></section>}
        <div className="form-row"><label>Name<input autoFocus={organizationMode === "existing"} required maxLength={120} value={name} onChange={(event) => setName(event.target.value)} placeholder="Full name" /></label><label>Title or role<input maxLength={120} value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Director, case manager…" /></label></div>
        <ContactMethodsEditor methods={contactMethods} onChange={setContactMethods} />
        <label>Email<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="name@example.org" /></label>
        <label>Address<textarea maxLength={500} value={address} onChange={(event) => setAddress(event.target.value)} placeholder="Street, city, state, ZIP" /></label>
        <div className="form-row"><label>Preferred method<select value={preferredContactMethod} onChange={(event) => setPreferredContactMethod(event.target.value as SourceContact["preferredContactMethod"])}><option value="any">Any method</option><option value="phone">Phone</option><option value="email">Email</option><option value="text">Text message</option><option value="visit">In-person visit</option></select></label><label>Best time to reach<input maxLength={160} value={bestContactTime} onChange={(event) => setBestContactTime(event.target.value)} placeholder="Weekdays, 8–10 AM" /></label></div>
        <div className="form-row"><label>Contact status<select value={contactStatus} onChange={(event) => setContactStatus(event.target.value as SourceContact["contactStatus"])}><option value="active">Active</option><option value="former">Former contact</option><option value="unresponsive">Unresponsive</option><option value="do-not-contact">Do not contact</option></select></label><label>Last verified<input type="date" value={lastVerifiedAt} onChange={(event) => setLastVerifiedAt(event.target.value)} /></label></div>
        {organizationMode === "new" && !contact ? <div className="address-contact-primary contact-primary-confirmed"><CheckCircle2 /><span><strong>Primary contact</strong><small>As the first person added, this contact will be primary for the new organization.</small></span></div> : organizationChanged && contact && selectedSource ? <div className="address-contact-primary contact-organization-primary-note"><ShieldCheck /><span><strong>Primary status stays consistent</strong><small>{organizationAction === "transfer" ? `Primary-contact status will be set for ${selectedSource.name} when the transfer is saved.` : `Existing primary-contact settings stay unchanged. ${selectedSource.name} will make this person primary only if they are its first contact.`}</small></span></div> : <label className="address-contact-primary"><input type="checkbox" checked={isPrimary} onChange={(event) => setIsPrimary(event.target.checked)} /><span><strong>Primary contact</strong><small>Show this person first on the organization record.</small></span></label>}
      </div>
      <footer><button type="button" className="button ghost" onClick={onClose}>Cancel</button><button className="button primary" disabled={busy || !name.trim() || (organizationMode === "existing" ? !sourceId : !organizationName.trim())}>{busy ? "Saving…" : contact && organizationChanged ? organizationAction === "transfer" ? "Save & transfer" : "Save & link" : contact ? "Save contact" : context === "event" ? "Add and select" : organizationMode === "new" ? "Create organization + contact" : "Add contact"}<Check /></button></footer>
    </form>
  </div>;
}

function SnoozeFollowUpModal({ sourceName, onClose, onSave }: { sourceName: string; onClose: () => void; onSave: (date: string) => Promise<boolean> }) {
  const [date, setDate] = useState(() => dateInputValue(1));
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    if (!await onSave(date)) setBusy(false);
  }
  return (
    <div className="modal-layer" role="dialog" aria-modal="true" aria-label={`Reschedule follow-up with ${sourceName}`}>
      <button className="modal-scrim" onClick={onClose} aria-label="Close" />
      <form className="modal compact-modal snooze-modal" onSubmit={submit}>
        <header><div><p className="eyebrow">Follow-up timing</p><h2>When should this return?</h2><p>Move {sourceName} out of today’s queue until the date you choose.</p></div><button type="button" className="icon-button" onClick={onClose}><X /></button></header>
        <div className="modal-body">
          <div className="snooze-presets" role="group" aria-label="Quick reschedule dates">
            <button type="button" onClick={() => setDate(dateInputValue(1))}>Tomorrow</button>
            <button type="button" onClick={() => setDate(dateInputValue(7))}>Next week</button>
            <button type="button" onClick={() => setDate(dateInputValue(30))}>In 30 days</button>
          </div>
          <label>Follow-up date<input required type="date" min={dateInputValue(1)} value={date} onChange={(event) => setDate(event.target.value)} /></label>
        </div>
        <footer><button type="button" className="button ghost" onClick={onClose}>Cancel</button><button className="button primary" disabled={busy}>{busy ? "Rescheduling…" : "Reschedule"}<Clock3 size={16} /></button></footer>
      </form>
    </div>
  );
}

type EventEditorValues = {
  sourceId: string;
  networkingGroupId: string;
  title: string;
  location: string;
  occurredAt: string;
  description: string;
  attendeeUserIds: string[];
  attendeeSourceIds: string[];
  attendeeContactIds: string[];
  photo: File | null;
};

type NetworkingGroupEditorValues = {
  name: string;
  description: string;
  website: string;
  primaryLocation: string;
  cadenceType: NetworkingGroupCadence;
  cadenceDetails: string;
  notes: string;
};

function NetworkingGroupEditorModal({ group, onClose, onSave }: {
  group?: NetworkingGroup;
  onClose: () => void;
  onSave: (values: NetworkingGroupEditorValues) => Promise<boolean>;
}) {
  const [name, setName] = useState(group?.name ?? "");
  const [description, setDescription] = useState(group?.description ?? "");
  const [website, setWebsite] = useState(group?.website ?? "");
  const [primaryLocation, setPrimaryLocation] = useState(group?.primaryLocation ?? "");
  const [cadenceType, setCadenceType] = useState<NetworkingGroupCadence>(group?.cadenceType ?? "monthly");
  const [cadenceDetails, setCadenceDetails] = useState(group?.cadenceDetails ?? "");
  const [notes, setNotes] = useState(group?.notes ?? "");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    const saved = await onSave({ name: name.trim(), description: description.trim(), website: website.trim(), primaryLocation: primaryLocation.trim(), cadenceType, cadenceDetails: cadenceDetails.trim(), notes: notes.trim() });
    if (!saved) setBusy(false);
  }
  return <div className="modal-layer" role="dialog" aria-modal="true" aria-label={group ? "Edit networking group" : "New networking group"}>
    <button className="modal-scrim" onClick={onClose} aria-label="Close" />
    <form className="modal networking-group-editor" onSubmit={submit}>
      <header><div><p className="eyebrow">Relationship community</p><h2>{group ? "Edit networking group" : "Create networking group"}</h2><p>Keep the purpose, meeting rhythm, future dates, and team knowledge in one reusable record.</p></div><button type="button" className="icon-button" onClick={onClose}><X /></button></header>
      <div className="networking-group-editor-body">
        <label>Group name<input required autoFocus maxLength={180} value={name} onChange={(event) => setName(event.target.value)} placeholder="HPANWFL" /></label>
        <label>What is this group?<textarea maxLength={4000} value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Who participates, the group’s purpose, and why it matters to the team" /></label>
        <div className="form-row"><label>Website <small>(optional)</small><input type="text" inputMode="url" maxLength={1000} value={website} onChange={(event) => setWebsite(event.target.value)} placeholder="hpanwfl.org" /></label><label>Primary meeting location <small>(optional)</small><input maxLength={300} value={primaryLocation} onChange={(event) => setPrimaryLocation(event.target.value)} placeholder="Venue or usual meeting area" /></label></div>
        <div className="form-row"><label>How often they meet<select value={cadenceType} onChange={(event) => setCadenceType(event.target.value as NetworkingGroupCadence)}><option value="weekly">Weekly</option><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="custom">Custom schedule</option><option value="irregular">Irregular / as announced</option></select></label><label>Meeting pattern <small>(optional)</small><input maxLength={300} value={cadenceDetails} onChange={(event) => setCadenceDetails(event.target.value)} placeholder="Second Tuesday · 8:00 AM" /></label></div>
        <label>Shared notes<textarea className="large" maxLength={8000} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Membership guidance, key introductions, sponsorship details, follow-up context, or anything the team should remember" /></label>
      </div>
      <footer><button type="button" className="button ghost" onClick={onClose}>Cancel</button><button className="button primary" disabled={busy}>{busy ? "Saving…" : group ? "Save group" : "Create group"}<Check /></button></footer>
    </form>
  </div>;
}

function EventEditorModal({ event, initialGroup, initialContact, newlyCreatedContactId, canAddContacts, groups, sources, contacts, teamMembers, currentUserId, onAddContact, onPhotoDeleted, onClose, onSave }: {
  event?: WorkspaceEvent;
  initialGroup?: NetworkingGroup;
  initialContact?: DirectoryContact;
  newlyCreatedContactId?: string;
  canAddContacts: boolean;
  groups: NetworkingGroup[];
  sources: DemoSource[];
  contacts: DirectoryContact[];
  teamMembers: SourceAssignee[];
  currentUserId: string;
  onAddContact: () => void;
  onPhotoDeleted: (eventId: string, photoId: string) => void;
  onClose: () => void;
  onSave: (values: EventEditorValues) => Promise<boolean>;
}) {
  const [sourceId, setSourceId] = useState(event?.sourceId ?? "");
  const [networkingGroupId, setNetworkingGroupId] = useState(event?.networkingGroupId ?? initialGroup?.id ?? "");
  const [title, setTitle] = useState(event?.title ?? (initialGroup ? `${initialGroup.name} meeting` : ""));
  const [location, setLocation] = useState(event?.location ?? initialGroup?.primaryLocation ?? "");
  const [occurredAt, setOccurredAt] = useState(taskDateTimeValue(event?.occurredAt ?? (initialGroup ? new Date(Date.now() + 86_400_000).toISOString() : new Date().toISOString())));
  const [description, setDescription] = useState(event?.description ?? "");
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState("");
  const [existingPhotos, setExistingPhotos] = useState<EventPhoto[]>([]);
  const [photoBusyId, setPhotoBusyId] = useState("");
  const [attendeeUserIds, setAttendeeUserIds] = useState<string[]>(event?.attendees.map((attendee) => attendee.id) ?? (initialGroup ? [] : [currentUserId]));
  const [attendeeSourceIds, setAttendeeSourceIds] = useState<string[]>(event?.sourceAttendees.map((attendee) => attendee.id) ?? []);
  const [attendeeContactIds, setAttendeeContactIds] = useState<string[]>(event?.contactAttendees.map((attendee) => attendee.id) ?? (initialContact ? [initialContact.id] : []));
  const [attendanceDirectoryTab, setAttendanceDirectoryTab] = useState<"sources" | "contacts">(initialContact ? "contacts" : "sources");
  const [sourceSearch, setSourceSearch] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [selectionError, setSelectionError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!photo) {
      setPhotoPreviewUrl("");
      return;
    }
    const previewUrl = URL.createObjectURL(photo);
    setPhotoPreviewUrl(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [photo]);
  useEffect(() => {
    if (!event) return;
    const controller = new AbortController();
    fetch(`/api/events/${encodeURIComponent(event.id)}/photos`, { signal: controller.signal })
      .then(async (response) => response.ok ? response.json() as Promise<{ data?: EventPhoto[] }> : { data: [] })
      .then((result) => setExistingPhotos(Array.isArray(result.data) ? result.data : []))
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) setExistingPhotos([]);
      });
    return () => controller.abort();
  }, [event]);
  useEffect(() => {
    if (!newlyCreatedContactId) return;
    setAttendeeContactIds((current) => current.includes(newlyCreatedContactId) ? current : [...current, newlyCreatedContactId]);
    setAttendanceDirectoryTab("contacts");
    setContactSearch("");
    setSelectionError("");
  }, [newlyCreatedContactId]);
  function toggleAttendee(userId: string) {
    setAttendeeUserIds((current) => current.includes(userId) ? current.filter((id) => id !== userId) : [...current, userId]);
    setSelectionError("");
  }
  function toggleContact(contactId: string) {
    setAttendeeContactIds((current) => current.includes(contactId) ? current.filter((id) => id !== contactId) : [...current, contactId]);
    setSelectionError("");
  }
  function toggleSource(sourceId: string) {
    setAttendeeSourceIds((current) => current.includes(sourceId) ? current.filter((id) => id !== sourceId) : [...current, sourceId]);
    setSelectionError("");
  }
  const sourceOptions = useMemo(() => {
    const options: Array<{ id: string; name: string; category: string }> = sources.map((source) => ({ id: source.id, name: source.name, category: source.category }));
    for (const attendee of event?.sourceAttendees ?? []) {
      if (!options.some((source) => source.id === attendee.id)) options.push({ id: attendee.id, name: attendee.name, category: attendee.category ?? "Archived source" });
    }
    return options;
  }, [sources, event]);
  const visibleSources = sourceOptions.filter((source) => `${source.name} ${source.category}`.toLowerCase().includes(sourceSearch.toLowerCase()));
  const contactOptions = useMemo(() => {
    const options: Array<{ id: string; name: string; title?: string; sourceId: string; sourceName?: string; contactStatus: DirectoryContact["contactStatus"] }> = contacts.map((contact) => ({
      id: contact.id, name: contact.name, title: contact.title, sourceId: contact.sourceId,
      sourceName: contact.sourceName, contactStatus: contact.contactStatus,
    }));
    for (const attendee of event?.contactAttendees ?? []) {
      if (!options.some((contact) => contact.id === attendee.id)) options.push({ ...attendee, contactStatus: "active" });
    }
    return options;
  }, [contacts, event]);
  const visibleContacts = contactOptions.filter((contact) => `${contact.name} ${contact.title ?? ""} ${contact.sourceName ?? ""}`.toLowerCase().includes(contactSearch.toLowerCase()));
  async function submit(submitEvent: FormEvent) {
    submitEvent.preventDefault();
    if (!attendeeUserIds.length && !attendeeSourceIds.length && !attendeeContactIds.length && new Date(occurredAt).getTime() <= Date.now()) {
      setSelectionError("Select at least one staff member, referral source, or contact for an event that has occurred.");
      return;
    }
    setBusy(true);
    const saved = await onSave({
      sourceId,
      networkingGroupId,
      title: title.trim(),
      location: location.trim(),
      occurredAt: new Date(occurredAt).toISOString(),
      description: description.trim(),
      attendeeUserIds,
      attendeeSourceIds,
      attendeeContactIds,
      photo,
    });
    if (!saved) setBusy(false);
  }
  async function removePhoto(existingPhoto: EventPhoto) {
    if (!event || !window.confirm(`Remove ${existingPhoto.fileName} from this event?`)) return;
    setPhotoBusyId(existingPhoto.id);
    const response = await fetch(`/api/event-photos/${encodeURIComponent(existingPhoto.id)}?version=${existingPhoto.version}`, { method: "DELETE" });
    setPhotoBusyId("");
    if (!response.ok) return;
    setExistingPhotos((current) => current.filter((item) => item.id !== existingPhoto.id));
    onPhotoDeleted(event.id, existingPhoto.id);
  }
  return <div className="modal-layer" role="dialog" aria-modal="true" aria-label={event ? "Edit event" : "New event"}>
    <button className="modal-scrim" onClick={onClose} aria-label="Close" />
    <form className="modal event-editor-modal" onSubmit={submit}>
      <header><div><p className="eyebrow">Relationship event</p><h2>{event ? "Edit event" : initialGroup ? "Schedule a group meeting" : "Record or schedule an event"}</h2><p>Future events may be saved before attendance is known. Add everyone who attended after they happen.</p></div><div className="modal-header-actions"><ContextHelp topic="events-files" label="Event help" /><button type="button" className="icon-button" onClick={onClose}><X /></button></div></header>
      <div className="event-editor-body">
        <label>Event name<input required autoFocus maxLength={200} value={title} onChange={(changeEvent) => setTitle(changeEvent.target.value)} placeholder="Community networking reception" /></label>
        <div className="form-row"><label>Networking group <small>(optional)</small><select value={networkingGroupId} onChange={(changeEvent) => { const nextId = changeEvent.target.value; setNetworkingGroupId(nextId); const group = groups.find((item) => item.id === nextId); if (group && !location) setLocation(group.primaryLocation ?? ""); }}><option value="">One-time event — no group</option>{groups.map((group) => <option value={group.id} key={group.id}>{group.name}</option>)}</select></label><label>Event date and time<input required type="datetime-local" value={occurredAt} onChange={(changeEvent) => setOccurredAt(changeEvent.target.value)} /></label></div>
        <label>Primary referral source <small>(optional event context—not attendance)</small><select value={sourceId} onChange={(changeEvent) => setSourceId(changeEvent.target.value)}><option value="">No primary referral source</option>{sources.map((source) => <option value={source.id} key={source.id}>{source.name}</option>)}</select></label>
        <label>Location<input required maxLength={300} value={location} onChange={(changeEvent) => setLocation(changeEvent.target.value)} placeholder="The Havens · Main community room" /></label>
        <label>Event notes<textarea maxLength={4000} value={description} onChange={(changeEvent) => setDescription(changeEvent.target.value)} placeholder="Purpose, outcomes, referral conversations, or next steps" /></label>
        <section className="event-photo-editor" aria-labelledby="event-photo-heading">
          <div className="event-photo-heading"><span><Camera /></span><div><h3 id="event-photo-heading">Event photos</h3><p>Add a photo from the meeting, venue, presentation, or networking group.</p></div></div>
          <label className={`event-photo-picker${photo ? " selected" : ""}`}>
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(changeEvent) => {
              const selected = changeEvent.target.files?.[0] ?? null;
              if (selected && selected.size > 10 * 1024 * 1024) {
                setSelectionError("Event photos must be 10 MB or smaller.");
                changeEvent.target.value = "";
                return;
              }
              setSelectionError("");
              setPhoto(selected);
            }} />
            {photoPreviewUrl ? <img src={photoPreviewUrl} alt="Selected event preview" /> : <span><Upload /></span>}
            <div><strong>{photo?.name ?? (event ? "Add another photo" : "Choose a photo")}</strong><small>{photo ? `${attachmentSizeLabel(photo.size)} · ready to upload when saved` : "JPEG, PNG, or WebP · up to 10 MB"}</small></div>
            <em>{photo ? "Change" : "Browse"}</em>
          </label>
          {photo && <button type="button" className="event-photo-clear" onClick={() => setPhoto(null)}><X />Remove selected photo</button>}
          {existingPhotos.length > 0 && <div className="event-photo-gallery">{existingPhotos.map((existingPhoto) => <article key={existingPhoto.id}><a href={`/api/event-photos/${existingPhoto.id}/content`} target="_blank" rel="noreferrer"><img src={`/api/event-photos/${existingPhoto.id}/content`} alt={existingPhoto.caption || existingPhoto.fileName} /></a><div><strong>{existingPhoto.fileName}</strong><small>{attachmentSizeLabel(existingPhoto.sizeBytes)} · {existingPhoto.createdBy}</small></div><button type="button" disabled={photoBusyId === existingPhoto.id} onClick={() => void removePhoto(existingPhoto)} aria-label={`Remove ${existingPhoto.fileName}`}><Trash2 /></button></article>)}</div>}
        </section>
        <fieldset className="event-attendees"><legend><span><Users />Staff who attended</span><small>Select every team member who was present.</small></legend><div className="event-attendee-actions"><button type="button" onClick={() => setAttendeeUserIds(teamMembers.map((member) => member.id))}>Select all</button><button type="button" onClick={() => setAttendeeUserIds([])}>Clear</button></div><div className="event-attendee-options">{teamMembers.map((teamMember) => <label className={attendeeUserIds.includes(teamMember.id) ? "selected" : ""} key={teamMember.id}><input type="checkbox" checked={attendeeUserIds.includes(teamMember.id)} onChange={() => toggleAttendee(teamMember.id)} /><span className="record-avatar">{teamMember.name.trim().slice(0, 1).toUpperCase()}</span><span><strong>{teamMember.name}</strong><small>{workspaceRoleLabel(teamMember.role)}</small></span><Check /></label>)}</div></fieldset>
        <fieldset className="event-attendees event-contact-attendees"><legend><span><Building2 />Organizations and contacts who attended</span><small>Track organizations separately from individual people.</small></legend>
          <div className="event-attendance-tabs" role="tablist" aria-label="External attendee type"><button type="button" role="tab" aria-selected={attendanceDirectoryTab === "sources"} className={attendanceDirectoryTab === "sources" ? "active" : ""} onClick={() => setAttendanceDirectoryTab("sources")}><Building2 /><span><strong>Referral sources</strong><small>Organizations</small></span><em>{attendeeSourceIds.length}</em></button><button type="button" role="tab" aria-selected={attendanceDirectoryTab === "contacts"} className={attendanceDirectoryTab === "contacts" ? "active" : ""} onClick={() => setAttendanceDirectoryTab("contacts")}><Contact /><span><strong>Contacts</strong><small>Individual people</small></span><em>{attendeeContactIds.length}</em></button></div>
          {attendanceDirectoryTab === "sources" ? <div role="tabpanel" aria-label="Referral sources who attended" className="event-attendance-panel">
            <div className="event-contact-toolbar"><label><Search /><input aria-label="Search referral sources" value={sourceSearch} onChange={(changeEvent) => setSourceSearch(changeEvent.target.value)} placeholder="Search organizations or categories" /></label><span>{attendeeSourceIds.length} selected</span></div>
            <div className="event-attendee-actions"><button type="button" onClick={() => setAttendeeSourceIds([...new Set([...attendeeSourceIds, ...visibleSources.map((source) => source.id)])])}>Select all shown</button><button type="button" onClick={() => setAttendeeSourceIds([])}>Clear</button></div>
            <div className="event-attendee-options source-options">{visibleSources.map((source) => <label className={attendeeSourceIds.includes(source.id) ? "selected" : ""} key={source.id}><input type="checkbox" checked={attendeeSourceIds.includes(source.id)} onChange={() => toggleSource(source.id)} /><span className="record-avatar source-avatar"><Building2 /></span><span><strong>{source.name}</strong><small>{source.category}</small></span><Check /></label>)}</div>
            {!visibleSources.length && <div className="event-contact-empty compact"><Search /><span><strong>No matching referral sources</strong><small>Try a different organization name or category.</small></span></div>}
          </div> : <div role="tabpanel" aria-label="Contacts who attended" className="event-attendance-panel">{contactOptions.length ? <>
            <div className="event-contact-toolbar"><label><Search /><input aria-label="Search all contacts" value={contactSearch} onChange={(changeEvent) => setContactSearch(changeEvent.target.value)} placeholder="Search people or organizations" /></label><span>{attendeeContactIds.length} selected</span>{canAddContacts && <button type="button" className="event-add-contact" onClick={onAddContact}><UserPlus />New contact</button>}</div>
            <div className="event-attendee-actions"><button type="button" onClick={() => setAttendeeContactIds([...new Set([...attendeeContactIds, ...visibleContacts.map((contact) => contact.id)])])}>Select all shown</button><button type="button" onClick={() => setAttendeeContactIds([])}>Clear</button></div>
            <div className="event-attendee-options contact-options">{visibleContacts.map((contact) => <label className={attendeeContactIds.includes(contact.id) ? "selected" : ""} key={contact.id}><input type="checkbox" checked={attendeeContactIds.includes(contact.id)} onChange={() => toggleContact(contact.id)} /><span className="record-avatar contact-avatar">{contact.name.trim().slice(0, 1).toUpperCase()}</span><span><strong>{contact.name}</strong><small>{[contact.title, contact.sourceName, contact.contactStatus !== "active" ? contactStatusLabel(contact.contactStatus) : ""].filter(Boolean).join(" · ") || "Address-book contact"}</small></span><Check /></label>)}</div>
            {!visibleContacts.length && <div className="event-contact-empty compact"><Search /><span><strong>No matching contacts</strong><small>Create this person now or try a different search.</small></span>{canAddContacts && <button type="button" className="event-empty-add" onClick={onAddContact}><UserPlus />Add “{contactSearch.trim() || "new contact"}”</button>}</div>}
          </> : <div className="event-contact-empty"><Users /><span><strong>No contacts in the address book</strong><small>Create the first attendee without leaving this event.</small></span>{canAddContacts && <button type="button" className="event-empty-add" onClick={onAddContact}><UserPlus />Add contact</button>}</div>}</div>}
          {selectionError && <p className="form-error"><CircleAlert />{selectionError}</p>}
          {!attendeeUserIds.length && !attendeeSourceIds.length && !attendeeContactIds.length && new Date(occurredAt).getTime() > Date.now() && <p className="event-future-attendance-note"><CalendarPlus />Attendance can be added after this scheduled meeting occurs.</p>}
        </fieldset>
      </div>
      <footer><button type="button" className="button ghost" onClick={onClose}>Cancel</button><button className="button primary" disabled={busy}>{busy ? "Saving…" : event ? "Save event" : new Date(occurredAt).getTime() > Date.now() ? "Schedule event" : "Record event"}<Check size={16} /></button></footer>
    </form>
  </div>;
}

type TaskEditorValues = {
  sourceId: string;
  title: string;
  description: string;
  assigneeUserId: string;
  dueAt: string;
  priority: DemoTask["priority"];
  recurrenceType: "" | "daily" | "weekly" | "monthly" | "custom";
  recurrenceDays?: number;
};

function taskDateTimeValue(value?: string) {
  const date = value ? new Date(value) : new Date(Date.now() + 86_400_000);
  date.setSeconds(0, 0);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function TaskEditorModal({ task, sourceId, sources, assignees, currentUser, canAssign, onClose, onSave }: {
  task?: DemoTask;
  sourceId?: string;
  sources: DemoSource[];
  assignees: SourceAssignee[];
  currentUser: SessionMember;
  canAssign: boolean;
  onClose: () => void;
  onSave: (values: TaskEditorValues) => Promise<boolean>;
}) {
  const initialSourceId = task?.sourceId ?? sourceId ?? "";
  const initialSource = sources.find((source) => source.id === initialSourceId);
  const [selectedSourceId, setSelectedSourceId] = useState(initialSourceId);
  const [title, setTitle] = useState(task?.title ?? (initialSource ? `Follow up with ${initialSource.name}` : ""));
  const [description, setDescription] = useState(task?.description ?? "");
  const [assigneeUserId, setAssigneeUserId] = useState(task?.assigneeUserId ?? initialSource?.ownerUserId ?? currentUser.userId);
  const [dueAt, setDueAt] = useState(taskDateTimeValue(task?.dueAtIso));
  const [priority, setPriority] = useState<DemoTask["priority"]>(task?.priority ?? "normal");
  const [recurrenceType, setRecurrenceType] = useState<TaskEditorValues["recurrenceType"]>(task?.recurrenceType ?? "");
  const [recurrenceDays, setRecurrenceDays] = useState(String(task?.recurrenceDays ?? 14));
  const [busy, setBusy] = useState(false);
  function changeSource(nextId: string) {
    const previous = sources.find((source) => source.id === selectedSourceId);
    const next = sources.find((source) => source.id === nextId);
    setSelectedSourceId(nextId);
    if (!task && (!title || title === `Follow up with ${previous?.name}`)) setTitle(next ? `Follow up with ${next.name}` : "");
    if (!task && next?.ownerUserId) setAssigneeUserId(next.ownerUserId);
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    const saved = await onSave({
      sourceId: selectedSourceId,
      title: title.trim(),
      description: description.trim(),
      assigneeUserId,
      dueAt: new Date(dueAt).toISOString(),
      priority,
      recurrenceType,
      recurrenceDays: recurrenceType === "custom" ? Number(recurrenceDays) : undefined,
    });
    if (!saved) setBusy(false);
  }
  return <div className="modal-layer" role="dialog" aria-modal="true" aria-label={task ? "Edit task" : "New task"}>
    <button className="modal-scrim" onClick={onClose} aria-label="Close" />
    <form className="modal task-editor-modal" onSubmit={submit}>
      <header><div><p className="eyebrow">Team work</p><h2>{task ? "Edit task" : "Create a task"}</h2></div><div className="modal-header-actions"><ContextHelp topic="tasks" label="Task help" /><button type="button" className="icon-button" onClick={onClose}><X /></button></div></header>
      <p className="form-intro">Set a clear next step, owner, timing, and urgency.</p>
      <label>Referral source <small>(optional)</small><select value={selectedSourceId} onChange={(event) => changeSource(event.target.value)} disabled={Boolean(task)}><option value="">General workspace task</option>{sources.map((source) => <option value={source.id} key={source.id}>{source.name}</option>)}</select></label>
      <label>Task title<input required maxLength={200} value={title} onChange={(event) => setTitle(event.target.value)} autoFocus placeholder="Prepare the weekly outreach summary" /></label>
      <label>Details<textarea maxLength={2000} value={description} onChange={(event) => setDescription(event.target.value)} placeholder="What should happen, and what context will help?" /></label>
      <div className="form-row"><label>Due date and time<input required type="datetime-local" value={dueAt} onChange={(event) => setDueAt(event.target.value)} /></label><label>Priority<select value={priority} onChange={(event) => setPriority(event.target.value as DemoTask["priority"])}><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option></select></label></div>
      <div className="form-row"><label>Assigned to<select value={assigneeUserId} disabled={!canAssign} onChange={(event) => setAssigneeUserId(event.target.value)}>{assignees.map((assignee) => <option value={assignee.id} key={assignee.id}>{assignee.name}{assignee.id === currentUser.userId ? " (you)" : ""}</option>)}</select></label><label>Repeat<select value={recurrenceType} onChange={(event) => setRecurrenceType(event.target.value as TaskEditorValues["recurrenceType"])}><option value="">Does not repeat</option><option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option><option value="custom">Custom interval</option></select></label></div>
      {recurrenceType === "custom" && <label>Repeat every <span className="inline-number"><input required min="1" max="3650" type="number" value={recurrenceDays} onChange={(event) => setRecurrenceDays(event.target.value)} /> days</span></label>}
      <footer><button type="button" className="button ghost" onClick={onClose}>Cancel</button><button className="button primary" disabled={busy}>{busy ? "Saving…" : task ? "Save task" : "Create task"}<Check size={16} /></button></footer>
    </form>
  </div>;
}

function LogContactModal({ source, contact, contacts, events, followUpTitle, activityTypes, teamMembers, currentUserId, onClose, onSave }: { source: DemoSource; contact?: Pick<DirectoryContact, "id" | "name">; contacts: DirectoryContact[]; events: WorkspaceEvent[]; followUpTitle?: string; activityTypes: ActiveLookupOption[]; teamMembers: SourceAssignee[]; currentUserId: string; onClose: () => void; onSave: (activity: LogContactInput) => Promise<boolean> }) {
  const availableActivityTypes = activityTypes.length ? activityTypes : [
    { type: "activity_type" as const, code: "call", label: "Phone call" },
    { type: "activity_type" as const, code: "email", label: "Email" },
    { type: "activity_type" as const, code: "marketing", label: "Marketing" },
    { type: "activity_type" as const, code: "event", label: "Event" },
    { type: "activity_type" as const, code: "visit", label: "Visit" },
    { type: "activity_type" as const, code: "meeting", label: "Meeting" },
    { type: "activity_type" as const, code: "note", label: "Note" },
  ];
  const [activityTypeCode, setActivityTypeCode] = useState(() => availableActivityTypes.some((option) => option.code === "call") ? "call" : availableActivityTypes[0].code);
  const [summary, setSummary] = useState("");
  const [notes, setNotes] = useState("");
  const [sourceContactId, setSourceContactId] = useState(contact?.id ?? "");
  const [eventId, setEventId] = useState("");
  const [customContext, setCustomContext] = useState("");
  const [referrals, setReferrals] = useState<Array<{ id: string; receivedByUserId: string; name: string }>>([]);
  const [busy, setBusy] = useState(false);
  const pastEvents = events
    .filter((workspaceEvent) => new Date(workspaceEvent.occurredAt).getTime() <= Date.now() + 300_000)
    .sort((left, right) => new Date(right.occurredAt).getTime() - new Date(left.occurredAt).getTime());
  const selectedEvent = pastEvents.find((workspaceEvent) => workspaceEvent.id === eventId);
  const contextPreset = eventId.startsWith("context:") ? eventId.slice("context:".length) : "";
  const contextLabel = contextPreset === "Other" ? customContext.trim() : contextPreset;
  const outcomeSuggestions = activityTypeCode === "call" || activityTypeCode === "phone-call"
    ? ["Spoke with contact", "Left voicemail", "No answer"]
    : activityTypeCode === "email"
      ? ["Sent follow-up email", "Received email response"]
      : activityTypeCode === "marketing"
        ? ["Shared program information", "Dropped off materials", "Checked on referrals"]
        : activityTypeCode === "event"
          ? ["Attended event", "Hosted event", "Invited contact to event"]
      : activityTypeCode === "visit"
        ? ["Completed in-person visit", "Dropped off materials"]
        : activityTypeCode === "meeting"
          ? ["Completed meeting", "Scheduled meeting"]
          : ["Added relationship update"];
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    const saved = await onSave({
      activityTypeCode,
      occurredAt: selectedEvent?.occurredAt ?? new Date().toISOString(),
      summary,
      notes,
      ...(sourceContactId ? { sourceContactId } : {}),
      ...(selectedEvent ? { eventId: selectedEvent.id } : {}),
      ...(contextLabel ? { contextLabel } : {}),
      referrals: referrals.map(({ receivedByUserId, name }) => ({ receivedByUserId, name: name.trim() })),
    });
    if (!saved) setBusy(false);
  }
  return (
    <div className="modal-layer" role="dialog" aria-modal="true" aria-label={contact ? `Log interaction with ${contact.name}` : `Log contact with ${source.name}`}>
      <button className="modal-scrim" onClick={onClose} aria-label="Close" />
      <form className="modal contact-modal" onSubmit={submit}>
        <header><div><p className="eyebrow">{contact ? "Individual contact" : "Source outreach"}</p><h2>{contact ? "Log interaction" : "Log contact"}</h2></div><div className="modal-header-actions"><ContextHelp topic="log-contact" label="Contact help" /><button type="button" className="icon-button" onClick={onClose}><X /></button></div></header>
        <p className="form-intro">Record the latest contact with <strong>{contact?.name ?? source.name}</strong>{contact ? ` at ${source.name}` : ""}. This updates the organization’s “last contacted” date.</p>
        {followUpTitle && <div className="contact-followup-note"><CheckCircle2 /><span><small>Completes follow-up</small><strong>{followUpTitle}</strong></span></div>}
        {!contact && <label>Who did you speak with?<select value={sourceContactId} onChange={(event) => setSourceContactId(event.target.value)}><option value="">Organization / no specific person</option>{contacts.map((person) => <option value={person.id} key={person.id}>{person.name}{person.title ? ` — ${person.title}` : ""}</option>)}</select><small>Select the individual at {source.name} so their contact history is updated too.</small></label>}
        <label>Where did this conversation happen? <small>(optional)</small><select value={eventId} onChange={(event) => { setEventId(event.target.value); if (event.target.value !== "context:Other") setCustomContext(""); }}><option value="">No meeting context</option><optgroup label="Quick meeting types"><option value="context:Breakfast">Breakfast</option><option value="context:Lunch">Lunch</option><option value="context:Coffee">Coffee</option><option value="context:Dinner">Dinner</option><option value="context:Office visit">Office visit</option><option value="context:Networking">Networking</option><option value="context:Other">Other…</option></optgroup>{pastEvents.length > 0 && <optgroup label="Recorded workspace events">{pastEvents.map((workspaceEvent) => <option value={workspaceEvent.id} key={workspaceEvent.id}>{workspaceEvent.title} · {eventDateLabel(workspaceEvent.occurredAt)}{workspaceEvent.location ? ` · ${workspaceEvent.location}` : ""}</option>)}</optgroup>}</select><small>Choose a quick meeting type, or link a recorded event to update its attendance.</small></label>
        {contextPreset === "Other" && <label>Meeting context<input required autoFocus maxLength={80} value={customContext} onChange={(event) => setCustomContext(event.target.value)} placeholder="For example: Community tour" /><small>Use a short, general label without sensitive details.</small></label>}
        {selectedEvent && <div className="contact-event-link"><CalendarDays /><span><small>Linked event</small><strong>{selectedEvent.title}</strong><em>{eventDateLabel(selectedEvent.occurredAt)} · {selectedEvent.location}</em></span></div>}
        {contextLabel && <div className="contact-event-link generic"><MapPin /><span><small>Meeting context</small><strong>{contextLabel}</strong><em>Saved to this interaction without creating an event.</em></span></div>}
        <label>Action type<select value={activityTypeCode} onChange={(event) => setActivityTypeCode(event.target.value)}>{availableActivityTypes.map((option) => <option value={option.code} key={option.code}>{option.label}</option>)}</select><small>Choose what kind of outreach or relationship activity occurred.</small></label>
        <div className="outcome-suggestions"><small>Quick outcome</small><div>{outcomeSuggestions.map((outcome) => <button type="button" className={summary === outcome ? "selected" : ""} aria-pressed={summary === outcome} onClick={() => setSummary(outcome)} key={outcome}>{outcome}</button>)}</div></div>
        <label>What happened?<input required value={summary} onChange={(event) => setSummary(event.target.value)} placeholder="Brief summary" autoFocus /></label>
        <label>Notes<textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Optional details" /></label>
        <section className={`contact-referral-capture${referrals.length ? " active" : ""}`}>
          <label className="contact-referral-toggle"><input type="checkbox" checked={Boolean(referrals.length)} onChange={(event) => setReferrals(event.target.checked ? [{ id: crypto.randomUUID(), receivedByUserId: currentUserId, name: "" }] : [])} /><span><strong>Referral{referrals.length > 1 ? "s" : ""} came from this {contact ? "contact" : "source"}</strong><small>Each referral is counted in the source total and staff reporting.</small></span></label>
          {Boolean(referrals.length) && <div className="contact-referral-list">
            {referrals.map((referral, index) => <div className="contact-referral-fields" key={referral.id}>
              <div className="referral-row-heading"><strong>Referral {index + 1}</strong>{referrals.length > 1 && <button type="button" onClick={() => setReferrals((current) => current.filter((item) => item.id !== referral.id))}>Remove</button>}</div>
              <label>Received by<select required value={referral.receivedByUserId} onChange={(event) => setReferrals((current) => current.map((item) => item.id === referral.id ? { ...item, receivedByUserId: event.target.value } : item))}>{teamMembers.map((person) => <option value={person.id} key={person.id}>{person.name}{person.id === currentUserId ? " (you)" : ""}</option>)}</select><small>The staff member who received the referral.</small></label>
              <label>Referral name<input required maxLength={160} value={referral.name} onChange={(event) => setReferrals((current) => current.map((item) => item.id === referral.id ? { ...item, name: event.target.value } : item))} placeholder="Name of referred person" /><small>Name only—keep clinical details in the clinical system.</small></label>
            </div>)}
            {referrals.length < 20 && <button type="button" className="add-referral-row" onClick={() => setReferrals((current) => [...current, { id: crypto.randomUUID(), receivedByUserId: currentUserId, name: "" }])}><Plus />Add another referral</button>}
          </div>}
        </section>
        <footer><button type="button" className="button ghost" onClick={onClose}>Cancel</button><button className="button primary" disabled={busy}>{busy ? "Saving…" : contact ? "Save interaction" : "Save contact"} <Check size={16} /></button></footer>
      </form>
    </div>
  );
}

function ContactNoteModal({ contact, teamMembers, currentUserId, onClose, onSave }: {
  contact: ContactProfile;
  teamMembers: SourceAssignee[];
  currentUserId: string;
  onClose: () => void;
  onSave: (input: ContactNoteInput) => Promise<boolean>;
}) {
  const [noteText, setNoteText] = useState("");
  const [pinned, setPinned] = useState(false);
  const [mentionedUserId, setMentionedUserId] = useState("");
  const [createFollowUp, setCreateFollowUp] = useState(false);
  const [followUpDate, setFollowUpDate] = useState(() => new Date(Date.now() + 86_400_000).toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const mentionOptions = teamMembers.filter((teammate) => teammate.id !== currentUserId);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!noteText.trim()) return;
    setBusy(true);
    const saved = await onSave({
      noteText: noteText.trim(),
      pinned,
      mentionedUserIds: mentionedUserId ? [mentionedUserId] : [],
      followUp: createFollowUp && mentionedUserId ? { assigneeUserId: mentionedUserId, dueAt: new Date(`${followUpDate}T09:00:00`).toISOString() } : undefined,
    });
    if (!saved) setBusy(false);
  }
  return <div className="modal-layer" role="dialog" aria-modal="true" aria-label={`Add note for ${contact.name}`}>
    <button className="modal-scrim" onClick={onClose} aria-label="Close" />
    <form className="modal contact-note-editor-modal" onSubmit={submit}>
      <header><div><p className="eyebrow">Individual contact</p><h2>Add a note for {contact.name}</h2><p>Keep durable relationship context attached to this person.</p></div><button type="button" className="icon-button" onClick={onClose}><X /></button></header>
      <label>Contact note<textarea required autoFocus maxLength={4000} value={noteText} onChange={(event) => setNoteText(event.target.value)} placeholder={`Standing preferences or relationship context for ${contact.name}…`} /></label>
      <div className="privacy-banner"><CircleAlert /><span><strong>Keep this non-clinical</strong><small>Never include patient-specific information, diagnoses, or dates of birth.</small></span></div>
      <div className="contact-note-options"><label className="check-option"><input type="checkbox" checked={pinned} onChange={(event) => setPinned(event.target.checked)} /><StickyNote />Pin as important</label><label>Mention teammate<select value={mentionedUserId} onChange={(event) => { setMentionedUserId(event.target.value); if (!event.target.value) setCreateFollowUp(false); }}><option value="">No mention</option>{mentionOptions.map((teammate) => <option key={teammate.id} value={teammate.id}>@{teammate.name}</option>)}</select></label></div>
      {mentionedUserId && <div className="mention-follow-up"><label className="check-option"><input type="checkbox" checked={createFollowUp} onChange={(event) => setCreateFollowUp(event.target.checked)} /><CalendarPlus />Also create a follow-up</label>{createFollowUp && <label>Due date<input type="date" min={new Date().toISOString().slice(0, 10)} value={followUpDate} onChange={(event) => setFollowUpDate(event.target.value)} /></label>}</div>}
      <footer><button type="button" className="button ghost" onClick={onClose}>Cancel</button><button className="button primary" disabled={busy}>{busy ? "Saving…" : "Save note"}<Check size={16} /></button></footer>
    </form>
  </div>;
}

function Toast({ text }: { text: string }) {
  return <div className="toast" role="status" aria-live="polite" aria-atomic="true"><CheckCircle2 size={18} /><span>{text}</span></div>;
}

export function App() {
  const urlParameters = new URLSearchParams(window.location.search);
  const initialView = urlParameters.get("view");
  const initialSourceId = urlParameters.get("source");
  const initialContactId = urlParameters.get("contact");
  const initialNetworkingGroupId = urlParameters.get("group");
  const initialEventId = urlParameters.get("event");
  const initialHelpTopic = urlParameters.get("topic");
  const resetToken = urlParameters.get("token");
  const resetError = urlParameters.get("error");
  const [auth, setAuth] = useState<AuthState>("loading");
  const [member, setMember] = useState<SessionMember | null>(null);
  const [assignees, setAssignees] = useState<SourceAssignee[]>([]);
  const [lookupOptions, setLookupOptions] = useState<ActiveLookupOption[]>([]);
  const [view, setView] = useState<View>(nav.some((item) => item.id === initialView) ? (initialView as View) : "sources");
  const [helpTopicId, setHelpTopicId] = useState(helpTopicById.has(initialHelpTopic ?? "") ? String(initialHelpTopic) : "getting-started");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tasks, setTasks] = useState<DemoTask[]>([]);
  const [events, setEvents] = useState<WorkspaceEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(initialEventId);
  const [networkingGroups, setNetworkingGroups] = useState<NetworkingGroup[]>([]);
  const [eventsSection, setEventsSection] = useState<"events" | "groups">(initialNetworkingGroupId || urlParameters.get("section") === "groups" ? "groups" : "events");
  const [selectedNetworkingGroupId, setSelectedNetworkingGroupId] = useState<string | null>(null);
  const [selectedNetworkingGroup, setSelectedNetworkingGroup] = useState<NetworkingGroupDetail | null>(null);
  const [networkingGroupLoading, setNetworkingGroupLoading] = useState(false);
  const [initialNetworkingGroupHandled, setInitialNetworkingGroupHandled] = useState(false);
  const [directoryContacts, setDirectoryContacts] = useState<DirectoryContact[]>([]);
  const [selectedDirectoryContactId, setSelectedDirectoryContactId] = useState<string | null>(null);
  const [selectedDirectoryContact, setSelectedDirectoryContact] = useState<ContactProfile | null>(null);
  const [contactProfileLoading, setContactProfileLoading] = useState(false);
  const [initialContactHandled, setInitialContactHandled] = useState(false);
  const [sourceRecords, setSourceRecords] = useState<DemoSource[]>([]);
  const [sourceActivity, setSourceActivity] = useState<SourceActivity[]>([]);
  const [leadRecords, setLeadRecords] = useState<LeadSummary[]>([]);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [selectedLead, setSelectedLead] = useState<LeadDetail | null>(null);
  const [leadLoading, setLeadLoading] = useState(false);
  const [creatingLead, setCreatingLead] = useState(false);
  const [loggingLeadActivity, setLoggingLeadActivity] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [dataState, setDataState] = useState<"loading" | "ready" | "error">("loading");
  const [freshness, setFreshness] = useState<"syncing" | "current" | "reconnecting">("syncing");
  const [dataReloadKey, setDataReloadKey] = useState(0);
  const hasWorkspaceData = useRef(false);
  const workspaceRevision = useRef<string | null>(null);
  const revisionRequestActive = useRef(false);
  const initialSourceLoaded = useRef(false);
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);
  const [sourceDetailLoading, setSourceDetailLoading] = useState(false);
  const [loggingSourceId, setLoggingSourceId] = useState<string | null>(null);
  const [loggingTaskId, setLoggingTaskId] = useState<string | null>(null);
  const [loggingSourceContactId, setLoggingSourceContactId] = useState<string | null>(null);
  const [editingSourceId, setEditingSourceId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [snoozingFollowUp, setSnoozingFollowUp] = useState<FollowUpItem | null>(null);
  const [taskEditor, setTaskEditor] = useState<{ task?: DemoTask; sourceId?: string } | null>(null);
  const [eventEditor, setEventEditor] = useState<WorkspaceEvent | "new" | null>(null);
  const [eventEditorGroupId, setEventEditorGroupId] = useState<string | null>(null);
  const [eventEditorContactId, setEventEditorContactId] = useState<string | null>(null);
  const [eventAttendeeManagerOpen, setEventAttendeeManagerOpen] = useState(false);
  const [eventContactEditorOpen, setEventContactEditorOpen] = useState(false);
  const [eventAddedContactId, setEventAddedContactId] = useState<string | null>(null);
  const [networkingGroupEditor, setNetworkingGroupEditor] = useState<NetworkingGroup | "new" | null>(null);
  const [contactEditor, setContactEditor] = useState<DirectoryContact | "new" | null>(null);
  const [businessCardScannerOpen, setBusinessCardScannerOpen] = useState(false);
  const [postScanAction, setPostScanAction] = useState<PostScanAction | null>(null);
  const [duplicateReviewOpen, setDuplicateReviewOpen] = useState(false);
  const [loggingDirectoryContact, setLoggingDirectoryContact] = useState<ContactProfile | null>(null);
  const [contactNoteEditor, setContactNoteEditor] = useState<ContactProfile | null>(null);
  const [toast, setToast] = useState("");
  const [logoutBusy, setLogoutBusy] = useState(false);

  function openHelp(topicId: string, historyMode: "push" | "replace" = "push") {
    const safeTopicId = helpTopicById.has(topicId) ? topicId : "getting-started";
    setHelpTopicId(safeTopicId);
    setView("help");
    setMobileOpen(false);
    updateAppHistory(viewPath("help", { topic: safeTopicId }), historyMode);
  }

  function navigateToView(nextView: View, historyMode: "push" | "replace" = "push") {
    setSelectedDirectoryContactId(null);
    setSelectedDirectoryContact(null);
    setSelectedNetworkingGroupId(null);
    setSelectedNetworkingGroup(null);
    setSelectedEventId(null);
    setSelectedSourceId(null);
    setSelectedLeadId(null);
    setSelectedLead(null);
    setView(nextView);
    setMobileOpen(false);
    if (nextView === "help") {
      openHelp(helpTopicId, historyMode);
      return;
    }
    updateAppHistory(viewPath(nextView, nextView === "events" ? { section: eventsSection } : {}), historyMode);
  }

  function changeEventsSection(section: "events" | "groups", historyMode: "push" | "replace" = "push") {
    setSelectedNetworkingGroupId(null);
    setSelectedNetworkingGroup(null);
    setSelectedEventId(null);
    setEventsSection(section);
    updateAppHistory(viewPath("events", { section }), historyMode);
  }

  function currentViewHelpTopic() {
    const topicByView: Record<View, string> = {
      sources: "sources",
      contacts: "contacts",
      leads: "sources",
      today: "tasks",
      events: "events-files",
      growth: "growth-operations",
      reports: "reports",
      admin: "admin-overview",
      platform: "admin-overview",
      help: helpTopicId,
    };
    return topicByView[view];
  }

  useEffect(() => {
    fetch("/api/me", { headers: { accept: "application/json" } })
      .then(async (response) => {
        if (!response.ok) {
          setAuth("signed-out");
          return;
        }
        const result = (await response.json()) as { data: SessionMember };
        setMember(result.data);
        setAssignees([{ id: result.data.userId, name: result.data.name, email: "", role: result.data.role }]);
        if (result.data.organizationIndependent) {
          setView("platform");
          updateAppHistory(viewPath("platform"), "replace");
        } else if (!canViewAppSection(result.data.role, view, result.data.platformRole, result.data.individualLeadTrackingEnabled)) {
          const fallbackView = result.data.role === "task_user" ? "today" : result.data.role === "reports" ? "reports" : "sources";
          setView(fallbackView);
          updateAppHistory(viewPath(fallbackView), "replace");
        }
        setAuth("live");
      })
      .catch(() => setAuth("signed-out"));
  }, []);

  useEffect(() => {
    if (auth !== "live" || !member) return;
    if ((window.history.state as { app?: string } | null)?.app === APP_HISTORY_MARKER) return;
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const safeRoot = viewPath(member.organizationIndependent ? "platform" : "sources");
    window.history.replaceState({ app: APP_HISTORY_MARKER, entry: true }, "", safeRoot);
    window.history.pushState({ app: APP_HISTORY_MARKER }, "", currentPath);
  }, [auth, member?.organizationIndependent]);

  useEffect(() => {
    if (auth !== "live" || !member || member.organizationIndependent || view !== "contacts" || !initialContactId || initialContactHandled) return;
    setInitialContactHandled(true);
    void openDirectoryContact(initialContactId, false, "replace");
  }, [auth, member?.workspaceId, view, initialContactId, initialContactHandled]);

  useEffect(() => {
    if (auth !== "live" || !member || member.organizationIndependent || view !== "events" || !initialNetworkingGroupId || initialNetworkingGroupHandled) return;
    setInitialNetworkingGroupHandled(true);
    void openNetworkingGroup(initialNetworkingGroupId, false, "replace");
  }, [auth, member?.workspaceId, view, initialNetworkingGroupId, initialNetworkingGroupHandled]);

  useEffect(() => {
    if (auth !== "live" || !member) return;
    const restoreLocation = () => {
      const state = window.history.state as { app?: string } | null;
      if (state?.app !== APP_HISTORY_MARKER) return;
      const parameters = new URLSearchParams(window.location.search);
      const requestedView = parameters.get("view");
      const nextView = nav.some((item) => item.id === requestedView) ? requestedView as View : member.organizationIndependent ? "platform" : "sources";
      if (!canViewAppSection(member.role, nextView, member.platformRole, member.individualLeadTrackingEnabled)) {
        navigateToView(member.organizationIndependent ? "platform" : "sources", "replace");
        return;
      }
      setView(nextView);
      setMobileOpen(false);
      setEventEditor(null);
      setNetworkingGroupEditor(null);
      setContactEditor(null);
      setTaskEditor(null);
      setCreating(false);
      const sourceId = parameters.get("source");
      setSelectedSourceId(sourceId);
      if (sourceId) void openSource(sourceId, true);
      const leadId = nextView === "leads" ? parameters.get("lead") : null;
      setSelectedLeadId(leadId);
      setSelectedLead(null);
      setLeadLoading(Boolean(leadId));
      if (leadId) void openLead(leadId, true).finally(() => setLeadLoading(false));
      const section = parameters.get("section") === "groups" ? "groups" : "events";
      setEventsSection(section);
      const topic = parameters.get("topic");
      if (nextView === "help" && topic && helpTopicById.has(topic)) setHelpTopicId(topic);
      const contactId = nextView === "contacts" ? parameters.get("contact") : null;
      setSelectedDirectoryContactId(contactId);
      setSelectedDirectoryContact(null);
      setContactProfileLoading(Boolean(contactId));
      if (contactId) void openDirectoryContact(contactId, true).finally(() => setContactProfileLoading(false));
      const groupId = nextView === "events" ? parameters.get("group") : null;
      setSelectedNetworkingGroupId(groupId);
      setSelectedNetworkingGroup(null);
      setNetworkingGroupLoading(Boolean(groupId));
      if (groupId) {
        setEventsSection("groups");
        void openNetworkingGroup(groupId, true, "replace").finally(() => setNetworkingGroupLoading(false));
      }
      const eventId = nextView === "events" ? parameters.get("event") : null;
      setSelectedEventId(eventId);
      if (eventId) window.scrollTo({ top: 0 });
    };
    window.addEventListener("popstate", restoreLocation);
    return () => window.removeEventListener("popstate", restoreLocation);
  }, [auth, member?.userId, member?.role, member?.platformRole, member?.individualLeadTrackingEnabled, member?.organizationIndependent]);

  useEffect(() => {
    if (auth !== "live") return;
    if (!member) return;
    if (member.organizationIndependent) {
      setDataState("ready");
      setFreshness("current");
      return;
    }
    let cancelled = false;
    const load = async () => {
      if (!hasWorkspaceData.current) {
        setDataState("loading");
        setFreshness("syncing");
      }
      try {
        const optionalFetch = (input: string) => fetch(input).catch(() => new Response(null, { status: 503 }));
        const [taskResponse, sourceResponse, contactResponse, assigneeResponse, dashboardResponse, lookupResponse, notificationResponse, eventResponse, networkingGroupResponse, leadResponse] = await Promise.all([
          optionalFetch(canAssignTasks(member.role) ? "/api/tasks" : "/api/tasks?scope=mine"),
          fetch("/api/sources"),
          optionalFetch("/api/contacts"),
          optionalFetch(member.role === "admin" ? "/api/source-assignees" : "/api/team-members"),
          optionalFetch("/api/dashboard"),
          optionalFetch("/api/lookups"),
          optionalFetch("/api/notifications"),
          optionalFetch("/api/events"),
          optionalFetch("/api/networking-groups"),
          member.individualLeadTrackingEnabled ? optionalFetch("/api/referrals?limit=100") : Promise.resolve(null),
        ]);
        if (!sourceResponse.ok) throw new Error(`Referral sources request failed (${sourceResponse.status})`);
        const taskResult = taskResponse.ok ? await taskResponse.json() as { data?: Array<Record<string, unknown>> } : { data: [] };
        const sourceResult = (await sourceResponse.json()) as { data?: Array<Record<string, unknown>> };
        const contactResult = contactResponse.ok ? await contactResponse.json() as { data?: Array<Record<string, unknown>> } : { data: [] };
        const leadResult = leadResponse?.ok
          ? await leadResponse.json() as { data?: Array<Record<string, unknown>> }
          : { data: [] };
        const nextTasks = (Array.isArray(taskResult.data) ? taskResult.data : [])
          .map(demoTaskFromApi)
          .filter((task): task is DemoTask => Boolean(task));
        const eventResult = eventResponse.ok ? await eventResponse.json() as { data?: Array<Record<string, unknown>> } : { data: [] };
        const nextEvents = (Array.isArray(eventResult.data) ? eventResult.data : []).map(workspaceEventFromApi);
        const networkingGroupResult = networkingGroupResponse.ok ? await networkingGroupResponse.json() as { data?: Array<Record<string, unknown>> } : { data: [] };
        const nextNetworkingGroups = (Array.isArray(networkingGroupResult.data) ? networkingGroupResult.data : []).map(networkingGroupFromApi);
        const nextSources = (Array.isArray(sourceResult.data) ? sourceResult.data : []).map((row) => {
            const effectiveCadenceDays = Number(row.effectiveCadenceDays ?? row.cadenceDays ?? cadenceTypeDays(row.cadenceType));
            const usesDefaultCadence = Boolean(row.usesDefaultCadence);
            const nextContactAt = row.nextContactAt ? String(row.nextContactAt) : undefined;
            const nextFollowUpAt = earliestDate(row.nextTaskAt, nextContactAt);
            return {
              id: String(row.id),
              version: Number(row.version ?? 1),
              name: String(row.name),
              category: String(row.categoryCode ?? "Uncategorized"),
              isHospital: Boolean(row.isHospital),
              referralPriority: row.referralPriority
                ? row.referralPriority as DemoSource["referralPriority"]
                : priorityFromCadenceValue(row.cadenceType),
              contact: String(row.contactName ?? "No contact added"),
              contactPhone: row.contactPhone ? String(row.contactPhone) : undefined,
              contactEmail: row.contactEmail ? String(row.contactEmail) : undefined,
              ownerUserId: row.ownerUserId ? String(row.ownerUserId) : undefined,
              ownerName: String(row.ownerName ?? "Unassigned"),
              cadence: cadenceLabel(effectiveCadenceDays, usesDefaultCadence),
              cadenceDays: row.cadenceDays ? Number(row.cadenceDays) : undefined,
              effectiveCadenceDays,
              defaultContactDays: Number(row.defaultContactDays ?? 30),
              usesDefaultCadence,
              lastInteractionAt: formatSourceDate(row.lastInteractionAt),
              lastInteractionAtIso: row.lastInteractionAt ? String(row.lastInteractionAt) : undefined,
              nextContactAt,
              nextTaskAt: nextFollowUpAt ? formatSourceDate(nextFollowUpAt) : "Not scheduled",
              activityCount: Number(row.activityCount ?? 0),
              referralTotal: Number(row.referralTotal ?? 0),
              lastActivityTypeCode: row.lastActivityTypeCode ? String(row.lastActivityTypeCode) : undefined,
              lastActivityNotes: row.lastActivityNotes ? String(row.lastActivityNotes) : undefined,
              organizationPhone: row.organizationPhone ? String(row.organizationPhone) : undefined,
              address: row.address ? String(row.address) : undefined,
              website: row.website ? String(row.website) : undefined,
              description: row.description ? String(row.description) : undefined,
              descriptionColor: row.descriptionColor ? row.descriptionColor as DemoSource["descriptionColor"] : "gold",
              descriptionUpdatedAt: row.descriptionUpdatedAt ? String(row.descriptionUpdatedAt) : undefined,
              descriptionUpdatedBy: row.descriptionUpdatedBy ? String(row.descriptionUpdatedBy) : undefined,
              notes: row.notes ? String(row.notes) : undefined,
            };
          });
        let nextActivity: SourceActivity[] = [];
        if (dashboardResponse.ok) {
          const dashboardResult = (await dashboardResponse.json()) as { data?: { activity?: Array<Record<string, unknown>> } };
          nextActivity = (Array.isArray(dashboardResult.data?.activity) ? dashboardResult.data.activity : [])
            .filter((row) => row.entityType === "source" && row.entityName)
            .slice(0, 4)
            .map((row) => ({
              id: String(row.id),
              actor: String(row.actorName ?? "Team member"),
              entity: String(row.entityName),
              summary: String(row.summary ?? "logged source activity for"),
              occurredAt: String(row.occurredAt ?? ""),
            }));
        }
        if (assigneeResponse?.ok) {
          const result = (await assigneeResponse.json()) as { data?: SourceAssignee[] };
          if (!cancelled && Array.isArray(result.data)) setAssignees(result.data);
        }
        if (lookupResponse.ok) {
          const result = (await lookupResponse.json()) as { data?: ActiveLookupOption[] };
          if (!cancelled && Array.isArray(result.data)) setLookupOptions(result.data);
        }
        if (notificationResponse.ok) {
          const result = (await notificationResponse.json()) as { data?: AppNotification[] };
          if (!cancelled && Array.isArray(result.data)) setNotifications(result.data);
        }
        if (cancelled) return;
        setTasks(nextTasks);
        setEvents(nextEvents);
        setNetworkingGroups(nextNetworkingGroups);
        setDirectoryContacts((Array.isArray(contactResult.data) ? contactResult.data : []).map(directoryContactFromApi));
        setSourceRecords((current) => nextSources.map((source) => ({
          ...current.find((existing) => existing.id === source.id),
          ...source,
        })));
        setSourceActivity(nextActivity);
        setLeadRecords((leadResult.data ?? []).map(leadSummaryFromApi));
        hasWorkspaceData.current = true;
        setDataState("ready");
        setFreshness("current");
      } catch (error) {
        if (cancelled) return;
        console.error("Workspace data could not be loaded", error);
        setFreshness("reconnecting");
        if (hasWorkspaceData.current) return;
        setTasks([]);
        setEvents([]);
        setNetworkingGroups([]);
        setSourceRecords([]);
        setSourceActivity([]);
        setLeadRecords([]);
        setDataState("error");
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [auth, member, dataReloadKey]);

  useEffect(() => {
    if (auth !== "live" || !member || member.organizationIndependent) return;
    let cancelled = false;
    const pollRevision = async () => {
      if (cancelled || revisionRequestActive.current || document.visibilityState === "hidden") return;
      revisionRequestActive.current = true;
      try {
        const response = await fetch("/api/workspace-revision");
        if (!response.ok) {
          setFreshness("reconnecting");
          return;
        }
        const result = await response.json() as { data?: { revision?: string } };
        const nextRevision = result.data?.revision;
        if (!nextRevision) return;
        if (workspaceRevision.current === null) {
          workspaceRevision.current = nextRevision;
          setFreshness("current");
          return;
        }
        if (workspaceRevision.current === nextRevision) {
          setFreshness("current");
          return;
        }
        workspaceRevision.current = nextRevision;
        setFreshness("syncing");
        const memberResponse = await fetch("/api/me").catch(() => null);
        if (memberResponse?.ok) {
          const memberResult = await memberResponse.json() as { data?: SessionMember };
          if (!cancelled && memberResult.data) setMember(memberResult.data);
        }
        if (!cancelled) setDataReloadKey((current) => current + 1);
      } catch {
        // Keep the last confirmed data visible; focus, online, and interval events retry automatically.
        if (!cancelled) setFreshness("reconnecting");
      } finally {
        revisionRequestActive.current = false;
      }
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") void pollRevision();
    };
    void pollRevision();
    const interval = window.setInterval(() => void pollRevision(), 10_000);
    window.addEventListener("focus", pollRevision);
    window.addEventListener("online", pollRevision);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener("focus", pollRevision);
      window.removeEventListener("online", pollRevision);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [auth, member?.workspaceId, member?.userId, member?.organizationIndependent]);

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(""), 2800);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  useEffect(() => {
    if (auth !== "live" || !member || member.organizationIndependent) return;
    let cancelled = false;
    const refreshNotifications = async () => {
      const response = await fetch("/api/notifications").catch(() => null);
      if (!response?.ok) return;
      const result = await response.json() as { data?: AppNotification[] };
      if (!cancelled && Array.isArray(result.data)) setNotifications(result.data);
    };
    const interval = window.setInterval(() => void refreshNotifications(), 30_000);
    return () => { cancelled = true; window.clearInterval(interval); };
  }, [auth, member?.userId]);

  useEffect(() => {
    if (!member || member.individualLeadTrackingEnabled || view !== "leads") return;
    navigateToView("sources", "replace");
  }, [member?.individualLeadTrackingEnabled, view]);

  useEffect(() => {
    if (dataReloadKey === 0 || auth !== "live") return;
    if (selectedSourceId) void loadSourceDetail(selectedSourceId);
    if (selectedDirectoryContactId) void openDirectoryContact(selectedDirectoryContactId, true);
    if (selectedNetworkingGroupId) void openNetworkingGroup(selectedNetworkingGroupId, true);
    if (selectedLeadId) void openLead(selectedLeadId, true);
  }, [dataReloadKey]);

  const selectedSource = sourceRecords.find((source) => source.id === selectedSourceId);
  const selectedEvent = events.find((event) => event.id === selectedEventId);
  const loggingSource = sourceRecords.find((source) => source.id === loggingSourceId);
  const loggingTask = tasks.find((task) => task.id === loggingTaskId);
  const editingSource = sourceRecords.find((source) => source.id === editingSourceId);
  const sourceCategories = [...new Set([
    ...lookupOptions.filter((option) => option.type === "source_category").map((option) => option.label),
    ...sourceRecords.map((source) => source.category).filter(Boolean),
  ])];
  const activityTypes = lookupOptions.filter((option) => option.type === "activity_type");
  const workspaceDefaultContactDays = sourceRecords[0]?.defaultContactDays ?? 30;

  useEffect(() => {
    if (auth !== "live" || !member || !initialSourceId || initialSourceLoaded.current || dataState !== "ready") return;
    initialSourceLoaded.current = true;
    if (!sourceRecords.some((source) => source.id === initialSourceId)) {
      updateAppHistory(viewPath(view, view === "events" ? { section: eventsSection } : {}), "replace");
      return;
    }
    void openSource(initialSourceId, true);
  }, [auth, member?.workspaceId, initialSourceId, dataState, sourceRecords.length]);

  async function reloadMyTasks() {
    if (auth !== "live") return;
    const response = await fetch(member && canAssignTasks(member.role) ? "/api/tasks" : "/api/tasks?scope=mine");
    if (!response.ok) return;
    const result = (await response.json()) as { data: Array<Record<string, unknown>> };
    setTasks(result.data.map(demoTaskFromApi).filter((task): task is DemoTask => Boolean(task)));
  }

  async function reloadLeads() {
    if (auth !== "live" || !member?.individualLeadTrackingEnabled) {
      setLeadRecords([]);
      return;
    }
    const response = await fetch("/api/referrals?limit=100");
    if (!response.ok) return;
    const result = await response.json() as { data?: Array<Record<string, unknown>> };
    setLeadRecords((result.data ?? []).map(leadSummaryFromApi));
  }

  async function openLead(lead: LeadSummary | string, background = false) {
    const leadId = typeof lead === "string" ? lead : lead.id;
    if (!background) {
      setSelectedLeadId(leadId);
      setSelectedLead(null);
      setLeadLoading(true);
      updateAppHistory(viewPath("leads", { lead: leadId }));
    }
    const response = await fetch(`/api/referrals/${leadId}`);
    if (!background) setLeadLoading(false);
    if (!response.ok) {
      if (background) return;
      setSelectedLeadId(null);
      setToast("The lead details could not be loaded.");
      return;
    }
    const result = await response.json() as { data: Record<string, unknown> };
    setSelectedLead(leadDetailFromApi(result.data));
  }

  async function createLead(input: LeadCreateInput): Promise<boolean> {
    const response = await fetch("/api/referrals", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
    const result = await response.json().catch(() => ({})) as { data?: { id: string }; error?: string };
    if (!response.ok || !result.data) {
      setToast(result.error ?? "The individual lead could not be added.");
      return false;
    }
    setCreatingLead(false);
    await reloadLeads();
    await openLead(result.data.id);
    setToast(`${input.prospectName} added to individual leads`);
    return true;
  }

  async function changeLeadStatus(statusCode: LeadStatus) {
    if (!selectedLead) return;
    const response = await fetch(`/api/referrals/${selectedLead.id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ version: selectedLead.version, statusCode }),
    });
    if (!response.ok) {
      if (response.status === 409) await openLead(selectedLead.id);
      setToast(response.status === 409 ? "This lead changed elsewhere. The latest version is shown." : "The lead status could not be updated.");
      return;
    }
    await Promise.all([reloadLeads(), openLead(selectedLead.id)]);
    setToast(`Lead moved to ${leadStatusLabel(statusCode)}`);
  }

  async function saveLeadActivity(input: { activityTypeCode: string; summary: string; notes: string }): Promise<boolean> {
    if (!selectedLead) return false;
    const response = await fetch(`/api/referrals/${selectedLead.id}/activities`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ ...input, notes: input.notes || null, occurredAt: new Date().toISOString() }),
    });
    if (!response.ok) {
      setToast("The lead activity could not be saved.");
      return false;
    }
    setLoggingLeadActivity(false);
    await Promise.all([reloadLeads(), openLead(selectedLead.id)]);
    setToast("Lead activity saved");
    return true;
  }

  async function loadSourceDetail(sourceId: string): Promise<boolean> {
    if (auth !== "live") return false;
    const response = await fetch(`/api/sources/${sourceId}`);
    if (!response.ok) {
      setToast("The latest source details could not be loaded.");
      return false;
    }
    const result = (await response.json()) as { data: Record<string, unknown> & { contacts?: Array<Record<string, unknown>>; activities?: Array<Record<string, unknown>>; contactNotes?: Array<Record<string, unknown>>; referralReceipts?: Array<Record<string, unknown>>; activityReferrals?: Array<Record<string, unknown>>; attachments?: Array<Record<string, unknown>>; contactPhotos?: Array<Record<string, unknown>> } };
    const contactNotes = (result.data.contactNotes ?? []).map((row): ContactNote => {
      let history: ContactNote["history"] = [];
      try {
        const parsedHistory = typeof row.historyJson === "string" ? JSON.parse(row.historyJson) : row.historyJson;
        if (Array.isArray(parsedHistory)) history = parsedHistory.map((entry: Record<string, unknown>) => ({
          id: String(entry.id), noteText: String(entry.noteText ?? ""), pinned: Boolean(entry.pinned),
          changedBy: entry.changedBy ? String(entry.changedBy) : undefined, createdAt: String(entry.createdAt ?? ""),
        })).sort((left, right) => right.createdAt.localeCompare(left.createdAt));
      } catch { history = []; }
      return {
        id: String(row.id), sourceContactId: String(row.sourceContactId), noteText: String(row.noteText ?? ""),
        pinned: Boolean(row.pinned), version: Number(row.version ?? 1),
        createdBy: row.createdBy ? String(row.createdBy) : undefined,
        updatedBy: row.updatedBy ? String(row.updatedBy) : undefined,
        createdAt: String(row.createdAt ?? ""), updatedAt: String(row.updatedAt ?? ""), history,
      };
    });
    const referralReceipts = (result.data.referralReceipts ?? []).map((row): ReferralReceipt => ({
      id: String(row.id), sourceContactId: String(row.sourceContactId), quantity: Number(row.quantity ?? 0),
      receivedOn: String(row.receivedOn ?? ""), recordedBy: row.recordedBy ? String(row.recordedBy) : undefined,
      createdAt: String(row.createdAt ?? ""),
    }));
    const contactPhotos = (result.data.contactPhotos ?? []).map(contactPhotoFromApi);
    const contacts: SourceContact[] = (result.data.contacts ?? []).map((contact) => ({
      id: String(contact.id),
      name: String(contact.name ?? "General contact"),
      title: contact.title ? String(contact.title) : undefined,
      phone: contact.phone ? String(contact.phone) : undefined,
      mobilePhone: contact.mobile_phone ? String(contact.mobile_phone) : undefined,
      fax: contact.fax ? String(contact.fax) : undefined,
      contactMethods: contactMethodsFromApi(contact),
      email: contact.email ? String(contact.email) : undefined,
      address: contact.address ? String(contact.address) : undefined,
      isPrimary: Boolean(contact.is_primary),
      isLinked: Boolean(contact.is_linked),
      preferredContactMethod: ["phone", "email", "text", "visit"].includes(String(contact.preferred_contact_method)) ? contact.preferred_contact_method as SourceContact["preferredContactMethod"] : "any",
      bestContactTime: contact.best_contact_time ? String(contact.best_contact_time) : undefined,
      contactStatus: ["former", "unresponsive", "do-not-contact"].includes(String(contact.contact_status)) ? contact.contact_status as SourceContact["contactStatus"] : "active",
      lastVerifiedAt: contact.last_verified_at ? String(contact.last_verified_at) : undefined,
      primaryPhotoId: contact.primary_photo_id ? String(contact.primary_photo_id) : undefined,
      photoCount: Number(contact.photo_count ?? 0),
      photos: contactPhotos.filter((photo) => photo.contactId === String(contact.id)),
      notes: contactNotes.filter((note) => note.sourceContactId === String(contact.id)),
      referralReceipts: referralReceipts.filter((receipt) => receipt.sourceContactId === String(contact.id)),
    }));
    const activityReferrals = result.data.activityReferrals ?? [];
    const activities: SourceActivityRecord[] = (result.data.activities ?? []).map((activity) => {
      const referrals = activityReferrals.filter((item) => String(item.sourceActivityId) === String(activity.id));
      return {
        id: String(activity.id),
        activityTypeCode: String(activity.activity_type_code ?? "note"),
        occurredAt: String(activity.occurred_at ?? ""),
        summary: String(activity.summary ?? "Source activity"),
        notes: activity.notes ? String(activity.notes) : undefined,
        actorName: activity.actor_name ? String(activity.actor_name) : undefined,
        sourceContactName: activity.source_contact_name ? String(activity.source_contact_name) : undefined,
        eventId: activity.event_id ? String(activity.event_id) : undefined,
        eventTitle: activity.event_title ? String(activity.event_title) : undefined,
        contextLabel: activity.context_label ? String(activity.context_label) : undefined,
        referrals: referrals.map((referral) => ({
          name: String(referral.referralName ?? "Referral"),
          receivedByName: referral.receivedByName ? String(referral.receivedByName) : undefined,
        })),
      };
    });
    const noteHistory: SourceNoteHistory[] = (Array.isArray(result.data.noteHistory) ? result.data.noteHistory : []).map((entry) => {
      const row = entry as Record<string, unknown>;
      return { id: String(row.id), noteText: row.noteText ? String(row.noteText) : undefined, color: (row.color ?? "gold") as SourceNoteHistory["color"], changedBy: row.changedBy ? String(row.changedBy) : undefined, createdAt: String(row.createdAt ?? "") };
    });
    const attachments = (result.data.attachments ?? []).map(sourceAttachmentFromApi);
    const primary = contacts.find((contact) => contact.isPrimary) ?? contacts[0];
    const effectiveCadenceDays = Number(result.data.effectiveCadenceDays ?? result.data.cadence_days ?? cadenceTypeDays(result.data.cadence_type));
    const usesDefaultCadence = Boolean(result.data.usesDefaultCadence);
    const nextContactAt = result.data.nextContactAt ? String(result.data.nextContactAt) : undefined;
    setSourceRecords((current) =>
      current.map((source) =>
        source.id === sourceId
          ? {
              ...source,
              version: Number(result.data.version ?? source.version),
              name: String(result.data.name ?? source.name),
              category: String(result.data.category_code ?? "Uncategorized"),
              isHospital: Boolean(result.data.is_hospital),
              referralPriority: result.data.referral_priority
                ? result.data.referral_priority as DemoSource["referralPriority"]
                : priorityFromCadenceValue(result.data.cadence_type),
              ownerUserId: result.data.owner_user_id ? String(result.data.owner_user_id) : source.ownerUserId,
              ownerName: String(result.data.owner_name ?? source.ownerName),
              cadence: cadenceLabel(effectiveCadenceDays, usesDefaultCadence),
              cadenceDays: result.data.cadence_days ? Number(result.data.cadence_days) : undefined,
              effectiveCadenceDays,
              defaultContactDays: Number(result.data.defaultContactDays ?? source.defaultContactDays),
              usesDefaultCadence,
              nextContactAt,
              nextTaskAt: formatSourceDate(earliestDate(nextContactAt) ?? source.nextTaskAt),
              contact: primary?.name ?? (result.data.phone ? "Organization contact" : "No contact added"),
              contactPhone: primary?.phone ?? (result.data.phone ? String(result.data.phone) : undefined),
              contactEmail: primary?.email,
              organizationPhone: result.data.phone ? String(result.data.phone) : undefined,
              address: result.data.address ? String(result.data.address) : undefined,
              website: result.data.website ? String(result.data.website) : undefined,
              description: result.data.profile_notes ? String(result.data.profile_notes) : undefined,
              descriptionColor: (result.data.profile_note_color ?? "gold") as DemoSource["descriptionColor"],
              descriptionUpdatedAt: result.data.profile_note_updated_at ? String(result.data.profile_note_updated_at) : undefined,
              descriptionUpdatedBy: result.data.profile_note_updated_by_name ? String(result.data.profile_note_updated_by_name) : undefined,
              noteHistory,
              notes: result.data.legacy_notes ? String(result.data.legacy_notes) : undefined,
              lastInteractionAtIso: activities[0]?.occurredAt ?? source.lastInteractionAtIso,
              contacts,
              activities,
              attachments,
              contactPhotos,
            }
          : source,
      ),
    );
    return true;
  }

  async function openSource(sourceId: string, background = false) {
    setSelectedSourceId(sourceId);
    setSourceDetailLoading(true);
    if (!background) updateAppHistory(viewPath(view, { ...(view === "events" ? { section: eventsSection } : {}), source: sourceId }));
    const loaded = await loadSourceDetail(sourceId).catch(() => false);
    setSourceDetailLoading(false);
    if (!loaded) {
      setSelectedSourceId(null);
      const parameters = new URLSearchParams(window.location.search);
      if (parameters.get("source") === sourceId) updateAppHistory(viewPath(view, view === "events" ? { section: eventsSection } : {}), "replace");
    }
  }

  function closeSourceDetail() {
    if ((window.history.state as { app?: string } | null)?.app === APP_HISTORY_MARKER && new URLSearchParams(window.location.search).has("source")) {
      window.history.back();
      return;
    }
    setSelectedSourceId(null);
  }

  function closeLeadDetail() {
    if ((window.history.state as { app?: string } | null)?.app === APP_HISTORY_MARKER && new URLSearchParams(window.location.search).has("lead")) {
      window.history.back();
      return;
    }
    setSelectedLeadId(null);
    setSelectedLead(null);
  }

  async function startEditingSource(sourceId: string) {
    await loadSourceDetail(sourceId);
    setEditingSourceId(sourceId);
  }

  async function completeTask(
    task: DemoTask,
    activity?: LogContactInput,
  ): Promise<boolean> {
    if (auth === "live") {
      const response = await fetch(`/api/tasks/${task.id}/complete`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ version: task.version, ...(activity ? { activity } : {}) }),
      });
      if (!response.ok) {
        setToast("This task changed—refresh and try again.");
        return false;
      }
    }
    setTasks((current) => current.filter((item) => item.id !== task.id));
    if (activity?.eventId && task.sourceId) {
      const source = sourceRecords.find((item) => item.id === task.sourceId);
      const sourceContact = activity.sourceContactId
        ? directoryContacts.find((item) => item.id === activity.sourceContactId)
        : undefined;
      setEvents((current) => current.map((workspaceEvent) => workspaceEvent.id !== activity.eventId
        ? workspaceEvent
        : sourceContact
          ? workspaceEvent.contactAttendees.some((attendee) => attendee.id === sourceContact.id)
            ? workspaceEvent
            : { ...workspaceEvent, contactAttendees: [...workspaceEvent.contactAttendees, { id: sourceContact.id, name: sourceContact.name, title: sourceContact.title, sourceId: sourceContact.sourceId, sourceName: sourceContact.sourceName }] }
          : source && !workspaceEvent.sourceAttendees.some((attendee) => attendee.id === source.id)
            ? { ...workspaceEvent, sourceAttendees: [...workspaceEvent.sourceAttendees, { id: source.id, name: source.name, category: source.category }] }
            : workspaceEvent));
    }
    setSourceRecords((current) =>
      current.map((source) => {
        if (source.name !== task.entity) return source;
        const loggedAt = activity?.occurredAt ?? new Date().toISOString();
        const previousTime = source.lastInteractionAtIso ? new Date(source.lastInteractionAtIso).getTime() : Number.NaN;
        const loggedTime = new Date(loggedAt).getTime();
        const latestTime = Number.isNaN(previousTime) ? loggedTime : Math.max(previousTime, loggedTime);
        const lastInteractionAtIso = new Date(latestTime).toISOString();
        const nextContactAt = new Date(latestTime + source.effectiveCadenceDays * 86_400_000).toISOString();
        return { ...source, lastInteractionAt: formatSourceDate(lastInteractionAtIso), lastInteractionAtIso, nextContactAt, nextTaskAt: formatSourceDate(nextContactAt), activityCount: source.activityCount + 1, referralTotal: source.referralTotal + (activity?.referrals.length ?? 0) };
      }),
    );
    const referralCount = activity?.referrals.length ?? 0;
    setToast(referralCount ? `Contact and ${referralCount} referral${referralCount === 1 ? "" : "s"} logged for ${task.entity}` : activity ? `Contact logged for ${task.entity}` : task.entityType === "general" ? "Task completed" : `${task.entity} marked contacted`);
    return true;
  }

  function openContactLog(sourceId: string, taskId?: string, sourceContactId?: string) {
    setLoggingSourceId(sourceId);
    setLoggingTaskId(taskId ?? null);
    setLoggingSourceContactId(sourceContactId ?? null);
  }

  function openTaskContactLog(task: DemoTask) {
    const source = sourceRecords.find((item) => item.id === task.sourceId) ?? sourceRecords.find((item) => item.name === task.entity);
    if (!source) {
      setToast("Open the referral source before logging this contact.");
      return;
    }
    openContactLog(source.id, task.id);
  }

  function closeContactLog() {
    setLoggingSourceId(null);
    setLoggingTaskId(null);
    setLoggingSourceContactId(null);
  }

  async function saveSnooze(date: string): Promise<boolean> {
    if (!snoozingFollowUp) return false;
    const due = new Date(`${date}T12:00:00`);
    if (Number.isNaN(due.getTime())) {
      setToast("Choose a valid follow-up date.");
      return false;
    }
    const dueAtIso = due.toISOString();
    const dueAt = due.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    const source = snoozingFollowUp.source
      ?? sourceRecords.find((item) => item.id === snoozingFollowUp.task?.sourceId)
      ?? sourceRecords.find((item) => item.name === snoozingFollowUp.entity);
    if (snoozingFollowUp.task) {
      const task = snoozingFollowUp.task;
      const response = await fetch(`/api/tasks/${task.id}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ dueAt: dueAtIso, version: task.version }),
      });
      if (!response.ok) {
        setToast(response.status === 409 ? "This follow-up changed elsewhere. Refresh and try again." : "The follow-up could not be rescheduled.");
        return false;
      }
      const result = (await response.json()) as { data: { version: number } };
      setTasks((current) => current.map((item) => item.id === task.id ? { ...item, dueAt, dueAtIso, timing: "upcoming", version: result.data.version } : item));
    } else {
      if (!source) {
        setToast("This referral source could not be found.");
        return false;
      }
      const response = await fetch("/api/tasks", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          sourceId: source.id,
          title: `Follow up with ${source.name}`,
          assigneeUserId: source.ownerUserId ?? member?.userId,
          dueAt: dueAtIso,
          recurrenceType: null,
          recurrenceDays: null,
        }),
      });
      if (!response.ok) {
        setToast("The follow-up could not be rescheduled.");
        return false;
      }
      const result = (await response.json()) as { data: { id: string; version: number } };
      setTasks((current) => [...current, {
        id: result.data.id,
        sourceId: source.id,
        title: `Follow up with ${source.name}`,
        entity: source.name,
        entityType: "source",
        dueAt,
        dueAtIso,
        timing: "upcoming",
        assignee: source.ownerName,
        priority: "normal",
        version: result.data.version,
      }]);
    }
    setSnoozingFollowUp(null);
    setToast(`${source?.name ?? snoozingFollowUp.entity} moved to ${formatSourceDate(dueAtIso)}`);
    return true;
  }

  async function saveTask(values: TaskEditorValues): Promise<boolean> {
    const task = taskEditor?.task;
    const payload = {
      title: values.title,
      description: values.description || null,
      assigneeUserId: values.assigneeUserId,
      dueAt: values.dueAt,
      priority: values.priority,
      recurrenceType: values.recurrenceType || null,
      recurrenceDays: values.recurrenceType === "custom" ? values.recurrenceDays : null,
    };
    const response = await fetch(task ? `/api/tasks/${task.id}` : "/api/tasks", {
      method: task ? "PATCH" : "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(task ? { ...payload, version: task.version } : { ...payload, sourceId: values.sourceId || null }),
    });
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      setToast(response.status === 409 ? "This task changed elsewhere. Refresh and try again." : result.error ?? "The task could not be saved.");
      return false;
    }
    await reloadMyTasks();
    setTaskEditor(null);
    setToast(task ? "Task updated" : "Task assigned");
    return true;
  }

  async function saveEvent(values: EventEditorValues): Promise<boolean> {
    const currentEvent = eventEditor && eventEditor !== "new" ? eventEditor : undefined;
    const payload = {
      sourceId: values.sourceId || null,
      networkingGroupId: values.networkingGroupId || null,
      title: values.title,
      location: values.location,
      occurredAt: values.occurredAt,
      description: values.description || null,
      attendeeUserIds: values.attendeeUserIds,
      attendeeSourceIds: values.attendeeSourceIds,
      attendeeContactIds: values.attendeeContactIds,
    };
    const response = await fetch(currentEvent ? `/api/events/${currentEvent.id}` : "/api/events", {
      method: currentEvent ? "PATCH" : "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(currentEvent ? { ...payload, version: currentEvent.version } : payload),
    });
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      setToast(response.status === 409
        ? "This event changed elsewhere. Refresh and try again."
        : result.error ?? "The event could not be saved.");
      return false;
    }
    const result = await response.json() as { data: { id: string; version: number } };
    let photoSaved = true;
    if (values.photo) {
      const photoBody = new FormData();
      photoBody.set("file", values.photo);
      const photoResponse = await fetch(`/api/events/${encodeURIComponent(result.data.id)}/photos`, {
        method: "POST",
        body: photoBody,
      });
      photoSaved = photoResponse.ok;
    }
    setEventEditor(null);
    setEventEditorGroupId(null);
    setEventEditorContactId(null);
    setEventContactEditorOpen(false);
    setEventAddedContactId(null);
    setDataReloadKey((current) => current + 1);
    if (selectedNetworkingGroupId) void openNetworkingGroup(selectedNetworkingGroupId);
    if (selectedDirectoryContactId) void openDirectoryContact(selectedDirectoryContactId);
    setToast(photoSaved
      ? values.photo
        ? currentEvent ? "Event and photo updated" : "Event and photo recorded"
        : currentEvent ? "Event updated" : "Event recorded"
      : "Event saved, but the photo could not be uploaded. Reopen the event to try again.");
    return true;
  }

  async function saveEventContactAttendees(event: WorkspaceEvent, contactIds: string[]): Promise<boolean> {
    const response = await fetch(`/api/events/${encodeURIComponent(event.id)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        version: event.version,
        sourceId: event.sourceId || null,
        networkingGroupId: event.networkingGroupId || null,
        title: event.title,
        location: event.location,
        occurredAt: event.occurredAt,
        description: event.description || null,
        attendeeUserIds: event.attendees.map((attendee) => attendee.id),
        attendeeSourceIds: event.sourceAttendees.map((attendee) => attendee.id),
        attendeeContactIds: contactIds,
      }),
    });
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      if (response.status === 409) setDataReloadKey((current) => current + 1);
      setToast(response.status === 409
        ? "This event changed elsewhere. The newest attendance has been loaded; please try again."
        : result.error ?? "The event attendance could not be saved.");
      return false;
    }
    const result = await response.json() as { data: { version: number } };
    const selectedContacts = contactIds.map((contactId) => {
      const contact = directoryContacts.find((item) => item.id === contactId);
      const existing = event.contactAttendees.find((item) => item.id === contactId);
      return contact ? {
        id: contact.id,
        name: contact.name,
        title: contact.title,
        sourceId: contact.sourceId,
        sourceName: contact.sourceName,
      } : existing;
    }).filter((contact): contact is WorkspaceEvent["contactAttendees"][number] => Boolean(contact));
    setEvents((current) => current.map((item) => item.id === event.id ? {
      ...item,
      version: result.data.version,
      contactAttendees: selectedContacts,
    } : item));
    setEventAttendeeManagerOpen(false);
    setEventContactEditorOpen(false);
    setEventAddedContactId(null);
    setDataReloadKey((current) => current + 1);
    setToast(`${selectedContacts.length} contact${selectedContacts.length === 1 ? "" : "s"} saved for ${event.title}`);
    return true;
  }

  function openEventDetail(event: WorkspaceEvent) {
    setView("events");
    setEventsSection("events");
    setSelectedNetworkingGroupId(null);
    setSelectedNetworkingGroup(null);
    setEventAttendeeManagerOpen(false);
    setEventContactEditorOpen(false);
    setEventAddedContactId(null);
    setSelectedEventId(event.id);
    updateAppHistory(viewPath("events", { section: "events", event: event.id }));
    window.scrollTo({ top: 0 });
  }

  function closeEventDetail() {
    setEventAttendeeManagerOpen(false);
    setEventContactEditorOpen(false);
    setEventAddedContactId(null);
    if ((window.history.state as { app?: string } | null)?.app === APP_HISTORY_MARKER && new URLSearchParams(window.location.search).has("event")) {
      window.history.back();
      return;
    }
    setSelectedEventId(null);
    changeEventsSection("events", "replace");
  }

  async function openNetworkingGroup(groupId: string, background = false, historyMode: "push" | "replace" = "push") {
    if (!background) {
      setView("events");
      setEventsSection("groups");
      setSelectedEventId(null);
      setSelectedNetworkingGroupId(groupId);
      setSelectedNetworkingGroup(null);
      setNetworkingGroupLoading(true);
      updateAppHistory(viewPath("events", { section: "groups", group: groupId }), historyMode);
    }
    const response = await fetch(`/api/networking-groups/${encodeURIComponent(groupId)}`).catch(() => null);
    if (!response?.ok) {
      if (background) return;
      setSelectedNetworkingGroupId(null);
      setNetworkingGroupLoading(false);
      setToast("The networking group could not be opened.");
      updateAppHistory(viewPath("events", { section: "groups" }), "replace");
      return;
    }
    const result = await response.json() as { data: Record<string, unknown> };
    setSelectedNetworkingGroup(networkingGroupDetailFromApi(result.data));
    if (!background) setNetworkingGroupLoading(false);
  }

  function closeNetworkingGroup() {
    if ((window.history.state as { app?: string } | null)?.app === APP_HISTORY_MARKER) {
      window.history.back();
      return;
    }
    setSelectedNetworkingGroupId(null);
    setSelectedNetworkingGroup(null);
    setNetworkingGroupLoading(false);
    changeEventsSection("groups", "replace");
  }

  function scheduleNetworkingGroupMeeting(group: NetworkingGroup) {
    setEventEditorGroupId(group.id);
    setEventEditor("new");
  }

  async function saveNetworkingGroup(values: NetworkingGroupEditorValues): Promise<boolean> {
    const current = networkingGroupEditor && networkingGroupEditor !== "new" ? networkingGroupEditor : undefined;
    const payload = {
      name: values.name,
      description: values.description || null,
      website: normalizeWebsite(values.website),
      primaryLocation: values.primaryLocation || null,
      cadenceType: values.cadenceType,
      cadenceDetails: values.cadenceDetails || null,
      notes: values.notes || null,
    };
    const response = await fetch(current ? `/api/networking-groups/${current.id}` : "/api/networking-groups", {
      method: current ? "PATCH" : "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(current ? { ...payload, version: current.version } : payload),
    });
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      setToast(response.status === 409 && current ? "This group changed elsewhere. Refresh and try again." : result.error ?? "The networking group could not be saved.");
      return false;
    }
    const result = await response.json() as { data: { id: string; version: number } };
    setNetworkingGroupEditor(null);
    setDataReloadKey((reload) => reload + 1);
    setToast(current ? "Networking group updated" : "Networking group created");
    void openNetworkingGroup(result.data.id);
    return true;
  }

  async function addNetworkingGroupContact(contactId: string): Promise<boolean> {
    if (!selectedNetworkingGroup) return false;
    const response = await fetch(`/api/networking-groups/${encodeURIComponent(selectedNetworkingGroup.id)}/contacts`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ contactId }),
    });
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      setToast(result.error ?? "The contact could not be added to this group.");
      return false;
    }
    await openNetworkingGroup(selectedNetworkingGroup.id, true);
    setDataReloadKey((reload) => reload + 1);
    setToast("Contact added to group");
    return true;
  }

  async function saveNetworkingGroupNotes(notes: string): Promise<boolean> {
    if (!selectedNetworkingGroup) return false;
    const group = selectedNetworkingGroup;
    const response = await fetch(`/api/networking-groups/${encodeURIComponent(group.id)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        name: group.name,
        description: group.description || null,
        website: group.website || null,
        primaryLocation: group.primaryLocation || null,
        cadenceType: group.cadenceType,
        cadenceDetails: group.cadenceDetails || null,
        notes: notes || null,
        version: group.version,
      }),
    });
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      setToast(response.status === 409 ? "This group changed elsewhere. Refresh and try again." : result.error ?? "The group note could not be saved.");
      return false;
    }
    await openNetworkingGroup(group.id, true);
    setDataReloadKey((reload) => reload + 1);
    setToast("Group note saved");
    return true;
  }

  async function reloadDirectoryContacts() {
    const response = await fetch("/api/contacts");
    if (!response.ok) return false;
    const result = await response.json() as { data?: Array<Record<string, unknown>> };
    setDirectoryContacts((Array.isArray(result.data) ? result.data : []).map(directoryContactFromApi));
    return true;
  }

  async function openDirectoryContact(contactId: string, background = false, historyMode: "push" | "replace" = "push") {
    if (!background) {
      setView("contacts");
      setSelectedDirectoryContactId(contactId);
      setSelectedDirectoryContact(null);
      setContactProfileLoading(true);
      updateAppHistory(viewPath("contacts", { contact: contactId }), historyMode);
    }
    const response = await fetch(`/api/contacts/${encodeURIComponent(contactId)}`);
    if (!background) setContactProfileLoading(false);
    if (!response.ok) {
      if (background) return;
      setSelectedDirectoryContactId(null);
      updateAppHistory(viewPath("contacts"), "replace");
      setToast("The contact profile could not be loaded.");
      return;
    }
    const result = await response.json() as { data: Record<string, unknown> };
    setSelectedDirectoryContact(contactProfileFromApi(result.data));
  }

  function closeDirectoryContact() {
    if ((window.history.state as { app?: string } | null)?.app === APP_HISTORY_MARKER) {
      window.history.back();
      return;
    }
    setSelectedDirectoryContactId(null);
    setSelectedDirectoryContact(null);
    setContactProfileLoading(false);
    navigateToView("contacts", "replace");
  }

  async function uploadDirectoryContactPhotos(contact: ContactProfile, files: File[]): Promise<boolean> {
    const invalid = files.find((file) => file.size > 20 * 1024 * 1024 || !["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"].includes(file.type) && !/\.(heic|heif)$/i.test(file.name));
    if (invalid) {
      setToast("Contact photos must be valid JPEG, PNG, WebP, HEIC, or HEIF images no larger than 20 MB each.");
      return false;
    }
    let uploaded = 0;
    for (const file of files) {
      const body = new FormData();
      body.set("file", file);
      const response = await fetch(`/api/contacts/${encodeURIComponent(contact.id)}/photos`, { method: "POST", body });
      if (!response.ok) {
        const result = await response.json().catch(() => ({})) as { error?: string };
        setToast(uploaded ? `${uploaded} photo${uploaded === 1 ? "" : "s"} uploaded; ${result.error ?? "another photo could not be saved"}.` : result.error ?? "The contact photos could not be uploaded.");
        await Promise.all([reloadDirectoryContacts(), openDirectoryContact(contact.id, true)]);
        if (selectedSourceId === contact.sourceId) await loadSourceDetail(contact.sourceId);
        return false;
      }
      uploaded += 1;
    }
    await Promise.all([reloadDirectoryContacts(), openDirectoryContact(contact.id, true)]);
    if (selectedSourceId === contact.sourceId) await loadSourceDetail(contact.sourceId);
    setDataReloadKey((current) => current + 1);
    setToast(`${uploaded} contact photo${uploaded === 1 ? "" : "s"} uploaded`);
    return true;
  }

  async function deleteDirectoryContactPhoto(contact: ContactProfile, photo: ContactPhoto): Promise<boolean> {
    const response = await fetch(`/api/contact-photos/${encodeURIComponent(photo.id)}?version=${photo.version}`, { method: "DELETE" });
    if (!response.ok) {
      setToast(response.status === 409 ? "This photo changed elsewhere. The latest contact profile is being loaded." : "The contact photo could not be deleted.");
      await openDirectoryContact(contact.id, true);
      return false;
    }
    await Promise.all([reloadDirectoryContacts(), openDirectoryContact(contact.id, true)]);
    if (selectedSourceId === contact.sourceId) await loadSourceDetail(contact.sourceId);
    setDataReloadKey((current) => current + 1);
    setToast("Contact photo deleted");
    return true;
  }

  async function saveDirectoryContact(values: DirectoryContactEditorValues): Promise<boolean> {
    const currentContact = contactEditor && contactEditor !== "new" ? contactEditor : undefined;
    if (values.newOrganization && !member) {
      setToast("Your session needs to be refreshed before creating the organization.");
      return false;
    }
    const contactPayload = {
      name: values.name,
      title: values.title || null,
      phone: values.phone || null,
      mobilePhone: values.mobilePhone || null,
      fax: values.fax || null,
      contactMethods: values.contactMethods,
      email: values.email || null,
      address: values.address || null,
      isPrimary: values.newOrganization ? true : values.isPrimary,
      preferredContactMethod: values.preferredContactMethod,
      bestContactTime: values.bestContactTime || null,
      contactStatus: values.contactStatus,
      lastVerifiedAt: values.lastVerifiedAt || null,
    };
    const response = values.newOrganization && !currentContact && member
      ? await fetch("/api/sources", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name: values.newOrganization.name,
          categoryCode: values.newOrganization.category,
          referralPriority: "other",
          ownerUserId: member.userId,
          cadenceType: "custom",
          cadenceDays: null,
          phone: null,
          website: normalizeWebsite(values.newOrganization.website),
          address: null,
          description: null,
          descriptionColor: "gold",
          notes: null,
          contacts: [contactPayload],
        }),
      })
      : await fetch(currentContact ? `/api/contacts/${encodeURIComponent(currentContact.id)}` : "/api/contacts", {
        method: currentContact ? "PATCH" : "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          sourceId: currentContact?.sourceId ?? values.sourceId,
          ...contactPayload,
          ...(currentContact ? { version: currentContact.version } : {}),
        }),
      });
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      setToast(response.status === 409 ? "This contact changed elsewhere. The latest profile is being loaded." : result.error ?? (values.newOrganization ? "The organization and contact could not be saved." : "The contact could not be saved."));
      if (response.status === 409 && currentContact) await openDirectoryContact(currentContact.id, true);
      return false;
    }
    const savedResult = await response.json().catch(() => ({})) as { data?: { version?: number; updatedAt?: string } };
    const organizationChanged = Boolean(currentContact && values.organizationAction && values.sourceId !== currentContact.sourceId);
    if (currentContact && values.organizationAction && organizationChanged) {
      const targetSource = sourceRecords.find((source) => source.id === values.sourceId);
      const affiliationResponse = await fetch(`/api/sources/${encodeURIComponent(values.sourceId)}/contacts/existing`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ contactId: currentContact.id, mode: values.organizationAction }),
      });
      if (!affiliationResponse.ok) {
        const result = await affiliationResponse.json().catch(() => ({})) as { error?: string };
        setContactEditor({
          ...currentContact,
          version: Number(savedResult.data?.version ?? currentContact.version + 1),
          updatedAt: savedResult.data?.updatedAt ?? new Date().toISOString(),
        });
        await reloadDirectoryContacts();
        if (selectedDirectoryContactId === currentContact.id) await openDirectoryContact(currentContact.id, true);
        setToast(`Contact details were saved, but the organization could not be changed${result.error ? `: ${result.error}` : "."}`);
        return false;
      }
      setToast(values.organizationAction === "transfer"
        ? `${values.name} updated and transferred to ${targetSource?.name ?? "the selected referral source"}`
        : `${values.name} updated and linked to ${targetSource?.name ?? "the selected referral source"}`);
    }
    await reloadDirectoryContacts();
    setDataReloadKey((current) => current + 1);
    setContactEditor(null);
    if (currentContact && selectedDirectoryContactId === currentContact.id) await openDirectoryContact(currentContact.id);
    if (!organizationChanged) setToast(currentContact ? `${values.name} updated` : values.newOrganization ? `${values.name} and ${values.newOrganization.name} added` : `${values.name} added to the address book`);
    return true;
  }

  async function saveScannedBusinessCard(values: BusinessCardReviewSaveValues): Promise<boolean> {
    if (!member) return false;
    const existingContact = values.existingContactId
      ? directoryContacts.find((contact) => contact.id === values.existingContactId)
      : undefined;
    if (values.existingContactId && !existingContact) {
      setToast("That possible matching contact changed. Review the latest address book and try again.");
      return false;
    }
    const contactPayload = {
      name: values.contact.name || existingContact?.name || "",
      title: values.contact.title || existingContact?.title || null,
      phone: values.contact.phone || existingContact?.phone || null,
      mobilePhone: values.contact.mobilePhone || existingContact?.mobilePhone || null,
      fax: values.contact.fax || existingContact?.fax || null,
      contactMethods: values.contact.contactMethods.length ? values.contact.contactMethods : existingContact?.contactMethods ?? [],
      email: values.contact.email || existingContact?.email || null,
      address: values.contact.address || existingContact?.address || null,
      isPrimary: existingContact?.isPrimary ?? values.contact.isPrimary,
      preferredContactMethod: existingContact?.preferredContactMethod ?? values.contact.preferredContactMethod,
      bestContactTime: existingContact?.bestContactTime || values.contact.bestContactTime || null,
      contactStatus: existingContact?.contactStatus ?? values.contact.contactStatus,
      lastVerifiedAt: values.contact.lastVerifiedAt || null,
    };
    const response = existingContact
      ? await fetch(`/api/contacts/${encodeURIComponent(existingContact.id)}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ sourceId: existingContact.sourceId, version: existingContact.version, ...contactPayload }),
      })
      : values.destination === "existing"
      ? await fetch("/api/contacts", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ sourceId: values.sourceId, ...contactPayload }),
      })
      : await fetch("/api/sources", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name: values.organizationName,
          categoryCode: values.category,
          referralPriority: "other",
          ownerUserId: member.userId,
          cadenceType: "custom",
          cadenceDays: null,
          phone: null,
          website: normalizeWebsite(values.website),
          address: values.contact.address || null,
          description: null,
          descriptionColor: "gold",
          notes: null,
          contacts: [{ ...contactPayload, isPrimary: true }],
        }),
      });
    const result = await response.json().catch(() => ({})) as {
      error?: string;
      data?: { id?: unknown; contacts?: Array<{ id?: unknown }> };
    };
    if (!response.ok) {
      setToast(result.error ?? "The scanned contact could not be saved.");
      return false;
    }
    const rawContactId = existingContact?.id ?? (values.destination === "existing" ? result.data?.id : result.data?.contacts?.[0]?.id);
    const contactId = typeof rawContactId === "string" && rawContactId ? rawContactId : null;
    const rawSourceId = existingContact?.sourceId ?? (values.destination === "existing" ? values.sourceId : result.data?.id);
    const savedSourceId = typeof rawSourceId === "string" && rawSourceId ? rawSourceId : null;
    let cardPhotoSaved = false;
    if (contactId) {
      const photoBody = new FormData();
      photoBody.set("file", values.cardPhoto);
      photoBody.set("caption", "Business card");
      photoBody.set("kind", "business_card");
      try {
        const photoResponse = await fetch(`/api/contacts/${encodeURIComponent(contactId)}/photos`, { method: "POST", body: photoBody });
        cardPhotoSaved = photoResponse.ok;
      } catch {
        cardPhotoSaved = false;
      }
    }
    await reloadDirectoryContacts().catch(() => false);
    if (!values.batchMode) setBusinessCardScannerOpen(false);
    setDataReloadKey((current) => current + 1);
    const destinationName = existingContact
      ? contactOrganizationNames(existingContact)
      : values.destination === "existing"
      ? sourceRecords.find((source) => source.id === values.sourceId)?.name
      : values.organizationName;
    const savedContactMessage = existingContact
      ? `${contactPayload.name} updated${destinationName ? ` at ${destinationName}` : ""}`
      : `${values.contact.name} added${destinationName ? ` to ${destinationName}` : ""}`;
    if (!values.batchMode && contactId && savedSourceId) {
      setPostScanAction({
        contactId,
        contactName: contactPayload.name,
        sourceId: savedSourceId,
        sourceName: destinationName || "the referral source",
      });
    }
    setToast(cardPhotoSaved
      ? `${savedContactMessage}; business card saved to photos`
      : `${savedContactMessage}, but the business card photo could not be saved. Upload it from the contact profile.`);
    return true;
  }

  async function refreshAfterDuplicateMerge() {
    setSelectedDirectoryContactId(null);
    setSelectedDirectoryContact(null);
    setSelectedSourceId(null);
    await reloadDirectoryContacts();
    setDataReloadKey((current) => current + 1);
  }

  async function saveEventContact(values: DirectoryContactEditorValues): Promise<boolean> {
    const response = await fetch("/api/contacts", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        sourceId: values.sourceId,
        name: values.name,
        title: values.title || null,
        phone: values.phone || null,
        mobilePhone: values.mobilePhone || null,
        fax: values.fax || null,
        contactMethods: values.contactMethods,
        email: values.email || null,
        address: values.address || null,
        isPrimary: values.isPrimary,
        preferredContactMethod: values.preferredContactMethod,
        bestContactTime: values.bestContactTime || null,
        contactStatus: values.contactStatus,
        lastVerifiedAt: values.lastVerifiedAt || null,
      }),
    });
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      setToast(result.error ?? "The attendee contact could not be created.");
      return false;
    }
    const result = await response.json() as { data: Record<string, unknown> };
    const contact = directoryContactFromApi(result.data);
    setDirectoryContacts((current) => [...current.filter((item) => item.id !== contact.id), contact].sort((left, right) => left.name.localeCompare(right.name)));
    setEventAddedContactId(contact.id);
    setEventContactEditorOpen(false);
    setDataReloadKey((current) => current + 1);
    setToast(`${contact.name} added and selected`);
    return true;
  }

  async function saveDirectoryContactInteraction(input: LogContactInput): Promise<boolean> {
    if (!loggingDirectoryContact) return false;
    const contact = loggingDirectoryContact;
    const response = await fetch(`/api/contacts/${encodeURIComponent(contact.id)}/interactions`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      setToast(result.error ?? "The interaction could not be saved.");
      return false;
    }
    setSourceRecords((current) => current.map((source) => source.id === contact.sourceId ? {
      ...source,
      ...(() => {
        const previousTime = source.lastInteractionAtIso ? new Date(source.lastInteractionAtIso).getTime() : Number.NaN;
        const interactionTime = new Date(input.occurredAt).getTime();
        const latestTime = Number.isNaN(previousTime) ? interactionTime : Math.max(previousTime, interactionTime);
        return { lastInteractionAt: formatSourceDate(new Date(latestTime).toISOString()), lastInteractionAtIso: new Date(latestTime).toISOString() };
      })(),
      activityCount: source.activityCount + 1,
      referralTotal: source.referralTotal + input.referrals.length,
    } : source));
    setLoggingDirectoryContact(null);
    setDataReloadKey((current) => current + 1);
    await openDirectoryContact(contact.id);
    setToast(input.referrals.length ? `Interaction and ${input.referrals.length} referral${input.referrals.length === 1 ? "" : "s"} logged for ${contact.name}` : `Interaction logged for ${contact.name}`);
    return true;
  }

  async function saveDirectoryContactNote(input: ContactNoteInput): Promise<boolean> {
    if (!contactNoteEditor) return false;
    const contact = contactNoteEditor;
    const response = await fetch(`/api/sources/${encodeURIComponent(contact.sourceId)}/contacts/${encodeURIComponent(contact.id)}/notes`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      setToast(result.error ?? "The contact note could not be saved.");
      return false;
    }
    const result = await response.json() as { data?: { taskId?: string } };
    if (result.data?.taskId) await reloadMyTasks();
    setContactNoteEditor(null);
    await openDirectoryContact(contact.id);
    const mentionedName = input.mentionedUserIds.length ? assignees.find((teammate) => teammate.id === input.mentionedUserIds[0])?.name : undefined;
    setToast(mentionedName ? `Note saved and @${mentionedName} notified` : `Note saved for ${contact.name}`);
    return true;
  }

  function changeEventAttachmentCount(eventId: string | undefined, difference: number) {
    if (!eventId || difference === 0) return;
    setEvents((current) => current.map((event) => event.id === eventId
      ? { ...event, attachmentCount: Math.max(0, event.attachmentCount + difference) }
      : event));
  }

  function removeEventPhotoLocal(eventId: string, photoId: string) {
    setEvents((current) => current.map((event) => event.id === eventId ? {
      ...event,
      attachmentCount: Math.max(0, event.attachmentCount - 1),
      coverPhotoId: event.coverPhotoId === photoId ? undefined : event.coverPhotoId,
    } : event));
    setSelectedNetworkingGroup((current) => current ? {
      ...current,
      photos: current.photos.filter((photo) => photo.id !== photoId),
      events: current.events.map((event) => event.id === eventId ? {
        ...event,
        attachmentCount: Math.max(0, event.attachmentCount - 1),
        coverPhotoId: event.coverPhotoId === photoId ? undefined : event.coverPhotoId,
      } : event),
    } : current);
    setDataReloadKey((current) => current + 1);
  }

  async function uploadSourceAttachment(
    sourceId: string,
    file: File,
    eventId: string,
    caption: string,
  ): Promise<boolean> {
    if (file.size > 25 * 1024 * 1024) {
      setToast("Attachments must be 25 MB or smaller.");
      return false;
    }
    const body = new FormData();
    body.set("file", file);
    body.set("eventId", eventId);
    body.set("caption", caption);
    const response = await fetch(`/api/sources/${sourceId}/attachments`, { method: "POST", body });
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      setToast(result.error ?? "The attachment could not be uploaded.");
      return false;
    }
    const result = await response.json() as { data: Record<string, unknown> };
    const attachment = sourceAttachmentFromApi(result.data);
    setSourceRecords((current) => current.map((source) => source.id === sourceId
      ? { ...source, attachments: [attachment, ...(source.attachments ?? [])] }
      : source));
    changeEventAttachmentCount(attachment.eventId, 1);
    setToast(`${attachment.fileName} uploaded`);
    return true;
  }

  async function updateSourceAttachment(
    sourceId: string,
    attachment: SourceAttachment,
    eventId: string,
    caption: string,
  ): Promise<boolean> {
    const response = await fetch(`/api/attachments/${attachment.id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ eventId: eventId || null, caption: caption.trim() || null, version: attachment.version }),
    });
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      if (response.status === 409) await loadSourceDetail(sourceId);
      setToast(response.status === 409 ? "This attachment changed elsewhere. Reopen it and try again." : result.error ?? "The attachment could not be updated.");
      return false;
    }
    const result = await response.json() as { data: Record<string, unknown> };
    const updates = sourceAttachmentFromApi({ ...attachment, ...result.data });
    setSourceRecords((current) => current.map((source) => source.id === sourceId
      ? { ...source, attachments: (source.attachments ?? []).map((item) => item.id === attachment.id ? updates : item) }
      : source));
    if (attachment.eventId !== updates.eventId) {
      changeEventAttachmentCount(attachment.eventId, -1);
      changeEventAttachmentCount(updates.eventId, 1);
    }
    setToast("Attachment details updated");
    return true;
  }

  async function deleteSourceAttachment(sourceId: string, attachment: SourceAttachment): Promise<boolean> {
    const response = await fetch(`/api/attachments/${attachment.id}?version=${attachment.version}`, { method: "DELETE" });
    if (!response.ok) {
      if (response.status === 409) await loadSourceDetail(sourceId);
      setToast(response.status === 409 ? "This attachment changed elsewhere. Reopen it and try again." : "The attachment could not be deleted.");
      return false;
    }
    setSourceRecords((current) => current.map((source) => source.id === sourceId
      ? { ...source, attachments: (source.attachments ?? []).filter((item) => item.id !== attachment.id) }
      : source));
    changeEventAttachmentCount(attachment.eventId, -1);
    setToast(`${attachment.fileName} deleted`);
    return true;
  }

  async function moveTask(task: DemoTask, dueAt: string) {
    const response = await fetch(`/api/tasks/${task.id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ dueAt, version: task.version }),
    });
    if (!response.ok) {
      setToast(response.status === 409 ? "This follow-up changed elsewhere. Refresh and try again." : "The follow-up could not be moved.");
      return;
    }
    await reloadMyTasks();
    setToast(`${task.entity} moved to ${formatSourceDate(dueAt)}`);
  }

  async function saveSource(input: SourceEditorValues) {
    const sourceCadence = cadenceForSourcePriority(
      input.referralPriority,
      input.referralPriority === "other" && !input.useDefaultCadence ? input.contactIntervalDays : null,
    );
    let sourceId = `preview_${Date.now()}`;
    let version = 1;
    let contacts: SourceContact[] = input.contacts.map((contact, index) => ({
      id: `preview_contact_${Date.now()}_${index}`,
      name: contact.name,
      title: contact.title || undefined,
      phone: contact.phone || undefined,
      mobilePhone: contact.mobilePhone || undefined,
      fax: contact.fax || undefined,
      contactMethods: contact.contactMethods,
      email: contact.email || undefined,
      address: contact.address || undefined,
      isPrimary: contact.isPrimary,
      preferredContactMethod: contact.preferredContactMethod,
      bestContactTime: contact.bestContactTime || undefined,
      contactStatus: contact.contactStatus,
      lastVerifiedAt: contact.lastVerifiedAt || undefined,
    }));
    if (auth === "live") {
      const response = await fetch("/api/sources", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name: input.name,
          categoryCode: input.category,
          isHospital: input.isHospital,
          referralPriority: input.referralPriority,
          ownerUserId: input.ownerUserId,
          cadenceType: sourceCadence.cadenceType,
          cadenceDays: sourceCadence.cadenceDays,
          phone: input.organizationPhone || null,
          website: normalizeWebsite(input.website),
          address: input.address || null,
          description: input.description || null,
          descriptionColor: input.descriptionColor,
          notes: input.notes || null,
          contacts: apiContacts(input.contacts),
        }),
      });
      if (!response.ok) {
        const result = (await response.json().catch(() => ({}))) as { error?: string };
        setToast(result.error ?? "The source could not be added.");
        return false;
      }
      const result = (await response.json()) as { data: { id: string; version: number; contacts?: Array<SourceContact & { isPrimary: boolean }> } };
      sourceId = result.data.id;
      version = result.data.version;
      if (result.data.contacts) contacts = result.data.contacts;
    }
    const primary = contacts.find((contact) => contact.isPrimary) ?? contacts[0];
    const effectiveCadenceDays = effectiveDaysForSourcePriority(input.referralPriority, input.contactIntervalDays, input.useDefaultCadence, workspaceDefaultContactDays);
    const nextContactAt = new Date(Date.now() + effectiveCadenceDays * 86_400_000).toISOString();
    const created: DemoSource = {
      id: sourceId,
      version,
      name: input.name,
      category: input.category,
      isHospital: input.isHospital,
      referralPriority: input.referralPriority,
      contact: primary?.name ?? (input.organizationPhone ? "Organization contact" : "No contact added"),
      contactPhone: primary?.phone ?? (input.organizationPhone || undefined),
      contactEmail: primary?.email,
      ownerUserId: input.ownerUserId,
      ownerName: assignees.find((assignee) => assignee.id === input.ownerUserId)?.name ?? member?.name ?? "Assigned user",
      cadence: cadenceLabel(effectiveCadenceDays, input.referralPriority === "other" && input.useDefaultCadence),
      cadenceDays: sourceCadence.cadenceDays ?? undefined,
      effectiveCadenceDays,
      defaultContactDays: workspaceDefaultContactDays,
      usesDefaultCadence: input.referralPriority === "other" && input.useDefaultCadence,
      lastInteractionAt: "Not recorded",
      nextContactAt,
      nextTaskAt: formatSourceDate(nextContactAt),
      activityCount: 0,
      referralTotal: 0,
      organizationPhone: input.organizationPhone || undefined,
      website: normalizeWebsite(input.website) ?? undefined,
      address: input.address || undefined,
      description: input.description || undefined,
      descriptionColor: input.descriptionColor,
      descriptionUpdatedAt: input.description ? new Date().toISOString() : undefined,
      descriptionUpdatedBy: input.description ? member?.name : undefined,
      notes: input.notes || undefined,
      contacts,
    };
    setSourceRecords((current) => [created, ...current]);
    setCreating(false);
    setSelectedSourceId(sourceId);
    updateAppHistory(viewPath("sources", { source: sourceId }));
    setToast(`${input.name} added to referral sources`);
    return true;
  }

  async function updateSource(input: SourceEditorValues) {
    if (!editingSource) return false;
    const sourceCadence = cadenceForSourcePriority(
      input.referralPriority,
      input.referralPriority === "other" && !input.useDefaultCadence ? input.contactIntervalDays : null,
    );
    let version = editingSource.version + 1;
    let contacts: SourceContact[] = input.contacts.map((contact, index) => ({
      id: contact.id ?? `preview_contact_${Date.now()}_${index}`,
      name: contact.name,
      title: contact.title || undefined,
      phone: contact.phone || undefined,
      mobilePhone: contact.mobilePhone || undefined,
      fax: contact.fax || undefined,
      contactMethods: contact.contactMethods,
      email: contact.email || undefined,
      address: contact.address || undefined,
      isPrimary: contact.isPrimary,
      preferredContactMethod: contact.preferredContactMethod,
      bestContactTime: contact.bestContactTime || undefined,
      contactStatus: contact.contactStatus,
      lastVerifiedAt: contact.lastVerifiedAt || undefined,
    }));
    if (auth === "live") {
      const response = await fetch(`/api/sources/${editingSource.id}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          version: editingSource.version,
          name: input.name,
          categoryCode: input.category === "Uncategorized" ? null : input.category,
          isHospital: input.isHospital,
          referralPriority: input.referralPriority,
          ownerUserId: input.ownerUserId,
          cadenceType: sourceCadence.cadenceType,
          cadenceDays: sourceCadence.cadenceDays,
          phone: input.organizationPhone || null,
          website: normalizeWebsite(input.website),
          address: input.address || null,
          description: input.description || null,
          descriptionColor: input.descriptionColor,
          notes: input.notes || null,
          contacts: apiContacts(input.contacts),
        }),
      });
      if (!response.ok) {
        const result = (await response.json().catch(() => ({}))) as { error?: string };
        setToast(response.status === 409 ? "This source changed elsewhere. Close it, reopen it, and try again." : result.error ?? "The source could not be updated.");
        return false;
      }
      const result = (await response.json()) as { data: { version: number; contacts?: SourceContact[] } };
      version = result.data.version;
      if (result.data.contacts) contacts = result.data.contacts;
    }
    const primary = contacts.find((contact) => contact.isPrimary) ?? contacts[0];
    const previousName = editingSource.name;
    const effectiveCadenceDays = effectiveDaysForSourcePriority(input.referralPriority, input.contactIntervalDays, input.useDefaultCadence, editingSource.defaultContactDays);
    setSourceRecords((current) => current.map((source) => source.id === editingSource.id ? {
      ...source,
      version,
      name: input.name,
      category: input.category,
      isHospital: input.isHospital,
      referralPriority: input.referralPriority,
      ownerUserId: input.ownerUserId,
      ownerName: assignees.find((assignee) => assignee.id === input.ownerUserId)?.name ?? editingSource.ownerName,
      cadence: cadenceLabel(effectiveCadenceDays, input.referralPriority === "other" && input.useDefaultCadence),
      cadenceDays: sourceCadence.cadenceDays ?? undefined,
      effectiveCadenceDays,
      usesDefaultCadence: input.referralPriority === "other" && input.useDefaultCadence,
      contact: primary?.name ?? (input.organizationPhone ? "Organization contact" : "No contact added"),
      contactPhone: primary?.phone ?? (input.organizationPhone || undefined),
      contactEmail: primary?.email,
      organizationPhone: input.organizationPhone || undefined,
      website: normalizeWebsite(input.website) ?? undefined,
      address: input.address || undefined,
      description: input.description || undefined,
      descriptionColor: input.descriptionColor,
      notes: input.notes || undefined,
      contacts,
    } : source));
    if (previousName !== input.name) {
      setTasks((current) => current.map((task) => task.entity === previousName ? { ...task, entity: input.name } : task));
    }
    if (auth === "live") {
      await Promise.all([loadSourceDetail(editingSource.id), reloadMyTasks()]);
    }
    setEditingSourceId(null);
    setToast(`${input.name} updated`);
    return true;
  }

  async function saveSourceDescription(source: DemoSource, description: string, color: DemoSource["descriptionColor"]): Promise<boolean> {
    let version = source.version + 1;
    if (auth === "live") {
      const response = await fetch(`/api/sources/${source.id}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ version: source.version, description: description || null, ...(color !== source.descriptionColor ? { descriptionColor: color ?? "gold" } : {}) }),
      });
      if (!response.ok) {
        if (response.status === 409) await loadSourceDetail(source.id);
        setToast(response.status === 409 ? "This source changed elsewhere. The latest details have been loaded." : "The pinned note could not be saved.");
        return false;
      }
      const result = (await response.json()) as { data: { version: number } };
      version = result.data.version;
    }
    setSourceRecords((current) => current.map((item) => item.id === source.id ? {
      ...item,
      version,
      description: description || undefined,
      descriptionColor: color ?? "gold",
      descriptionUpdatedAt: new Date().toISOString(),
      descriptionUpdatedBy: member?.name,
    } : item));
    setToast(description ? `Pinned note saved for ${source.name}` : `Pinned note removed from ${source.name}`);
    return true;
  }

  async function addContactNote(contactId: string, input: ContactNoteInput): Promise<boolean> {
    if (!selectedSource) return false;
    const response = await fetch(`/api/sources/${selectedSource.id}/contacts/${contactId}/notes`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      setToast("The contact note could not be saved.");
      return false;
    }
    const result = (await response.json()) as { data: ContactNote & { taskId?: string } };
    setSourceRecords((current) => current.map((source) => source.id === selectedSource.id ? {
      ...source,
      contacts: source.contacts?.map((contact) => contact.id === contactId
        ? { ...contact, notes: [result.data, ...(contact.notes ?? [])] }
        : contact),
    } : source));
    if (result.data.taskId) await reloadMyTasks();
    const mentionedName = input.mentionedUserIds.length
      ? assignees.find((teammate) => teammate.id === input.mentionedUserIds[0])?.name
      : undefined;
    setToast(mentionedName ? `Note saved and @${mentionedName} notified` : `Note saved for ${selectedSource.name}`);
    return true;
  }

  async function updateContactNote(contactId: string, note: ContactNote, noteText: string, pinned: boolean): Promise<boolean> {
    if (!selectedSource || !noteText) return false;
    const response = await fetch(`/api/sources/${selectedSource.id}/contacts/${contactId}/notes/${note.id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ noteText, pinned, version: note.version }),
    });
    if (!response.ok) {
      if (response.status === 409) await loadSourceDetail(selectedSource.id);
      setToast(response.status === 409 ? "That note changed elsewhere. The latest version is now shown." : "The contact note could not be updated.");
      return false;
    }
    const result = (await response.json()) as { data: { version: number; updatedAt: string; updatedBy: string; historyId: string } };
    const historyEntry = { id: result.data.historyId, noteText, pinned, changedBy: result.data.updatedBy, createdAt: result.data.updatedAt };
    setSourceRecords((current) => current.map((source) => source.id === selectedSource.id ? {
      ...source,
      contacts: source.contacts?.map((contact) => contact.id === contactId ? {
        ...contact,
        notes: contact.notes?.map((item) => item.id === note.id ? {
          ...item, noteText, pinned, version: result.data.version, updatedAt: result.data.updatedAt,
          updatedBy: result.data.updatedBy, history: [historyEntry, ...item.history],
        } : item).sort((left, right) => Number(right.pinned) - Number(left.pinned) || right.updatedAt.localeCompare(left.updatedAt)),
      } : contact),
    } : source));
    setToast("Contact note updated");
    return true;
  }

  async function addReferralReceipt(contactId: string, quantity: number, receivedOn: string): Promise<boolean> {
    if (!selectedSource) return false;
    const response = await fetch(`/api/sources/${selectedSource.id}/contacts/${contactId}/referral-receipts`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ quantity, receivedOn }),
    });
    if (!response.ok) {
      setToast("The aggregate referral count could not be saved.");
      return false;
    }
    const result = (await response.json()) as { data: ReferralReceipt };
    setSourceRecords((current) => current.map((source) => source.id === selectedSource.id ? {
      ...source,
      referralTotal: source.referralTotal + quantity,
      contacts: source.contacts?.map((contact) => contact.id === contactId
        ? { ...contact, referralReceipts: [result.data, ...(contact.referralReceipts ?? [])] }
        : contact),
    } : source));
    setToast(`${quantity} referral${quantity === 1 ? "" : "s"} recorded for this contact—no patient information stored.`);
    return true;
  }

  async function associateExistingContact(source: DemoSource, contact: DirectoryContact, mode: "transfer" | "link"): Promise<boolean> {
    const response = await fetch(`/api/sources/${encodeURIComponent(source.id)}/contacts/existing`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ contactId: contact.id, mode }),
    });
    if (!response.ok) {
      const result = await response.json().catch(() => ({})) as { error?: string };
      setToast(result.error ?? "The contact’s employer relationship could not be updated.");
      return false;
    }
    await Promise.all([reloadDirectoryContacts(), loadSourceDetail(source.id)]);
    if (selectedDirectoryContactId === contact.id) await openDirectoryContact(contact.id, true);
    setDataReloadKey((current) => current + 1);
    setToast(mode === "transfer" ? `${contact.name} transferred to ${source.name}` : `${contact.name} linked to ${source.name}`);
    return true;
  }

  async function openAppNotification(notification: AppNotification) {
    if (!notification.readAt) {
      void fetch(`/api/notifications/${notification.id}/read`, { method: "POST" });
      setNotifications((current) => current.map((item) => item.id === notification.id ? { ...item, readAt: new Date().toISOString() } : item));
    }
    if (notification.taskId) {
      navigateToView("today");
      return;
    }
    if (notification.sourceId) await openSource(notification.sourceId);
  }

  async function bulkUpdateSources(ids: string[], changes: BulkSourceChange): Promise<boolean> {
    const selected = sourceRecords.filter((source) => ids.includes(source.id));
    const results = await Promise.all(selected.map(async (source) => {
      const response = await fetch(`/api/sources/${source.id}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          version: source.version,
          ...(changes.category !== undefined ? { categoryCode: changes.category === "Uncategorized" ? null : changes.category } : {}),
          ...(changes.isHospital !== undefined ? { isHospital: changes.isHospital } : {}),
          ...(changes.ownerUserId !== undefined ? { ownerUserId: changes.ownerUserId } : {}),
        }),
      });
      if (!response.ok) return { id: source.id, ok: false, version: source.version };
      const result = (await response.json()) as { data: { version: number } };
      return { id: source.id, ok: true, version: result.data.version };
    }));
    const successful = new Map(results.filter((result) => result.ok).map((result) => [result.id, result.version]));
    const ownerName = changes.ownerUserId ? assignees.find((assignee) => assignee.id === changes.ownerUserId)?.name : undefined;
    setSourceRecords((current) => current.map((source) => successful.has(source.id) ? {
      ...source,
      version: successful.get(source.id) ?? source.version,
      ...(changes.category !== undefined ? { category: changes.category } : {}),
      ...(changes.isHospital !== undefined ? { isHospital: changes.isHospital } : {}),
      ...(changes.ownerUserId !== undefined ? { ownerUserId: changes.ownerUserId, ownerName: ownerName ?? source.ownerName } : {}),
    } : source));
    if (changes.ownerUserId && ownerName) {
      setTasks((current) => current.map((task) => task.sourceId && successful.has(task.sourceId) ? { ...task, assignee: ownerName } : task));
    }
    const failed = results.length - successful.size;
    if (failed) {
      setToast(`${successful.size} sources updated; ${failed} changed elsewhere and need another try.`);
      return false;
    }
    setToast(`${successful.size} source${successful.size === 1 ? "" : "s"} updated.`);
    return true;
  }

  async function archiveSource() {
    if (!editingSource) return false;
    if (auth === "live") {
      const response = await fetch(`/api/sources/${editingSource.id}?version=${editingSource.version}`, { method: "DELETE" });
      if (!response.ok) {
        setToast(response.status === 409 ? "This source changed elsewhere. Reopen it before archiving." : "The source could not be archived.");
        return false;
      }
    }
    const name = editingSource.name;
    setSourceRecords((current) => current.filter((source) => source.id !== editingSource.id));
    setTasks((current) => current.filter((task) => task.entity !== name));
    setEditingSourceId(null);
    setSelectedSourceId(null);
    updateAppHistory(viewPath("sources"), "replace");
    setToast(`${name} archived`);
    return true;
  }

  function applyWorkspaceDefaultContactDays(defaultContactDays: number) {
    setSourceRecords((current) => current.map((source) => {
      if (!source.usesDefaultCadence) return { ...source, defaultContactDays };
      const dayDifference = defaultContactDays - source.effectiveCadenceDays;
      const adjustedNextContact = source.nextContactAt
        ? new Date(new Date(source.nextContactAt).getTime() + dayDifference * 86_400_000).toISOString()
        : undefined;
      return {
        ...source,
        defaultContactDays,
        effectiveCadenceDays: defaultContactDays,
        cadence: cadenceLabel(defaultContactDays, true),
        nextContactAt: adjustedNextContact,
        nextTaskAt: adjustedNextContact ? formatSourceDate(adjustedNextContact) : source.nextTaskAt,
      };
    }));
  }

  async function saveContact(activity: LogContactInput) {
    if (!loggingSource) return false;
    if (loggingTask) {
      const completed = await completeTask(loggingTask, activity);
      if (!completed) return false;
      closeContactLog();
      return true;
    }
    if (auth === "live") {
      const response = await fetch(`/api/sources/${loggingSource.id}/activities`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(activity),
      });
      if (!response.ok) {
        setToast("The contact could not be saved.");
        return false;
      }
    }
    setSourceRecords((current) =>
      current.map((source) =>
        source.id === loggingSource.id
          ? (() => {
              const previousTime = source.lastInteractionAtIso ? new Date(source.lastInteractionAtIso).getTime() : Number.NaN;
              const activityTime = new Date(activity.occurredAt).getTime();
              const latestTime = Number.isNaN(previousTime) ? activityTime : Math.max(previousTime, activityTime);
              const lastInteractionAtIso = new Date(latestTime).toISOString();
              const nextContactAt = new Date(latestTime + source.effectiveCadenceDays * 86_400_000).toISOString();
              return { ...source, lastInteractionAt: formatSourceDate(lastInteractionAtIso), lastInteractionAtIso, nextContactAt, nextTaskAt: formatSourceDate(nextContactAt), activityCount: source.activityCount + 1, referralTotal: source.referralTotal + activity.referrals.length };
            })()
          : source,
      ),
    );
    setDataReloadKey((current) => current + 1);
    closeContactLog();
    setToast(activity.referrals.length ? `Contact and ${activity.referrals.length} referral${activity.referrals.length === 1 ? "" : "s"} logged for ${loggingSource.name}` : `Contact logged for ${loggingSource.name}`);
    return true;
  }

  async function stopImpersonating() {
    const response = await fetch("/api/impersonation/stop", { method: "POST" });
    if (!response.ok) {
      setToast("The administrator session could not be restored.");
      return;
    }
    window.location.assign("/?view=admin");
  }

  async function signOut() {
    setLogoutBusy(true);
    try {
      const response = await fetch("/api/auth/sign-out", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!response.ok) throw new Error("Sign out failed");
      window.history.replaceState({}, "", "/");
      setMobileOpen(false);
      setMember(null);
      setAssignees([]);
      setTasks([]);
      setEvents([]);
      setNetworkingGroups([]);
      setSelectedNetworkingGroupId(null);
      setSelectedNetworkingGroup(null);
      setDirectoryContacts([]);
      setSelectedDirectoryContactId(null);
      setSelectedDirectoryContact(null);
      setSourceRecords([]);
      setSourceActivity([]);
      setLeadRecords([]);
      setSelectedLeadId(null);
      setSelectedLead(null);
      setNotifications([]);
      hasWorkspaceData.current = false;
      workspaceRevision.current = null;
      setAuth("signed-out");
    } catch {
      setToast("You could not be signed out. Please try again.");
    } finally {
      setLogoutBusy(false);
    }
  }

  useEffect(() => {
    document.title = member
      ? `${member.brandName || member.workspaceName || "Referral Tracking"} · Referral Tracking`
      : "Referral Tracking";
  }, [member?.brandName, member?.workspaceName]);

  if (resetToken || resetError) return <SetPassword token={resetToken} errorCode={resetError} />;
  if (auth === "loading" || (auth === "live" && !member)) return <div className="loading-screen"><AppMark /><span className="loading-line" /></div>;
  if (auth === "signed-out" && urlParameters.get("signup") === "1") return <Signup />;
  if (auth === "signed-out" && urlParameters.get("login") === "1") return <SignIn />;
  if (auth === "signed-out") return <MarketingSite />;
  if (!member) return null;
  const tenantTheme = {
    "--tenant-primary": member.brandPrimaryColor || "#4D058F",
    "--tenant-accent": member.brandAccentColor || "#00A99C",
    "--tenant-on-primary": readableForeground(member.brandPrimaryColor || "#4D058F"),
  } as CSSProperties;

  return (
    <div className="app-shell tenant-branded" style={tenantTheme}>
      {mobileOpen && <button className="mobile-nav-scrim" onClick={() => setMobileOpen(false)} aria-label="Close navigation" />}
      <Sidebar view={view} onView={navigateToView} mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} memberRole={member.role} memberName={member.name} memberAvatarUrl={member.avatarUrl} platformRole={member.platformRole} brandName={member.brandName || member.workspaceName || "Referral Tracking"} brandLogoUrl={member.brandLogoUrl ?? null} leadTrackingEnabled={member.individualLeadTrackingEnabled} sourceLabelPlural={member.sourceLabelPlural} leadLabelPlural={member.leadLabelPlural} organizationIndependent={member.organizationIndependent} onLogout={() => void signOut()} logoutBusy={logoutBusy} />
      <div className="app-main">
        {member.impersonation && <div className="impersonation-banner"><span><LogIn />Viewing Referral Source Tracker as <strong>{member.name}</strong></span><button onClick={() => void stopImpersonating()}>Return to {member.impersonation.administrator.name}</button></div>}
        <Topbar view={view} onMenu={() => setMobileOpen(true)} onCreateContact={() => setContactEditor("new")} onCreateSource={() => setCreating(true)} onNotice={setToast} notifications={notifications} onOpenNotification={(notification) => void openAppNotification(notification)} canCreateContact={view !== "help" && view !== "admin" && view !== "platform" && (canCreateSources(member.role) || sourceRecords.some((source) => canAccessSource(member.role, member.userId, source.ownerUserId ?? null)))} canCreateSource={view !== "help" && view !== "admin" && view !== "platform" && canCreateSources(member.role)} onHelp={() => openHelp(currentViewHelpTopic())} brandName={member.brandName || member.workspaceName || "Referral Tracking"} sourceLabelSingular={member.sourceLabelSingular} sourceLabelPlural={member.sourceLabelPlural} leadLabelPlural={member.leadLabelPlural} freshness={freshness} />
        {selectedSourceId && (sourceDetailLoading || !selectedSource) && <div className="page simple-page contact-profile-loading"><span className="loading-line" /><strong>Loading referral source…</strong></div>}
        {!sourceDetailLoading && selectedSource && <SourceDetailPage source={selectedSource} canManage={canAccessSource(member.role, member.userId, selectedSource.ownerUserId ?? null)} canLog={canLogSourceActivity(member.role, member.userId, selectedSource.ownerUserId ?? null)} canCollaborate={canCollaborateOnSources(member.role)} events={events.filter((event) => event.sourceId === selectedSource.id)} teamMembers={assignees} currentUserId={member.userId} directoryContacts={directoryContacts} onAssociateContact={(contact, mode) => associateExistingContact(selectedSource, contact, mode)} onAddContactNote={addContactNote} onUpdateContactNote={updateContactNote} onAddReferralReceipt={addReferralReceipt} onUploadAttachment={(file, eventId, caption) => uploadSourceAttachment(selectedSource.id, file, eventId, caption)} onUpdateAttachment={(attachment, eventId, caption) => updateSourceAttachment(selectedSource.id, attachment, eventId, caption)} onDeleteAttachment={(attachment) => deleteSourceAttachment(selectedSource.id, attachment)} onBack={closeSourceDetail} onLog={() => openContactLog(selectedSource.id)} onSchedule={() => setTaskEditor({ sourceId: selectedSource.id })} onEdit={() => void startEditingSource(selectedSource.id)} onSaveDescription={(description, color) => saveSourceDescription(selectedSource, description, color)} />}
        {!selectedSourceId && <>
        {view === "sources" && <SourcesPage sourceRecords={sourceRecords} tasks={tasks} currentUserId={member.userId} role={member.role} assignees={assignees} onOpen={openSource} onLog={(sourceId) => openContactLog(sourceId)} onContacted={openTaskContactLog} onSourceContacted={(source) => openContactLog(source.id)} onSnooze={setSnoozingFollowUp} onBulkUpdate={bulkUpdateSources} onViewTasks={() => navigateToView("today")} sourceLabelSingular={member.sourceLabelSingular} sourceLabelPlural={member.sourceLabelPlural} />}
        {view === "contacts" && contactProfileLoading && <div className="page simple-page contact-profile-loading"><span className="loading-line" /><strong>Loading contact profile…</strong></div>}
        {view === "contacts" && !contactProfileLoading && selectedDirectoryContact && <ContactProfilePage profile={selectedDirectoryContact} canEdit={canEditDirectoryContact(member.role, member.userId, selectedDirectoryContact)} canLog={canLogSourceActivity(member.role, member.userId, selectedDirectoryContact.ownerUserId ?? null)} canAddNote={canCollaborateOnSources(member.role)} canRecordEvent={canManageEvents(member.role)} onBack={closeDirectoryContact} onEdit={() => setContactEditor(selectedDirectoryContact)} onOpenSource={openSource} onOutreach={() => { sessionStorage.setItem("growth-outreach-contact", selectedDirectoryContact.id); navigateToView("growth"); }} onLogInteraction={() => setLoggingDirectoryContact(selectedDirectoryContact)} onAddNote={() => setContactNoteEditor(selectedDirectoryContact)} onRecordEvent={() => { setEventEditorGroupId(null); setEventEditorContactId(selectedDirectoryContact.id); setEventEditor("new"); }} onUploadPhotos={(files) => uploadDirectoryContactPhotos(selectedDirectoryContact, files)} onDeletePhoto={(photo) => deleteDirectoryContactPhoto(selectedDirectoryContact, photo)} />}
        {view === "contacts" && !contactProfileLoading && !selectedDirectoryContactId && <ContactsPage contacts={directoryContacts} canAdd={canCreateSources(member.role) || sourceRecords.some((source) => canAccessSource(member.role, member.userId, source.ownerUserId ?? null))} canScan={canCollaborateOnSources(member.role)} canReviewDuplicates={member.role === "admin"} canEdit={(contact) => canEditDirectoryContact(member.role, member.userId, contact)} onAdd={() => setContactEditor("new")} onScan={() => setBusinessCardScannerOpen(true)} onReviewDuplicates={() => setDuplicateReviewOpen(true)} onEdit={setContactEditor} onOpenContact={(contactId) => void openDirectoryContact(contactId)} onOpenSource={openSource} onNotice={setToast} />}
        {view === "leads" && member.individualLeadTrackingEnabled && <LeadsPage leads={leadRecords} features={member} canEdit={member.role !== "reports"} loading={dataState === "loading"} onCreate={() => setCreatingLead(true)} onOpen={(lead) => void openLead(lead)} leadLabelSingular={member.leadLabelSingular} leadLabelPlural={member.leadLabelPlural} />}
        {view === "today" && <FollowUps tasks={tasks} sources={member.role === "task_user" ? [] : sourceRecords} activity={sourceActivity} dataState={dataState} onContacted={openTaskContactLog} onCompleteTask={(task) => void completeTask(task)} onSourceContacted={(source) => openContactLog(source.id)} onOpenSource={openSource} onSnooze={setSnoozingFollowUp} onCreateTask={() => setTaskEditor({})} onEditTask={(task) => setTaskEditor({ task })} onMoveTask={moveTask} onViewSources={() => navigateToView("sources")} onRetry={() => setDataReloadKey((current) => current + 1)} canCreateTask={canCreateTasks(member.role)} />}
        {view === "events" && selectedEventId && !selectedEvent && <div className="page simple-page contact-profile-loading"><span className="loading-line" /><strong>Loading event details…</strong></div>}
        {view === "events" && selectedEvent && <EventDetailPage event={selectedEvent} canManage={canManageEvents(member.role)} onBack={closeEventDetail} onEdit={() => setEventEditor(selectedEvent)} onManageContacts={() => { setEventAddedContactId(null); setEventAttendeeManagerOpen(true); }} onOpenSource={(sourceId) => void openSource(sourceId)} onOpenGroup={(groupId) => void openNetworkingGroup(groupId)} onOpenContact={(contactId) => void openDirectoryContact(contactId)} />}
        {view === "events" && networkingGroupLoading && <div className="page simple-page contact-profile-loading"><span className="loading-line" /><strong>Loading networking group…</strong></div>}
        {view === "events" && !networkingGroupLoading && selectedNetworkingGroup && <NetworkingGroupProfilePage group={selectedNetworkingGroup} contacts={directoryContacts} canManage={canManageEvents(member.role)} onBack={closeNetworkingGroup} onEdit={() => setNetworkingGroupEditor(selectedNetworkingGroup)} onSchedule={() => scheduleNetworkingGroupMeeting(selectedNetworkingGroup)} onEditEvent={setEventEditor} onOpenContact={(contactId) => void openDirectoryContact(contactId)} onAddContact={addNetworkingGroupContact} onSaveNotes={saveNetworkingGroupNotes} />}
        {view === "events" && !selectedEventId && !networkingGroupLoading && !selectedNetworkingGroupId && <EventsPage events={events} groups={networkingGroups} canManage={canManageEvents(member.role)} section={eventsSection} onSectionChange={changeEventsSection} onCreate={() => { setEventEditorGroupId(null); setEventEditor("new"); }} onOpenEvent={openEventDetail} onEdit={setEventEditor} onOpenSource={openSource} onOpenGroup={(groupId) => void openNetworkingGroup(groupId)} onCreateGroup={() => setNetworkingGroupEditor("new")} />}
        {view === "growth" && <Suspense fallback={<div className="page simple-page contact-profile-loading"><span className="loading-line" /><strong>Loading Growth Operations…</strong></div>}><GrowthCenter onOpenSource={openSource} onOpenContact={(contactId) => void openDirectoryContact(contactId)} /></Suspense>}
        {view === "reports" && <ReportsPage sourceRecords={sourceRecords} tasks={tasks} onOpenSource={openSource} onOpenContact={(contactId) => void openDirectoryContact(contactId)} showTeamReports={canViewTeamReports(member.role)} />}
        {view === "admin" && member.role === "admin" && <AdminPage onWorkspaceSaved={(settings) => { applyWorkspaceDefaultContactDays(settings.defaultSourceContactDays); const vertical = verticalForCode(settings.verticalCode); setMember((current) => current ? { ...current, workspaceName: settings.name, brandName: settings.brandName, brandLogoUrl: settings.brandLogoUrl, brandPrimaryColor: settings.brandPrimaryColor, brandAccentColor: settings.brandAccentColor, verticalCode: vertical.code, sourceLabelSingular: vertical.sourceLabelSingular, sourceLabelPlural: vertical.sourceLabelPlural, leadLabelSingular: vertical.leadLabelSingular, leadLabelPlural: vertical.leadLabelPlural, leadPipelineEnabled: settings.leadPipelineEnabled, leadContactDetailsEnabled: settings.leadContactDetailsEnabled, leadActivityTrackingEnabled: settings.leadActivityTrackingEnabled, individualLeadTrackingEnabled: individualLeadTrackingEnabled(settings) } : current); }} currentUserId={member.userId} onCurrentAvatarChange={(avatarUrl) => setMember((current) => current ? { ...current, avatarUrl } : current)} onOpenSource={openSource} onOpenContact={(contactId) => void openDirectoryContact(contactId)} onReviewDuplicates={() => setDuplicateReviewOpen(true)} />}
        {view === "platform" && member.platformRole === "superadmin" && <PlatformDashboard />}
        {view === "help" && <HelpCenter selectedTopicId={helpTopicId} onSelectTopic={openHelp} />}
        </>}
      </div>
      {selectedLeadId && leadLoading && <div className="drawer-layer"><button className="drawer-scrim" onClick={closeLeadDetail} aria-label="Close lead" /><aside className="drawer lead-drawer"><div className="settings-loading"><span className="loading-line" /><p>Loading lead details…</p></div></aside></div>}
      {selectedLeadId && selectedLead && !leadLoading && <LeadDrawer lead={selectedLead} features={member} canEdit={member.role !== "reports"} onClose={closeLeadDetail} onStatusChange={changeLeadStatus} onLogActivity={() => setLoggingLeadActivity(true)} />}
      {creatingLead && <LeadEditorModal features={member} sources={sourceRecords} assignees={assignees} lookups={lookupOptions} onClose={() => setCreatingLead(false)} onSave={createLead} />}
      {loggingLeadActivity && selectedLead && <LeadActivityModal lead={selectedLead} activityTypes={activityTypes} onClose={() => setLoggingLeadActivity(false)} onSave={saveLeadActivity} />}
      {creating && <SourceEditorModal categories={sourceCategories} defaultContactDays={workspaceDefaultContactDays} currentUser={member} assignees={assignees} canAssign={member.role === "admin"} onClose={() => setCreating(false)} onSave={saveSource} sourceLabelSingular={member.sourceLabelSingular} />}
      {editingSource && <SourceEditorModal source={editingSource} categories={sourceCategories} defaultContactDays={editingSource.defaultContactDays} currentUser={member} assignees={assignees} canAssign={member.role === "admin"} onClose={() => setEditingSourceId(null)} onSave={updateSource} onArchive={archiveSource} sourceLabelSingular={member.sourceLabelSingular} />}
      {loggingSource && <LogContactModal source={loggingSource} contact={loggingSourceContactId ? directoryContacts.find((contact) => contact.id === loggingSourceContactId) : undefined} contacts={directoryContacts.filter((contact) => contact.sourceId === loggingSource.id)} events={events} followUpTitle={loggingTask?.title} activityTypes={activityTypes} teamMembers={assignees} currentUserId={member.userId} onClose={closeContactLog} onSave={saveContact} />}
      {loggingDirectoryContact && sourceRecords.find((source) => source.id === loggingDirectoryContact.sourceId) && <LogContactModal source={sourceRecords.find((source) => source.id === loggingDirectoryContact.sourceId)!} contact={loggingDirectoryContact} contacts={directoryContacts.filter((contact) => contact.sourceId === loggingDirectoryContact.sourceId)} events={events} activityTypes={activityTypes} teamMembers={assignees} currentUserId={member.userId} onClose={() => setLoggingDirectoryContact(null)} onSave={saveDirectoryContactInteraction} />}
      {contactNoteEditor && <ContactNoteModal contact={contactNoteEditor} teamMembers={assignees} currentUserId={member.userId} onClose={() => setContactNoteEditor(null)} onSave={saveDirectoryContactNote} />}
      {snoozingFollowUp && <SnoozeFollowUpModal sourceName={snoozingFollowUp.entity} onClose={() => setSnoozingFollowUp(null)} onSave={saveSnooze} />}
      {taskEditor && <TaskEditorModal task={taskEditor.task} sourceId={taskEditor.sourceId} sources={sourceRecords} assignees={assignees} currentUser={member} canAssign={canAssignTasks(member.role)} onClose={() => setTaskEditor(null)} onSave={saveTask} />}
      {eventEditor && <EventEditorModal event={eventEditor === "new" ? undefined : eventEditor} initialGroup={eventEditor === "new" && eventEditorGroupId ? networkingGroups.find((group) => group.id === eventEditorGroupId) ?? selectedNetworkingGroup ?? undefined : undefined} initialContact={eventEditor === "new" && eventEditorContactId ? directoryContacts.find((contact) => contact.id === eventEditorContactId) ?? selectedDirectoryContact ?? undefined : undefined} newlyCreatedContactId={eventAddedContactId ?? undefined} canAddContacts={sourceRecords.some((source) => canAccessSource(member.role, member.userId, source.ownerUserId ?? null))} groups={networkingGroups} sources={sourceRecords} contacts={directoryContacts} teamMembers={assignees} currentUserId={member.userId} onAddContact={() => setEventContactEditorOpen(true)} onPhotoDeleted={removeEventPhotoLocal} onClose={() => { setEventEditor(null); setEventEditorGroupId(null); setEventEditorContactId(null); setEventContactEditorOpen(false); setEventAddedContactId(null); }} onSave={saveEvent} />}
      {selectedEvent && eventAttendeeManagerOpen && <EventContactAttendeesModal event={selectedEvent} contacts={directoryContacts} newlyCreatedContactId={eventAddedContactId ?? undefined} canAddContacts={sourceRecords.some((source) => canAccessSource(member.role, member.userId, source.ownerUserId ?? null))} onAddContact={() => setEventContactEditorOpen(true)} onClose={() => { setEventAttendeeManagerOpen(false); setEventContactEditorOpen(false); setEventAddedContactId(null); }} onSave={(contactIds) => saveEventContactAttendees(selectedEvent, contactIds)} />}
      {(eventEditor || eventAttendeeManagerOpen) && eventContactEditorOpen && <ContactEditorModal context="event" sources={sourceRecords.filter((source) => canAccessSource(member.role, member.userId, source.ownerUserId ?? null))} onClose={() => setEventContactEditorOpen(false)} onSave={saveEventContact} />}
      {networkingGroupEditor && <NetworkingGroupEditorModal group={networkingGroupEditor === "new" ? undefined : networkingGroupEditor} onClose={() => setNetworkingGroupEditor(null)} onSave={saveNetworkingGroup} />}
      {contactEditor && <ContactEditorModal contact={contactEditor === "new" ? undefined : contactEditor} sources={sourceRecords.filter((source) => canAccessSource(member.role, member.userId, source.ownerUserId ?? null))} categories={sourceCategories} canCreateSource={canCreateSources(member.role)} sourceLabelSingular={member.sourceLabelSingular} onScanBusinessCard={contactEditor === "new" && canCollaborateOnSources(member.role) ? () => { setContactEditor(null); setBusinessCardScannerOpen(true); } : undefined} onClose={() => setContactEditor(null)} onSave={saveDirectoryContact} />}
      {businessCardScannerOpen && <BusinessCardScannerModal sources={sourceRecords.filter((source) => canAccessSource(member.role, member.userId, source.ownerUserId ?? null))} contacts={directoryContacts} categories={sourceCategories} canCreateSource={canCreateSources(member.role)} sourceLabelSingular={member.sourceLabelSingular} onClose={() => setBusinessCardScannerOpen(false)} onSave={saveScannedBusinessCard} />}
      {postScanAction && <PostScanNextStepsModal saved={postScanAction} onClose={() => setPostScanAction(null)} onViewContact={() => { const saved = postScanAction; setPostScanAction(null); void openDirectoryContact(saved.contactId); }} onLogInteraction={() => { const saved = postScanAction; setPostScanAction(null); openContactLog(saved.sourceId, undefined, saved.contactId); }} onScheduleFollowUp={() => { const saved = postScanAction; setPostScanAction(null); setTaskEditor({ sourceId: saved.sourceId }); }} onAddToEvent={() => { const saved = postScanAction; setPostScanAction(null); setEventEditorGroupId(null); setEventEditorContactId(saved.contactId); setEventEditor("new"); }} />}
      {duplicateReviewOpen && <DuplicateReviewModal onClose={() => setDuplicateReviewOpen(false)} onMerged={refreshAfterDuplicateMerge} onNotice={setToast} />}
      {toast && <Toast text={toast} />}
    </div>
  );
}
